import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";

const FONT = `@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');`;

// ── SUPABASE CONFIG ───────────────────────────────────────────

// tenant_id atual — definido no login
let CURRENT_TENANT = "kevin_admin";
const setTenant = (id) => { CURRENT_TENANT = id || "kevin_admin"; };

const getTenant = () => { try { const u = JSON.parse(sessionStorage.getItem("tfx_user")); if (u?.tenant_id) return u.tenant_id; } catch(e) {} return CURRENT_TENANT; };

const autoLancarCaixa = async (tipo, descricao, valor, data, categoria = "operacional", config = {}) => {
  // Verifica se deve lançar automaticamente baseado no modo configurado
  if (categoria === "vendas_loja") {
    if (config?.vendas_auto === false) return;
  }
  if (categoria === "gastos_loja") {
    if (config?.gastos_auto === false) return;
  }
  if (categoria === "trafego") {
    if (config?.trafego_auto === false) return;
  }
  if (categoria === "compras_fornecedores") {
    if (config?.compras_auto === false) return;
  }
  
  try {
    const row = { tenant_id: getTenant(), tipo, categoria, descricao, valor: Number(valor) || 0, data };
    await fetch(`${API_BASE}?table=caixa_movimentos`, {
      method: "POST",
      headers: { Authorization: "Bearer " + getJWT(), "Content-Type": "application/json", Prefer: "return=minimal" },
      body: JSON.stringify(row)
    });
  } catch(e) { console.warn("Caixa auto-lançamento falhou:", e.message); }
};
const API_BASE = "https://gestao-tfx-saas.vercel.app/api/data";

const getJWT = () => {
  try { return sessionStorage.getItem("tfx_token") || ""; } catch(e) { return ""; }
};

// Gravacoes na mesma linha (tabela+id) saem em fila, na ordem da chamada.
// Evita que dois PATCH paralelos (ex.: devolver estoque e depois baixar) cheguem fora de ordem.
const _filaUpdate = {};

const sb = {
  async get(table) {
    const order = table === "clientes" ? "criado_em.asc" : "created_at.asc";
    const r = await fetch(`${API_BASE}?table=${table}&order=${order}`, {
      headers: { Authorization: `Bearer ${getJWT()}` }
    });
    if (r.status === 401) { sessionStorage.removeItem("tfx_user"); sessionStorage.removeItem("tfx_token"); window.location.reload(); return []; }
    return r.json();
  },
  async insert(table, row) {
    const r = await fetch(`${API_BASE}?table=${table}`, {
      method: "POST",
      headers: { Authorization: `Bearer ${getJWT()}`, "Content-Type": "application/json", Prefer: "return=representation" },
      body: JSON.stringify(row)
    });
    if (r.status === 401) { sessionStorage.removeItem("tfx_user"); sessionStorage.removeItem("tfx_token"); window.location.reload(); return []; }
    const data = await r.json();
    return Array.isArray(data) ? data : [data];
  },
  update(table, id, row) {
    const k = table + ":" + id;
    const anterior = _filaUpdate[k] || Promise.resolve();
    const atual = anterior.catch(() => {}).then(() => sb._update(table, id, row));
    _filaUpdate[k] = atual;
    atual.catch(() => {}).then(() => { if (_filaUpdate[k] === atual) delete _filaUpdate[k]; });
    return atual;
  },
  async _update(table, id, row) {
    const r = await fetch(`${API_BASE}?table=${table}&id=eq.${id}`, {
      method: "PATCH",
      headers: { Authorization: `Bearer ${getJWT()}`, "Content-Type": "application/json", Prefer: "return=representation" },
      body: JSON.stringify(row)
    });
    if (r.status === 401) { sessionStorage.removeItem("tfx_user"); sessionStorage.removeItem("tfx_token"); window.location.reload(); return []; }
    return r.json();
  },
  async remove(table, id) {
    const r = await fetch(`${API_BASE}?table=${table}&id=eq.${id}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${getJWT()}` }
    });
    if (r.status === 401) { sessionStorage.removeItem("tfx_user"); sessionStorage.removeItem("tfx_token"); window.location.reload(); return { ok: false, status: 401 }; }
    if (!r.ok) {
      let msg = "Erro ao excluir";
      try { const err = await r.json(); msg = err.error || msg; } catch(e) {}
      alert("Nao foi possivel excluir: " + msg);
      return { ok: false, status: r.status, error: msg };
    }
    return { ok: true };
  }
};

// ── DATA INICIAL ──────────────────────────────────────────────
const initialData = {
  vendas: [],
  gastos: [],
  estoque: [],
  compras: [],
  kits: [],
  adiantamentos: [],
};

// ── HELPERS ───────────────────────────────────────────────────
const fmt = (v) => (Number(v) || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const pct = (v) => (v * 100).toFixed(1) + "%";

// ── CORES & ESTILOS ───────────────────────────────────────────
const TEMAS = {
  dark_gold:   { bg:"#f0f2f5", surface:"#f8fafc", card:"rgba(255, 255, 255, 0.05)", border:"rgba(255, 255, 255, 0.1)", accent:"#111827", accentDim:"#FFB80020", accentMid:"#FFB80050", warn:"#b91c1c", warnDim:"#fb923c20", info:"#374151", infoDim:"#3b82f620", text:"#e4e4e7", muted:"#a1a1aa", gold:"#111827" },
  dark_blue:   { bg:"#080C14", surface:"#f0f2f5", card:"#f0f2f5", border:"#1A3050", accent:"#4B8EF0", accentDim:"#4B8EF020", accentMid:"#4B8EF050", warn:"#C0392B", warnDim:"#C0392B20", info:"#3A7BD5", infoDim:"#3A7BD520", text:"#E8F0FF", muted:"#3A4E6B", gold:"#60C8FF" },
  dark_purple: { bg:"#0A0810", surface:"#ffffff", card:"#ffffff", border:"#2A1F40", accent:"#9B6DFF", accentDim:"#9B6DFF20", accentMid:"#9B6DFF50", warn:"#C0392B", warnDim:"#C0392B20", info:"#7B52E0", infoDim:"#7B52E020", text:"#EDE8FF", muted:"#4A3860", gold:"#C89BFF" },
  dark_green:  { bg:"#060E0A", surface:"#f0f2f5", card:"#f0f2f5", border:"#1A3A22", accent:"#2ECC71", accentDim:"#2ECC7120", accentMid:"#2ECC7150", warn:"#C0392B", warnDim:"#C0392B20", info:"#15803d", infoDim:"#27AE6020", text:"#E0F5E8", muted:"#2A4A35", gold:"#7EFFC0" },
  dark_red:    { bg:"#0E0608", surface:"#ffffff", card:"#ffffff", border:"#3A1A1A", accent:"#E74C3C", accentDim:"#E74C3C20", accentMid:"#E74C3C50", warn:"#C0392B", warnDim:"#C0392B20", info:"#C0392B", infoDim:"#C0392B20", text:"#FFE8E8", muted:"#4A2020", gold:"#FF8080" },
  dark_orange: { bg:"#0E0A06", surface:"#ffffff", card:"#ffffff", border:"#3A2010", accent:"#F97316", accentDim:"#F9731620", accentMid:"#F9731650", warn:"#C0392B", warnDim:"#C0392B20", info:"#EA6B0A", infoDim:"#EA6B0A20", text:"#FFF0E0", muted:"#4A3010", gold:"#FFAA60" },
  dark_pink:   { bg:"#0E0610", surface:"#ffffff", card:"#ffffff", border:"#3A1030", accent:"#EC4899", accentDim:"#EC489920", accentMid:"#EC489950", warn:"#C0392B", warnDim:"#C0392B20", info:"#DB2777", infoDim:"#DB277720", text:"#FFE8F8", muted:"#4A2040", gold:"#FF90C8" },

};

const C = {
    bg: "#f0f2f5",
    bgElevated: "#ffffff",
    surface: "#f8fafc",
    card: "rgba(0, 0, 0, 0.0)",
    hover: "rgba(0, 0, 0, 0.04)",
    text: "#111827",
    muted: "#6b7280",
    dim: "#9ca3af",
    border: "rgba(0, 0, 0, 0.06)",
    accent: "#111827",  // cor principal - preto
    gold: "#374151",    // cinza escuro
    success: "#15803d", // verde
    warn: "#b91c1c",    // vermelho (gastos/perda)
    danger: "#b91c1c",
    primary: "#111827",
    info: "#374151"
  };

function aplicarTema(tema) {
  const t = TEMAS[tema] || TEMAS.dark_gold;
  Object.assign(C, t);
  document.body.style.background = t.bg;
  document.body.style.color = t.text;
}

const styles = {
  app: {
    fontFamily: "'Inter', sans-serif",
    background: "#f0f2f5",
    minHeight: "100vh",
    color: "#111827",
    display: "flex",
  },
  sidebar: {
    width: 210,
    minHeight: "100vh",
    background: "#f0f2f5",
    borderRight: "1px solid #e5e7eb",
    display: "flex",
    flexDirection: "column",
    padding: "0 0 24px 0",
    position: "sticky",
    top: 0,
    flexShrink: 0,
    boxShadow: `4px 0 24px ${C.border}60`,
  },
  logo: {
    padding: "24px 16px 20px",
    borderBottom: "1px solid #f3f4f6",
    marginBottom: 8,
  },
  logoText: {
    fontFamily: "'Inter', sans-serif",
    fontWeight: 800,
    fontSize: 15,
    color: "#111827",
    letterSpacing: -0.3,
  },
  logoSub: { fontSize: 9, color: "#374151", letterSpacing: 2, textTransform: "uppercase", marginTop: 2 },
  navItem: (active) => ({
    display: "flex",
    alignItems: "center",
    gap: 9,
    padding: "8px 10px",
    margin: "1px 8px",
    borderRadius: 8,
    cursor: "pointer",
    fontSize: 12,
    letterSpacing: 0.3,
    color: active ? C.accent : C.muted,
    background: active ? C.accentDim : "transparent",
    transition: "all 0.15s",
    fontWeight: active ? 600 : 400,
  }),
  main: { flex: 1, padding: "0 32px 32px", overflow: "auto" },
  pageTitle: {
    fontFamily: "'Inter', sans-serif",
    fontWeight: 800,
    fontSize: 24,
    letterSpacing: -0.8,
    color: "#111827",
    marginBottom: 4,
  },
  pageSub: { fontSize: 11, color: "#374151", letterSpacing: 1, textTransform: "uppercase", marginBottom: 28 },
  grid: (cols) => ({
    display: "grid",
    gridTemplateColumns: `repeat(${cols}, 1fr)`,
    gap: 16,
    marginBottom: 24,
  }),
  card: {
    background: C.bgElevated,
    border: `1px solid ${C.border}25`,
    borderRadius: 16,
    padding: 20,
    boxShadow: "0 2px 12px rgba(0,0,0,0.4)",
    transition: "border-color 0.15s",
  },
  kpiCard: (color = C.accent) => ({
    background: C.bgElevated,
    border: `1px solid ${C.border}25`,
    borderRadius: 16,
    padding: 20,
    position: "relative",
    overflow: "hidden",
    boxShadow: "0 2px 12px rgba(0,0,0,0.4)",
    borderTop: `2px solid ${color}`,
  }),
  kpiLabel: { fontSize: 10, color: "#374151", letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 10, fontWeight: 500 },
  kpiValue: (color = C.accent) => ({
    fontFamily: "'Inter', sans-serif",
    fontWeight: 800,
    fontSize: 26,
    color,
    letterSpacing: -1,
    lineHeight: 1.1,
  }),
  kpiSub: { fontSize: 11, color: "#374151", marginTop: 4 },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 12 },
  th: {
    textAlign: "left",
    padding: "8px 12px",
    borderBottom: "1px solid #e5e7eb",
    color: "#374151",
    fontSize: 10,
    letterSpacing: 1.5,
    textTransform: "uppercase",
    fontWeight: 600,
  },
  td: { padding: "11px 12px", borderBottom: "1px solid #f3f4f6", transition: "background 0.1s" },
  badge: (color) => ({
    display: "inline-block",
    padding: "2px 8px",
    borderRadius: 4,
    fontSize: 10,
    fontWeight: 500,
    background: color + "20",
    color,
    letterSpacing: 0.5,
  }),
  sectionTitle: {
    fontFamily: "'Inter', sans-serif",
    fontWeight: 700,
    fontSize: 14,
    color: "#111827",
    marginBottom: 14,
    letterSpacing: -0.3,
  },
  btn: (variant = "primary") => ({
    padding: "9px 18px",
    borderRadius: 8,
    cursor: "pointer",
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: 0.5,
    fontFamily: "'Inter', sans-serif",
    background: variant === "primary" ? "#111827" : variant === "danger" ? "#b91c1c" : "#ffffff",
    color: variant === "primary" ? "#ffffff" : variant === "danger" ? "#fff" : "#374151",
    border: variant === "secondary" ? "1px solid #e5e7eb" : "none",
    transition: "all 0.15s",
  }),
  input: {
    background: C.bgElevated,
    border: "1px solid #e5e7eb",
    borderRadius: 8,
    padding: "9px 13px",
    color: "#111827",
    fontSize: 12,
    fontFamily: "'Inter', sans-serif",
    width: "100%",
    outline: "none",
    transition: "border-color 0.15s, box-shadow 0.15s",
  },
  label: { fontSize: 10, color: "#374151", letterSpacing: 1, textTransform: "uppercase", marginBottom: 4, display: "block" },
};

// ── MESES ─────────────────────────────────────────────────────
const MESES = ["Jan","Fev","Mar","Abr","Mai","Jun","Jul","Ago","Set","Out","Nov","Dez"];
const getMes = (data) => data ? data.slice(0,7) : "";
const hojeManaus = () => { const d = new Date(Date.now() - 4*60*60*1000); return d.toISOString().slice(0,10); };

// ── ALÍQUOTA DE IMPOSTO DO TRÁFEGO (por loja; vazio = 13.88) ──
const ALIQUOTA_TRAFEGO_PADRAO = 13.88;
function aliquotaTrafego(cfg) {
  const v = cfg ? cfg.imposto_trafego : null;
  if (v === null || v === undefined || v === "") return ALIQUOTA_TRAFEGO_PADRAO;
  const n = Number(v);
  return Number.isFinite(n) && n >= 0 && n <= 100 ? n : ALIQUOTA_TRAFEGO_PADRAO;
}
function fatorTrafego(cfg) { return Math.round((1 + aliquotaTrafego(cfg) / 100) * 1e6) / 1e6; }
function fmtAliquota(cfg) { return String(aliquotaTrafego(cfg)); }

// ── INFERENCIA DE GENERO ──────────────────────────────────────
const NOMES_FEMININOS = new Set(["ana","maria","julia","juliana","beatriz","amanda","fernanda","camila","carolina","aline","adriana","jessica","patricia","gabriela","luciana","thais","alessandra","vanessa","renata","claudia","debora","natalia","larissa","leticia","mariana","viviane","tatiane","simone","cristiane","elaine","fabiana","karine","priscila","isabel","luzia","rosa","marta","alice","helena","luana","bruna","sabrina","jaqueline","danielle","monique","carla","roberta","silvia","vera","tereza","irene","cintia","flavia","gisele","lidiane","lorena","maira","nathalia","paloma","raquel","samara","suelen","tania","valdete","yara","zelia","andressa","bianca","celia","dalva","edna","elisa","eulalia","fatima","gilda","heloise","ines","jacira","keila","leila","ligia","milena","miriam","nadia","odete","otilia","pricila","rejane","selma","sonia","sueli","terezinha","valdirene","walkiria","zenaide","aparecida","benedita","conceicao","dilma","eunice","florinda","gracinda","hilda","iracema","joana","leonora","madalena","nair","olga","perpétua","quiteria","rosalia","sebastiana","sebastiana","telma","ursula","vanda","wanda","xena","yolanda","zilda"]);
const NOMES_MASCULINOS = new Set(["joao","jose","antonio","carlos","paulo","pedro","lucas","marcos","luis","gabriel","rafael","daniel","marcelo","rodrigo","manoel","francisco","eduardo","andre","felipe","sergio","alexandre","anderson","diego","gustavo","leandro","fabio","thiago","vinicius","wagner","wallace","wanderson","wellington","weverton","wesley","wilder","william","wilson","wladimir","alan","alex","alexsandro","alisson","allan","almir","aloysio","alvaro","alves","amadeus","amaro","americo","amilton","amilcar","amintas","ampelio","amorim","andr","andrei","andrews","anibal","antenor","arcenio","argemiro","arildo","ariovaldo","aristides","armando","arnaldo","arnaldo","arney","arnoldo","artur","augusto","aurelio","bento","bernardo","caio","cesar","cicero","claudio","cleber","cleiton","cleuber","clodovil","clóvis","colombo","cristiano","dario","davi","david","denilson","denison","deoclecio","diogo","domingos","edemir","ederlando","edgard","edilson","edmilson","edmundo","edson","edvaldo","edvaldo","edvaldo","elio","elizeu","emerson","emilio","enoque","erico","ernani","ernesto","erony","evandro","ewerton","ezequiel","fabricio","fabrício","fausto","feliciano","felicio","felix","fernado","flavio","floriano","fortunato","gelson","genivaldo","gerson","gilberto","gilmar","gilson","givanildo","gladiston","glauco","gleisson","glênio","gonçalo","graciliano","helvecio","heriberto","hermano","herminio","hilario","hilton","humberto","igor","ilario","iran","irineu","italo","ivan","ivaldo","ivanildo","ivo","jacinto","jailson","jair","jairinho","jairo","janio","jarbas","jeferson","jefferson","jeroni","jeronimo","jilvan","joel","jonathas","jonatas","jorge","josias","josue","jovino","juarez","jubileu","julierme","julio","junior","jurandir","laercio","lazaro","lenilson","leonaldo","leonel","leônidas","levi","linaldo","lineu","livaldo","livio","lourenco","luciano","lucidio","lucio","luiz","luther","machado","manassés","marciano","marcio","mario","marivaldo","marlon","marvio","mauro","maximiano","messias","micael","miguel","milton","moacir","moises","murilo","neldinho","nelinho","nelson","neto","nildo","nilson","nilton","nizo","norberto","odair","odilon","odivaldo","odmar","olavo","oldemar","olimpio","olivaldo","olivio","omar","onaldo","onivaldo","oraci","orestes","orlando","orlandogon","osmar","osmario","osvaldo","otavio","ozias","paulo","plínio","quirino","raimundo","reinaldo","reginaldo","renato","reynaldo","ricardino","richard","rinaldo","rivaldo","roberto","rogerio","ronan","ronaldo","ronildo","ronivaldo","roque","roris","rosemiro","rui","sabino","salomao","salustiano","samuel","sandoval","sandrino","sandro","saulo","sebastiao","senator","silvano","silvestre","silverio","silvio","sipriano","socrates","solano","solon","tadeu","tarcisio","tassio","teobaldo","teodoro","timoteo","tito","tobias","ulisses","valdemar","valdenor","valdir","valdomiro","valentim","valter","vanderlei","vanderlino","vanil","vasco","venancio","venildo","veridiano","vicencio","vicente","vilmar","vilson","vitor","waltinho","wanderley","wanderlon","wanildo","warinton","werlon","werton","ximenes","zacarias","zelino","zenobio","zumbi"]);

function inferirGenero(nome) {
  if (!nome) return 'desconhecido';
  const primeiro = nome.trim().toLowerCase().split(' ')[0].normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  if (NOMES_FEMININOS.has(primeiro)) return 'feminino';
  if (NOMES_MASCULINOS.has(primeiro)) return 'masculino';
  return 'desconhecido';
}

const _cacheGenero = {};
async function buscarGeneroIBGE(nome) {
  const primeiro = (nome || "").trim().split(/\s+/)[0].normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/[^a-z]/g, "");
  if (primeiro.length < 2) return null;
  if (primeiro in _cacheGenero) return _cacheGenero[primeiro];
  const soma = async (sexo) => {
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), 4000);
    try {
      const r = await fetch("https://servicodados.ibge.gov.br/api/v2/censos/nomes/" + primeiro + "?sexo=" + sexo, { signal: ctrl.signal });
      if (!r.ok) return null;
      const d = await r.json();
      if (!Array.isArray(d)) return null;
      return d.length ? d[0].res.reduce((s, x) => s + x.frequencia, 0) : 0;
    } catch (err) { return null; } finally { clearTimeout(t); }
  };
  const [m, f] = await Promise.all([soma("M"), soma("F")]);
  if (m === null || f === null) return null;
  const tot = m + f;
  let g = null;
  if (tot >= 50) { if (m / tot >= 0.85) g = "masculino"; else if (f / tot >= 0.85) g = "feminino"; }
  _cacheGenero[primeiro] = g;
  return g;
}

// ── PARSER DE DADOS DO CLIENTE (texto do WhatsApp) ──
function parseDadosCliente(texto) {
  const out = { nome: "", telefone: "", cep: "", cpf: "", endereco: "", numero: "", complemento: "", bairro: "", cidade: "", estado: "" };
  if (!texto || !texto.trim()) return out;
  let t = texto.replace(/\r/g, "").replace(/[\u2060\u200b\u200e\u200f]/g, "");
  const strip = s => (s || "").trim().replace(/^[-–•*:,.\s]+|[-–•*,.\s]+$/g, "");
  const norm = s => s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

  const cf = t.match(/\b\d{3}\.\d{3}\.\d{3}-\d{2}\b/);
  if (cf) { out.cpf = cf[0].replace(/\D/g, ""); t = t.replace(cf[0], " "); }
  const DDD_PADRAO = "92";
  const ph = t.match(/(?:\+?55[\s-]*)?\(?\b(\d{2})\)?[\s.-]*(9?)[\s.-]*(\d{4})[\s.-]*(\d{4})\b/);
  if (ph) { out.telefone = ph[1] + ph[2] + ph[3] + ph[4]; t = t.replace(ph[0], " "); }
  else {
    const ph2 = t.match(/\b(9\d{4})[\s.-]?(\d{4})\b/);
    if (ph2) { out.telefone = DDD_PADRAO + ph2[1] + ph2[2]; t = t.replace(ph2[0], " "); }
  }
  const cp = t.match(/\b(\d{5})-?(\d{3})\b/);
  if (cp) { out.cep = cp[1] + cp[2]; t = t.replace(cp[0], " "); }

  const lines = t.split("\n").map(l => l.replace(/^[\s•·*\-–]+/, "").trim()).filter(Boolean);
  const rest = [];
  let addr = "";
  const setCidade = v => {
    const m = v.match(/^(.+?)\s*[/-]\s*([A-Za-z]{2})$/);
    if (m) { out.cidade = strip(m[1]); out.estado = m[2].toUpperCase(); } else out.cidade = strip(v);
  };
  for (const l of lines) {
    if (/^(pix|dinheiro|cart[aã]o.*|cr[eé]dito|d[eé]bito|boleto|[àa] vista)\.?$/i.test(l)) continue;
    if (/^cep\b/i.test(l)) continue;
    if (/^\d{1,5}[A-Za-z]?$/.test(l)) { if (!out.numero) out.numero = l; continue; }
    const m = l.match(/^([^:]{2,60}?)\s*:\s*(.*)$/);
    if (!m) { rest.push(l); continue; }
    const label = norm(m[1]);
    const v = strip(m[2]);
    if (/cpf/.test(label)) { if (v && !out.cpf) out.cpf = v.replace(/\D/g, ""); continue; }
    if (/pagament|forma de/.test(label)) continue;
    if (/contato|telefone|whats|celular|fone|\btel\b/.test(label)) continue;
    if (/\bcep\b/.test(label)) continue;
    if (/bairro/.test(label)) { if (v) out.bairro = v; continue; }
    if (/cidade|municipio/.test(label)) { if (v) setCidade(v); continue; }
    if (/^(estado|uf)\b/.test(label)) { if (v) out.estado = v.slice(0, 2).toUpperCase(); continue; }
    if (/complemento|refer|localiza|ponto/.test(label)) { if (v) out.complemento = v; continue; }
    if (/\brua\b|endere|logradouro|resid/.test(label)) { if (v) addr = v; continue; }
    if (/^(numero\b|n[º°]|no\.)/.test(label)) { if (v) out.numero = v; continue; }
    if (/nome/.test(label)) { if (v) out.nome = v; continue; }
    if (v) rest.push(v);
  }

  const viaRe = /^(rua|r\.?|av(?:enida|endia|da|d)?\.?|travessa|trav\.?|tv\.?|alameda|estrada|rodovia|rod\.?|ramal|beco|passagem|conjunto|conj\.?|cj\.?|residencial|res\.?|vila|quadra|loteamento|comunidade)\s+/i;
  let addrPos = -1;
  if (!addr) {
    let i = rest.findIndex(l => viaRe.test(l));
    if (i === -1 && out.nome) i = rest.findIndex(l => /\d/.test(l) && l !== out.nome);
    if (i >= 0) { addr = rest[i]; addrPos = i; rest.splice(i, 1); }
  }
  if (addr) {
    let a = addr;
    const cm = a.match(/\b(?:apto|apartamento|apt|ap|bloco|casa)\b\.?\s*\S+/i);
    if (cm && !out.complemento) { out.complemento = strip(cm[0]); a = a.replace(cm[0], " "); }
    const parts = a.split(/\s+-\s+|,/).map(strip).filter(Boolean);
    let seg0 = parts[0] || "";
    let i = 1;
    const mm = seg0.match(/^(.*?\D)\s+n[º°o.]?\s*(\d+[A-Za-z]?)\s+(\D.+)$/i);
    if (mm && !out.numero) { seg0 = strip(mm[1]); out.numero = mm[2]; if (!out.bairro) out.bairro = strip(mm[3]); }
    const mn = seg0.match(/^(.*\D)\s+(?:n[º°o.]?\s*)?(\d+[A-Za-z]?|s\/n)$/i);
    const mp = parts[1] && parts[1].match(/^(?:n[º°o.]?\s*)?(\d+[A-Za-z]?|s\/n)(?:\s+(.+))?$/i);
    if (mn && !out.numero) { seg0 = strip(mn[1]); out.numero = mn[2]; }
    else if (mp && !out.numero) { out.numero = mp[1]; i = 2; if (mp[2] && !out.bairro) out.bairro = strip(mp[2]); }
    out.endereco = seg0.replace(/\s+/g, " ");
    if (parts[i] && !out.bairro && !/^\d+$/.test(parts[i])) out.bairro = parts[i];
  }
  for (let k = 0; k < rest.length; k++) {
    const m = rest[k].match(/^(.+?)\s*[/-]\s*([A-Za-z]{2})$/);
    if (m && !out.cidade) { setCidade(rest[k]); rest.splice(k, 1); break; }
  }
  const refRe = /^(pr[oó]xim[oa]|perto|ao lado|em frente|refer[eê]ncia|ponto de ref|(no|na|o|a)\s+(port[aã]o|casa|pr[eé]dio)|port[aã]o|fundos|esquina)/i;
  for (let k = rest.length - 1; k >= 0; k--) {
    if (refRe.test(rest[k]) || rest[k].split(/\s+/).length > 6) {
      const ref = strip(rest[k]);
      out.complemento = out.complemento ? out.complemento + " - " + ref : ref;
      rest.splice(k, 1); if (addrPos > k) addrPos--;
    }
  }
  if (addrPos >= 0 && !out.nome) {
    const cand = rest.map((l, idx) => ({ l, idx, antes: idx < addrPos })).filter(x => !/\d/.test(x.l) && x.l.split(/\s+/).length <= 6);
    const antes = cand.filter(x => x.antes), depois = cand.filter(x => !x.antes);
    let nomeC = null, bairroC = null;
    if (antes.length) { nomeC = antes[0]; bairroC = depois[0] || null; }
    else if (depois.length >= 2) { bairroC = depois[0]; nomeC = depois[depois.length - 1]; }
    else if (depois.length === 1) { nomeC = depois[0]; }
    if (nomeC) out.nome = strip(nomeC.l);
    if (bairroC && !out.bairro) {
      const pm = bairroC.l.match(/^(.*?)\s*\(([^)]*)\)\s*$/);
      if (pm) { out.bairro = strip(pm[1]); out.complemento = out.complemento ? out.complemento + " - " + strip(pm[2]) : strip(pm[2]); }
      else out.bairro = strip(bairroC.l);
    }
    const usados = [nomeC, bairroC].filter(Boolean).map(x => x.idx);
    for (let k = rest.length - 1; k >= 0; k--) if (usados.includes(k)) rest.splice(k, 1);
  }
  if (!out.nome) {
    const k = rest.findIndex(l => !/\d/.test(l) && l.split(/\s+/).length <= 5);
    if (k >= 0) out.nome = strip(rest[k]);
  }
  const part = new Set(["da", "de", "do", "das", "dos", "e"]);
  out.nome = out.nome.split(/\s+/).filter(Boolean).map(w => part.has(w.toLowerCase()) ? w.toLowerCase() : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(" ");
  return out;
}


// ── BAR CHART SIMPLES ─────────────────────────────────────────
function MiniBar({ value, max, color = C.accent, height = 6 }) {
  const w = max > 0 ? (value / max) * 100 : 0;
  return (
    <div style={{ background: C.border, borderRadius: 3, height, overflow: "hidden", flex: 1 }}>
      <div style={{ width: `${w}%`, height: "100%", background: color, borderRadius: 3, transition: "width 0.6s ease" }} />
    </div>
  );
}

// ── GRÁFICO MENSAL ────────────────────────────────────────────
function GraficoMensal({ vendas, gastos }) {
  // Gerar últimos 6 meses até hoje (não pega meses futuros)
  const hoje = new Date(Date.now() - 4*60*60*1000); // Manaus GMT-4
  const ultimos6Meses = [];
  for (let i = 5; i >= 0; i--) {
    const d = new Date(hoje.getFullYear(), hoje.getMonth() - i, 1);
    const mesStr = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`;
    ultimos6Meses.push(mesStr);
  }

  const barras = ultimos6Meses.map(mes => {
    const fat = vendas.filter(v => getMes(v.data) === mes).reduce((s, v) => s + v.valor * v.qtd, 0);
    const gasto = gastos.filter(g => getMes(g.data) === mes).reduce((s, g) => s + g.valor, 0);
    const lucro = fat - gasto;
    const label = MESES[parseInt(mes.slice(5,7)) - 1] + "/" + mes.slice(2,4);
    return { mes, label, fat, gasto, lucro };
  });

  const maxVal = Math.max(...barras.map(b => Math.max(b.fat, b.gasto)));

  return (
    <div style={{ 
      ...styles.card,
      marginTop: 16,
      background: C.bgElevated,
      border: "1px solid #e5e7eb"
    }}>
      <div style={{ 
        fontSize: 14, 
        fontWeight: 700, 
        color: "#111827", 
        marginBottom: 20,
        paddingBottom: 12,
        borderBottom: `2px solid ${C.accent}30`
      }}>📈 Evolução Mensal</div>
      <div style={{ display: "flex", alignItems: "flex-end", gap: 12, height: 140, paddingBottom: 28, position: "relative" }}>
        {barras.map((b) => (
          <div key={b.mes} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 3, height: "100%", justifyContent: "flex-end" }}>
            <div style={{ width: "100%", display: "flex", gap: 3, alignItems: "flex-end", height: "100%" }}>
              <div title={`Faturamento: ${fmt(b.fat)}`} style={{ flex: 1, height: `${maxVal > 0 ? (b.fat/maxVal)*100 : 0}%`, background: C.accent, borderRadius: "3px 3px 0 0", minHeight: b.fat > 0 ? 4 : 0, transition: "height 0.5s ease" }} />
              <div title={`Gastos: ${fmt(b.gasto)}`} style={{ flex: 1, height: `${maxVal > 0 ? (b.gasto/maxVal)*100 : 0}%`, background: C.warn, borderRadius: "3px 3px 0 0", minHeight: b.gasto > 0 ? 4 : 0, transition: "height 0.5s ease" }} />
            </div>
            <div style={{ fontSize: 9, color: "#374151", textAlign: "center", marginTop: 6 }}>{b.label}</div>
            <div style={{ fontSize: 9, fontWeight: 700, color: b.lucro >= 0 ? C.accent : C.warn, textAlign: "center" }}>
              {b.lucro >= 0 ? "+" : ""}{fmt(b.lucro).replace("R$","")}
            </div>
          </div>
        ))}
      </div>
      <div style={{ display: "flex", gap: 16, marginTop: 4 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}><div style={{ width: 10, height: 10, background: C.accent, borderRadius: 2 }}/><span style={{ fontSize: 10, color: "#374151" }}>Faturamento</span></div>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}><div style={{ width: 10, height: 10, background: C.warn, borderRadius: 2 }}/><span style={{ fontSize: 10, color: "#374151" }}>Gastos</span></div>
      </div>
    </div>
  );
}

// ── PIZZA CHART ───────────────────────────────────────────────
function PizzaChart({ data: slices, size = 140 }) {
  const total = slices.reduce((s, d) => s + d.value, 0);
  if (total === 0) return <div style={{ width: size, height: size, borderRadius: "50%", background: C.border, display: "flex", alignItems: "center", justifyContent: "center" }}><span style={{ fontSize: 10, color: "#374151" }}>Sem dados</span></div>;
  let cumAngle = -Math.PI / 2;
  const cx = size / 2, cy = size / 2, r = size / 2 - 8;
  const paths = slices.map(slice => {
    const angle = (slice.value / total) * 2 * Math.PI;
    const x1 = cx + r * Math.cos(cumAngle);
    const y1 = cy + r * Math.sin(cumAngle);
    cumAngle += angle;
    const x2 = cx + r * Math.cos(cumAngle);
    const y2 = cy + r * Math.sin(cumAngle);
    const largeArc = angle > Math.PI ? 1 : 0;
    const d = `M${cx},${cy} L${x1},${y1} A${r},${r} 0 ${largeArc},1 ${x2},${y2} Z`;
    return { d, color: slice.color, label: slice.label, value: slice.value, pct: total > 0 ? slice.value / total : 0 };
  });
  return (
    <svg width={size} height={size} style={{ filter: "drop-shadow(0 4px 12px rgba(0,0,0,0.5))" }}>
      {paths.map((p, i) => <path key={i} d={p.d} fill={p.color} stroke={C.card} strokeWidth={2} />)}
      <circle cx={cx} cy={cy} r={r * 0.45} fill={C.card} />
    </svg>
  );
}

// ── MODAL DE EDIÇÃO ───────────────────────────────────────────
function EditModal({ fields, values, onSave, onClose, title }) {
  const [form, setForm] = useState(values);
  return createPortal(
    <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.5)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", top: 0, left: 0, right: 0, bottom: 0 }} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={{ background: C.card, border: `1px solid ${C.accent}40`, borderRadius: 16, padding: 28, width: 480, maxWidth: "90vw" }}>
        <div style={{ fontWeight: 700, fontSize: 16, color: C.accent, marginBottom: 28 }}>✏️ {title}</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 28 }}>
          {fields.map(f => (
            <div key={f.key}>
              <label style={styles.label}>{f.label}</label>
              {f.options ? (
                <select style={styles.input} value={form[f.key] || ""} onChange={e => setForm({ ...form, [f.key]: e.target.value })}>
                  {f.options.map(o => <option key={o}>{o}</option>)}
                </select>
              ) : (
                <input style={styles.input} type={f.type || "text"} value={form[f.key] || ""} onChange={e => setForm({ ...form, [f.key]: e.target.value })} />
              )}
            </div>
          ))}
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button style={styles.btn("primary")} onClick={() => onSave(form)}>Salvar</button>
          <button style={styles.btn("secondary")} onClick={onClose}>Cancelar</button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ── TOAST ────────────────────────────────────────────────────
function Toast({ msg, tipo = "ok" }) {
  const [visible, setVisible] = useState(false);
  const [progress, setProgress] = useState(100);

  useEffect(() => {
    if (!msg) return;
    setVisible(true);
    setProgress(100);
    const dur = 3000;
    const interval = 30;
    const step = (interval / dur) * 100;
    const timer = setInterval(() => setProgress(p => Math.max(0, p - step)), interval);
    const hide = setTimeout(() => setVisible(false), dur);
    return () => { clearInterval(timer); clearTimeout(hide); };
  }, [msg]);

  if (!msg) return null;
  const isOk = tipo === "ok";
  const cor = isOk ? C.accent : C.warn;
  const icon = isOk ? "✓" : "✕";
  return (
    <div style={{
      position: "fixed", bottom: 28, right: 24,
      background: C.card,
      border: `1px solid ${cor}40`,
      borderRadius: 16,
      padding: "14px 18px 10px",
      fontSize: 14, color: "#111827", fontWeight: 500,
      zIndex: 4000,
      boxShadow: `0 8px 32px rgba(0,0,0,0.7), 0 0 0 1px ${cor}20`,
      pointerEvents: "none",
      minWidth: 220,
      transform: visible ? "translateY(0)" : "translateY(20px)",
      opacity: visible ? 1 : 0,
      transition: "transform 0.25s cubic-bezier(0.34,1.56,0.64,1), opacity 0.2s",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
        <div style={{ width: 22, height: 22, borderRadius: "50%", background: cor + "20", border: `1px solid ${cor}50`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, color: cor, fontWeight: 800 }}>
          {icon}
        </div>
        <span style={{ color: "#111827", fontSize: 13 }}>{msg}</span>
      </div>
      <div style={{ height: 2, background: C.border, borderRadius: 2, overflow: "hidden" }}>
        <div style={{ height: "100%", width: progress + "%", background: cor, borderRadius: 2, transition: "width 0.03s linear" }} />
      </div>
    </div>
  );
}


// ── SKELETON ─────────────────────────────────────────────────
function Skeleton({ w = "100%", h = 16, radius = 6, style = {} }) {
  return (
    <div style={{
      width: w, height: h, borderRadius: radius,
      background: `linear-gradient(90deg, ${C.surface} 25%, #1a1a0a 50%, ${C.surface} 75%)`,
      backgroundSize: "200% 100%",
      animation: "skeletonShimmer 1.4s infinite",
      ...style
    }} />
  );
}

// eslint-disable-next-line no-unused-vars
function SkeletonDashboard() {
  return (
    <div>
      <style>{`@keyframes skeletonShimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
        .tfx-fade-in { animation: fadeIn 0.4s ease-out; }
        .tfx-card-hover { transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1); }
        .tfx-card-hover::before { content: ""; position: absolute; top: 0; left: 0; right: 0; height: 2px; background: var(--accent-color, #FFB800); opacity: 0; transition: opacity 0.3s; }
        .tfx-card-hover:hover { transform: translateY(-2px); box-shadow: 0 8px 20px rgba(0,0,0,0.3), 0 0 0 1px rgba(212,160,23,0.2); }
        .tfx-card-hover { transition: all 0.3s; position: relative; }
        .tfx-card-hover:hover { transform: translateY(-3px); box-shadow: 0 8px 16px rgba(0,0,0,0.3); }`}</style>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 16, marginBottom: 24 }}>
        {[...Array(4)].map((_,i) => (
          <div key={i} style={{ ...styles.card, borderTop: `2px solid ${C.border}` }}>
            <Skeleton w="60%" h={10} style={{ marginBottom: 12 }} />
            <Skeleton w="80%" h={28} style={{ marginBottom: 8 }} />
            <Skeleton w="50%" h={10} />
          </div>
        ))}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 24 }}>
        {[...Array(2)].map((_,i) => (
          <div key={i} style={styles.card}>
            <Skeleton w="40%" h={14} style={{ marginBottom: 16 }} />
            <Skeleton h={80} radius={8} style={{ marginBottom: 8 }} />
            <Skeleton w="70%" h={10} />
          </div>
        ))}
      </div>
    </div>
  );
}

// ── HOOK DE FILTRO (evita duplicação) ────────────────────────
function useFiltro(chaveSession) {
  const mesAtualDefault = new Date(Date.now() - 4*60*60*1000).toISOString().slice(0,7);
  const [mesFiltro, setMesFiltro] = useState(() => sessionStorage.getItem(chaveSession + "_mes") || mesAtualDefault);
  const [periodoRapido, setPeriodoRapido] = useState(() => sessionStorage.getItem(chaveSession + "_periodo") || "mes");
  const [dataInicio, setDataInicio] = useState(() => {
    const periodo = sessionStorage.getItem(chaveSession + "_periodo") || "mes";
    const hj = new Date(Date.now() - 4*60*60*1000).toISOString().slice(0,10);
    const subDias = (n) => { const d = new Date(Date.now() - 4*60*60*1000); d.setDate(d.getDate()-n); return d.toISOString().slice(0,10); };
    if (periodo === "hoje") return hj;
    if (periodo === "ontem") return subDias(1);
    if (periodo === "7d") return subDias(6);
    if (periodo === "15d") return subDias(14);
    if (periodo === "30d") return subDias(29);
    if (periodo === "90d") return subDias(89);
    return sessionStorage.getItem(chaveSession + "_inicio") || "";
  });
  const [dataFim, setDataFim] = useState(() => {
    const periodo = sessionStorage.getItem(chaveSession + "_periodo") || "mes";
    const hj = new Date(Date.now() - 4*60*60*1000).toISOString().slice(0,10);
    const subDias = (n) => { const d = new Date(Date.now() - 4*60*60*1000); d.setDate(d.getDate()-n); return d.toISOString().slice(0,10); };
    if (periodo === "hoje") return hj;
    if (periodo === "ontem") return subDias(1);
    if (periodo === "7d" || periodo === "15d" || periodo === "30d" || periodo === "90d") return hj;
    return sessionStorage.getItem(chaveSession + "_fim") || "";
  });
  const [showFiltro, setShowFiltro] = useState(false);

  const aplicarPeriodo = (valor) => {
    setPeriodoRapido(valor); setMesFiltro("todos");
    sessionStorage.setItem(chaveSession + "_periodo", valor);
    sessionStorage.setItem(chaveSession + "_mes", "todos");
    const hj = hojeManaus();
    const subDias = (n) => { const d = new Date(Date.now() - 4*60*60*1000); d.setDate(d.getDate()-n); return d.toISOString().slice(0,10); };
    let ini = "", fim = "";
    if (valor === "hoje") { ini = hj; fim = hj; }
    else if (valor === "ontem") { ini = subDias(1); fim = subDias(1); }
    else if (valor === "7d") { ini = subDias(6); fim = hj; }
    else if (valor === "15d") { ini = subDias(14); fim = hj; }
    else if (valor === "30d") { ini = subDias(29); fim = hj; }
    else if (valor === "90d") { ini = subDias(89); fim = hj; }
    setDataInicio(ini); setDataFim(fim);
    sessionStorage.setItem(chaveSession + "_inicio", ini);
    sessionStorage.setItem(chaveSession + "_fim", fim);
  };

  const selecionarMes = (m) => {
    setMesFiltro(m); setPeriodoRapido("mes"); setDataInicio(""); setDataFim("");
    sessionStorage.setItem(chaveSession + "_mes", m);
    sessionStorage.setItem(chaveSession + "_periodo", "mes");
    sessionStorage.setItem(chaveSession + "_inicio", "");
    sessionStorage.setItem(chaveSession + "_fim", "");
  };

  const filtrarLista = (lista, campoData = "data") => lista.filter(item => {
    if (dataInicio && dataFim) return item[campoData] >= dataInicio && item[campoData] <= dataFim;
    if (mesFiltro !== "todos") return item[campoData]?.startsWith(mesFiltro);
    return true;
  });

  const labelPeriodo = () => {
    if (periodoRapido === "todos" && mesFiltro === "todos") return "Todos";
    if (periodoRapido === "hoje") return "Hoje";
    if (periodoRapido === "ontem") return "Ontem";
    if (periodoRapido === "7d") return "7 dias";
    if (periodoRapido === "15d") return "15 dias";
    if (periodoRapido === "30d") return "30 dias";
    if (periodoRapido === "90d") return "90 dias";
    if (periodoRapido === "custom") return `${dataInicio} → ${dataFim}`;
    return MESES[parseInt(mesFiltro.slice(5,7))-1] + "/" + mesFiltro.slice(2,4);
  };

  return { mesFiltro, periodoRapido, dataInicio, setDataInicio, dataFim, setDataFim, showFiltro, setShowFiltro, aplicarPeriodo, selecionarMes, filtrarLista, labelPeriodo };
}

// ── HOOK COUNT UP ────────────────────────────────────────────
function useCountUp(target, duration = 800) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!target || target === 0) { setValue(0); return; }
    const start = Date.now();
    const tick = () => {
      const elapsed = Date.now() - start;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(target * eased);
      if (progress < 1) requestAnimationFrame(tick);
      else setValue(target);
    };
    requestAnimationFrame(tick);
  }, [target, duration]);
  return value;
}

// ── ANIMATED KPI VALUE ───────────────────────────────────────
function AnimatedKpiValue({ value, formatted, color, privado }) {
  const animated = useCountUp(privado ? 0 : (value || 0), 900);
  if (privado) return <div style={styles.kpiValue(color)}>••••••</div>;
  const isNegative = value < 0;
  const display = "R$ " + Math.abs(animated).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return <div style={styles.kpiValue(color)}>{isNegative ? "-" : ""}{display}</div>;
}

// ── DASHBOARD ─────────────────────────────────────────────────


function Dashboard({ data, setData, setPage, privado, showToast }) {
  const [metaAds, setMetaAds] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [expandirNaoLancados, setExpandirNaoLancados] = useState(false);



  const [metaLoading, setMetaLoading] = useState(false);
  const [metaLancado, setMetaLancado] = useState(false);
  const [metaConectado, setMetaConectado] = useState(false);
  const [showConectarMeta, setShowConectarMeta] = useState(false);
  const [metaForm, setMetaForm] = useState({ token: "", adAccount: "" });
  const [salvandoMeta, setSalvandoMeta] = useState(false);
  const [showResumoDia, setShowResumoDia] = useState(false);
  const [mesComparacao, setMesComparacao] = useState(null);
  const [resumoDia, setResumoDia] = useState(null);
  const [resumoDiaLoading, setResumoDiaLoading] = useState(false);

  const buscarMetaAds = (since, until) => {
    fetch("https://gestao-tfx-saas.vercel.app/api/meta-ads?action=insights&since=" + since + "&until=" + until + "&tenant_id=" + getTenant())
      .then(r => r.json())
      .then(d => {
        if (d.naoConectado) { setMetaConectado(false); return; }
        if (d.ok) { setMetaAds(d.data); setMetaConectado(true); }
      }).catch(() => {});
  };

  const lancarGastoMeta = async () => {
    setMetaLoading(true);
    try {
      const ontem = new Date(Date.now()-86400000).toISOString().slice(0,10);
      const r = await fetch("https://gestao-tfx-saas.vercel.app/api/meta-ads?action=lancar-gasto&tenant_id=" + getTenant() + "&since=" + ontem + "&until=" + ontem);
      const d = await r.json();
      if (d.ok) setMetaLancado(true);
      else alert("Erro: " + d.error);
    } catch(e) { alert("Erro: " + e.message); }
    setMetaLoading(false);
  };

  const buscarResumoDia = async () => {
    setResumoDiaLoading(true);
    setShowResumoDia(true);
    const hoje = hojeManaus();
    const vendasHoje = data.vendas.filter(v => v.data === hoje);
    const faturamento = vendasHoje.reduce((s, v) => s + v.valor * v.qtd, 0);
    const cmv = vendasHoje.reduce((s, v) => s + v.custo * v.qtd, 0);
    const numVendas = vendasHoje.length;
    const gastosHoje = (data.gastos || []).filter(g => g.data === hoje);
    const entrega = gastosHoje.filter(g => g.categoria === "Entrega / Frete" || g.categoria === "Motoboy" || (g.categoria === "Operacional" && g.descricao && g.descricao.toLowerCase().includes("motoboy"))).reduce((s, g) => s + g.valor, 0);
    const uber = gastosHoje.filter(g => g.categoria === "Uber Loja").reduce((s, g) => s + g.valor, 0);
    let trafego = 0;
    let trafegoComImposto = 0;
    try {
      const r = await fetch("https://gestao-tfx-saas.vercel.app/api/meta-ads?action=insights&since=" + hoje + "&until=" + hoje + "&tenant_id=" + getTenant());
      const d = await r.json();
      if (d.ok && d.data) { trafego = parseFloat(d.data.spend) || 0; trafegoComImposto = trafego * fatorTrafego(data.caixa_config); }
    } catch(e) {}
    const roi = trafegoComImposto > 0 ? (faturamento / trafegoComImposto) : null;
    const cpv = numVendas > 0 ? (trafegoComImposto / numVendas) : null;
    setResumoDia({ faturamento, numVendas, cmv, trafego, trafegoComImposto, entrega, uber, roi, cpv, hoje });
    setResumoDiaLoading(false);
  };

  const salvarMetaCredenciais = async () => {
    if (!metaForm.token || !metaForm.adAccount) return alert("Preencha todos os campos!");
    setSalvandoMeta(true);
    try {
      const adAccount = metaForm.adAccount.startsWith("act_") ? metaForm.adAccount : "act_" + metaForm.adAccount;
      const r = await fetch("https://gestao-tfx-saas.vercel.app/api/meta-ads?action=salvar-credenciais&tenant_id=" + getTenant(), {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: metaForm.token, adAccount })
      });
      const d = await r.json();
      if (d.ok) { setMetaConectado(true); setShowConectarMeta(false); const ontem = new Date(Date.now()-86400000).toISOString().slice(0,10); buscarMetaAds(ontem, ontem); }
      else alert("Erro: " + (d.error || "Credenciais invalidas"));
    } catch(e) { alert("Erro: " + e.message); }
    setSalvandoMeta(false);
  };

  const desconectarMeta = async () => {
    if (!window.confirm("Desconectar o Meta Ads?")) return;
    await fetch("https://uhayvtncounslqlchtpz.supabase.co/rest/v1/clientes?tenant_id=eq." + getTenant(), {
      method: "PATCH",
      headers: { Authorization: `Bearer ${getJWT()}`, "Content-Type": "application/json" },
      body: JSON.stringify({ meta_token: null, meta_ad_account: null })
    });
    setMetaConectado(false); setMetaAds(null); setShowConectarMeta(false);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps

  const { mesFiltro, periodoRapido: periodoRapidoD, dataInicio: dataInicioD, setDataInicio: setDataInicioD, dataFim: dataFimD, setDataFim: setDataFimD, showFiltro: showFiltroD, setShowFiltro: setShowFiltroD, aplicarPeriodo: aplicarPeriodoRapidoD, selecionarMes: selecionarMesD, filtrarLista: filtrarListaD, labelPeriodo: labelPeriodoD } = useFiltro("tfx_dash");

  useEffect(() => {
    // Calcular range baseado no filtro
    let since, until;
    
    if (periodoRapidoD === "hoje") {
      since = until = new Date(Date.now()-4*60*60*1000).toISOString().slice(0,10);
    } else if (periodoRapidoD === "ontem") {
      since = until = new Date(Date.now()-86400000-4*60*60*1000).toISOString().slice(0,10);
    } else if (periodoRapidoD === "7d") {
      until = new Date(Date.now()-4*60*60*1000).toISOString().slice(0,10);
      since = new Date(Date.now()-7*86400000-4*60*60*1000).toISOString().slice(0,10);
    } else if (periodoRapidoD === "15d") {
      until = new Date(Date.now()-4*60*60*1000).toISOString().slice(0,10);
      since = new Date(Date.now()-15*86400000-4*60*60*1000).toISOString().slice(0,10);
    } else if (periodoRapidoD === "30d") {
      until = new Date(Date.now()-4*60*60*1000).toISOString().slice(0,10);
      since = new Date(Date.now()-30*86400000-4*60*60*1000).toISOString().slice(0,10);
    } else if (periodoRapidoD === "90d") {
      until = new Date(Date.now()-4*60*60*1000).toISOString().slice(0,10);
      since = new Date(Date.now()-90*86400000-4*60*60*1000).toISOString().slice(0,10);
    } else if (mesFiltro !== "todos") {
      // Filtro de mês: pegar primeiro e último dia
      const [ano, mes] = mesFiltro.split('-');
      since = `${ano}-${mes}-01`;
      const ultimoDia = new Date(Number(ano), Number(mes), 0).getDate();
      until = `${ano}-${mes}-${String(ultimoDia).padStart(2, '0')}`;
    } else if (dataInicioD && dataFimD) {
      // Período customizado
      since = dataInicioD;
      until = dataFimD;
    } else {
      // Default: ontem
      since = until = new Date(Date.now()-86400000-4*60*60*1000).toISOString().slice(0,10);
    }
    
    // Pequeno delay pra garantir que o filtro foi restaurado do sessionStorage
    const timeout = setTimeout(() => buscarMetaAds(since, until), 300);
    const intervalo = setInterval(() => {
      buscarMetaAds(since, until);
    }, 5 * 60 * 1000);
    return () => { clearTimeout(timeout); clearInterval(intervalo); };
  }, [periodoRapidoD, mesFiltro, dataInicioD, dataFimD]);

  // Atualizar Meta Ads quando filtro de data mudar
  useEffect(() => {
    if (dataInicioD && dataFimD) {
      buscarMetaAds(dataInicioD, dataFimD);
    } else {
      const ontem = new Date(Date.now()-86400000).toISOString().slice(0,10);
      buscarMetaAds(ontem, ontem);
    }
  }, [dataInicioD, dataFimD]); // eslint-disable-line

  const ultimos12MesesD = Array.from({length: 12}, (_, i) => mesRelativo(-i));
  const mesesDisponiveis = [...new Set([...ultimos12MesesD, ...data.vendas.map(v => getMes(v.data)), ...data.gastos.map(g => getMes(g.data))])].filter(Boolean).sort().reverse();

  const vendasD = filtrarListaD(data.vendas);
  const gastosD = filtrarListaD(data.gastos);

  // Tráfego de hoje ao vivo (Meta, já com imposto) — o cron só lança dias fechados
  const [metaHojeDash, setMetaHojeDash] = useState(null);
  useEffect(() => {
    let vivo = true;
    const carregar = async () => {
      if (document.hidden) return;
      try {
        const dia = hojeManaus();
        const r = await fetch(`https://gestao-tfx-saas.vercel.app/api/meta-ads?action=insights&tenant_id=${getTenant()}&since=${dia}&until=${dia}`);
        const d = await r.json();
        if (vivo && d.ok && d.data && !d.naoConectado) setMetaHojeDash(Number(((parseFloat(d.data.spend) || 0) * fatorTrafego(data.caixa_config || {})).toFixed(2)));
      } catch(e) {}
    };
    carregar();
    const t = setInterval(carregar, 120000);
    const onVis = () => { if (!document.hidden) carregar(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { vivo = false; clearInterval(t); document.removeEventListener("visibilitychange", onVis); };
  }, [data.caixa_config && data.caixa_config.imposto_trafego]); // eslint-disable-line react-hooks/exhaustive-deps

  const totalVendas = vendasD.reduce((s, v) => s + v.valor * v.qtd, 0);
  const totalCMV = vendasD.reduce((s, v) => s + (v.custo || 0) * v.qtd, 0);
  const lucroBruto = totalVendas - totalCMV;
  const totalGastos = gastosD.filter(g => g.tipo === "loja").reduce((s, g) => s + g.valor, 0);
  const lucroLiquido = lucroBruto - totalGastos;
  const gastosPessoal = gastosD.filter(g => g.tipo === "pessoal").reduce((s, g) => s + g.valor, 0);
  const resultado = lucroLiquido - gastosPessoal;
  const margem = totalVendas > 0 ? lucroLiquido / totalVendas : 0;

  const online = vendasD.filter(v => v.canal === "Online").reduce((s, v) => s + v.valor * v.qtd, 0);
  const fisica = vendasD.filter(v => v.canal === "Física").reduce((s, v) => s + v.valor * v.qtd, 0);

  const catColors = { Imposto: C.warn, "Tráfego Pago": "#b91c1c", Operacional: C.gold, Pessoal: "#6b7280" };
  const pizzaGastos = Object.entries(catColors).map(([label, color]) => ({
    label, color, value: gastosD.filter(g => g.categoria === label).reduce((s, g) => s + g.valor, 0)
  })).filter(d => d.value > 0);

  const pizzaCanais = [
    { label: "Online", color: C.accent, value: online },
    { label: "Física", color: "#374151", value: fisica },
  ].filter(d => d.value > 0);

  const semEstoque = data.estoque.filter(p => p.estoque === 0).length;
  const critico = data.estoque.filter(p => p.estoque > 0 && p.estoque <= p.minimo).length;
  const pendentes = data.compras.filter(c => c.status === "Pendente").length;

  // Gastos recorrentes não lançados no mês atual
  const mesAtualStr = hojeManaus().slice(0,7);
  const recorrentes = data.gastos.filter(g => g.recorrente);
  // eslint-disable-next-line no-unused-vars
  const recorrentesNaoLancados = recorrentes.filter(r => {
    const jaLancado = data.gastos.some(g => 
      g.subcategoria === r.subcategoria && 
      g.categoria === r.categoria && 
      g.data?.startsWith(mesAtualStr) &&
      g.id !== r.id
    );
    return !jaLancado && !r.data?.startsWith(mesAtualStr);
  });



  // Gerar últimos 6 meses até hoje (incluindo mês atual)
  const hoje = new Date(Date.now() - 4*60*60*1000); // Manaus GMT-4
  const ultimos6Meses = [];
  for (let i = 5; i >= 0; i--) {
    const d = new Date(hoje.getFullYear(), hoje.getMonth() - i, 1);
    const mesStr = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`;
    ultimos6Meses.push(mesStr);
  }
  
  const barras = ultimos6Meses.map(mes => ({
    label: MESES[parseInt(mes.slice(5,7))-1],
    fat: data.vendas.filter(v => v.data?.startsWith(mes)).reduce((s,v)=>s+v.valor*v.qtd,0),
    gas: gastosD.filter(g => g.data?.startsWith(mes)).reduce((s,g)=>s+g.valor,0),
  }));
  const maxBar = Math.max(...barras.map(b => Math.max(b.fat, b.gas)), 1);

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 28 }}>
        <div>
          <div style={styles.pageTitle}>Dashboard</div>
          <div style={styles.pageSub}>Visão geral do negócio</div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={buscarResumoDia} style={{ ...styles.btn("primary"), fontSize: 11, padding: "6px 12px" }}>📊 Resumo do Dia</button>
          <button onClick={() => setShowFiltroD(!showFiltroD)} style={{ ...styles.btn("secondary"), fontSize: 11, padding: "6px 12px" }}>📅 {labelPeriodoD()} ▾</button>
        </div>
      </div>

      {showResumoDia && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(15,23,42,0.5)", zIndex: 999, display: "flex", alignItems: "center", justifyContent: "center", backdropFilter: "blur(4px)" }} onClick={() => setShowResumoDia(false)}>
          <div style={{ background: "#ffffff", border: "1px solid #e5e7eb", borderRadius: 24, padding: 0, width: 520, maxWidth: "92vw", overflow: "hidden", boxShadow: "0 8px 32px rgba(0,0,0,0.12)" }} onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div style={{ background: "#f9fafb", borderBottom: "1px solid #e5e7eb", padding: "24px 28px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 900, color: "#111827", letterSpacing: 0.5, textTransform: "uppercase" }}>Resumo do Dia</div>
                {resumoDia && <div style={{ fontSize: 12, color: "#374151", marginTop: 3, fontWeight: 500 }}>{resumoDia.hoje} • tempo real</div>}
              </div>
              <button onClick={() => setShowResumoDia(false)} style={{ background: "#f3f4f6", border: "1px solid #e5e7eb", borderRadius: 10, color: "#6b7280", fontSize: 16, width: 32, height: 32, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>✕</button>
            </div>
            {resumoDiaLoading ? (
              <div style={{ textAlign: "center", color: "#374151", padding: "48px 0", fontSize: 13 }}>Buscando dados em tempo real...</div>
            ) : resumoDia ? (() => {
              const lucro = resumoDia.faturamento - resumoDia.cmv - resumoDia.trafegoComImposto - resumoDia.entrega - resumoDia.uber;
              const roiColor = resumoDia.roi && resumoDia.roi < 5 ? "#ef4444" : "#15803d";
              const cpvColor = resumoDia.cpv && resumoDia.cpv > 70 ? "#ef4444" : "#15803d";
              return (
                <div style={{ padding: "8px 0 24px" }}>
                  {/* KPIs topo - 3 cards */}
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 1, borderBottom: "1px solid #f3f4f6", marginBottom: 8 }}>
                    {[
                      { label: "Faturamento", value: fmt(resumoDia.faturamento), color: "#111827", icon: "💰" },
                      { label: "Lucro do Dia", value: fmt(lucro), color: lucro >= 0 ? "#15803d" : "#ef4444", icon: "💹" },
                      { label: "Vendas", value: resumoDia.numVendas + "x", color: "#374151", icon: "🛍️" },
                    ].map((k, i) => (
                      <div key={i} style={{ padding: "20px 0", textAlign: "center", borderRight: i < 2 ? "1px solid #1e1e1e" : "none" }}>
                        <div style={{ fontSize: 20, marginBottom: 6 }}>{k.icon}</div>
                        <div style={{ fontSize: 17, fontWeight: 900, color: k.color, marginBottom: 4 }}>{k.value}</div>
                        <div style={{ fontSize: 10, color: "#374151", textTransform: "uppercase", letterSpacing: 1 }}>{k.label}</div>
                      </div>
                    ))}
                  </div>
                  {/* Linhas detalhadas */}
                  <div style={{ padding: "0 28px" }}>
                    {[
                      { section: "TRÁFEGO" },
                      { label: "Meta Ads (bruto)", value: fmt(resumoDia.trafego), color: "#b91c1c" },
                      { label: "c/ Imposto " + fmtAliquota(data.caixa_config) + "%", value: fmt(resumoDia.trafegoComImposto), color: "#ef4444" },
                      { section: "PERFORMANCE" },
                      { label: "ROI", value: resumoDia.roi ? resumoDia.roi.toFixed(2) + "x" : "—", color: roiColor, badge: resumoDia.roi && resumoDia.roi < 5 ? "abaixo do ideal" : null },
                      { label: "CPV", value: resumoDia.cpv ? fmt(resumoDia.cpv) : "—", color: cpvColor, badge: resumoDia.cpv && resumoDia.cpv > 70 ? "acima do ideal" : null },
                      { section: "CUSTOS" },
                      { label: "CMV (produtos)", value: fmt(resumoDia.cmv), color: "#f87171" },
                      { label: "Entrega / Frete", value: fmt(resumoDia.entrega), color: "#374151" },
                      { label: "Uber Loja", value: fmt(resumoDia.uber), color: "#374151" },
                    ].map((item, i) => item.section ? (
                      <div key={i} style={{ fontSize: 9, color: "#374151", letterSpacing: 2, fontWeight: 700, textTransform: "uppercase", marginTop: 20, marginBottom: 4 }}>{item.section}</div>
                    ) : (
                      <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderBottom: "1px solid #f3f4f6" }}>
                        <div style={{ fontSize: 13, color: "#374151" }}>{item.label}</div>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          {item.badge && <span style={{ fontSize: 9, color: "#ef4444", background: "#ef444415", border: "1px solid #ef444430", borderRadius: 6, padding: "2px 6px", fontWeight: 700 }}>{item.badge}</span>}
                          <div style={{ fontSize: 14, fontWeight: 800, color: item.color }}>{item.value}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })() : null}
          </div>
        </div>
      )}

      {showFiltroD && (
        <div style={{ ...styles.card, marginBottom: 16, padding: 16 }}>
          <div style={{ fontSize: 11, color: "#374151", marginBottom: 10, letterSpacing: 1 }}>PERÍODO RÁPIDO</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 14 }}>
            {[["todos","Todos"],["hoje","Hoje"],["ontem","Ontem"],["7d","7 dias"],["15d","15 dias"],["30d","30 dias"],["90d","90 dias"]].map(([v,l]) => (
              <button key={v} onClick={() => { aplicarPeriodoRapidoD(v); }} style={{ ...styles.btn(periodoRapidoD === v && mesFiltro === "todos" ? "primary" : "secondary"), fontSize: 11, padding: "5px 12px" }}>{l}</button>
            ))}
          </div>
          <div style={{ fontSize: 11, color: "#374151", marginBottom: 10, letterSpacing: 1 }}>POR MÊS</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 14 }}>
            {mesesDisponiveis.map(m => (
              <button key={m} onClick={() => selecionarMesD(m)} style={{ ...styles.btn(mesFiltro === m ? "primary" : "secondary"), fontSize: 11, padding: "5px 12px" }}>{MESES[parseInt(m.slice(5,7))-1] + "/" + m.slice(2,4)}</button>
            ))}
          </div>
          <div style={{ fontSize: 11, color: "#374151", marginBottom: 10, letterSpacing: 1 }}>PERÍODO CUSTOMIZADO</div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <input type="date" style={{ ...styles.input, width: "auto", fontSize: 12 }} value={dataInicioD} onChange={e => setDataInicioD(e.target.value)} />
            <span style={{ color: "#374151" }}>→</span>
            <input type="date" style={{ ...styles.input, width: "auto", fontSize: 12 }} value={dataFimD} onChange={e => setDataFimD(e.target.value)} />
            <button onClick={() => setShowFiltroD(false)} style={{ ...styles.btn("primary"), fontSize: 11 }}>Aplicar</button>
          </div>
        </div>
      )}

      {/* Onboarding para novos clientes */}
      {(() => {
        const temProduto = data.estoque.length > 0;
        const temVenda = data.vendas.length > 0;
        const temGasto = data.gastos.length > 0;
        // const temCliente = data.clientes_loja ? data.clientes_loja.length > 0 : false;
        const temMeta = (() => { try { return localStorage.getItem("tfx_onboarding_meta") === "done"; } catch { return false; } })();
        const fechado = (() => { try { return localStorage.getItem("tfx_onboarding_fechado") === "1"; } catch { return false; } })();
        const tudo = temProduto && temVenda && temGasto && temMeta;
        if (tudo || fechado) return null;
        const passos = [
          { id: "produto", label: "Cadastrar produto", desc: "Adicione seus produtos no estoque", icon: "📦", done: temProduto, action: () => setPage("estoque") },
          { id: "venda", label: "Primeira venda", desc: "Lance sua primeira venda no sistema", icon: "💰", done: temVenda, action: () => setPage("vendas") },
          { id: "gasto", label: "Primeiro gasto", desc: "Categorize seus gastos fixos e variáveis", icon: "↘", done: temGasto, action: () => setPage("gastos") },
          { id: "meta", label: "Definir metas", desc: "Configure suas metas de faturamento", icon: "🎯", done: temMeta, action: () => setPage("metas") },
        ];
        const concluidos = passos.filter(p => p.done).length;
        const pct = Math.round((concluidos / passos.length) * 100);
        const tudo_concluido = concluidos === passos.length;
        return (
          <div style={{ background: tudo_concluido ? "#f0fdf4" : "#f8fafc", border: `1px solid ${tudo_concluido ? "#15803d" : C.accent}30`, borderRadius: 16, padding: 20, marginBottom: 28 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 800, color: "#111827", marginBottom: 2 }}>
                  {tudo_concluido ? "✅ Sistema configurado!" : "🚀 Configure seu sistema"}
                </div>
                <div style={{ fontSize: 11, color: "#374151" }}>
                  {tudo_concluido ? "Parabéns! Seu sistema está pronto para uso." : `${concluidos} de ${passos.length} passos concluídos`}
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontSize: 22, fontWeight: 900, color: tudo_concluido ? "#15803d" : C.accent }}>{pct}%</div>
                  <div style={{ fontSize: 9, color: "#374151" }}>completo</div>
                </div>
                <button onClick={() => { try { localStorage.setItem("tfx_onboarding_fechado", "1"); } catch(e){} window.location.reload(); }} style={{ background: "none", border: "none", color: "#374151", cursor: "pointer", fontSize: 16, lineHeight: 1, padding: 4 }} title="Fechar">✕</button>
              </div>
            </div>
            <div style={{ height: 5, background: C.border, borderRadius: 3, marginBottom: 16, overflow: "hidden" }}>
              <div style={{ height: "100%", width: pct + "%", background: tudo_concluido ? "#15803d" : `linear-gradient(90deg, ${C.accent}, #FFD700)`, borderRadius: 3, transition: "width 0.6s ease" }} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8 }}>
              {passos.map((p, i) => (
                <div key={p.id} onClick={() => !p.done && p.action()} className={!p.done ? "tfx-card-hover" : ""} style={{ background: p.done ? "#27AE6015" : C.surface, border: `1px solid ${p.done ? "#27AE6040" : C.border}`, borderRadius: 10, padding: "12px", cursor: p.done ? "default" : "pointer", transition: "all 0.2s", position: "relative" }}>
                  {p.done && <div style={{ position: "absolute", top: 8, right: 8, width: 16, height: 16, borderRadius: "50%", background: "#15803d", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, color: "#fff", fontWeight: 800 }}>✓</div>}
                  <div style={{ fontSize: 20, marginBottom: 8 }}>{p.done ? "✅" : p.icon}</div>
                  <div style={{ fontSize: 10, fontWeight: 700, color: p.done ? "#15803d" : C.text, marginBottom: 3, lineHeight: 1.3 }}>{p.label}</div>
                  <div style={{ fontSize: 9, color: "#374151", lineHeight: 1.4 }}>{p.done ? "Concluído!" : p.desc}</div>
                  {!p.done && <div style={{ fontSize: 9, color: C.accent, marginTop: 6, fontWeight: 700 }}>Ir agora →</div>}
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      <div style={{ ...styles.card, marginBottom: 16, borderColor: "#e2e8f0", background: "#ffffff" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: metaConectado && metaAds ? 14 : 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ width: 44, height: 44, borderRadius: 12, background: "#1877F2", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, fontWeight: 700, color: "#fff", boxShadow: "0 4px 12px rgba(24, 119, 242, 0.4)" }}>f</div>
            <div>
              <div style={{ fontFamily: "Inter, sans-serif", fontWeight: 800, fontSize: 16, color: "#111827" }}>Meta Ads</div>
              {metaAds && <div style={{ fontSize: 10, color: "#374151", marginTop: 2 }}>{metaAds.data}</div>}
            </div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            {metaConectado && <button onClick={desconectarMeta} style={{ ...styles.btn("secondary"), fontSize: 10, padding: "5px 10px" }}>🔌 Desconectar</button>}
            {metaConectado && <button onClick={() => setShowConectarMeta(true)} style={{ ...styles.btn("secondary"), fontSize: 10, padding: "5px 10px" }}>🔄 Trocar</button>}
            {metaConectado && metaAds && <button onClick={lancarGastoMeta} disabled={metaLoading || metaLancado} style={{ ...styles.btn(metaLancado ? "secondary" : "primary"), fontSize: 10, padding: "5px 12px", opacity: metaLancado ? 0.6 : 1 }}>{metaLancado ? "✅ Lançado" : metaLoading ? "Lançando..." : "⚡ Lançar Gasto"}</button>}
            {!metaConectado && !showConectarMeta && <button onClick={() => setShowConectarMeta(true)} style={{ ...styles.btn("primary"), fontSize: 10, padding: "5px 12px" }}>🔗 Conectar Meta Ads</button>}
          </div>
        </div>
        {showConectarMeta && (
          <div style={{ marginTop: 12, padding: 16, background: C.bgElevated, borderRadius: 10, border: "1px solid " + C.border }}>
            <div style={{ fontSize: 11, color: "#374151", marginBottom: 12 }}>Cole seu Access Token e ID da conta de anúncios.</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 12 }}>
              <div style={{ background: "#fffbeb", border: "1px solid #f6e05e", borderRadius: 8, padding: "10px 12px", fontSize: 12, color: "#744210", marginBottom: 8 }}>
                <strong>📋 Como conectar o Meta Ads (siga com atenção):</strong>
                <ol style={{ margin: "6px 0 0 0", paddingLeft: 16, lineHeight: 1.8 }}>
                  <li><strong>Avise o suporte:</strong> Antes de começar, informe seu <strong>Business ID</strong> ao suporte para liberar o acesso ao app. Seu Business ID aparece na URL do Gerenciador de Anúncios após <code>business_id=</code></li>
                  <li><strong>Criar usuário do sistema:</strong> Acesse <a href="https://business.facebook.com/settings" target="_blank" rel="noreferrer" style={{ color: "#2b6cb0" }}>Configurações do Business</a> → ícone ⚙️ no menu lateral → <strong>Usuários → Usuários do sistema</strong> → clique em <strong>+ Adicionar</strong> → dê um nome (ex: "Admin Gestão") → selecione <strong>Admin</strong> → confirme</li>
                  <li><strong>Atribuir conta de anúncios:</strong> Com o usuário criado, clique em <strong>Atribuir ativos</strong> → selecione <strong>Contas de anúncios</strong> → escolha sua conta → ative <strong>Gerenciar campanhas</strong> → clique em <strong>Atribuir ativos</strong></li>
                  <li><strong>Verificar o app:</strong> Vá em <strong>Contas → Apps</strong> no menu lateral e confirme que o app <strong>Gestão TFX</strong> aparece lá (se não aparecer, aguarde a liberação do suporte)</li>
                  <li><strong>Gerar o token:</strong> Volte em <strong>Usuários do sistema</strong> → clique no usuário criado → clique em <strong>Gerar token</strong> → selecione o app <strong>Gestão TFX</strong> → em expiração selecione <strong>60 dias</strong> → marque as permissões: <code>ads_management</code>, <code>ads_read</code> e <code>business_management</code> → clique em <strong>Gerar token</strong></li>
                  <li><strong>Copiar o token e o ID da conta:</strong> Copie o token gerado e cole abaixo. O Ad Account ID está no Gerenciador de Anúncios no canto superior (ex: <code>act_123456789</code>)</li>
                </ol>
                <p style={{ margin: "6px 0 0 0" }}>⚠️ Dúvidas? Entre em contato com o suporte antes de começar.</p>
              </div>
              <div><label style={styles.label}>Access Token</label><input style={styles.input} value={metaForm.token} onChange={e => setMetaForm(f => ({...f, token: e.target.value}))} placeholder="EAFy61q..." /></div>
              <div><label style={styles.label}>Ad Account ID</label><input style={styles.input} value={metaForm.adAccount} onChange={e => setMetaForm(f => ({...f, adAccount: e.target.value}))} placeholder="act_369245..." /></div>
            </div>
            <div style={{ fontSize: 10, color: "#374151", marginBottom: 10 }}>📌 Token: developers.facebook.com/tools/explorer | ID: adsmanager.facebook.com</div>
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={salvarMetaCredenciais} disabled={salvandoMeta} style={{ ...styles.btn("primary"), fontSize: 11 }}>{salvandoMeta ? "Verificando..." : "Conectar →"}</button>
              <button onClick={() => setShowConectarMeta(false)} style={styles.btn("secondary")}>Cancelar</button>
            </div>
          </div>
        )}
        {metaConectado && metaAds && (
          <div>
            {(() => {
              const spend = parseFloat(metaAds.spend) || 0;
              // Calcular faturamento do período filtrado
              const vendasFiltradas = filtrarListaD(data.vendas);
              const fatMes = vendasFiltradas.reduce((s,v)=>s+v.valor*v.qtd,0);
              const roasVal = spend > 0 ? (fatMes / spend) : null;
              const roasStr = roasVal ? roasVal.toFixed(2) + "x" : "—";
              const roasColor = roasVal >= 3 ? "#15803d" : roasVal >= 1.5 ? C.gold : C.warn;
              return (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12 }}>
                  {[
                    { label: "ROAS", value: roasStr, color: roasColor, sub: "faturamento ÷ gasto no Meta", icon: "🎯" },
                    { label: "Gasto", value: "R$ " + metaAds.spend, color: C.warn, sub: "investido no período", icon: "💸" },
                    { label: "Compras", value: String(metaAds.compras), color: C.accent, sub: "conversões", icon: "🛒" },
                    { label: "CPV", icon: "💳", value: "R$ " + metaAds.cpv, color: C.gold, sub: "custo por venda" },
                  ].map((k, i) => (
                    <div key={k.label} style={{ 
                      padding: "16px",
                      background: `${k.color}08`,
                      border: `1px solid rgba(0,0,0,0.07)`,
                      borderRadius: 12,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      textAlign: "center",
                      transition: "all 0.3s"
                    }} className="tfx-card-hover">
                      <div style={{ fontSize: 24, marginBottom: 8 }}>{k.icon}</div>
                      <div style={{ fontSize: 9, color: "#374151", letterSpacing: 1.5, textTransform: "uppercase", fontWeight: 600, marginBottom: 8 }}>{k.label}</div>
                      <div style={{ fontFamily: "Inter, sans-serif", fontWeight: 800, fontSize: 22, color: k.color, lineHeight: 1, letterSpacing: -0.5 }}>{privado ? "••••" : k.value}</div>
                      <div style={{ fontSize: 9, color: "#374151", marginTop: 6 }}>{k.sub}</div>
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>
        )}
      </div>


      {/* KPIs principais — linha 1: cards grandes */}
      {(() => {
        const saldoCaixa = (() => { const cfg = data.caixa_config || {}; const movs = data.caixa_movimentos || []; const saldoIni = Number(cfg.saldo_inicial || 0); const entradas = movs.filter(m=>m.tipo==="entrada").reduce((s,m)=>s+Number(m.valor),0); const saidas = movs.filter(m=>m.tipo==="saida").reduce((s,m)=>s+Number(m.valor),0); return saldoIni + entradas - saidas; })();
        // Usar vendas e gastos filtrados (respeitam o filtro ativo)
        console.log('🔍 Filtro Dashboard:', { periodoRapidoD, mesFiltro, dataInicioD, dataFimD });
        const vendasFiltradas = filtrarListaD(data.vendas);
        const gastosFiltrados = filtrarListaD(gastosD);
        console.log('📊 Dados filtrados:', { vendasFiltradas: vendasFiltradas.length, gastosFiltrados: gastosFiltrados.length });
        const _hojeD = hojeManaus();
        const _usaLive = metaHojeDash !== null && filtrarListaD([{ data: _hojeD }]).length > 0;
        const trafegoMes = gastosFiltrados.filter(g => g.categoria === "Tráfego Pago" && !(_usaLive && g.data === _hojeD)).reduce((s,g)=>s+Number(g.valor||0),0) + (_usaLive ? metaHojeDash : 0);
        const fatMes = vendasFiltradas.reduce((s,v)=>s+v.valor*v.qtd,0);
        const kpisPrimarios = [
          { label: "Faturamento", icon: "💰", value: privado ? "••••••" : fmt(totalVendas), sub: privado ? "•• vendas" : `${vendasD.length} vendas`, color: C.accent, raw: totalVendas },
          { label: "Lucro Operacional", icon: "📈", value: privado ? "••••••" : fmt(lucroLiquido), sub: privado ? "••••" : pct(margem) + " de margem · após gastos da loja", color: C.gold, raw: lucroLiquido },
          { label: "Total Gastos", icon: "💸", value: privado ? "••••••" : fmt(totalGastos + gastosPessoal), sub: "Loja + Pessoal", color: C.warn, raw: totalGastos + gastosPessoal },
          { label: "Resultado Final", icon: "🎯", value: privado ? "••••••" : fmt(resultado), sub: "Após tudo", color: resultado >= 0 ? C.accent : C.warn, raw: resultado },
        ];
        const kpisSecundarios = [
          { label: "Saldo em Caixa", icon: "🏦", value: privado ? "••••••" : fmt(saldoCaixa), sub: "Caixa atual", color: saldoCaixa >= 0 ? C.accent : C.warn },
          { label: "Ticket Médio", icon: "🎫", value: privado ? "••••••" : fmt(vendasD.length > 0 ? totalVendas / vendasD.length : 0), sub: "Por venda", color: C.info },
          { label: "ROAS (lançado)", icon: "📊", value: privado ? "••••" : (trafegoMes > 0 ? (fatMes / trafegoMes).toFixed(2) + "x" : "—"), sub: "faturamento ÷ tráfego lançado", color: C.gold },
          { label: "CPV", icon: "💳", value: privado ? "••••••" : (vendasD.length > 0 ? fmt(trafegoMes / vendasD.length) : "—"), sub: "Custo por venda", color: C.warn },
          { label: "Investido Tráfego", icon: "🚀", value: privado ? "••••••" : fmt(trafegoMes), sub: "Mês atual", color: C.info },
        ];
        return (
          <div style={{ marginBottom: 24 }}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 20, marginBottom: 16 }}>
              {kpisPrimarios.map((k, idx) => (
                <div key={k.label} className={`tfx-fade-in-delay-${idx}`} style={{ background: C.bgElevated, border: `1px solid rgba(0,0,0,0.08)`, borderRadius: 20, padding: 28, position: "relative", overflow: "hidden", boxShadow: `0 1px 3px rgba(0,0,0,0.08), 0 0 0 1px rgba(0,0,0,0.06)`, transition: "all 0.3s", cursor: "pointer" }}
                onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-6px)"; e.currentTarget.style.boxShadow = `0 20px 60px ${k.color}50`; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = `0 10px 40px ${k.color}30`; }}>
                  <div style={{ position: "absolute", top: -80, right: -80, width: 160, height: 160, background: `radial-gradient(circle, ${k.color}25 0%, transparent 70%)` }} />
                  <div style={{ position: "absolute", top: 24, right: 24, width: 52, height: 52, borderRadius: 16, background: `${k.color}18`, border: `2px solid ${k.color}30`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24 }}>{k.icon}</div>
                  <div style={{ position: "relative", zIndex: 1 }}>
                    <div style={{ fontSize: 11, color: "#374151", fontWeight: 700, marginBottom: 14, textTransform: "uppercase", letterSpacing: 1.5 }}>{k.label}</div>
                    {k.raw !== undefined ? <AnimatedKpiValue value={k.raw} formatted={k.value} color={k.color} privado={privado} /> : <div style={{ fontSize: 38, fontWeight: 900, color: k.color, letterSpacing: -2, marginBottom: 10 }}>{k.value}</div>}
                    <div style={{ fontSize: 13, color: "#374151", fontWeight: 500 }}>{k.sub}</div>
                  </div>
                </div>
              ))}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 16 }}>
              {kpisSecundarios.map((k, idx) => (
                <div key={k.label} className={`tfx-fade-in-delay-${idx}`} style={{ background: C.bgElevated, border: `1px solid rgba(0,0,0,0.07)`, borderRadius: 16, padding: 20, position: "relative", overflow: "hidden", boxShadow: `0 1px 2px rgba(0,0,0,0.06), 0 0 0 1px rgba(0,0,0,0.05)`, transition: "all 0.3s", cursor: "pointer" }}
                onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-4px)"; e.currentTarget.style.boxShadow = `0 8px 30px ${k.color}30`; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = `0 4px 20px ${k.color}15`; }}>
                  <div style={{ position: "absolute", top: -40, right: -40, width: 80, height: 80, background: `radial-gradient(circle, ${k.color}20 0%, transparent 70%)` }} />
                  {k.icon && (
                    <div style={{ position: "absolute", top: 16, right: 16, width: 40, height: 40, borderRadius: 12, background: `${k.color}15`, border: `1px solid ${k.color}25`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>
                      {k.icon}
                    </div>
                  )}
                  <div style={{ position: "relative", zIndex: 1 }}>
                    <div style={{ fontSize: 10, color: "#374151", fontWeight: 700, marginBottom: 10, textTransform: "uppercase", letterSpacing: 1 }}>{k.label}</div>
                    <div style={{ fontSize: 22, fontWeight: 800, color: k.color, letterSpacing: -1, marginBottom: 6 }}>{k.value}</div>
                    <div style={{ fontSize: 11, color: "#374151", fontWeight: 500 }}>{k.sub}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })()}


      
      {/* GRÁFICO */}
      <div style={{ background: C.bgElevated, border: "1px solid #e5e7eb", borderRadius: 20, padding: 32, marginBottom: 28 }}>
        <h3 style={{ fontSize: 20, fontWeight: 800, color: "#111827", marginBottom: 28 }}>📈 Últimos 6 Meses</h3>
        {(() => {
          const meses = [];
          for (let i = 5; i >= 0; i--) {
            meses.push(mesRelativo(-i));
          }
          const valores = meses.map(m => data.vendas.filter(v => v.data?.startsWith(m)).reduce((s,v) => s + v.valor * v.qtd, 0));
          const maxV = Math.max(...valores.filter(v => v > 0), 1);
          return (
            <div style={{ height: 220, display: "flex", alignItems: "flex-end", gap: 20, position: "relative" }}>
              {valores.map((val, idx) => {
                const altura = val > 0 ? Math.floor((val / maxV) * 180) : 0;
                return (
                  <div key={idx} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 10 }}>
                    <div style={{ width: "100%", height: Math.max(altura, val > 0 ? 24 : 4), background: `linear-gradient(180deg, ${C.accent}, ${C.accent}dd)`, borderRadius: "10px 10px 0 0", border: `2px solid ${C.accent}80`, position: "relative", boxShadow: `0 -4px 16px ${C.accent}40` }}>
                      {val > 0 && (
                        <div style={{ position: "absolute", top: -26, left: "50%", transform: "translateX(-50%)", fontSize: 11, fontWeight: 900, color: C.accent, whiteSpace: "nowrap", background: "#f0f2f5", padding: "3px 8px", borderRadius: 6, border: `1px solid ${C.accent}40` }}>
                          {fmt(val)}
                        </div>
                      )}
                    </div>
                    <div style={{ fontSize: 11, color: "#374151", fontWeight: 700 }}>{meses[idx].slice(5)}/{meses[idx].slice(2,4)}</div>
                  </div>
                );
              })}
            </div>
          );
        })()}
      </div>

      {/* Comparativo mês anterior */}
      {(() => {
        // Usar mês do filtro do dashboard se selecionado, senão mês atual
        const mesAtualComp = (mesFiltro && mesFiltro !== "todos") ? mesFiltro : hojeManaus().slice(0, 7);
        const diaAtual = hojeManaus().slice(8, 10);
        const [anoC, mesC] = mesAtualComp.split("-").map(Number);
        const mesAntData = mesComparacao ? new Date(mesComparacao + "-01") : new Date(anoC, mesC - 2, 1);
        const mesAnt = mesComparacao || `${mesAntData.getFullYear()}-${String(mesAntData.getMonth() + 1).padStart(2, "0")}`;
        // Se é mês atual real, limitar ao dia atual; senão, mês completo
        const ehMesAtualReal = mesAtualComp === hojeManaus().slice(0, 7);
        const diaLimiteAnt = ehMesAtualReal ? `${mesAnt}-${diaAtual}` : `${mesAnt}-31`;
        const diaLimiteAtual = ehMesAtualReal ? `${mesAtualComp}-${diaAtual}` : `${mesAtualComp}-31`;
        const vendasAnt = data.vendas.filter(v => v.data && v.data.startsWith(mesAnt) && v.data <= diaLimiteAnt);
        const gastosAnt = data.gastos.filter(g => g.data && g.data.startsWith(mesAnt) && g.data <= diaLimiteAnt);
        const fatAnt = vendasAnt.reduce((s, v) => s + v.valor * v.qtd, 0);
        const fatAtual = data.vendas.filter(v => v.data && v.data.startsWith(mesAtualComp) && v.data <= diaLimiteAtual).reduce((s, v) => s + v.valor * v.qtd, 0);
        const lucroAnt = fatAnt - vendasAnt.reduce((s,v)=>s+(v.custo||0)*v.qtd,0) - gastosAnt.reduce((s,g)=>s+g.valor,0);
        const lucroAtual = data.vendas.filter(v=>v.data&&v.data.startsWith(mesAtualComp)&&v.data<=diaLimiteAtual).reduce((s,v)=>s+v.valor*v.qtd,0) - data.vendas.filter(v=>v.data&&v.data.startsWith(mesAtualComp)&&v.data<=diaLimiteAtual).reduce((s,v)=>s+(v.custo||0)*v.qtd,0) - data.gastos.filter(g=>g.data&&g.data.startsWith(mesAtualComp)&&g.data<=diaLimiteAtual).reduce((s,g)=>s+g.valor,0);
        const vendAnt = vendasAnt.length;
        const vendAtual = data.vendas.filter(v=>v.data&&v.data.startsWith(mesAtualComp)).length;
        if (!fatAnt && !fatAtual) return null;
        const diff = (atual, ant) => {
          if (!ant) return null;
          const p = ((atual - ant) / ant * 100).toFixed(1);
          return { p, up: atual >= ant };
        };
        const compItems = [
          { label: "Faturamento", icon: "💰", atual: fatAtual, ant: fatAnt, fmt: true },
          { label: "Lucro", icon: "📈", atual: lucroAtual, ant: lucroAnt, fmt: true },
          { label: "Vendas", icon: "🛒", atual: vendAtual, ant: vendAnt, fmt: false },
        ];
        const diaComp = new Date().getDate();
        const diaFmt = String(diaComp).padStart(2, '0');
        const mesAntFmt = String(mesAntData.getMonth() + 1).padStart(2, '0');
        const mesAtualFmt = String(mesC).padStart(2, '0');
        const labelAnt = `${diaFmt}/${mesAntFmt}`;
        const labelAtual = `${diaFmt}/${mesAtualFmt}`;
        return (
          <div style={{ background: C.bgElevated, border: "1px solid #e5e7eb", borderRadius: 16, padding: "16px 20px", marginBottom: 28 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, marginTop: 48 }}>
              <div style={{ fontSize: 11, color: "#374151", letterSpacing: 1 }}>📊 COMPARATIVO MENSAL</div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 10, color: "#9ca3af" }}>Comparar com:</span>
                <select value={mesComparacao || ""} onChange={e => setMesComparacao(e.target.value || null)} style={{ fontSize: 11, border: "1px solid #e5e7eb", borderRadius: 6, padding: "4px 8px", background: "#fff", color: "#374151", cursor: "pointer" }}>
                  <option value="">Mês anterior</option>
                  {Array.from({length: 12}, (_, i) => mesRelativo(-i - 1)).map(m => (
                    <option key={m} value={m}>{MESES[parseInt(m.slice(5,7))-1] + "/" + m.slice(2,4)}</option>
                  ))}
                </select>
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 20 }}>
              {compItems.map(item => {
                const d = diff(item.atual, item.ant);
                return (
                  <div key={item.label} style={{ 
                    background: C.bgElevated,
                    border: "1px solid #e5e7eb",
                    borderRadius: 12,
                    padding: "16px 18px"
                  }}>
                    <div style={{ fontSize: 10, color: "#374151", letterSpacing: 1.5, marginBottom: 12, textTransform: "uppercase", fontWeight: 600 }}>{item.label}</div>
                    <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 9, color: "#374151", marginBottom: 4 }}>{labelAnt}</div>
                        <div style={{ fontSize: 16, fontWeight: 700, color: "#374151" }}>{privado ? "••••" : item.fmt ? fmt(item.ant) : item.ant}</div>
                      </div>
                      <div style={{ fontSize: 20, color: C.accent }}>→</div>
                      <div style={{ flex: 1, textAlign: "right" }}>
                        <div style={{ fontSize: 9, color: "#374151", marginBottom: 4 }}>{labelAtual}</div>
                        <div style={{ fontSize: 16, fontWeight: 800, color: "#111827" }}>{privado ? "••••" : item.fmt ? fmt(item.atual) : item.atual}</div>
                      </div>
                    </div>
                    {d && <div style={{ 
                      background: d.up ? `${C.accent}15` : `${C.warn}15`,
                      border: `1px solid ${d.up ? C.accent : C.warn}30`,
                      borderRadius: 6,
                      padding: "6px 10px",
                      display: "flex",
                      alignItems: "center",
                      gap: 6
                    }}>
                      <span style={{ fontSize: 11, fontWeight: 700, color: d.up ? C.accent : C.warn }}>{d.up ? "▲" : "▼"} {Math.abs(d.p)}%</span>
                      <span style={{ fontSize: 10, color: "#374151" }}>{d.up ? "acima" : "abaixo"} do mês anterior</span>
                    </div>}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16, marginBottom: 16 }}>
        {/* Gráfico mensal */}
        <div style={styles.card} className="tfx-card-hover">
          <div style={styles.sectionTitle}>Evolução Mensal</div>
          {barras.length === 0 ? <div style={{ textAlign:"center", padding:"24px 0", color:C.muted }}><div style={{fontSize:28,marginBottom:8}}>📉</div><div style={{fontSize:12}}>Sem vendas no período selecionado</div></div> : (
            <div style={{ display: "flex", alignItems: "flex-end", gap: 10, height: 110, marginTop: 12 }}>
              {barras.map((b, i) => (
                <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 2, height: "100%", justifyContent: "flex-end" }}>
                  <div style={{ width: "100%", display: "flex", gap: 2, alignItems: "flex-end", height: "85%" }}>
                    <div style={{ flex: 1, height: `${(b.fat/maxBar)*100}%`, background: C.accent, borderRadius: "2px 2px 0 0", minHeight: b.fat>0?3:0 }} />
                    <div style={{ flex: 1, height: `${(b.gas/maxBar)*100}%`, background: C.warn, borderRadius: "2px 2px 0 0", minHeight: b.gas>0?3:0 }} />
                  </div>
                  <div style={{ fontSize: 9, color: "#374151" }}>{b.label}</div>
                </div>
              ))}
            </div>
          )}
          <div style={{ display: "flex", gap: 12, marginTop: 8 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}><div style={{ width: 8, height: 8, background: C.accent, borderRadius: 2 }}/><span style={{ fontSize: 9, color: "#374151" }}>Fat.</span></div>
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}><div style={{ width: 8, height: 8, background: C.warn, borderRadius: 2 }}/><span style={{ fontSize: 9, color: "#374151" }}>Gastos</span></div>
          </div>
        </div>

        {/* Pizza gastos */}
        <div style={styles.card} className="tfx-card-hover">
          <div style={styles.sectionTitle}>Composição dos Gastos</div>
          <div style={{ display: "flex", alignItems: "center", gap: 16, marginTop: 12 }}>
            <PizzaChart data={pizzaGastos} size={110} />
            <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 6 }}>
              {pizzaGastos.map(d => (
                <div key={d.label} style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                    <div style={{ width: 7, height: 7, borderRadius: 2, background: d.color }} />
                    <span style={{ fontSize: 10, color: "#374151" }}>{d.label}</span>
                  </div>
                  <span style={{ fontSize: 10, fontWeight: 700, color: d.color }}>{pct(pizzaGastos.reduce((s,x)=>s+x.value,0) > 0 ? d.value / pizzaGastos.reduce((s,x)=>s+x.value,0) : 0)}</span>
                </div>
              ))}
              {pizzaGastos.length === 0 && <span style={{ fontSize: 11, color: "#374151" }}>Sem gastos</span>}
            </div>
          </div>
        </div>

        {/* Pizza canais */}
        <div style={styles.card} className="tfx-card-hover">
          <div style={styles.sectionTitle}>Vendas por Canal</div>
          <div style={{ display: "flex", alignItems: "center", gap: 16, marginTop: 12 }}>
            <PizzaChart data={pizzaCanais} size={110} />
            <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 10 }}>
              {[{ label: "Online", color: C.accent, value: online }, { label: "Física", color: "#374151", value: fisica }].map(d => (
                <div key={d.label}>
                  <div style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: 3 }}>
                    <div style={{ width: 7, height: 7, borderRadius: 2, background: d.color }} />
                    <span style={{ fontSize: 10, color: "#374151" }}>{d.label}</span>
                  </div>
                  <div style={{ fontWeight: 700, fontSize: 14, color: d.color }}>{privado ? "••••••" : fmt(d.value)}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Gráfico linha mês atual + Saúde */}
      {(() => {
        // ── Gráfico de linha do mês atual ───────────────────────
        const hoje2 = hojeManaus();
        const mesAtualG = hoje2.slice(0, 7);
        const [anoG, mesG] = mesAtualG.split("-").map(Number);
        // const diasNoMesG = new Date(anoG, mesG, 0).getDate();
        const diaHojeG = parseInt(hoje2.slice(8, 10));

        // Faturamento por dia
        const fatDia = {};
        data.vendas.filter(v => v.data && v.data.startsWith(mesAtualG)).forEach(v => {
          fatDia[v.data] = (fatDia[v.data] || 0) + v.valor * v.qtd;
        });

        // Acumulado dia a dia
        let acum = 0;
        const pontosG = [];
        for (let d = 1; d <= diaHojeG; d++) {
          const dataStr = `${mesAtualG}-${String(d).padStart(2, "0")}`;
          acum += fatDia[dataStr] || 0;
          pontosG.push({ dia: d, acum });
        }

        const maxG = Math.max(...pontosG.map(p => p.acum), 1);
        const wG = 100, hG = 80;
        const xStep = pontosG.length > 1 ? wG / (pontosG.length - 1) : wG;
        const toY = v => hG - (v / maxG) * hG * 0.9;
        const pathD = pontosG.map((p, i) => `${i === 0 ? "M" : "L"}${(i * xStep).toFixed(1)},${toY(p.acum).toFixed(1)}`).join(" ");
        const areaD = pontosG.length > 1 ? `${pathD} L${((pontosG.length-1)*xStep).toFixed(1)},${hG} L0,${hG} Z` : "";

        // ── Indicador de saúde ───────────────────────────────────
        const scores = [];
        // 1. Margem líquida (0-30pts)
        const margemScore = margem >= 0.3 ? 30 : margem >= 0.2 ? 22 : margem >= 0.1 ? 14 : margem > 0 ? 6 : 0;
        scores.push({ label: "Margem líquida", score: margemScore, max: 30, value: (margem * 100).toFixed(1) + "%", cor: margemScore >= 22 ? "#15803d" : margemScore >= 14 ? C.accent : C.warn });

        // 2. Crescimento vs mês anterior (0-25pts)
        const mesAntG = new Date(anoG, mesG - 2, 1);
        const mesAntStr = `${mesAntG.getFullYear()}-${String(mesAntG.getMonth() + 1).padStart(2, "0")}`;
        const fatMesAnt = data.vendas.filter(v => v.data && v.data.startsWith(mesAntStr)).reduce((s, v) => s + v.valor * v.qtd, 0);
        const crescimento = fatMesAnt > 0 ? ((totalVendas - fatMesAnt) / fatMesAnt) : 0;
        const crescScore = crescimento >= 0.2 ? 25 : crescimento >= 0.1 ? 18 : crescimento >= 0 ? 10 : 0;
        scores.push({ label: "Crescimento", score: crescScore, max: 25, value: (crescimento * 100).toFixed(1) + "%", cor: crescScore >= 18 ? "#15803d" : crescScore >= 10 ? C.accent : C.warn });

        // 3. Controle de gastos (0-25pts)
        const ratioGastos = totalVendas > 0 ? (totalGastos + gastosPessoal) / totalVendas : 1;
        const gastosScore = ratioGastos <= 0.4 ? 25 : ratioGastos <= 0.6 ? 18 : ratioGastos <= 0.8 ? 10 : 0;
        scores.push({ label: "Controle de gastos", score: gastosScore, max: 25, value: (ratioGastos * 100).toFixed(0) + "% do fat.", cor: gastosScore >= 18 ? "#15803d" : gastosScore >= 10 ? C.accent : C.warn });

        // 4. Vendas registradas (0-20pts)
        const qtdVendas = vendasD.length;
        const vendasScore = qtdVendas >= 50 ? 20 : qtdVendas >= 20 ? 15 : qtdVendas >= 5 ? 8 : qtdVendas > 0 ? 4 : 0;
        scores.push({ label: "Volume de vendas", score: vendasScore, max: 20, value: qtdVendas + " vendas", cor: vendasScore >= 15 ? "#15803d" : vendasScore >= 8 ? C.accent : C.warn });

        const totalScore = scores.reduce((s, x) => s + x.score, 0);
        const saudeLabel = totalScore >= 80 ? "Excelente 🚀" : totalScore >= 60 ? "Bom 💪" : totalScore >= 40 ? "Regular ⚠️" : "Atenção 🔴";
        const saudeCor = totalScore >= 80 ? "#15803d" : totalScore >= 60 ? C.accent : totalScore >= 40 ? C.gold : C.warn;

        return (
          <div style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr", gap: 16, marginBottom: 16 }}>
            {/* Gráfico linha */}
            <div style={styles.card} className="tfx-card-hover">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                <div style={styles.sectionTitle}>📈 Faturamento Acumulado — {new Date(anoG, mesG-1).toLocaleDateString("pt-BR", { month: "long" })}</div>
                <div style={{ fontFamily: "Inter, sans-serif", fontWeight: 800, fontSize: 18, color: C.accent }}>{privado ? "••••••" : fmt(acum)}</div>
              </div>
              {pontosG.length > 1 ? (
                <svg viewBox={`0 0 ${wG} ${hG}`} style={{ width: "100%", height: 120, overflow: "visible" }} preserveAspectRatio="none">
                  <defs>
                    <linearGradient id="fatGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#111827" stopOpacity="0.3" />
                      <stop offset="100%" stopColor="#111827" stopOpacity="0.02" />
                    </linearGradient>
                  </defs>
                  {/* Grid lines */}
                  {[0.25, 0.5, 0.75, 1].map(f => (
                    <line key={f} x1="0" y1={toY(maxG * f)} x2={wG} y2={toY(maxG * f)} stroke="#2A220015" strokeWidth="0.5" />
                  ))}
                  {/* Área */}
                  {areaD && <path d={areaD} fill="url(#fatGrad)" />}
                  {/* Linha */}
                  <path d={pathD} fill="none" stroke="#111827" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
                  {/* Ponto atual */}
                  <circle
                    cx={((pontosG.length-1)*xStep).toFixed(1)}
                    cy={toY(pontosG[pontosG.length-1].acum).toFixed(1)}
                    r="2" fill="#111827"
                    style={{ filter: "drop-shadow(0 0 4px #FFB800)" }}
                  />
                </svg>
              ) : (
                <div style={{ textAlign: "center", padding: "32px 0", color: "#374151" }}>
                  <div style={{ fontSize: 28, marginBottom: 8 }}>📊</div>
                  <div style={{ fontSize: 12 }}>Registre vendas para ver o gráfico</div>
                </div>
              )}
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4 }}>
                <div style={{ fontSize: 9, color: "#374151" }}>Dia 1</div>
                <div style={{ fontSize: 9, color: "#374151" }}>Dia {diaHojeG}</div>
              </div>
            </div>

            {/* Saúde do negócio */}
            <div style={styles.card} className="tfx-card-hover">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
                <div style={styles.sectionTitle}>💊 Saúde do Negócio</div>
                <div style={{ fontSize: 9, color: "#374151", textAlign: "right", lineHeight: 1.6 }}>
                  <div>Score 0–100 baseado em:</div>
                  <div>margem · crescimento · gastos · vendas</div>
                </div>
              </div>
              <div style={{ textAlign: "center", margin: "0 0 16px" }}>
                <div style={{ fontFamily: "Inter, sans-serif", fontWeight: 900, fontSize: 48, color: saudeCor, letterSpacing: -2, lineHeight: 1 }}>
                  {privado ? "••" : totalScore}
                </div>
                <div style={{ fontSize: 11, color: saudeCor, fontWeight: 600, marginTop: 4 }}>{privado ? "••••••" : saudeLabel}</div>
                <div style={{ height: 6, background: C.border, borderRadius: 3, margin: "10px 0", overflow: "hidden" }}>
                  <div style={{ height: "100%", width: (privado ? 0 : totalScore) + "%", background: `linear-gradient(90deg, ${C.warn}, ${C.accent}, #27AE60)`, borderRadius: 3, transition: "width 1s ease" }} />
                </div>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {scores.map(s => (
                  <div key={s.label}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
                      <span style={{ fontSize: 10, color: "#374151" }}>{s.label}</span>
                      <span style={{ fontSize: 10, fontWeight: 700, color: s.cor }}>{privado ? "••" : s.score}/{s.max}</span>
                    </div>
                    <div style={{ height: 3, background: C.border, borderRadius: 2, overflow: "hidden" }}>
                      <div style={{ height: "100%", width: privado ? "0%" : (s.score / s.max * 100) + "%", background: s.cor, borderRadius: 2, transition: "width 0.8s ease" }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Alertas + atalhos */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        <div style={styles.card} className="tfx-card-hover">
          <div style={styles.sectionTitle}>Alertas</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 12 }}>
            {semEstoque > 0 && <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", background: "#FF3B3015", border: "1px solid #FF3B3040", borderRadius: 8 }}>
              <span style={{ fontSize: 16 }}>🔴</span>
              <span style={{ fontSize: 12, color: "#FF3B30" }}><b>{semEstoque}</b> produto(s) esgotado(s)</span>
              <button onClick={() => setPage("estoque")} style={{ ...styles.btn("secondary"), marginLeft: "auto", fontSize: 10, padding: "4px 10px" }}>Ver</button>
            </div>}
            {critico > 0 && <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", background: C.warnDim, border: `1px solid ${C.warn}40`, borderRadius: 8 }}>
              <span style={{ fontSize: 16 }}>🟡</span>
              <span style={{ fontSize: 12, color: C.warn }}><b>{critico}</b> produto(s) em estoque crítico</span>
              <button onClick={() => setPage("estoque")} style={{ ...styles.btn("secondary"), marginLeft: "auto", fontSize: 10, padding: "4px 10px" }}>Ver</button>
            </div>}
            {pendentes > 0 && <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", background: C.accentDim, border: `1px solid ${C.accent}40`, borderRadius: 8 }}>
              <span style={{ fontSize: 16 }}>📦</span>
              <span style={{ fontSize: 12, color: C.accent }}><b>{pendentes}</b> compra(s) pendente(s)</span>
              <button onClick={() => setPage("compras")} style={{ ...styles.btn("secondary"), marginLeft: "auto", fontSize: 10, padding: "4px 10px" }}>Ver</button>
            </div>}
            {semEstoque === 0 && critico === 0 && pendentes === 0 && <div style={{ fontSize: 12, color: "#374151", padding: "10px 0" }}>✅ Tudo em ordem!</div>}
          </div>
        </div>

        <div style={styles.card}>
          <div style={styles.sectionTitle}>Acesso Rápido</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 12 }}>
            {[
              { label: "Nova Venda", icon: "↗", page: "vendas", color: C.accent },
              { label: "Novo Gasto", icon: "↘", page: "gastos", color: C.warn },
              { label: "Ver Estoque", icon: "▦", page: "estoque", color: C.gold },
              { label: "Ver DRE", icon: "◈", page: "dre", color: C.info },
            ].map(a => (
              <button key={a.label} onClick={() => setPage(a.page)} style={{ background: a.color + "15", border: `1px solid ${a.color}30`, borderRadius: 10, padding: "14px", cursor: "pointer", textAlign: "left" }}>
                <div style={{ fontSize: 18, marginBottom: 6 }}>{a.icon}</div>
                <div style={{ fontFamily: "'Inter', sans-serif", fontWeight: 600, fontSize: 12, color: a.color }}>{a.label}</div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}


function DRE({ data, privado }) {
  const { mesFiltro, periodoRapido: periodoRapidoD, dataInicio: dataInicioD, setDataInicio: setDataInicioD, dataFim: dataFimD, setDataFim: setDataFimD, showFiltro: showFiltroD, setShowFiltro: setShowFiltroD, aplicarPeriodo: aplicarPeriodoRapidoD, selecionarMes: selecionarMesDRE, filtrarLista: filtrarListaDRE, labelPeriodo: labelPeriodoDRE } = useFiltro("tfx_dre");

  const mesesDisponiveis = [...new Set([...data.vendas.map(v => getMes(v.data)), ...data.gastos.map(g => getMes(g.data))])].filter(Boolean).sort().reverse();
  const vendas = filtrarListaDRE(data.vendas);
  // eslint-disable-next-line no-unused-vars
  const gastosPorMes = filtrarListaDRE(data.gastos);

  const totalVendas = vendas.reduce((s, v) => s + v.valor * v.qtd, 0);
  const totalCMV = vendas.reduce((s, v) => s + (v.custo||0) * v.qtd, 0);
  const lucroBruto = totalVendas - totalCMV;
  const margBruta = lucroBruto / totalVendas;

  const gastosLoja = gastosPorMes.filter((g) => g.tipo === "loja").reduce((s, g) => s + g.valor, 0);
  const gastosPessoal = gastosPorMes.filter((g) => g.tipo === "pessoal").reduce((s, g) => s + g.valor, 0);

  const lucroLiquido = lucroBruto - gastosLoja;
  const lucroAposRetorno = lucroLiquido - gastosPessoal;
  const margLiquida = lucroLiquido / totalVendas;

  const impostos = gastosPorMes.filter((g) => g.categoria === "Imposto").reduce((s, g) => s + g.valor, 0);
  const trafego = gastosPorMes.filter((g) => g.categoria === "Tráfego Pago").reduce((s, g) => s + g.valor, 0);
  const operacional = gastosPorMes.filter((g) => g.categoria === "Operacional").reduce((s, g) => s + g.valor, 0);
  const compras = filtrarListaDRE(data.compras.filter(c => c.status === "Recebido")).reduce((s, c) => s + c.total, 0);

  const dreLines = [
    { label: "FATURAMENTO BRUTO", value: totalVendas, color: C.accent, bold: true, indent: 0 },
    { label: "(-) Custo das Mercadorias (CMV)", value: -totalCMV, color: C.warn, bold: false, indent: 1 },
    { label: "= LUCRO BRUTO", value: lucroBruto, color: C.gold, bold: true, indent: 0, pct: margBruta },
    { label: "(-) Impostos", value: -impostos, color: C.warn, bold: false, indent: 1 },
    { label: "(-) Tráfego Pago", value: -trafego, color: C.warn, bold: false, indent: 1 },
    { label: "(-) Operacional", value: -operacional, color: C.warn, bold: false, indent: 1 },
    { label: "= LUCRO OPERACIONAL (EBITDA)", value: lucroLiquido, color: C.accent, bold: true, indent: 0, pct: margLiquida },
    { label: "(-) Gastos Pessoais / Pró-labore", value: -gastosPessoal, color: "#374151", bold: false, indent: 1 },
    { label: "= RESULTADO FINAL", value: lucroAposRetorno, color: lucroAposRetorno >= 0 ? C.accent : C.warn, bold: true, indent: 0, pct: lucroAposRetorno / totalVendas, showSign: true },
  ];

  const cats = [
    { name: "Impostos", value: impostos, color: C.warn },
    { name: "Tráfego Pago", value: trafego, color: C.info },
    { name: "Operacional", value: operacional, color: C.gold },
    { name: "Pessoal", value: gastosPessoal, color: "#374151" },
    { name: "Compras/Fornec.", value: compras, color: C.gold },
  ];
  const maxCat = Math.max(...cats.map((c) => c.value));

  const exportPDF = () => {
    const mesLabel = mesFiltro === "todos" ? "Todos os Períodos" : MESES[parseInt(mesFiltro.slice(5,7))-1] + "/" + mesFiltro.slice(0,4);
    const now = new Date().toLocaleDateString("pt-BR");
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>DRE TFX</title>
    <style>body{font-family:Arial,sans-serif;padding:40px;color:#111;max-width:800px;margin:0 auto}
    h1{color:#FFB800;font-size:28px;margin-bottom:4px}h2{color:#555;font-size:14px;margin-bottom:30px;font-weight:normal}
    table{width:100%;border-collapse:collapse;margin-bottom:24px}
    th{background:#f5f5f5;padding:10px 14px;text-align:left;font-size:12px;text-transform:uppercase;letter-spacing:1px;border-bottom:2px solid #ddd}
    td{padding:10px 14px;border-bottom:1px solid #eee;font-size:13px}
    .bold{font-weight:bold;font-size:14px}.accent{color:#FFB800}.warn{color:#C0392B}.gold{color:#B8860B}
    .indent{padding-left:30px;color:#374151}.kpi{display:grid;grid-template-columns:1fr 1fr 1fr 1fr;gap:16px;margin-bottom:30px}
    .kpi-card{background:#f9f9f9;border:1px solid #ddd;border-radius:8px;padding:16px}
    .kpi-label{font-size:11px;color:#374151;text-transform:uppercase;letter-spacing:1px;margin-bottom:6px}
    .kpi-value{font-size:20px;font-weight:bold;color:#FFB800}
    .footer{margin-top:40px;font-size:11px;color:#aaa;text-align:center;border-top:1px solid #eee;padding-top:16px}
    @media print{body{padding:20px}}</style></head><body>
    <h1>GESTÃO TFX — DRE</h1><h2>${mesLabel} · Gerado em ${now}</h2>
    <div class="kpi">
      <div class="kpi-card"><div class="kpi-label">Faturamento</div><div class="kpi-value accent">${fmt(totalVendas)}</div></div>
      <div class="kpi-card"><div class="kpi-label">Lucro Bruto</div><div class="kpi-value gold">${fmt(lucroBruto)}</div></div>
      <div class="kpi-card"><div class="kpi-label">Lucro Operacional</div><div class="kpi-value">${fmt(lucroLiquido)}</div></div>
      <div class="kpi-card"><div class="kpi-label">Resultado Final</div><div class="kpi-value ${lucroAposRetorno>=0?'accent':'warn'}">${fmt(lucroAposRetorno)}</div></div>
    </div>
    <table><thead><tr><th>Descrição</th><th style="text-align:right">Valor</th><th style="text-align:right">Margem</th></tr></thead><tbody>
    <tr><td class="bold accent">FATURAMENTO BRUTO</td><td class="bold accent" style="text-align:right">${fmt(totalVendas)}</td><td></td></tr>
    <tr><td class="indent">(-) Custo das Mercadorias (CMV)</td><td class="warn" style="text-align:right">${fmt(totalCMV)}</td><td></td></tr>
    <tr><td class="bold gold">= LUCRO BRUTO</td><td class="bold gold" style="text-align:right">${fmt(lucroBruto)}</td><td style="text-align:right;color:#374151">${pct(margBruta)}</td></tr>
    ${["Imposto","Tráfego Pago","Operacional"].map(cat=>{const v=gastosPorMes.filter(g=>g.categoria===cat).reduce((s,g)=>s+g.valor,0);return`<tr><td class="indent">(-) ${cat}</td><td class="warn" style="text-align:right">${fmt(v)}</td><td></td></tr>`;}).join("")}
    <tr><td class="bold accent">= LUCRO OPERACIONAL (EBITDA)</td><td class="bold accent" style="text-align:right">${fmt(lucroLiquido)}</td><td style="text-align:right;color:#374151">${pct(margLiquida)}</td></tr>
    <tr><td class="indent">(-) Gastos Pessoais / Pró-labore</td><td style="text-align:right;color:#374151">${fmt(gastosPessoal)}</td><td></td></tr>
    <tr><td class="bold ${lucroAposRetorno>=0?'accent':'warn'}">= RESULTADO FINAL</td><td class="bold ${lucroAposRetorno>=0?'accent':'warn'}" style="text-align:right">${fmt(lucroAposRetorno)}</td><td style="text-align:right;color:#374151">${totalVendas>0?pct(lucroAposRetorno/totalVendas):""}</td></tr>
    </tbody></table>
    <div class="footer">GESTÃO TFX · Relatório gerado automaticamente · ${now}</div>
    </body></html>`;
    const w = window.open("", "_blank");
    w.document.write(html);
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 500);
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 28 }}>
        <div>
          <div style={styles.pageTitle}>DRE — {mesFiltro === "todos" ? "Todos os Períodos" : MESES[parseInt(mesFiltro.slice(5,7))-1] + " " + mesFiltro.slice(0,4)}</div>
          <div style={styles.pageSub}>Demonstrativo de Resultado do Exercício</div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <button onClick={() => setShowFiltroD(!showFiltroD)} style={{ ...styles.btn("secondary"), fontSize: 11, padding: "6px 12px" }}>
            📅 {labelPeriodoDRE()} ▾
          </button>
          <button style={{ ...styles.btn("secondary"), fontSize: 11 }} onClick={exportPDF}>📄 Exportar PDF</button>
        </div>
      </div>

      {showFiltroD && (
        <div style={{ ...styles.card, marginBottom: 16, padding: 16 }}>
          <div style={{ fontSize: 11, color: "#374151", marginBottom: 10, letterSpacing: 1 }}>PERÍODO RÁPIDO</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 14 }}>
            {[["todos","Todos"],["hoje","Hoje"],["ontem","Ontem"],["7d","7 dias"],["15d","15 dias"],["30d","30 dias"],["90d","90 dias"]].map(([v,l]) => (
              <button key={v} onClick={() => aplicarPeriodoRapidoD(v)} style={{ ...styles.btn(periodoRapidoD === v && mesFiltro === "todos" ? "primary" : "secondary"), fontSize: 11, padding: "5px 12px" }}>{l}</button>
            ))}
          </div>
          <div style={{ fontSize: 11, color: "#374151", marginBottom: 10, letterSpacing: 1 }}>POR MÊS</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 14 }}>
            {mesesDisponiveis.map(m => (
              <button key={m} onClick={() => selecionarMesDRE(m)} style={{ ...styles.btn(mesFiltro === m ? "primary" : "secondary"), fontSize: 11, padding: "5px 12px" }}>{MESES[parseInt(m.slice(5,7))-1] + "/" + m.slice(2,4)}</button>
            ))}
          </div>
          <div style={{ fontSize: 11, color: "#374151", marginBottom: 10, letterSpacing: 1 }}>PERÍODO CUSTOMIZADO</div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <input type="date" style={{ ...styles.input, width: "auto", fontSize: 12 }} value={dataInicioD} onChange={e => { setDataInicioD(e.target.value); }} />
            <span style={{ color: "#374151" }}>→</span>
            <input type="date" style={{ ...styles.input, width: "auto", fontSize: 12 }} value={dataFimD} onChange={e => { setDataFimD(e.target.value); }} />
            <button onClick={() => setShowFiltroD(false)} style={{ ...styles.btn("primary"), fontSize: 11 }}>Aplicar</button>
          </div>
        </div>
      )}

      {/* KPIs DRE */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 20, marginBottom: 28 }}>
        <div className="tfx-fade-in-delay-0 tfx-card-hover" style={{ 
          background: C.bgElevated,
          border: `2px solid ${C.accent}25`,
          borderRadius: 20,
          padding: 24,
          position: "relative",
          overflow: "hidden",
          boxShadow: `0 10px 40px ${C.accent}30`,
          transition: "all 0.3s"
        }}>
          <div style={{ position: "absolute", top: -30, right: -30, width: 120, height: 120, background: `radial-gradient(circle, ${C.accent}15, transparent)`, borderRadius: "50%" }} />
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
            <div style={{ width: 48, height: 48, borderRadius: 12, background: `${C.accent}20`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}>💰</div>
            <div style={{ fontSize: 9, color: "#374151", letterSpacing: 2, textTransform: "uppercase", fontWeight: 600 }}>Faturamento Total</div>
          </div>
          <div style={{ fontFamily: "'Inter', sans-serif", fontWeight: 800, fontSize: 28, color: C.accent, letterSpacing: -1.2 }}>
            {privado ? "••••••" : fmt(totalVendas)}
          </div>
          <div style={{ fontSize: 10, color: "#374151", marginTop: 8 }}>Bruto no período</div>
        </div>

        <div className="tfx-fade-in-delay-1 tfx-card-hover" style={{ 
          background: C.bgElevated,
          border: `2px solid ${C.gold}25`,
          borderRadius: 20,
          padding: 24,
          position: "relative",
          overflow: "hidden",
          boxShadow: `0 10px 40px ${C.gold}30`,
          transition: "all 0.3s"
        }}>
          <div style={{ position: "absolute", top: -30, right: -30, width: 120, height: 120, background: `radial-gradient(circle, ${C.gold}15, transparent)`, borderRadius: "50%" }} />
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
            <div style={{ width: 48, height: 48, borderRadius: 12, background: `${C.gold}20`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}>📊</div>
            <div style={{ fontSize: 9, color: "#374151", letterSpacing: 2, textTransform: "uppercase", fontWeight: 600 }}>Lucro Bruto</div>
          </div>
          <div style={{ fontFamily: "'Inter', sans-serif", fontWeight: 800, fontSize: 28, color: C.gold, letterSpacing: -1.2 }}>
            {privado ? "••••••" : fmt(lucroBruto)}
          </div>
          <div style={{ fontSize: 10, color: "#374151", marginTop: 8 }}>{privado ? "••%" : pct(margBruta) + " de margem"}</div>
        </div>

        <div className="tfx-fade-in-delay-2 tfx-card-hover" style={{ 
          background: C.bgElevated,
          border: `2px solid ${C.info}25`,
          borderRadius: 20,
          padding: 24,
          position: "relative",
          overflow: "hidden",
          boxShadow: `0 10px 40px ${C.info}30`,
          transition: "all 0.3s"
        }}>
          <div style={{ position: "absolute", top: -30, right: -30, width: 120, height: 120, background: `radial-gradient(circle, ${C.info}15, transparent)`, borderRadius: "50%" }} />
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
            <div style={{ width: 48, height: 48, borderRadius: 12, background: `${C.info}20`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}>📈</div>
            <div style={{ fontSize: 9, color: "#374151", letterSpacing: 2, textTransform: "uppercase", fontWeight: 600 }}>Lucro Operacional</div>
          </div>
          <div style={{ fontFamily: "'Inter', sans-serif", fontWeight: 800, fontSize: 28, color: C.info, letterSpacing: -1.2 }}>
            {privado ? "••••••" : fmt(lucroLiquido)}
          </div>
          <div style={{ fontSize: 10, color: "#374151", marginTop: 8 }}>{privado ? "••%" : pct(margLiquida) + " de margem"}</div>
        </div>

        <div className="tfx-fade-in-delay-3 tfx-card-hover" style={{ 
          background: C.bgElevated,
          border: `2px solid ${lucroAposRetorno >= 0 ? C.accent : C.warn}25`,
          borderRadius: 20,
          padding: 24,
          position: "relative",
          overflow: "hidden",
          boxShadow: `0 10px 40px ${lucroAposRetorno >= 0 ? C.accent : C.warn}30`,
          transition: "all 0.3s"
        }}>
          <div style={{ position: "absolute", top: -30, right: -30, width: 120, height: 120, background: `radial-gradient(circle, ${lucroAposRetorno >= 0 ? C.accent : C.warn}15, transparent)`, borderRadius: "50%" }} />
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
            <div style={{ width: 48, height: 48, borderRadius: 12, background: `${lucroAposRetorno >= 0 ? C.accent : C.warn}20`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}>🎯</div>
            <div style={{ fontSize: 9, color: "#374151", letterSpacing: 2, textTransform: "uppercase", fontWeight: 600 }}>Resultado Final</div>
          </div>
          <div style={{ fontFamily: "'Inter', sans-serif", fontWeight: 800, fontSize: 28, color: lucroAposRetorno >= 0 ? C.accent : C.warn, letterSpacing: -1.2 }}>
            {privado ? "••••••" : fmt(lucroAposRetorno)}
          </div>
          <div style={{ fontSize: 10, color: "#374151", marginTop: 8 }}>Após gastos pessoais</div>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr", gap: 16 }}>
        {/* DRE Table */}
        <div style={{ 
          ...styles.card,
          background: C.bgElevated,
          border: "1px solid #e5e7eb"
        }}>
          <div style={{ 
            fontSize: 14, 
            fontWeight: 700, 
            color: "#111827", 
            marginBottom: 20,
            paddingBottom: 12,
            borderBottom: `2px solid ${C.accent}30`
          }}>📋 Demonstrativo Completo</div>
          <table style={styles.table}>
            <tbody>
              {dreLines.map((l, i) => (
                <tr key={i}>
                  <td style={{ ...styles.td, paddingLeft: 12 + l.indent * 20, color: l.bold ? l.color : "#374151", fontWeight: l.bold ? 600 : 400, fontFamily: l.bold ? "'Inter', sans-serif" : "'Inter', sans-serif", fontSize: l.bold ? 13 : 12, borderBottom: "1px solid #f3f4f6" }}>
                    {l.label}
                  </td>
                  <td style={{ ...styles.td, textAlign: "right", color: l.value < 0 ? C.warn : l.color, fontWeight: l.bold ? 700 : 400, fontFamily: "'Inter', sans-serif", fontSize: l.bold ? 14 : 12 }}>
                    {privado ? "••••••" : fmt(l.showSign ? l.value : Math.abs(l.value))}
                  </td>
                  <td style={{ ...styles.td, textAlign: "right", width: 60, color: l.value < 0 ? C.warn : C.muted, fontSize: 11 }}>
                    {l.pct !== undefined ? (privado ? "••%" : pct(l.showSign ? l.pct : Math.abs(l.pct))) : ""}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Breakdown de gastos */}
        <div style={{ 
          ...styles.card,
          background: C.bgElevated,
          border: "1px solid #e5e7eb",
          transition: "all 0.3s"
        }} className="tfx-card-hover">
          <div style={{ 
            fontSize: 14, 
            fontWeight: 700, 
            color: "#111827", 
            marginBottom: 20,
            paddingBottom: 12,
            borderBottom: `2px solid ${C.accent}30`
          }}>💸 Composição dos Gastos</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {cats.map((c) => (
              <div key={c.name}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                  <span style={{ fontSize: 12, color: "#111827" }}>{c.name}</span>
                  <span style={{ fontSize: 12, fontWeight: 700, color: c.color }}>{privado ? "••••" : fmt(c.value)}</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <MiniBar value={c.value} max={maxCat} color={c.color} height={8} />
                  <span style={{ fontSize: 10, color: "#374151", width: 36, textAlign: "right" }}>{pct(c.value / totalVendas)}</span>
                </div>
              </div>
            ))}
          </div>

          <div style={{ 
            marginTop: 24, 
            padding: "16px", 
            background: `${C.accent}08`, 
            border: `1px solid ${C.accent}20`,
            borderRadius: 12 
          }}>
            <div style={{ 
              fontSize: 10, 
              color: C.accent, 
              letterSpacing: 1.5, 
              textTransform: "uppercase", 
              fontWeight: 700,
              marginBottom: 12 
            }}>💎 RESUMO DE MARGENS</div>
            {[
              { l: "Margem Bruta", v: margBruta, c: C.gold },
              { l: "Margem Líquida", v: margLiquida, c: C.info },
              { l: "Margem Final", v: lucroAposRetorno / totalVendas, c: lucroAposRetorno >= 0 ? C.accent : C.warn },
            ].map((m) => (
              <div key={m.l} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <span style={{ fontSize: 11, color: "#374151" }}>{m.l}</span>
                <span style={{ fontWeight: 800, fontSize: 15, color: m.c }}>{privado ? "••%" : pct(m.v)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
      <GraficoMensal vendas={data.vendas} gastos={data.gastos} />
    </div>
  );
}

// ── VENDAS PAGE ───────────────────────────────────────────────
// ══════════════════════════════════════════════════════════
// ETIQUETA TERMICA (80mm, sem acentos/emojis)
// ══════════════════════════════════════════════════════════

async function gerarReciboPDF(venda, configLojaParam) {
  // Buscar config atualizada direto do banco
  let configLoja = configLojaParam || {};
  try {
    const res = await fetch(`${API_BASE}?table=configuracoes_loja`, {
      headers: { Authorization: "Bearer " + getJWT() }
    });
    const configs = await res.json();
    if (configs && configs.length > 0) {
      configLoja = configs[0];
    }
  } catch (err) {
    console.log("Usando config do parâmetro");
  }
  
  const { jsPDF } = window.jspdf;
  const doc = new jsPDF();
  
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 20;
  let y = 20;
  
  // Cores
  const navy = [15, 23, 42];
  const gold = [255, 184, 0];
  const darkGray = [51, 51, 51];
  const lightGray = [248, 249, 250];
  const green = [16, 185, 129];
  
  // HEADER azul escuro
  doc.setFillColor(...navy);
  doc.rect(0, 0, pageWidth, 45, 'F');
  
  // Nome da loja
  doc.setFontSize(24);
  doc.setFont(undefined, 'bold');
  doc.setTextColor(255, 255, 255);
  doc.text(configLoja.nome_loja || 'LOJA', pageWidth / 2, 20, { align: 'center' });
  
  // Dados da loja
  doc.setFontSize(9);
  doc.setFont(undefined, 'normal');
  doc.setTextColor(220, 220, 220);
  
  let headerY = 28;
  if (configLoja.cnpj) {
    doc.text(`CNPJ: ${configLoja.cnpj}`, pageWidth / 2, headerY, { align: 'center' });
    headerY += 4;
  }
  if (configLoja.endereco) {
    doc.text(configLoja.endereco, pageWidth / 2, headerY, { align: 'center' });
    headerY += 4;
  }
  const contatos = [];
  if (configLoja.telefone) contatos.push(`Tel: ${configLoja.telefone}`);
  if (configLoja.email) contatos.push(configLoja.email);
  if (contatos.length > 0) {
    doc.text(contatos.join(' • '), pageWidth / 2, headerY, { align: 'center' });
  }
  
  y = 55;
  
  // Badge número recibo
  const numeroRecibo = `#${String(venda.id || Date.now()).slice(-6)}`;
  doc.setFillColor(...navy);
  doc.roundedRect(margin, y, 45, 10, 2, 2, 'F');
  doc.setFontSize(9);
  doc.setFont(undefined, 'bold');
  doc.setTextColor(255, 255, 255);
  doc.text(numeroRecibo, margin + 22.5, y + 7, { align: 'center' });
  
  // Título RECIBO
  doc.setFontSize(20);
  doc.setFont(undefined, 'bold');
  doc.setTextColor(...navy);
  doc.text('RECIBO DE VENDA', pageWidth / 2, y + 7, { align: 'center' });
  
  y += 20;
  
  // Box INFO (cinza arredondado)
  doc.setFillColor(...lightGray);
  doc.roundedRect(margin, y, pageWidth - 2*margin, 35, 4, 4, 'F');
  doc.setDrawColor(220, 220, 220);
  doc.setLineWidth(0.5);
  doc.roundedRect(margin, y, pageWidth - 2*margin, 35, 4, 4, 'S');
  
  doc.setFontSize(10);
  doc.setFont(undefined, 'normal');
  doc.setTextColor(...darkGray);
  
  // Data corrigida
  const [ano, mes, dia] = venda.data.split('-');
  const dataVenda = new Date(Number(ano), Number(mes) - 1, Number(dia)).toLocaleDateString('pt-BR', { 
    day: '2-digit', 
    month: 'long', 
    year: 'numeric' 
  });
  
  const infoY = y + 8;
  doc.setFont(undefined, 'bold');
  doc.text('Data:', margin + 5, infoY);
  doc.setFont(undefined, 'normal');
  doc.text(dataVenda, margin + 17, infoY);
  
  doc.setFont(undefined, 'bold');
  doc.text('Cliente:', margin + 5, infoY + 7);
  doc.setFont(undefined, 'normal');
  doc.text(venda.cliente || 'Não informado', margin + 20, infoY + 7);
  
  doc.setFont(undefined, 'bold');
  doc.text('Vendedor:', margin + 5, infoY + 14);
  doc.setFont(undefined, 'normal');
  doc.text(venda.vendedor || 'Não informado', margin + 24, infoY + 14);
  
  doc.setFont(undefined, 'bold');
  doc.text('Canal:', margin + 5, infoY + 21);
  doc.setFont(undefined, 'normal');
  doc.text(venda.canal, margin + 18, infoY + 21);
  
  y += 43;
  
  // Título ITENS
  doc.setFontSize(12);
  doc.setFont(undefined, 'bold');
  doc.setTextColor(...navy);
  doc.text('ITENS DA VENDA', margin, y);
  y += 10;
  
  // Header tabela (azul escuro)
  doc.setFillColor(...navy);
  doc.roundedRect(margin, y - 5, pageWidth - 2*margin, 10, 2, 2, 'F');
  doc.setFontSize(9);
  doc.setFont(undefined, 'bold');
  doc.setTextColor(255, 255, 255);
  doc.text('QTD', margin + 5, y);
  doc.text('DESCRIÇÃO', margin + 20, y);
  doc.text('VALOR UNIT.', pageWidth - margin - 40, y, { align: 'right' });
  doc.text('SUBTOTAL', pageWidth - margin - 5, y, { align: 'right' });
  y += 10;
  
  // Itens zebrados
  doc.setFont(undefined, 'normal');
  doc.setTextColor(...darkGray);
  const itens = venda.itens || [{ produto: venda.produto || venda.descricao, qtd: venda.qtd, valor: venda.valor }];
  
  itens.forEach((item, idx) => {
    if (idx % 2 === 0) {
      doc.setFillColor(252, 252, 252);
      doc.rect(margin, y - 5, pageWidth - 2*margin, 8, 'F');
    }
    doc.setFontSize(10);
    doc.text(String(item.qtd), margin + 5, y);
    doc.text(item.produto, margin + 20, y);
    const itemValor = Number(item.valor) || 0;
    const itemQtd = Number(item.qtd) || 1;
    doc.text(`R$ ${itemValor.toFixed(2)}`, pageWidth - margin - 40, y, { align: 'right' });
    doc.setFont(undefined, 'bold');
    doc.text(`R$ ${(itemValor * itemQtd).toFixed(2)}`, pageWidth - margin - 5, y, { align: 'right' });
    doc.setFont(undefined, 'normal');
    y += 8;
  });
  
  y += 8;
  
  // Box TOTAL (azul navy)
  const total = venda.valor * venda.qtd;
  doc.setFillColor(...navy);
  doc.roundedRect(pageWidth - margin - 80, y, 80, 16, 3, 3, 'F');
  doc.setFontSize(11);
  doc.setFont(undefined, 'bold');
  doc.setTextColor(255, 255, 255);
  doc.text('TOTAL', pageWidth - margin - 75, y + 6);
  doc.setFontSize(16);
  doc.text(`R$ ${total.toFixed(2)}`, pageWidth - margin - 5, y + 11, { align: 'right' });
  
  y += 25;
  
  // Garantia (se tiver)
  if (venda.garantia_meses > 0) {
    doc.setFillColor(...green);
    doc.roundedRect(margin, y, pageWidth - 2*margin, 18, 3, 3, 'F');
    doc.setFontSize(12);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(255, 255, 255);
    doc.text('GARANTIA INCLUÍDA', margin + 5, y + 7);
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    const dataFim = fmtDataBR(venda.garantia_fim);
    doc.text(`${venda.garantia_meses} ${venda.garantia_meses === 1 ? 'mês' : 'meses'} de garantia • Válido até ${dataFim}`, margin + 5, y + 13);
    y += 25;
  }
  
  // Observações
  if (y < pageHeight - 60) {
    doc.setFontSize(9);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(...darkGray);
    doc.text('OBSERVAÇÕES:', margin, y);
    y += 5;
    doc.setFont(undefined, 'normal');
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.3);
    doc.line(margin, y, pageWidth - margin, y);
    y += 5;
    doc.line(margin, y, pageWidth - margin, y);
    y += 12;
    
    doc.setFont(undefined, 'bold');
    doc.text('ASSINATURA DO CLIENTE:', margin, y);
    y += 8;
    doc.line(margin + 50, y, pageWidth - margin, y);
  }
  
  // RODAPÉ (box azul claro arredondado)
  y = pageHeight - 28;
  
  doc.setFillColor(239, 246, 255);
  doc.roundedRect(margin, y, pageWidth - 2*margin, 18, 3, 3, 'F');
  
  doc.setFontSize(10);
  doc.setFont(undefined, 'bold');
  doc.setTextColor(59, 130, 246);
  doc.text('DÚVIDAS OU SUPORTE?', pageWidth / 2, y + 6, { align: 'center' });
  
  doc.setFont(undefined, 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...darkGray);
  const contatosFooter = [];
  if (configLoja.telefone) contatosFooter.push(`Tel: ${configLoja.telefone}`);
  if (configLoja.email) contatosFooter.push(configLoja.email);
  if (contatosFooter.length > 0) {
    doc.text(contatosFooter.join('  •  '), pageWidth / 2, y + 12, { align: 'center' });
  }
  
  y += 20;
  
  doc.setFontSize(7);
  doc.setFont(undefined, 'italic');
  doc.setTextColor(120, 120, 120);
  doc.text('Gerado automaticamente por', pageWidth / 2, y, { align: 'center' });
  y += 3;
  doc.setFont(undefined, 'bold');
  doc.setTextColor(...gold);
  doc.text('GESTÃO TFX', pageWidth / 2, y, { align: 'center' });
  
  const nomeArquivo = `recibo_${venda.data}_${venda.cliente || 'cliente'}.pdf`;
  doc.save(nomeArquivo);
}


function Vendas({ data, setData, privado, showToast, logado }) {
  let configLoja = {};
  
  const loadConfigLoja = async () => {
    try {
      const res = await fetch(`${API_BASE}?table=configuracoes_loja`, {
        headers: { Authorization: "Bearer " + getJWT() }
      });
      const configs = await res.json();
      if (configs && configs.length > 0) {
        configLoja = configs[0];
      }
    } catch (err) {
      console.log("Erro ao carregar config:", err);
    }
  };
  
  useEffect(() => {
    loadConfigLoja();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
    const [draggedVenda, setDraggedVenda] = useState(null);
  const [modalEtiqueta, setModalEtiqueta] = useState(null);
  const [formEtiqueta, setFormEtiqueta] = useState({ nome: '', bairro: '', pagamento: 'Dinheiro', valor: '', horario: '' });
  // eslint-disable-next-line no-unused-vars
  const [imprimindo, setImprimindo] = useState(false);
  const [upsellModal, setUpsellModal] = useState({ show: false, vendaId: null });
  const [upsellItens, setUpsellItens] = useState([{ produto: "", produto_id: null, valor: 0, custo: 0, quantidade: 1 }]);
  const [salvandoUpsell, setSalvandoUpsell] = useState(false);

  // eslint-disable-next-line no-unused-vars
  const abrirModalEtiqueta = (venda) => {
    const total = venda.valor * venda.qtd;
    const horario = new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    setFormEtiqueta({ nome: venda.cliente || '', bairro: venda.bairro || '', pagamento: venda.canal || 'Dinheiro', valor: 'R$ ' + total.toFixed(2), horario });
    setModalEtiqueta(venda);
  };

  // eslint-disable-next-line no-unused-vars
  const imprimirEtiqueta = () => {
    const w = window.open('', '_blank', 'width=300,height=400');
    w.document.write(`<!DOCTYPE html><html><head><meta charset="UTF-8">
<style>
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { font-family: monospace; width: 58mm; padding: 4mm; font-size: 12px; }
  .center { text-align: center; }
  .bold { font-weight: bold; }
  .big { font-size: 18px; font-weight: bold; }
  .line { border-top: 1px dashed #000; margin: 4px 0; }
  .row { display: flex; justify-content: space-between; margin: 2px 0; }
  @media print { body { margin: 0; } }
</style></head><body>
<div class="center bold" style="font-size:14px; margin-bottom:4px">KTSMARTS</div>
<div class="line"></div>
<div class="bold" style="font-size:13px; margin: 4px 0">${formEtiqueta.nome}</div>
<div>${formEtiqueta.bairro}</div>
<div class="line"></div>
<div class="row"><span>Pagamento:</span><span class="bold">${formEtiqueta.pagamento}</span></div>
<div class="row"><span>Valor:</span><span class="big">${formEtiqueta.valor}</span></div>
<div class="row"><span>Horario:</span><span>${formEtiqueta.horario}</span></div>
<div class="line"></div>
<div class="center" style="font-size:10px; margin-top:4px">Pedido: ${(modalEtiqueta?.id?.slice(0,8)||'').toUpperCase()}</div>
</body></html>`);
    w.document.close();
    setTimeout(() => { w.print(); w.close(); }, 400);
    setModalEtiqueta(null);
  };
  const [dragOverVenda, setDragOverVenda] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [modalMandaBem, setModalMandaBem] = useState(null);
  const [modalCadastroCliente, setModalCadastroCliente] = useState(false);
  const [formNovoCliente, setFormNovoCliente] = useState({ nome: "", telefone: "", cep: "", endereco: "", numero: "", complemento: "", bairro: "", cidade: "", estado: "" });
  const buscarCEPVenda = async (cep) => {
    const c = cep.replace(/\D/g, "");
    if (c.length !== 8) return;
    try {
      const r = await fetch(`https://viacep.com.br/ws/${c}/json/`);
      const d = await r.json();
      if (!d.erro) setFormNovoCliente(f => ({ ...f, endereco: d.logradouro, bairro: d.bairro, cidade: d.localidade, estado: d.uf }));
    } catch(e) {}
  };

  const salvarNovoCliente = async () => {
    if (!formNovoCliente.nome) return alert("Nome é obrigatório!");

    try {
      console.log('📋 Salvando cliente:', formNovoCliente);
      const novo = await sb.insert("clientes_loja", { ...formNovoCliente, tenant_id: getTenant() });
      setData(d => ({ ...d, clientes_loja: [...(d.clientes_loja || []), novo] }));
      if (showToast) showToast("✅ Cliente cadastrado!");
      setModalCadastroCliente(false);
      setFormNovoCliente({ nome: "", telefone: "", cep: "", endereco: "", numero: "", complemento: "", bairro: "", cidade: "", estado: "" });
    } catch (error) {
      alert("Erro ao cadastrar: " + error.message);
    }
  };

  // eslint-disable-next-line no-unused-vars
  const [formMB, setFormMB] = useState({ peso: '0.3', altura: '2', largura: '11', comprimento: '16', forma_envio: 'PAC', valor_nota: '', numero_nota: '' });
  // eslint-disable-next-line no-unused-vars
  const [gerandoEtiqueta, setGerandoEtiqueta] = useState(false);
  const emptyItem = { produto: "", qtd: "1", valor: "", custo: "", brinde: "" };
  const [canal, setCanal] = useState("Online");
  const [entregaTipo, setEntregaTipo] = useState("");
  const [bandeira, setBandeira] = useState("Nenhuma");
  const [parcelas, setParcelas] = useState(1);
  const [bandeira2, setBandeira2] = useState("Nenhuma");
  const [valorPag2, setValorPag2] = useState("");
  const [frete, setFrete] = useState("");
  const [desconto, setDesconto] = useState("");
  const [tipoDesconto, setTipoDesconto] = useState("percentual");
  const [cupomCodigo, setCupomCodigo] = useState("");
  const [cupomAplicado, setCupomAplicado] = useState(null);
  const [garantiaMeses, setGarantiaMeses] = useState("0");
  const [garantiaMesesCustom, setGarantiaMesesCustom] = useState("");
  const [garantiaFimManual, setGarantiaFimManual] = useState(null);

  const calcularDesconto = () => {
    const totalProdutos = itens.filter(i => i.produto && i.qtd).reduce((s, i) => s + Number(i.valor) * Number(i.qtd), 0);
    if (cupomAplicado) {
      if (cupomAplicado.tipo === "percentual") return parseFloat((totalProdutos * cupomAplicado.valor / 100).toFixed(2));
      return parseFloat(Math.min(cupomAplicado.valor, totalProdutos).toFixed(2));
    }
    if (!desconto || Number(desconto) <= 0) return 0;
    if (tipoDesconto === "percentual") return parseFloat((totalProdutos * Number(desconto) / 100).toFixed(2));
    return parseFloat(Math.min(Number(desconto), totalProdutos).toFixed(2));
  };

  const aplicarCupom = () => {
    const cupom = (data.cupons || []).find(c => c.codigo.toUpperCase() === cupomCodigo.toUpperCase() && c.ativo);
    if (!cupom) return alert("Cupom inválido ou inativo!");
    if (cupom.validade && new Date(cupom.validade) < new Date()) return alert("Cupom expirado!");
    if (cupom.limite_usos && cupom.usos >= cupom.limite_usos) return alert("Cupom esgotado!");
    setCupomAplicado(cupom);
    if (showToast) showToast(`Cupom aplicado! -${cupom.tipo === "percentual" ? cupom.valor + "%" : "R$ " + cupom.valor}`);
  };
  const [taxasMaquininha, setTaxasMaquininha] = useState(data.taxas_maquininha || []);
  const [showConfigTaxas, setShowConfigTaxas] = useState(false);
  const [taxasForm, setTaxasForm] = useState({});
  const [salvandoTaxas, setSalvandoTaxas] = useState(false);

  const BANDEIRAS = ["Visa", "Mastercard", "Elo", "Débito", "Pix", "Dinheiro", "Link"];
  const SEM_TAXA = ["Dinheiro", "Link"];

  // Carrega taxas do banco
  const carregarTaxas = async () => {
    const taxas = await sb.get("taxas_maquininha");
    setData(d => ({ ...d, taxas_maquininha: taxas || [] }));
    setTaxasMaquininha(taxas || []);
    // Monta form com valores atuais
    const form = {};
    for (const b of BANDEIRAS) {
      form[b] = {};
      for (let p = 1; p <= 12; p++) {
        const taxa = (taxas || []).find(t => t.bandeira === b && t.parcela === p);
        form[b][p] = taxa ? taxa.taxa : "";
      }
    }
    setTaxasForm(form);
  };

  const salvarTaxas = async () => {
    setSalvandoTaxas(true);
    for (const b of BANDEIRAS) {
      for (let p = 1; p <= 12; p++) {
        const val = parseFloat(taxasForm[b]?.[p] || 0);
        const existing = taxasMaquininha.find(t => t.bandeira === b && t.parcela === p);
        if (existing) {
          await sb.update("taxas_maquininha", existing.id, { taxa: val });
        } else {
          await sb.insert("taxas_maquininha", { bandeira: b, parcela: p, taxa: val });
        }
      }
    }
    await carregarTaxas();
    setSalvandoTaxas(false);
    if (showToast) showToast("Taxas salvas!");
    setShowConfigTaxas(false);
  };

  const getTaxa = (b, p) => {
    const taxa = taxasMaquininha.find(t => t.bandeira === b && t.parcela === p);
    return taxa ? taxa.taxa : 0;
  };

  const calcularTaxaReais = () => {
    const totalVenda = itens.filter(i => i.produto && i.qtd).reduce((s, i) => s + Number(i.valor) * Number(i.qtd), 0);
    let taxa1 = 0;
    let taxa2 = 0;
    if (bandeira !== "Nenhuma" && !SEM_TAXA.includes(bandeira)) {
      const valorPag1 = bandeira2 !== "Nenhuma" && valorPag2 ? totalVenda - Number(valorPag2) : totalVenda;
      taxa1 = parseFloat((valorPag1 * getTaxa(bandeira, parcelas) / 100).toFixed(2));
    }
    if (bandeira2 !== "Nenhuma" && valorPag2 && !SEM_TAXA.includes(bandeira2)) {
      taxa2 = parseFloat((Number(valorPag2) * getTaxa(bandeira2, 1) / 100).toFixed(2));
    }
    return parseFloat((taxa1 + taxa2).toFixed(2));
  };

  // Carrega taxas ao montar
  useState(() => { carregarTaxas(); }, []);
  const [custosDia, setCustosDia] = useState({ motoboy: "", uber: "", trafego: "" });
  const [showCustosDia, setShowCustosDia] = useState(false);

  const salvarCustoDia = async (tipo, valor) => {
    if (!valor || Number(valor) <= 0) return;
    const hoje = hojeManaus();
    const cats = { motoboy: { cat: "Operacional", desc: "Motoboy" }, uber: { cat: "Uber Loja", desc: "Uber" }, trafego: { cat: "Tráfego Pago", desc: "Tráfego Pago" } };
    const { cat, desc } = cats[tipo];
    const valorFinal = tipo === "trafego" ? Number(valor) * fatorTrafego(data.caixa_config) : Number(valor);
    const row = { data: hoje, descricao: desc, categoria: cat, tipo: "loja", valor: valorFinal };
    const ins = await sb.insert("gastos", row);
    const inserted = Array.isArray(ins) ? ins[0] : ins;
    setData(d => ({ ...d, gastos: [...d.gastos, { ...inserted, subcategoria: desc }] }));
    
    // Lança no caixa automaticamente
    const catCaixa = tipo === "trafego" ? "trafego" : "gastos_loja";
    await autoLancarCaixa("saida", desc, valorFinal, hoje, catCaixa, data.caixa_config);
    
    setCustosDia(c => ({ ...c, [tipo]: "" }));
  };
  const [dataVenda, setDataVenda] = useState(hojeManaus());
  const [nomeCliente, setNomeCliente] = useState("");
  const [vendedor, setVendedor] = useState("");
  const [itens, setItens] = useState([{ ...emptyItem }]);
  // eslint-disable-next-line no-unused-vars
  const [isEnvio, setIsEnvio] = useState(false);
  const [dadosEnvio, setDadosEnvio] = useState({ cliente_nome: "", telefone: "", cep: "", endereco: "", numero: "", complemento: "", bairro: "", cidade: "", estado: "AM" });
  const [textoColado, setTextoColado] = useState("");
  const [clienteSelId, setClienteSelId] = useState(null);
  const aoDigitarCliente = (valor) => {
    setNomeCliente(valor);
    const v = (valor || "").trim().toLowerCase();
    const achado = v ? (data.clientes_loja || []).find(c => (c.nome || "").toLowerCase() === v) : null;
    if (achado) {
      setClienteSelId(achado.id);
      setDadosEnvio({ cliente_nome: achado.nome || "", telefone: achado.telefone || "", cep: achado.cep || "", endereco: achado.endereco || "", numero: achado.numero || "", complemento: achado.complemento || "", bairro: achado.bairro || "", cidade: achado.cidade || "", estado: achado.estado || "" });
    } else if (clienteSelId) {
      setClienteSelId(null);
      setDadosEnvio({ cliente_nome: "", telefone: "", cep: "", endereco: "", numero: "", complemento: "", bairro: "", cidade: "", estado: "" });
    }
  };
  const preencherDadosCliente = () => {
    const r = parseDadosCliente(textoColado);
    if (!r.nome && !r.telefone && !r.endereco) { alert("Não consegui identificar dados no texto. Confira o que foi colado."); return; }
    if (r.nome) setNomeCliente(r.nome);
    setDadosEnvio({ cliente_nome: r.nome, telefone: r.telefone, cep: r.cep, endereco: r.endereco, numero: r.numero, complemento: r.complemento, bairro: r.bairro, cidade: r.cidade, estado: r.estado });
    setTextoColado("");
  };
  // eslint-disable-next-line no-unused-vars
  const buscarCEPVendaForm = async (cep) => {
    const c = cep.replace(/\D/g, "");
    if (c.length !== 8) return;
    try {
      const r = await fetch(`https://viacep.com.br/ws/${c}/json/`);
      const d = await r.json();
      if (!d.erro) setDadosEnvio(f => ({ ...f, endereco: d.logradouro, bairro: d.bairro, cidade: d.localidade, estado: d.uf }));
    } catch(e) {}
  };

  const [showForm, setShowForm] = useState(false);
  const [salvandoVenda, setSalvandoVenda] = useState(false);
  const [buscaVendas, setBuscaVendas] = useState("");
  // tfx-paginacao
  const [limiteVendas, setLimiteVendas] = useState(30);
  const [sortDir, setSortDir] = useState("desc");
  const [selecionados, setSelecionados] = useState([]);
  const toggleSel = (id) => setSelecionados(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
  const toggleAll = () => setSelecionados(s => s.length === vendasOrdenadas.length ? [] : vendasOrdenadas.map(v => v.id));
  const recalcularCliente = async (clienteId, excluirIds) => {
    const cli = (data.clientes_loja || []).find(c => c.id === clienteId);
    if (!cli) return;
    const ex = new Set(excluirIds);
    const restantes = data.vendas.filter(v => v.cliente_id === clienteId && !ex.has(v.id));
    const patch = {
      num_pedidos: restantes.length,
      total_compras: restantes.reduce((s, v) => s + Number(v.valor || 0) * Number(v.qtd || 1), 0),
      ultima_compra: restantes.reduce((m, v) => (!m || v.data > m) ? v.data : m, null),
    };
    await sb.update("clientes_loja", clienteId, patch);
    setData(d => ({ ...d, clientes_loja: (d.clientes_loja || []).map(c => c.id === clienteId ? { ...c, ...patch } : c) }));
  };
  const deletarSelecionados = async () => {
    if (!selecionados.length) return;
    if (!window.confirm(`Deletar ${selecionados.length} vendas selecionadas?`)) return;
    const removidos = [];
    const clientesAfetados = new Set();
    for (const id of selecionados) {
      const venda = data.vendas.find(v => v.id === id);
      const resRem = await sb.remove("vendas", id);
      if (!(resRem && resRem.ok === false)) {
        removidos.push(id);
        if (venda && venda.cliente_id) clientesAfetados.add(venda.cliente_id);
      }
      setData(d => {
        let est = [...d.estoque];
        for (const item of (venda?.itens || [])) {
          const idx = est.findIndex(p => p.produto?.toLowerCase() === item.produto?.toLowerCase());
          if (idx >= 0) { const q = (est[idx].estoque||0)+Number(item.qtd); const nc = ajustarCores(est[idx], item.cor, Number(item.qtd)); est[idx]={...est[idx],estoque:q,qtd:q,...(nc ? { cores: nc } : {})}; sb.update("estoque",est[idx].id,{qtd:q,...(nc ? { cores: nc } : {})}); }
        }
        return { ...d, vendas: d.vendas.filter(v => v.id !== id), estoque: est };
      });
    }
    for (const cid of clientesAfetados) await recalcularCliente(cid, removidos);
    setSelecionados([]);
  };
  const { mesFiltro, periodoRapido, dataInicio, setDataInicio, dataFim, setDataFim, showFiltro: showFiltroAvancado, setShowFiltro: setShowFiltroAvancado, aplicarPeriodo: aplicarPeriodoRapido, selecionarMes: selecionarMesVendas, filtrarLista: filtrarListaVendas, labelPeriodo: labelPeriodoVendas } = useFiltro("tfx_vendas");

  const addItem = () => setItens(i => [...i, { ...emptyItem }]);
  const removeItem = (idx) => setItens(i => i.filter((_,j) => j !== idx));
  const updateItem = (idx, key, val) => setItens(i => i.map((it,j) => j===idx ? {...it,[key]:val} : it));

  const ultimos12Meses = Array.from({length: 12}, (_, i) => {
    return mesRelativo(-i);
  });
  const mesesDisponiveis = [...new Set([...ultimos12Meses, ...data.vendas.map(v => getMes(v.data))])].filter(Boolean).sort().reverse();
  const vendasFiltradas = filtrarListaVendas(data.vendas).sort((a, b) => (a.ordem || 999) - (b.ordem || 999));

  const totalFaturamento = vendasFiltradas.reduce((s, v) => s + v.valor * v.qtd, 0);
  const totalLucro = vendasFiltradas.reduce((s, v) => s + (v.valor - v.custo) * v.qtd - (v.taxa_maquininha || 0) - (v.desconto || 0), 0);
  // eslint-disable-next-line no-unused-vars
  const onlineVendas = vendasFiltradas.filter((v) => v.canal === "Online").reduce((s, v) => s + v.valor * v.qtd, 0);
  // eslint-disable-next-line no-unused-vars
  const fisicaVendas = vendasFiltradas.filter((v) => v.canal === "Física").reduce((s, v) => s + v.valor * v.qtd, 0);
  const onlineQtd = vendasFiltradas.filter((v) => v.canal === "Online").reduce((s, v) => s + v.qtd, 0);
  const fisicaQtd = vendasFiltradas.filter((v) => v.canal === "Física").reduce((s, v) => s + v.qtd, 0);

  const vendasBuscadas = buscaVendas ? vendasFiltradas.filter(v => (v.produto||'').toLowerCase().includes(buscaVendas.toLowerCase()) || (v.canal||'').toLowerCase().includes(buscaVendas.toLowerCase())) : vendasFiltradas;
  const vendasOrdenadas = [...vendasBuscadas].sort((a, b) => {
    // Ordena por data (mais recente ou mais antiga)
    const dateA = new Date(a.data || '1970-01-01');
    const dateB = new Date(b.data || '1970-01-01');
    return sortDir === 'desc' ? dateB - dateA : dateA - dateB;
  });

  const [nfModal, setNfModal] = useState(null);
  const [nfForm, setNfForm] = useState({ cpf: "", nome: "", email: "", telefone: "", cep: "", endereco: "", numero: "", bairro: "", municipio: "Manaus", uf: "AM" });
  const [nfLoading, setNfLoading] = useState(false);

  // eslint-disable-next-line no-unused-vars
  const emitirNF = (venda) => {
    setNfModal(venda);
    // Pré-preencher NCM: busca pelo primeiro item real da venda no estoque
    const primeiroProd = (venda.itens && venda.itens.length > 0)
      ? venda.itens.find(i => Number(i.valor) > 0)?.produto || venda.produto
      : venda.produto;
    const prodEstoque = data.estoque.find(e => e.produto === primeiroProd || e.produto === venda.produto || e.produto === venda.descricao);
    const ncm = prodEstoque?.ncm || "";
    setNfForm({ cpf: "", nome: "", email: "", telefone: "", cep: "", endereco: "", numero: "", bairro: "", municipio: "Manaus", uf: "AM", ncm });
  };

  const confirmarNF = async () => {
    if (!nfForm.cpf || !nfForm.nome) { alert("CPF e Nome são obrigatórios!"); return; }
    setNfLoading(true);
    try {
      const _agora = new Date();
      const _pad = n => String(n).padStart(2,"0");
      const hoje = `${_agora.getFullYear()}-${_pad(_agora.getMonth()+1)}-${_pad(_agora.getDate())} ${_pad(_agora.getHours())}:${_pad(_agora.getMinutes())}:${_pad(_agora.getSeconds())}`;
      // Usa itens reais da venda excluindo brindes (valor = 0), ou produto principal
      const itensVenda = nfModal.itens && nfModal.itens.length > 0
        ? nfModal.itens.filter(i => Number(i.valor) > 0 && Number(i.qtd) > 0)
        : [];
      // Produto principal = antes do "+" (remove brindes do nome)
      const nomePrincipal = (nfModal.produto || "").split("+")[0].trim();
      const itensBase = itensVenda.length > 0
        ? itensVenda
        : [{ produto: nomePrincipal, qtd: nfModal.qtd, valor: nfModal.valor }];
      const itens = itensBase.map((i, idx) => {
        const nomeProd = (i.produto || "").split("+")[0].trim();
        const prodEst = data.estoque.find(e => e.produto === nomeProd || e.produto === i.produto);
        const ncmFinal = prodEst?.ncm || nfForm.ncm || "";
        return { ...i, produto: nomeProd, sku: prodEst?.sku || i.sku || nomeProd, ncm: ncmFinal };
      });
      const apiUrl = "https://gestao-tfx-saas.vercel.app/api/" + (window._emissorNF === "tiny" ? "tiny-nfe" : "bling-nfe") + "?action=emitir&tenant_id=" + getTenant();
      const r = await fetch(apiUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ venda: { ...nfModal, ...nfForm, itens, dataOperacao: hoje } })
      });
      const resp = await r.json();
      setNfLoading(false);
      if (resp.data?.id) {
        alert(`✅ NF emitida com sucesso! Número: ${resp.data.numero || resp.data.id}`);
        setNfModal(null);
      } else {
        alert("Erro: " + JSON.stringify(resp.error?.fields?.map(f => f.msg).join(", ") || resp.error || resp));
      }
    } catch (e) {
      setNfLoading(false);
      alert("Erro: " + e.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Deletar esta venda? O estoque será restaurado.")) return;
    const venda = data.vendas.find(v => v.id === id);
    const resRem = await sb.remove("vendas", id);
    if (venda && venda.cliente_id && !(resRem && resRem.ok === false)) await recalcularCliente(venda.cliente_id, [id]);
    setData(d => {
      let estoqueAtual = [...d.estoque];
      const itensVenda = venda?.itens || [];
      for (const item of itensVenda) {
        const idx = estoqueAtual.findIndex(p => p.produto?.toLowerCase() === item.produto?.toLowerCase());
        if (idx >= 0) {
          const novaQtd = (estoqueAtual[idx].estoque || 0) + Number(item.qtd);
          const novasCoresDev = ajustarCores(estoqueAtual[idx], item.cor, Number(item.qtd));
          estoqueAtual[idx] = { ...estoqueAtual[idx], estoque: novaQtd, qtd: novaQtd, ...(novasCoresDev ? { cores: novasCoresDev } : {}) };
          sb.update("estoque", estoqueAtual[idx].id, { qtd: novaQtd, ...(novasCoresDev ? { cores: novasCoresDev } : {}) });
        }
      }
      return { ...d, vendas: d.vendas.filter(v => v.id !== id), estoque: estoqueAtual };
    });
  };

  const [editando, setEditando] = useState(null);
  const [editandoOriginal, setEditandoOriginal] = useState(null);
  const handleEdit = async (form) => {
    const validos = (form.itens || []).filter(i => i.produto && i.qtd);
    const origV = editandoOriginal || editando;
    const origItensV = (origV && origV.itens) || [];
    const semCorV = validos.find(i => {
      const pc = (data.estoque || []).find(pp => pp.produto === i.produto);
      const temCores = pc && Array.isArray(pc.cores) && pc.cores.length > 0;
      return temCores && !i.cor && !origItensV.some(o => o.produto === i.produto && !o.cor);
    });
    if (semCorV) { alert("Selecione a cor de: " + semCorV.produto); return; }
    const totalValor = validos.reduce((s, i) => s + Number(i.valor) * Number(i.qtd), 0);
    const totalCusto = validos.reduce((s, i) => s + Number(i.custo) * Number(i.qtd), 0);
    const totalQtd = validos.reduce((s, i) => s + Number(i.qtd), 0);
    const descricao = validos.map(i => `${i.qtd}x ${i.produto}`).join(" + ");
    const row = {
      data: form.data,
      descricao: descricao || form.produto,
      canal: form.canal,
      cliente: form.cliente || "",
      vendedor: form.vendedor || "",
      valor: totalQtd > 0 ? totalValor / totalQtd : Number(form.valor || 0),
      qtd: totalQtd || 1,
      custo: totalQtd > 0 ? totalCusto / totalQtd : Number(form.custo || 0),
      garantia_meses: form.garantia_meses || 0,
      garantia_inicio: form.garantia_inicio || null,
      garantia_fim: form.garantia_fim || null,
      entrega: !!(form.entrega_tipo || Number(form.valor_entrega) > 0),
      entrega_tipo: form.entrega_tipo || null,
      valor_entrega: Number(form.valor_entrega) || 0,
      itens: validos.map(i => ({ produto: i.produto, qtd: Number(i.qtd), valor: Number(i.valor) || 0, custo: Number(i.custo) || 0, ...(i.cor ? { cor: i.cor } : {}) })),
    };
    await sb.update("vendas", editando.id, row);

    // Sincroniza gasto de entrega (ligado por venda_id)
    try {
      const valEnt = Number(row.valor_entrega) || 0;
      const descEnt = row.entrega_tipo && row.entrega_tipo !== "Envio" ? "Entrega — " + row.entrega_tipo : "Frete / Envio";
      const gastoExist = (data.gastos || []).find(g => g.venda_id === editando.id);
      if (gastoExist) {
        const patchG = { valor: valEnt, descricao: descEnt, data: row.data };
        await sb.update("gastos", gastoExist.id, patchG);
        setData(d => ({ ...d, gastos: (d.gastos || []).map(g => g.id === gastoExist.id ? { ...g, ...patchG } : g) }));
      } else if (valEnt > 0) {
        const rg = await sb.insert("gastos", { data: row.data, descricao: descEnt, categoria: "Operacional", tipo: "loja", valor: valEnt, recorrente: false, venda_id: editando.id });
        const ng = Array.isArray(rg) ? rg[0] : rg;
        if (ng && ng.id) setData(d => ({ ...d, gastos: [...(d.gastos || []), ng] }));
      }
    } catch (e) { console.log("Erro ao sincronizar gasto de entrega:", e); }

    // Ajustar estoque: devolver itens antigos e descontar itens novos
    const orig = editandoOriginal || editando;
    const itensAntigos = orig.itens && orig.itens.length > 0 ? orig.itens : [{ produto: orig.produto, qtd: orig.qtd || 1 }];
    const estoqueAtual = [...data.estoque];
    // Devolver itens antigos
    for (const item of itensAntigos) {
      const idx = estoqueAtual.findIndex(p => p.produto === item.produto);
      if (idx >= 0) {
        const novaQtd = (estoqueAtual[idx].estoque || 0) + Number(item.qtd);
        const novasCoresDev = ajustarCores(estoqueAtual[idx], item.cor, Number(item.qtd));
        estoqueAtual[idx] = { ...estoqueAtual[idx], estoque: novaQtd, qtd: novaQtd, ...(novasCoresDev ? { cores: novasCoresDev } : {}) };
        sb.update("estoque", estoqueAtual[idx].id, { qtd: novaQtd, ...(novasCoresDev ? { cores: novasCoresDev } : {}) });
      }
    }
    // Descontar novos itens
    for (const item of validos) {
      const idx = estoqueAtual.findIndex(p => p.produto === item.produto);
      if (idx >= 0) {
        const novaQtd = Math.max(0, (estoqueAtual[idx].estoque || 0) - Number(item.qtd));
        const novasCoresBx = ajustarCores(estoqueAtual[idx], item.cor, -Number(item.qtd));
        estoqueAtual[idx] = { ...estoqueAtual[idx], estoque: novaQtd, qtd: novaQtd, ...(novasCoresBx ? { cores: novasCoresBx } : {}) };
        sb.update("estoque", estoqueAtual[idx].id, { qtd: novaQtd, ...(novasCoresBx ? { cores: novasCoresBx } : {}) });
      }
    }
    setData(d => ({ ...d, vendas: d.vendas.map(v => v.id === editando.id ? { ...v, ...row, produto: row.descricao, valor: Number(row.valor), qtd: Number(row.qtd), custo: Number(row.custo), itens: row.itens } : v), estoque: estoqueAtual }));
    setEditando(null);
    setEditandoOriginal(null);
    if (showToast) showToast("Venda atualizada!");
  };

  const handleAdd = async () => {
    if (salvandoVenda) return;
    const validos = itens.filter(i => i.produto && i.qtd);
    if (validos.length === 0) return alert("Preencha pelo menos um produto!");
    setSalvandoVenda(true);
    
    const descricao = validos.map(i => `${i.qtd}x ${i.produto}`).join(" + ");
    const totalValor = validos.reduce((s, i) => s + Number(i.valor) * Number(i.qtd), 0);
    const totalCusto = validos.reduce((s, i) => s + Number(i.custo) * Number(i.qtd), 0);
    const totalQtd = validos.reduce((s, i) => s + Number(i.qtd), 0);
    const brindes = validos.map(i => i.brinde).filter(Boolean).join(", ");
    const semCor = validos.find(i => { const pc = (data.estoque || []).find(p => p.produto === i.produto); return pc && Array.isArray(pc.cores) && pc.cores.length > 0 && !i.cor; });
    if (semCor) { setSalvandoVenda(false); alert("Selecione a cor de: " + semCor.produto); return; }

    try {
      const taxaReaisVenda = calcularTaxaReais();
      const descontoReais = calcularDesconto();
      const cupomUsado = cupomAplicado?.codigo || "";
      // Se é envio, criar/atualizar cliente
      let clienteFinal = nomeCliente || "";
      if (isEnvio) {
        if (!dadosEnvio.cliente_nome || !dadosEnvio.endereco || !dadosEnvio.numero || !dadosEnvio.cidade || !dadosEnvio.estado) {
          alert("Preencha todos os campos obrigatórios de envio!");
          return;
        }
        
        const clienteExiste = (data.clientes_loja || []).find(c => c.nome === dadosEnvio.cliente_nome);
        if (clienteExiste) {
          await sb.update("clientes_loja", clienteExiste.id, dadosEnvio);
        } else {
          await sb.insert("clientes_loja", { nome: dadosEnvio.cliente_nome, ...dadosEnvio });
        }
        clienteFinal = dadosEnvio.cliente_nome;
      }
      
      const mesesGarantia = garantiaMeses === 'custom' ? Number(garantiaMesesCustom) : Number(garantiaMeses);
    const garantiaInicio = mesesGarantia > 0 ? dataVenda : null;
    const garantiaFim = mesesGarantia > 0 ? ((garantiaFimManual && garantiaFimManual.k === dataVenda + "|" + mesesGarantia) ? garantiaFimManual.v : somarMeses(dataVenda, mesesGarantia)) : null;
    
    const row = { data: dataVenda, descricao, canal, cliente: clienteFinal, vendedor: vendedor || "", valor: (totalValor - descontoReais) / totalQtd, qtd: totalQtd, custo: totalCusto / totalQtd, brinde: brindes, taxa_maquininha: taxaReaisVenda, desconto: descontoReais, cupom_usado: cupomUsado, garantia_meses: mesesGarantia, garantia_inicio: garantiaInicio, garantia_fim: garantiaFim, entrega: !!(entregaTipo || Number(frete) > 0), entrega_tipo: entregaTipo || (Number(frete) > 0 ? "Envio" : null), valor_entrega: Number(frete) || 0, itens: validos.map(i => ({ produto: i.produto, qtd: Number(i.qtd), valor: Number(i.valor) || 0, custo: Number(i.custo) || 0, ...(i.cor ? { cor: i.cor } : {}) })) };
      const result = await sb.insert("vendas", row);
      const inserted = Array.isArray(result) ? result[0] : result;
      if (!inserted || inserted.code) return alert("Erro ao salvar venda. Tente novamente.");
      // Abre modal de upsell com o ID da venda recém-salva
      setUpsellModal({ show: true, vendaId: inserted.id });
      setEntregaTipo("");
      
      const nova = { ...inserted, produto: inserted.descricao, qtd: totalQtd, valor: inserted.valor, custo: inserted.custo, taxa_maquininha: taxaReaisVenda, desconto: descontoReais, itens: validos.map(i => ({ produto: i.produto, qtd: Number(i.qtd), valor: Number(i.valor) || 0, custo: Number(i.custo) || 0, ...(i.cor ? { cor: i.cor } : {}) })) };
      
      let clienteIdVenda = null;
      // Auto-cadastro de cliente (usa dados do "Colar dados do cliente" quando houver)
      if (clienteFinal && clienteFinal.trim() && !isEnvio) {
        const nomeNorm = clienteFinal.trim();
        const lista = data.clientes_loja || [];
        const tel = (dadosEnvio.telefone || "").replace(/\D/g, "");
        const temEnd = !!dadosEnvio.endereco;
        const extras = {
          telefone: tel,
          cep: dadosEnvio.cep || "",
          endereco: dadosEnvio.endereco || "",
          numero: dadosEnvio.numero || "",
          complemento: dadosEnvio.complemento || "",
          bairro: dadosEnvio.bairro || "",
          cidade: dadosEnvio.cidade || "",
          estado: temEnd ? (dadosEnvio.estado || "") : "",
        };
        const valorVenda = totalValor - descontoReais;
        const clienteExistente = (tel && lista.find(c => (c.telefone || "").replace(/\D/g, "") === tel)) || lista.find(c => c.nome?.toLowerCase() === nomeNorm.toLowerCase());
        if (clienteExistente) {
          const patch = {
            total_compras: Number(clienteExistente.total_compras || 0) + valorVenda,
            num_pedidos: Number(clienteExistente.num_pedidos || 0) + 1,
            ultima_compra: dataVenda,
          };
          Object.keys(extras).forEach(k => { if (extras[k] && !clienteExistente[k]) patch[k] = extras[k]; });
          await sb.update("clientes_loja", clienteExistente.id, patch);
          clienteIdVenda = clienteExistente.id;
          setData(d => ({ ...d, clientes_loja: (d.clientes_loja || []).map(c => c.id === clienteExistente.id ? { ...c, ...patch } : c) }));
        } else {
          const novoRow = { tenant_id: getTenant(), nome: nomeNorm, genero: inferirGenero(nomeNorm), canal_origem: canal, total_compras: valorVenda, num_pedidos: 1, ultima_compra: dataVenda };
          Object.keys(extras).forEach(k => { if (extras[k]) novoRow[k] = extras[k]; });
          const novoCliente = await sb.insert("clientes_loja", novoRow);
          if (novoCliente && !novoCliente.code) {
            const nc = Array.isArray(novoCliente) ? novoCliente[0] : novoCliente;
            if (nc && nc.id) clienteIdVenda = nc.id;
            setData(d => ({ ...d, clientes_loja: [...(d.clientes_loja || []), nc] }));
            if (nc && nc.id && (!nc.genero || nc.genero === "desconhecido")) {
              buscarGeneroIBGE(nomeNorm).then(g => {
                if (!g) return;
                sb.update("clientes_loja", nc.id, { genero: g });
                setData(d => ({ ...d, clientes_loja: (d.clientes_loja || []).map(c => c.id === nc.id ? { ...c, genero: g } : c) }));
              });
            }
          }
        }
      }

      if (clienteIdVenda && inserted && inserted.id) {
        nova.cliente_id = clienteIdVenda;
        await sb.update("vendas", inserted.id, { cliente_id: clienteIdVenda });
      }

      // Lança venda no caixa automaticamente
      const totalVenda = (totalValor - descontoReais);
      await autoLancarCaixa("entrada", descricao, totalVenda, dataVenda, "vendas_loja", data.caixa_config);
      
      // Lança frete como gasto automaticamente
      if (frete && Number(frete) > 0) {
        await sb.insert("gastos", { data: dataVenda, descricao: entregaTipo ? "Entrega — " + entregaTipo : "Frete / Envio", categoria: "Operacional", tipo: "loja", valor: parseFloat(Number(frete).toFixed(2)), recorrente: false, venda_id: inserted.id });
      }

      // Lança gasto de taxa de maquininha automaticamente
      let novoGastoTaxa = null;
      if (bandeira !== "Nenhuma") {
        const taxaReais = calcularTaxaReais();
        if (taxaReais > 0) {
          const rowTaxa = { data: dataVenda, descricao: `Taxa Maquininha - ${bandeira} ${parcelas}x`, categoria: "Operacional", tipo: "loja", valor: taxaReais, recorrente: false };
          novoGastoTaxa = await sb.insert("gastos", rowTaxa);
        }
      }

      setData((d) => {
        let estoqueAtual = [...d.estoque];
        for (const item of validos) {
          const idx = estoqueAtual.findIndex(p => p.produto?.toLowerCase() === item.produto?.toLowerCase());
          if (idx >= 0) {
            const novaQtd = Math.max(0, (estoqueAtual[idx].estoque || 0) - Number(item.qtd));
            const novasCores = ajustarCores(estoqueAtual[idx], item.cor, -Number(item.qtd));
            estoqueAtual[idx] = { ...estoqueAtual[idx], estoque: novaQtd, qtd: novaQtd, ...(novasCores ? { cores: novasCores } : {}) };
            sb.update("estoque", estoqueAtual[idx].id, { qtd: novaQtd, ...(novasCores ? { cores: novasCores } : {}) });
          }
        }
        const novosGastos = novoGastoTaxa ? [...d.gastos, { ...novoGastoTaxa, subcategoria: novoGastoTaxa.descricao }] : d.gastos;
        return { ...d, vendas: [...d.vendas, nova], estoque: estoqueAtual, gastos: novosGastos };
      });

      // Reset bandeira/parcelas/frete
      setGarantiaFimManual(null);
      setBandeira("Nenhuma");
      setParcelas(1);
      setBandeira2("Nenhuma");
      setValorPag2("");
      setFrete("");
      setDesconto("");
      setCupomAplicado(null);
      setCupomCodigo("");
    } catch(e) {
      alert("Erro: " + e.message);
    }
    setSalvandoVenda(false);
    setNomeCliente("");
    setDadosEnvio({ cliente_nome: "", telefone: "", cep: "", endereco: "", numero: "", complemento: "", bairro: "", cidade: "", estado: "" });
    setTextoColado("");
    setClienteSelId(null);
    setVendedor("");
    setItens([{ ...emptyItem }]);
    setShowForm(false);
    if (showToast) showToast("Venda salva com sucesso!");
  };

  const handleSalvarUpsell = async () => {
    const itensValidos = upsellItens.filter(i => i.produto && i.valor > 0);
    if (itensValidos.length === 0) {
      setUpsellModal({ show: false, vendaId: null });
      setUpsellItens([{ produto: "", produto_id: null, valor: 0, custo: 0, quantidade: 1 }]);
      return;
    }
    setSalvandoUpsell(true);
    try {
      for (const item of itensValidos) {
        const result = await sb.insert("vendas_upsell", {
          venda_id: upsellModal.vendaId,
          produto_estoque_id: item.produto_id || null,
          produto_nome: item.produto,
          valor: Number(item.valor),
          custo: Number(item.custo) || 0,
          quantidade: Number(item.quantidade) || 1,
          funcionario: vendedor || "",
          registrado_por: logado?.perfil || "admin"
        });
        const saved = Array.isArray(result) ? result[0] : result;
        if (!saved || saved.code || saved.error) {
          console.error("Erro ao salvar upsell:", saved);
          throw new Error(saved?.message || saved?.error || "Erro desconhecido ao salvar upsell");
        }
        // Descontar do estoque
        if (item.produto_id) {
          const prodEstoque = (data.estoque || []).find(p => p.id === item.produto_id);
          if (prodEstoque) {
            const novaQtd = Math.max(0, (prodEstoque.estoque || 0) - Number(item.quantidade));
            await sb.update("estoque", item.produto_id, { qtd: novaQtd });
            setData(d => ({
              ...d,
              estoque: d.estoque.map(p => p.id === item.produto_id
                ? { ...p, estoque: novaQtd, qtd: novaQtd }
                : p
              )
            }));
          }
        }
        // Somar upsell ao valor da venda principal
        const vendaPrincipal = (data.vendas || []).find(v => v.id === upsellModal.vendaId);
        if (vendaPrincipal) {
          const valorUpsell = Number(item.valor) * Number(item.quantidade);
          const novoValorTotal = (vendaPrincipal.valor * vendaPrincipal.qtd) + valorUpsell;
          const novoValorUnit = novoValorTotal / vendaPrincipal.qtd;
          await sb.update("vendas", upsellModal.vendaId, { valor: novoValorUnit });
          setData(d => ({
            ...d,
            vendas: d.vendas.map(v => v.id === upsellModal.vendaId
              ? { ...v, valor: novoValorUnit }
              : v
            )
          }));
          await autoLancarCaixa("entrada", `Upsell: ${item.produto}`, valorUpsell, new Date().toISOString().slice(0,10), "vendas_loja", data.caixa_config);
        }
      }
      // Atualizar estado local da venda com os novos itens de upsell
      setData(d => ({
        ...d,
        vendas: d.vendas.map(v => {
          if (v.id !== upsellModal.vendaId) return v;
          const itensAtuais = v.itens || [];
          const novosUpsells = itensValidos.map(item => ({
            produto: item.produto + " [upsell]",
            qtd: Number(item.quantidade) || 1,
            valor: Number(item.valor) || 0,
            custo: Number(item.custo) || 0,
            upsell: true
          }));
          return { ...v, itens: [...itensAtuais, ...novosUpsells] };
        })
      }));
      if (showToast) showToast("Upsell registrado!");
    } catch(e) {
      alert("Erro ao salvar upsell: " + e.message);
    }
    setSalvandoUpsell(false);
    setUpsellModal({ show: false, vendaId: null });
    setUpsellItens([{ produto: "", produto_id: null, valor: 0, custo: 0, quantidade: 1 }]);
  };

  const handleUpsellProduto = (idx, nomeProduto) => {
    const prodEstoque = (data.estoque || []).find(p => p.produto === nomeProduto);
    setUpsellItens(prev => prev.map((it, i) => i !== idx ? it : {
      ...it,
      produto: nomeProduto,
      produto_id: prodEstoque?.id || null,
      valor: prodEstoque?.preco || prodEstoque?.preco_venda || 0,
      custo: prodEstoque?.custo || prodEstoque?.preco_custo || 0
    }));
  };

  // eslint-disable-next-line no-unused-vars
  const handleDuplicar = async (v) => {
    const row = { data: new Date().toISOString().slice(0, 10), descricao: v.produto, canal: v.canal, valor: v.valor, qtd: v.qtd, custo: v.custo };
    const result = await sb.insert("vendas", row);
    const inserted = Array.isArray(result) ? result[0] : result;
    const nova = { ...inserted, produto: inserted.descricao, qtd: v.qtd, valor: v.valor, custo: v.custo };
    setData((d) => ({ ...d, vendas: [...d.vendas, nova] }));
  };

  
  // eslint-disable-next-line no-unused-vars
  const gerarEtiquetaMandaBem = async (venda) => {
    setGerandoEtiqueta(true);
    try {
      const cliente = (data.clientes_loja || data.clientes || []).find(c => c.nome === venda.cliente);
      if (!cliente) {
        alert("Cliente não encontrado! Cadastre o endereço do cliente primeiro.");
        setGerandoEtiqueta(false);
        return;
      }

      const response = await fetch('https://www.gestaotfx.com.br/api/cupom-trial?action=gerar_etiqueta_mb', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          destinatario: cliente.nome,
          cep: cliente.cep || '',
          logradouro: cliente.endereco || '',
          numero: cliente.numero || 'S/N',
          complemento: cliente.complemento || '',
          cidade: cliente.cidade || 'Manaus',
          bairro: cliente.bairro || '',
          estado: cliente.uf || 'AM',
          peso: formMB.peso,
          altura: formMB.altura,
          largura: formMB.largura,
          comprimento: formMB.comprimento,
          forma_envio: formMB.forma_envio,
          ref_id: venda.id,
        })
      });

      const result = await response.json();
      
      if (result.resultado?.sucesso === "true") {
        const envioId = result.resultado.envio_id;
        await sb.update("vendas", venda.id, { 
          manda_bem_envio_id: envioId,
          manda_bem_status: "Gerada"
        });
        setData(d => ({ 
          ...d, 
          vendas: d.vendas.map(v => v.id === venda.id ? { ...v, manda_bem_envio_id: envioId, manda_bem_status: "Gerada" } : v) 
        }));
        if (showToast) showToast("✅ Etiqueta gerada com sucesso!");
        setModalMandaBem(null);
      } else {
        alert("Erro ao gerar etiqueta: " + (result.resultado?.erro || "Erro desconhecido"));
      }
    } catch (error) {
      alert("Erro: " + error.message);
    }
    setGerandoEtiqueta(false);
  };

  return (
    <div>
      {nfModal && (
        <div style={{ position: "fixed", inset: 0, background: "#000000CC", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ ...styles.card, width: 480, maxWidth: "95vw", borderColor: C.gold + "60" }}>
            <div style={{ fontWeight: 700, fontSize: 16, color: C.gold, marginBottom: 16 }}>🧾 Emitir Nota Fiscal</div>
            <div style={{ fontSize: 12, color: "#374151", marginBottom: 16 }}>Venda: <span style={{ color: "#111827" }}>{nfModal.produto}</span> — {fmt(nfModal.valor * nfModal.qtd)}</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <div style={{ gridColumn: "1/-1" }}>
                <label style={styles.label}>Nome do Cliente *</label>
                <input style={styles.input} value={nfForm.nome} onChange={e => setNfForm(f => ({...f, nome: e.target.value}))} placeholder="Nome completo" />
              </div>
              <div>
                <label style={styles.label}>CPF/CNPJ *</label>
                <input style={styles.input} value={nfForm.cpf} onChange={e => setNfForm(f => ({...f, cpf: e.target.value}))} placeholder="000.000.000-00" />
              </div>
              <div>
                <label style={styles.label}>Telefone</label>
                <input style={styles.input} value={nfForm.telefone} onChange={e => setNfForm(f => ({...f, telefone: e.target.value}))} placeholder="92999999999" />
              </div>
              <div style={{ gridColumn: "1/-1" }}>
                <label style={styles.label}>Email</label>
                <input style={styles.input} value={nfForm.email} onChange={e => setNfForm(f => ({...f, email: e.target.value}))} placeholder="cliente@email.com" />
              </div>
              <div>
                <label style={styles.label}>CEP</label>
                <input style={styles.input} value={nfForm.cep} onChange={e => {
                  const val = e.target.value;
                  const cepLimpo = val.replace(/\D/g,"");
                  setNfForm(f => ({...f, cep: val}));
                  if (cepLimpo.length === 8) {
                    fetch("https://viacep.com.br/ws/" + cepLimpo + "/json/")
                      .then(r => r.json())
                      .then(d => { if (!d.erro) setNfForm(f => ({...f, endereco: d.logradouro, bairro: d.bairro, municipio: d.localidade, uf: d.uf})); })
                      .catch(() => {});
                  }
                }} placeholder="69000000" />
              </div>
              <div>
                <label style={styles.label}>Número</label>
                <input style={styles.input} value={nfForm.numero} onChange={e => setNfForm(f => ({...f, numero: e.target.value}))} placeholder="123" />
              </div>
              <div style={{ gridColumn: "1/-1" }}>
                <label style={styles.label}>Endereço</label>
                <input style={styles.input} value={nfForm.endereco} onChange={e => setNfForm(f => ({...f, endereco: e.target.value}))} placeholder="Rua, Av..." />
              </div>
              <div>
                <label style={styles.label}>Bairro</label>
                <input style={styles.input} value={nfForm.bairro} onChange={e => setNfForm(f => ({...f, bairro: e.target.value}))} placeholder="Bairro" />
              </div>
              <div>
                <label style={styles.label}>Cidade</label>
                <input style={styles.input} value={nfForm.municipio} onChange={e => setNfForm(f => ({...f, municipio: e.target.value}))} />
              </div>
              <div>
                <label style={styles.label}>NCM do Produto</label>
                <input style={styles.input} value={nfForm.ncm || ""} onChange={e => setNfForm(f => ({...f, ncm: e.target.value}))} placeholder="Ex: 84713012" />
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
              <button style={styles.btn("primary")} onClick={confirmarNF} disabled={nfLoading}>{nfLoading ? "Emitindo..." : "✅ Emitir NF"}</button>
              <button style={styles.btn("secondary")} onClick={() => setNfModal(null)}>Cancelar</button>
            </div>
          </div>
        </div>
      )}

      {editando && (() => {
        const itensEdit = editando.itens && editando.itens.length > 0
          ? editando.itens.map(i => ({ produto: i.produto, qtd: String(i.qtd), valor: String(i.valor), custo: String(i.custo), cor: i.cor || "" }))
          : [{ produto: editando.produto || "", qtd: String(editando.qtd || 1), valor: String(editando.valor || ""), custo: String(editando.custo || "") }];
        let formEdit = {
          data: editando.data || hojeManaus(),
          canal: editando.canal || "Online",
          cliente: editando.cliente || "",
          vendedor: editando.vendedor || "",
          entrega_tipo: editando.entrega_tipo || "",
          valor_entrega: editando.valor_entrega || "",
          garantia_meses: editando.garantia_meses || 0,
          garantia_inicio: editando.garantia_inicio || null,
          garantia_fim: editando.garantia_fim || null,
          itens: itensEdit,
        };
        const updateEdit = (key, val) => { formEdit = { ...formEdit, [key]: val }; };
        const updateItemEdit = (idx, key, val) => {
          formEdit.itens = formEdit.itens.map((it, j) => j === idx ? { ...it, [key]: val } : it);
        };
        return (
          <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.5)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", top: 0, left: 0, right: 0, bottom: 0 }}>
            <div style={{ background: "#ffffff", border: `1px solid ${C.accent}40`, borderRadius: 16, padding: 28, width: 640, maxWidth: "95vw", maxHeight: "90vh", overflowY: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.25)" }}>
              <div style={{ fontWeight: 700, fontSize: 16, color: C.accent, marginBottom: 28 }}>✏️ Editar Venda</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 12, marginBottom: 16 }}>
                <div><label style={styles.label}>Data</label><input style={styles.input} type="date" defaultValue={formEdit.data} onChange={e => updateEdit("data", e.target.value)} /></div>
                <div><label style={styles.label}>Canal</label><select style={styles.input} defaultValue={formEdit.canal} onChange={e => updateEdit("canal", e.target.value)}><option>Online</option><option>Física</option></select></div>
                <div><label style={styles.label}>Cliente</label><input style={styles.input} type="text" defaultValue={formEdit.cliente} onChange={e => updateEdit("cliente", e.target.value)} placeholder="Nome do cliente" /></div>
                <div><label style={styles.label}>Vendedor</label><select style={styles.input} defaultValue={formEdit.vendedor} onChange={e => updateEdit("vendedor", e.target.value)}>
                  <option value="">— Nenhum —</option>
                  {(data.funcionarios || []).map(f => <option key={f.id} value={f.nome}>{f.nome}</option>)}
                </select></div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 2fr", gap: 12, marginBottom: 16 }}>
                <div>
                  <label style={styles.label}>🛡️ Garantia</label>
                  <select style={styles.input} value={formEdit.garantia_meses || 0} onChange={e => {
                    const meses = e.target.value === 'custom' ? formEdit.garantia_meses : Number(e.target.value);
                    updateEdit("garantia_meses", meses);
                    if (e.target.value !== 'custom' && Number(e.target.value) > 0) {
                      updateEdit("garantia_fim", somarMeses(formEdit.garantia_inicio || formEdit.data, Number(e.target.value)));
                      updateEdit("garantia_inicio", formEdit.garantia_inicio || formEdit.data);
                    } else if (Number(e.target.value) === 0) {
                      updateEdit("garantia_inicio", null);
                      updateEdit("garantia_fim", null);
                    } setEditando({ ...editando, ...formEdit });
                  }}>
                    <option value="0">Sem garantia</option>
                    <option value="3">3 meses</option>
                    <option value="6">6 meses</option>
                    <option value="12">1 ano</option>
                    <option value="24">2 anos</option>
                    {![0, 3, 6, 12, 24].includes(Number(formEdit.garantia_meses)) && <option value={formEdit.garantia_meses}>{formEdit.garantia_meses} meses</option>}
                  </select>
                </div>
                {formEdit.garantia_meses > 0 && (
                  <>
                    <div>
                      <label style={styles.label}>Início</label>
                      <input style={styles.input} type="date" value={formEdit.garantia_inicio || formEdit.data} onChange={e => {
                        updateEdit("garantia_inicio", e.target.value);
                        updateEdit("garantia_fim", e.target.value ? somarMeses(e.target.value, Number(formEdit.garantia_meses)) : null); setEditando({ ...editando, ...formEdit });
                      }} />
                    </div>
                    <div>
                      <label style={styles.label}>Válido até</label>
                      <input style={styles.input} type="date" value={formEdit.garantia_fim || ""} onChange={e => { updateEdit("garantia_fim", e.target.value); setEditando({ ...editando, ...formEdit }); }} />
                    </div>
                  </>
                )}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
                <div>
                  <label style={styles.label}>Entrega</label>
                  <select style={styles.input} defaultValue={formEdit.entrega_tipo || ""} onChange={e => updateEdit("entrega_tipo", e.target.value)}>
                    <option value="">Sem entrega</option>
                    <option value="Motoboy">Motoboy</option>
                    <option value="Uber">Uber</option>
                    <option value="Envio">Correios / Envio</option>
                  </select>
                </div>
                <div>
                  <label style={styles.label}>Valor da entrega (R$)</label>
                  <input style={styles.input} type="number" step="0.01" min="0" defaultValue={formEdit.valor_entrega ?? ""} onChange={e => updateEdit("valor_entrega", e.target.value)} placeholder="0,00" />
                </div>
              </div>
              <div style={{ fontSize: 11, color: "#374151", marginBottom: 8, letterSpacing: 1 }}>ITENS DA VENDA</div>
              <div style={{ background: C.bgElevated, borderRadius: 8, padding: 12, marginBottom: 16 }}>
                <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr 24px", gap: 8, marginBottom: 6 }}>
                  {["Produto", "Qtd", "Valor Unit.", "Custo Unit.", ""].map(h => <div key={h} style={{ fontSize: 9, color: "#374151", letterSpacing: 1 }}>{h}</div>)}
                </div>
                {formEdit.itens.map((it, idx) => (
                  <div key={idx} style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr 24px", gap: 8, marginBottom: 8 }}>
                    <select style={styles.input} value={it.produto} onChange={e => { const prod = (data.estoque||[]).find(p => p.produto === e.target.value); updateItemEdit(idx, "produto", e.target.value); updateItemEdit(idx, "cor", ""); if (prod) { updateItemEdit(idx, "custo", String(prod.custo || "")); updateItemEdit(idx, "valor", String(prod.preco || "")); } setEditando({ ...editando, ...formEdit}); }}>
                      <option value="">— Selecionar —</option>
                      {(data.estoque || []).map(p => <option key={p.id} value={p.produto}>{p.produto}</option>)}
                      <option value={it.produto}>{it.produto}</option>
                    </select>
                    <input style={styles.input} type="number" value={it.qtd} onChange={e => { updateItemEdit(idx, "qtd", e.target.value); setEditando({ ...editando, ...formEdit}); }} placeholder="1" />
                    <input style={styles.input} type="number" value={it.valor} onChange={e => { updateItemEdit(idx, "valor", e.target.value); setEditando({ ...editando, ...formEdit}); }} placeholder="0,00" />
                    <input style={styles.input} type="number" value={it.custo} onChange={e => { updateItemEdit(idx, "custo", e.target.value); setEditando({ ...editando, ...formEdit}); }} placeholder="0,00" />
                    {formEdit.itens.length > 1
                      ? <button onClick={() => { setEditando({ ...editando, ...formEdit, itens: formEdit.itens.filter((_,j) => j !== idx)}); }} style={{ background: "none", border: "none", color: C.warn, cursor: "pointer", fontSize: 16 }}>✕</button>
                      : <div />}
                  {(() => {
                      const prodC = (data.estoque || []).find(pp => pp.produto === it.produto);
                      const coresC = prodC && Array.isArray(prodC.cores) ? prodC.cores : [];
                      if (coresC.length === 0) return null;
                      return (
                        <div data-k="cor-edit" style={{ gridColumn: "1 / -1" }}>
                          <select style={{ ...styles.input, borderColor: it.cor ? undefined : "#f59e0b" }} value={it.cor || ""} onChange={e => { updateItemEdit(idx, "cor", e.target.value); setEditando({ ...editando, ...formEdit }); }}>
                            <option value="">🎨 Selecione a cor…</option>
                            {coresC.map(c => <option key={c.cor} value={c.cor}>{c.cor}</option>)}
                          </select>
                        </div>
                      );
                    })()}
                  </div>
                ))}
                <button onClick={() => { setEditando({ ...editando, ...formEdit, itens: [...formEdit.itens, { produto: "", qtd: "1", valor: "", custo: "" }]}); }} style={{ ...styles.btn("secondary"), fontSize: 11, marginTop: 4 }}>+ Item</button>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button style={styles.btn("primary")} onClick={() => handleEdit(formEdit)}>💾 Salvar</button>
                <button style={styles.btn("secondary")} onClick={() => setEditando(null)}>Cancelar</button>
              </div>
            </div>
          </div>
        );
      })()}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 28 }}>
        <div>
          <div style={styles.pageTitle}>Controle de Vendas</div>
          <div style={styles.pageSub}>Físico + Online</div>
        </div>

        
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <button onClick={() => setShowFiltroAvancado(!showFiltroAvancado)} style={{ 
            ...styles.btn("secondary"), 
            fontSize: 12, 
            padding: "8px 16px",
            borderRadius: 10,
            fontWeight: 600,
            background: showFiltroAvancado ? `${C.accent}20` : C.bgElevated,
            border: `1px solid ${showFiltroAvancado ? C.accent : C.border}40`
          }}>
            📅 {labelPeriodoVendas()} ▾
          </button>
          <input style={{ 
            ...styles.input, 
            width: 220, 
            fontSize: 12,
            borderRadius: 10,
            paddingLeft: 12,
            background: C.bgElevated,
            border: "1px solid #e5e7eb"
          }} 
          placeholder="🔍 Buscar produto, cliente..." 
          value={buscaVendas} 
          onChange={e => setBuscaVendas(e.target.value)} />
          <button style={{
            ...styles.btn("primary"),
            fontSize: 14,
            fontWeight: 700,
            padding: "10px 20px",
            borderRadius: 12,
            background: `linear-gradient(135deg, ${C.accent}, ${C.accentMid})`,
            boxShadow: `0 4px 16px ${C.accent}40`,
            border: "none"
          }} 
          onClick={() => setShowForm(!showForm)}
          >+ Nova Venda</button>
        </div>
      </div>

      {showFiltroAvancado && (
        <div style={{ ...styles.card, marginBottom: 16, padding: 16 }}>
          <div style={{ fontSize: 11, color: "#374151", marginBottom: 10, letterSpacing: 1 }}>PERÍODO RÁPIDO</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 14 }}>
            {[["todos","Todos"],["hoje","Hoje"],["ontem","Ontem"],["7d","7 dias"],["15d","15 dias"],["30d","30 dias"],["90d","90 dias"]].map(([v,l]) => (
              <button key={v} onClick={() => { aplicarPeriodoRapido(v); }} style={{ ...styles.btn(periodoRapido === v && mesFiltro === "todos" ? "primary" : "secondary"), fontSize: 11, padding: "5px 12px" }}>{l}</button>
            ))}
          </div>
          <div style={{ fontSize: 11, color: "#374151", marginBottom: 10, letterSpacing: 1 }}>POR MÊS</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 14 }}>
            {mesesDisponiveis.map(m => (
              <button key={m} onClick={() => selecionarMesVendas(m)} style={{ ...styles.btn(mesFiltro === m ? "primary" : "secondary"), fontSize: 11, padding: "5px 12px" }}>{MESES[parseInt(m.slice(5,7))-1] + "/" + m.slice(2,4)}</button>
            ))}
          </div>
          <div style={{ fontSize: 11, color: "#374151", marginBottom: 10, letterSpacing: 1 }}>PERÍODO CUSTOMIZADO</div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <input type="date" style={{ ...styles.input, width: "auto", fontSize: 12 }} value={dataInicio} onChange={e => setDataInicio(e.target.value)} />
            <span style={{ color: "#374151" }}>→</span>
            <input type="date" style={{ ...styles.input, width: "auto", fontSize: 12 }} value={dataFim} onChange={e => setDataFim(e.target.value)} />
            <button onClick={() => setShowFiltroAvancado(false)} style={{ ...styles.btn("primary"), fontSize: 11 }}>Aplicar</button>
          </div>
        </div>
      )}

      {showForm && (
        <div style={{ 
          background: C.bgElevated,
          border: `2px solid ${C.accent}30`,
          borderRadius: 20,
          padding: 32,
          marginBottom: 24,
          boxShadow: `0 10px 40px ${C.accent}20`
        }}>
          <div style={{ fontWeight: 800, fontSize: 20, color: C.accent, letterSpacing: -0.8, marginBottom: 24, display: "flex", alignItems: "center", gap: 12 }}>
              <span style={{ fontSize: 24 }}>📝</span> Registrar Venda
            </div>
          {/* ── Seção 1: Info da venda ── */}
          <div style={{ background: "#f0f2f5", borderRadius: 10, padding: 14, marginBottom: 14, border: "1px solid #e5e7eb" }}>
            <div style={{ fontSize: 10, color: "#374151", letterSpacing: 1, marginBottom: 12 }}>🧾 VENDA</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 12 }}>
              <div>
                <label style={styles.label}>Canal</label>
                <select style={styles.input} value={canal} onChange={e => setCanal(e.target.value)}>
                  <option>Online</option>
                  <option>Física</option>
                </select>
              </div>
              <div>
                <label style={styles.label}>Data</label>
                <input style={styles.input} type="date" value={dataVenda} onChange={e => setDataVenda(e.target.value)} />
              </div>
              <div>
                <label style={styles.label}>Vendedor</label>
                <select style={styles.input} value={vendedor} onChange={e => setVendedor(e.target.value)}>
                  <option value="">— Nenhum —</option>
                  {(data.funcionarios || []).map(f => <option key={f.id} value={f.nome}>{f.nome}</option>)}
                </select>
              </div>
              <div>
                <label style={styles.label}>🛡️ Garantia</label>
                <select style={styles.input} value={garantiaMeses} onChange={e => setGarantiaMeses(e.target.value)}>
                  <option value="0">Sem garantia</option>
                  <option value="3">3 meses</option>
                  <option value="6">6 meses</option>
                  <option value="12">1 ano</option>
                  <option value="24">2 anos</option>
                  <option value="custom">Personalizado</option>
                </select>
              </div>
              {garantiaMeses === 'custom' && (
                <div>
                  <label style={styles.label}>Meses (personalizado)</label>
                  <input style={styles.input} type="number" placeholder="Qtd meses" value={garantiaMesesCustom} onChange={e => setGarantiaMesesCustom(e.target.value)} />
                </div>
              )}
              {(() => {
                const mG = garantiaMeses === 'custom' ? Number(garantiaMesesCustom) : Number(garantiaMeses);
                if (!(mG > 0) || !dataVenda) return null;
                const kG = dataVenda + "|" + mG;
                const vG = (garantiaFimManual && garantiaFimManual.k === kG) ? garantiaFimManual.v : somarMeses(dataVenda, mG);
                return (
                  <div>
                    <label style={styles.label}>Válido até</label>
                    <input style={styles.input} type="date" value={vG} onChange={e => setGarantiaFimManual({ k: kG, v: e.target.value })} />
                  </div>
                );
              })()}
              <div>
                <label style={styles.label}>🎁 Adicionar Kit/Combo</label>
                <select style={{ ...styles.input, color: C.gold }} onChange={e => {
                  const kit = (data.kits || []).find(k => k.id === e.target.value);
                  if (!kit) return;
                  const novosItens = kit.itens.map(i => ({
                    produto: i.produto,
                    qtd: String(i.qtd),
                    valor: String(data.estoque.find(p => p.produto === i.produto)?.preco || 0),
                    custo: String(data.estoque.find(p => p.produto === i.produto)?.custo || 0),
                    brinde: ""
                  }));
                  // Adiciona ao invés de substituir — suporte a múltiplos kits
                  setItens(prev => {
                    const temVazio = prev.length === 1 && !prev[0].produto;
                    return temVazio ? novosItens : [...prev, ...novosItens];
                  });
                  e.target.value = "";
                }}>
                  <option value="">-- Adicionar kit --</option>
                  {(data.kits || []).map(k => <option key={k.id} value={k.id}>📦 {k.nome}</option>)}
                </select>
                <div style={{ fontSize: 10, color: "#374151", marginTop: 3 }}>Vários kits para pedidos duplos/triplos</div>
              </div>
            </div>
          </div>
          <div style={{ background: "#f0f2f5", borderRadius: 10, padding: 14, marginBottom: 14, border: "1px solid #e5e7eb" }}>
            <div style={{ fontSize: 10, color: "#374151", letterSpacing: 1, marginBottom: 12 }}>👤 CLIENTE</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 12, alignItems: "start" }}>
              <div>
                <label style={styles.label}>Cliente</label>
                <input style={styles.input} type="text" list="clientes-sugestoes" placeholder="Nome do cliente (opcional)" value={nomeCliente} onChange={e => aoDigitarCliente(e.target.value)} />
                <datalist id="clientes-sugestoes">
                  {(data.clientes_loja || []).map(c => <option key={c.id} value={c.nome}>{c.telefone || ""}</option>)}
                </datalist>
                {(dadosEnvio.telefone || dadosEnvio.endereco) && (
                  <div style={{ marginTop: 8, fontSize: 11, color: "#6b7280", lineHeight: 1.6 }}>
                    <div style={{ fontWeight: 600, color: "#374151" }}>Dados capturados (serão salvos no cadastro):</div>
                    {dadosEnvio.telefone && <div>Tel: {dadosEnvio.telefone}</div>}
                    {dadosEnvio.endereco && <div>{dadosEnvio.endereco}{dadosEnvio.numero ? ", " + dadosEnvio.numero : ""}{dadosEnvio.complemento ? " — " + dadosEnvio.complemento : ""}</div>}
                    {(dadosEnvio.bairro || dadosEnvio.cidade) && <div>{[dadosEnvio.bairro, dadosEnvio.cidade, dadosEnvio.estado].filter(Boolean).join(" · ")}</div>}
                  </div>
                )}
              </div>
              <div>
                <label style={styles.label}>Colar dados do cliente</label>
                <textarea
                  style={{ ...styles.input, minHeight: 64, resize: "vertical", fontSize: 12 }}
                  placeholder="Nome, telefone, endereço"
                  value={textoColado}
                  onChange={e => setTextoColado(e.target.value)}
                />
                <button
                  type="button"
                  disabled={!textoColado.trim()}
                  onClick={preencherDadosCliente}
                  style={{ ...styles.btn("secondary"), marginTop: 6, fontSize: 11, opacity: textoColado.trim() ? 1 : 0.5 }}
                >
                  Preencher
                </button>
              </div>
            </div>
          </div>
          <div style={{ marginBottom: 8 }}>
            <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr 32px", gap: 8, marginBottom: 6 }}>
              {["Produto","Qtd","Valor Unit. (R$)","Custo Unit. (R$)",""].map((h,i) => (
                <div key={i} style={{ fontSize: 9, color: "#374151", letterSpacing: 1, textTransform: "uppercase" }}>{h}</div>
              ))}
            </div>
            {itens.map((item, idx) => (
              <div key={idx} style={{ marginBottom: 8 }}>
                <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr 32px", gap: 8, marginBottom: 4 }}>
                  <select style={{ ...styles.input, color: item.produto ? "#111827" : "#6b7280" }} value={item.produto} onChange={e => {
                    const nome = e.target.value;
                    const prod = data.estoque.find(p => p.produto === nome);
                    if (prod) {
                      setItens(its => its.map((it, j) => j === idx ? { ...it, produto: nome, valor: String(prod.preco), custo: String(prod.custo), cor: "" } : it));
                    } else {
                      updateItem(idx, "produto", nome);
                    }
                  }}>
                    <option value="">-- Selecionar produto --</option>
                    {(data.estoque || []).map(p => (
                      <option key={p.id} value={p.produto}>{p.produto} {p.estoque <= 0 ? "⚠️ Sem estoque" : `(${p.estoque} un)`}</option>
                    ))}
                  </select>
                  <input style={styles.input} type="number" placeholder="1" value={item.qtd} onChange={e => updateItem(idx,"qtd",e.target.value)} disabled={!!item.upsell} />
                  <input style={{...styles.input, opacity: item.upsell ? 0.5 : 1}} type="number" placeholder="0,00" value={item.valor} onChange={e => updateItem(idx,"valor",e.target.value)} disabled={!!item.upsell} title={item.upsell ? "Edite o upsell pela tabela de upsells" : ""} />
                  <input style={{...styles.input, opacity: item.upsell ? 0.5 : 1}} type="number" placeholder="0,00" value={item.custo} onChange={e => updateItem(idx,"custo",e.target.value)} disabled={!!item.upsell} title={item.upsell ? "Edite o upsell pela tabela de upsells" : ""} />
                  {item.upsell ? <div style={{ fontSize: 9, color: C.accent, textAlign: "center" }}>upsell</div> : itens.length > 1 ? <button onClick={() => removeItem(idx)} style={{ background:"none", border:"none", color:C.warn, cursor:"pointer", fontSize:16 }}>✕</button> : <div/>}
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  {(() => {
                    const pc = (data.estoque || []).find(p => p.produto === item.produto);
                    if (!pc || !Array.isArray(pc.cores) || pc.cores.length === 0) return null;
                    return (
                      <select style={{ ...styles.input, width: 200, flexShrink: 0, borderColor: item.cor ? undefined : "#f59e0b" }} value={item.cor || ""} onChange={e => updateItem(idx, "cor", e.target.value)}>
                        <option value="">-- Cor --</option>
                        {pc.cores.map(c => <option key={c.cor} value={c.cor} disabled={Number(c.qtd) <= 0}>{c.cor} ({c.qtd} un)</option>)}
                      </select>
                    );
                  })()}
                  <input style={{...styles.input, fontSize:11, opacity:0.7, flex: 1}} placeholder="🎁 Brinde incluído (ex: Pulseira, Fone)" value={item.brinde} onChange={e => updateItem(idx,"brinde",e.target.value)} />
                </div>
              </div>
            ))}
            <button onClick={addItem} style={{ ...styles.btn("secondary"), fontSize: 11, padding: "6px 12px", marginTop: 4 }}>+ Adicionar produto</button>
          </div>
          {/* ── Seção 3: Pagamento ── */}
          <div style={{ background: "#f0f2f5", borderRadius: 10, padding: 14, marginBottom: 14, border: "1px solid #e5e7eb" }}>
            <div style={{ fontSize: 10, color: "#374151", letterSpacing: 1, marginBottom: 12 }}>💳 PAGAMENTO</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 12, marginBottom: 12 }}>
              <div>
                <label style={styles.label}>Forma de pagamento</label>
                <select style={styles.input} value={bandeira} onChange={e => { setBandeira(e.target.value); setParcelas(1); }}>
                  <option value="Nenhuma">— Selecionar —</option>
                  {BANDEIRAS.map(b => <option key={b} value={b}>{b}</option>)}
                </select>
              </div>
              {bandeira !== "Nenhuma" && !SEM_TAXA.includes(bandeira) && bandeira !== "Débito" && bandeira !== "Pix" && (
                <div>
                  <label style={styles.label}>Parcelas</label>
                  <select style={styles.input} value={parcelas} onChange={e => setParcelas(Number(e.target.value))}>
                    {[...Array(12)].map((_, i) => (
                      <option key={i+1} value={i+1}>{i+1}x {getTaxa(bandeira, i+1) > 0 ? `(${getTaxa(bandeira, i+1)}%)` : ""}</option>
                    ))}
                  </select>
                </div>
              )}
              <div>
                <label style={styles.label}>Entrega</label>
                <select style={styles.input} value={entregaTipo} onChange={e => setEntregaTipo(e.target.value)}>
                  <option value="">Sem entrega</option>
                  <option value="Motoboy">Motoboy</option>
                  <option value="Uber">Uber</option>
                  <option value="Envio">Correios / Envio</option>
                </select>
              </div>
              <div>
                <label style={styles.label}>Valor da entrega (R$)</label>
                <input type="number" step="0.01" min="0" style={styles.input} value={frete} onChange={e => setFrete(e.target.value)} placeholder="0,00" />
              </div>
            </div>
            {/* Desconto */}
            <div style={{ borderTop: "1px solid #e5e7eb", paddingTop: 10, marginTop: 4 }}>
              <div style={{ fontSize: 10, color: "#374151", marginBottom: 8, letterSpacing: 1 }}>🏷️ DESCONTO (OPCIONAL)</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
                <div>
                  <label style={styles.label}>Tipo</label>
                  <select style={styles.input} value={tipoDesconto} onChange={e => { setTipoDesconto(e.target.value); setDesconto(""); setCupomAplicado(null); }}>
                    <option value="percentual">% Percentual</option>
                    <option value="fixo">R$ Valor fixo</option>
                  </select>
                </div>
                <div>
                  <label style={styles.label}>{tipoDesconto === "percentual" ? "Desconto (%)" : "Desconto (R$)"}</label>
                  <input type="number" style={styles.input} value={desconto} onChange={e => { setDesconto(e.target.value); setCupomAplicado(null); }} placeholder={tipoDesconto === "percentual" ? "Ex: 10" : "Ex: 50"} />
                </div>
                <div>
                  <label style={styles.label}>Ou cupom</label>
                  <div style={{ display: "flex", gap: 4 }}>
                    <input style={{ ...styles.input, flex: 1 }} value={cupomCodigo} onChange={e => setCupomCodigo(e.target.value.toUpperCase())} placeholder="CODIGO" />
                    <button onClick={aplicarCupom} style={{ ...styles.btn("primary"), padding: "0 10px", fontSize: 11 }}>OK</button>
                  </div>
                  {cupomAplicado && <div style={{ fontSize: 10, color: C.accent, marginTop: 3 }}>✅ {cupomAplicado.codigo} aplicado!</div>}
                </div>
              </div>
            </div>
            {/* Segundo pagamento */}
            <div style={{ borderTop: "1px solid #e5e7eb", paddingTop: 10 }}>
              <div style={{ fontSize: 10, color: "#374151", marginBottom: 8, letterSpacing: 1 }}>+ SEGUNDO PAGAMENTO (OPCIONAL)</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
                <div>
                  <label style={styles.label}>Forma</label>
                  <select style={styles.input} value={bandeira2} onChange={e => { setBandeira2(e.target.value); setValorPag2(""); }}>
                    <option value="Nenhuma">— Nenhum —</option>
                    {BANDEIRAS.map(b => <option key={b} value={b}>{b}</option>)}
                  </select>
                </div>
                {bandeira2 !== "Nenhuma" && (
                  <div>
                    <label style={styles.label}>Valor (R$)</label>
                    <input type="number" style={styles.input} value={valorPag2} onChange={e => setValorPag2(e.target.value)} placeholder="0,00" />
                  </div>
                )}
                {bandeira2 !== "Nenhuma" && valorPag2 && !SEM_TAXA.includes(bandeira2) && (
                  <div style={{ display: "flex", alignItems: "flex-end", paddingBottom: 8 }}>
                    <div style={{ fontSize: 11, color: C.warn }}>Taxa: R$ {parseFloat((Number(valorPag2) * getTaxa(bandeira2, 1) / 100).toFixed(2)).toFixed(2)}</div>
                  </div>
                )}
              </div>
            </div>
            {/* Resumo */}
            {(() => {
              const totalProdutos = itens.filter(i => i.produto && i.qtd).reduce((s, i) => s + Number(i.valor) * Number(i.qtd), 0);
              const taxaTotal = calcularTaxaReais();
              const freteVal = Number(frete || 0);
              if (totalProdutos === 0) return null;
              return (
                <div style={{ borderTop: "1px solid #e5e7eb", paddingTop: 10, marginTop: 10 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "#374151", marginBottom: 4 }}>
                    <span>Produtos</span><span>{fmt(totalProdutos)}</span>
                  </div>
                  {freteVal > 0 && <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "#374151", marginBottom: 4 }}>
                    <span>Frete</span><span>{fmt(freteVal)}</span>
                  </div>}
                  {taxaTotal > 0 && <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: C.warn, marginBottom: 4 }}>
                    <span>Taxa maquininha</span><span>-{fmt(taxaTotal)}</span>
                  </div>}
                  {calcularDesconto() > 0 && <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: C.accent, marginBottom: 4 }}>
                    <span>Desconto</span><span>-{fmt(calcularDesconto())}</span>
                  </div>}
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14, fontWeight: 700, color: C.accent, borderTop: "1px solid #e5e7eb", paddingTop: 6, marginTop: 4 }}>
                    <span>Total</span><span>{fmt(totalProdutos + freteVal - calcularDesconto())}</span>
                  </div>
                </div>
              );
            })()}
          </div>

          {/* Painel configuração de taxas */}
          <div style={{ marginTop: 16, borderTop: "1px solid #e5e7eb", paddingTop: 12 }}>
            <button onClick={() => { setShowConfigTaxas(s => !s); if (!showConfigTaxas) carregarTaxas(); }} style={{ background: "none", border: "none", color: "#374151", cursor: "pointer", fontSize: 11 }}>
              ⚙️ {showConfigTaxas ? "Fechar" : "Configurar"} taxas de maquininha
            </button>
            {showConfigTaxas && (
              <div style={{ marginTop: 12 }}>
                <div style={{ overflowX: "auto" }}>
                  <table style={{ borderCollapse: "collapse", fontSize: 11, width: "100%" }}>
                    <thead>
                      <tr>
                        <th style={{ ...styles.th, textAlign: "left" }}>Parcelas</th>
                        {BANDEIRAS.map(b => <th key={b} style={styles.th}>{b}</th>)}
                      </tr>
                    </thead>
                    <tbody>
                      {[...Array(12)].map((_, i) => (
                        <tr key={i+1}>
                          <td style={{ ...styles.td, color: "#374151", fontWeight: 600 }}>{i+1}x</td>
                          {BANDEIRAS.map(b => (
                            <td key={b} style={styles.td}>
                              {(b === "Débito" || b === "Pix") && i > 0 ? (
                                <span style={{ color: "#374151", fontSize: 10 }}>—</span>
                              ) : (
                                <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
                                  <input
                                    type="number"
                                    step="0.01"
                                    style={{ ...styles.input, width: 60, padding: "4px 6px", fontSize: 11 }}
                                    value={taxasForm[b]?.[i+1] || ""}
                                    onChange={e => setTaxasForm(f => ({ ...f, [b]: { ...f[b], [i+1]: e.target.value } }))}
                                    placeholder="0"
                                  />
                                  <span style={{ fontSize: 10, color: "#374151" }}>%</span>
                                </div>
                              )}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <button onClick={salvarTaxas} disabled={salvandoTaxas} style={{ ...styles.btn("primary"), fontSize: 11, marginTop: 12 }}>
                  {salvandoTaxas ? "Salvando..." : "💾 Salvar Taxas"}
                </button>
              </div>
            )}
          </div>

          <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
            <button style={{
                ...styles.btn("primary"),
                opacity: salvandoVenda ? 0.6 : 1,
                fontSize: 15,
                fontWeight: 700,
                padding: "14px 28px",
                borderRadius: 12,
                boxShadow: salvandoVenda ? "none" : `0 4px 20px ${C.accent}40`
              }} onClick={handleAdd} disabled={salvandoVenda}>
                {salvandoVenda ? "⏳ Salvando..." : "✅ Salvar Venda"}
              </button>
            <button style={styles.btn("secondary")} onClick={() => { setShowForm(false); setItens([{...emptyItem}]); setBandeira("Nenhuma"); setParcelas(1); setBandeira2("Nenhuma"); setValorPag2(""); setFrete(""); setDesconto(""); setCupomAplicado(null); setCupomCodigo(""); }}>Cancelar</button>
          </div>
        </div>
      )}

      {/* ── Modal de Upsell ── */}
      {upsellModal.show && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(15,23,42,0.5)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ background: C.surface, borderRadius: 20, padding: 32, width: "90%", maxWidth: 520, border: `2px solid ${C.accent}40`, boxShadow: `0 20px 60px rgba(0,0,0,0.5)` }}>
            <div style={{ fontWeight: 800, fontSize: 18, color: C.accent, marginBottom: 8, display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: 24 }}>🎯</span> Upsell — Produto Adicional
            </div>
            <div style={{ fontSize: 12, color: "#374151", marginBottom: 24 }}>
              Venda #{upsellModal.vendaId} registrada ✅ — houve algum produto adicional vendido?
            </div>

            {upsellItens.map((item, idx) => (
              <div key={idx} style={{ background: C.bgElevated, borderRadius: 12, padding: 16, marginBottom: 12, border: "1px solid #e5e7eb" }}>
                <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr auto", gap: 8, alignItems: "end" }}>
                  <div>
                    <label style={{ ...styles.label, fontSize: 11 }}>Produto do estoque</label>
                    <select
                      style={{ ...styles.input, fontSize: 12 }}
                      value={item.produto}
                      onChange={e => handleUpsellProduto(idx, e.target.value)}
                    >
                      <option value="">— Selecionar produto —</option>
                      {(data.estoque || []).map(p => (
                        <option key={p.id} value={p.produto}>{p.produto}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label style={{ ...styles.label, fontSize: 11 }}>Qtd</label>
                    <input
                      type="number"
                      style={{ ...styles.input, fontSize: 12 }}
                      value={item.quantidade}
                      min={1}
                      onChange={e => setUpsellItens(prev => prev.map((it, i) => i !== idx ? it : { ...it, quantidade: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label style={{ ...styles.label, fontSize: 11 }}>Valor (R$)</label>
                    <input
                      type="number"
                      style={{ ...styles.input, fontSize: 12 }}
                      value={item.valor}
                      onChange={e => setUpsellItens(prev => prev.map((it, i) => i !== idx ? it : { ...it, valor: e.target.value }))}
                    />
                  </div>
                  {upsellItens.length > 1 && (
                    <button
                      onClick={() => setUpsellItens(prev => prev.filter((_, i) => i !== idx))}
                      style={{ background: "none", border: "none", color: C.warn, cursor: "pointer", fontSize: 18, paddingBottom: 6 }}
                    >✕</button>
                  )}
                </div>
              </div>
            ))}

            <button
              onClick={() => setUpsellItens(prev => [...prev, { produto: "", produto_id: null, valor: 0, custo: 0, quantidade: 1 }])}
              style={{ ...styles.btn("secondary"), fontSize: 11, padding: "6px 14px", marginBottom: 20 }}
            >+ Adicionar produto</button>

            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button
                onClick={() => { setUpsellModal({ show: false, vendaId: null }); setUpsellItens([{ produto: "", produto_id: null, valor: 0, custo: 0, quantidade: 1 }]); }}
                style={styles.btn("secondary")}
              >Não houve upsell</button>
              <button
                onClick={handleSalvarUpsell}
                disabled={salvandoUpsell}
                style={{ ...styles.btn("primary"), opacity: salvandoUpsell ? 0.6 : 1 }}
              >{salvandoUpsell ? "⏳ Salvando..." : "✅ Salvar Upsell"}</button>
            </div>
          </div>
        </div>
      )}

      {/* Card Custos do Dia */}
      <div style={{ ...styles.card, marginBottom: 20, borderColor: C.gold + "30" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer" }} onClick={() => setShowCustosDia(s => !s)}>
          <div style={{ fontSize: 11, color: C.gold, letterSpacing: 2, textTransform: "uppercase", fontWeight: 700 }}>💰 Lançar Custos do Dia</div>
          <span style={{ color: "#374151", fontSize: 12 }}>{showCustosDia ? "▲" : "▼"}</span>
        </div>
        {showCustosDia && (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginTop: 14 }}>
            {[
              { key: "motoboy", label: "🛵 Motoboy (R$)", placeholder: "0,00" },
              { key: "uber", label: "🚗 Uber (R$)", placeholder: "0,00" },
              { key: "trafego", label: "📣 Tráfego Pago (R$)", placeholder: "+ imposto" },
            ].map(({ key, label, placeholder }) => (
              <div key={key}>
                <label style={styles.label}>{label}</label>
                <div style={{ display: "flex", gap: 6 }}>
                  <input style={{ ...styles.input, flex: 1 }} type="number" placeholder={placeholder} value={custosDia[key]} onChange={e => setCustosDia(c => ({ ...c, [key]: e.target.value }))} />
                  <button style={{ ...styles.btn("primary"), padding: "0 12px", fontSize: 16 }} onClick={() => salvarCustoDia(key, custosDia[key])}>+</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* KPIs MODERNOS */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 20, marginBottom: 32 }}>
        {/* Faturamento Total */}
        <div className="tfx-fade-in-delay-0 tfx-card-hover" style={{ 
          background: C.bgElevated,
          border: `2px solid ${C.accent}25`,
          borderRadius: 20,
          padding: 28,
          position: "relative",
          overflow: "hidden",
          boxShadow: `0 10px 40px ${C.accent}30`,
          transition: "all 0.3s"
        }}>
          <div style={{ position: "absolute", top: -30, right: -30, width: 120, height: 120, background: `radial-gradient(circle, ${C.accent}15, transparent)`, borderRadius: "50%" }} />
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
            <div style={{ width: 52, height: 52, borderRadius: 14, background: `${C.accent}20`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24 }}>💰</div>
            <div style={{ fontSize: 10, color: "#374151", letterSpacing: 2, textTransform: "uppercase", fontWeight: 600 }}>Faturamento Total</div>
          </div>
          <div style={{ fontFamily: "'Inter', sans-serif", fontWeight: 800, fontSize: 32, color: C.accent, letterSpacing: -1.2, marginBottom: 8 }}>
            {privado ? "••••••" : fmt(totalFaturamento)}
          </div>
          <div style={{ fontSize: 11, color: "#374151", fontWeight: 500 }}>{vendasFiltradas.length} vendas no período</div>
        </div>

        {/* Lucro Líquido */}
        <div className="tfx-fade-in-delay-1 tfx-card-hover" style={{ 
          background: C.bgElevated,
          border: `2px solid #22c55e25`,
          borderRadius: 20,
          padding: 28,
          position: "relative",
          overflow: "hidden",
          boxShadow: `0 10px 40px #22c55e30`,
          transition: "all 0.3s"
        }}>
          <div style={{ position: "absolute", top: -30, right: -30, width: 120, height: 120, background: `radial-gradient(circle, #22c55e15, transparent)`, borderRadius: "50%" }} />
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
            <div style={{ width: 52, height: 52, borderRadius: 14, background: `#22c55e20`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24 }}>📈</div>
            <div style={{ fontSize: 10, color: "#374151", letterSpacing: 2, textTransform: "uppercase", fontWeight: 600 }}>Lucro Bruto</div>
          </div>
          <div style={{ fontFamily: "'Inter', sans-serif", fontWeight: 800, fontSize: 32, color: "#15803d", letterSpacing: -1.2, marginBottom: 8 }}>
            {privado ? "••••••" : fmt(totalLucro)}
          </div>
          <div style={{ fontSize: 11, color: "#374151", fontWeight: 500 }}>{pct(totalLucro / totalFaturamento)} de margem média</div>
        </div>

        {/* Ticket Médio */}
        <div className="tfx-fade-in-delay-2 tfx-card-hover" style={{ 
          background: C.bgElevated,
          border: `2px solid #fb923c25`,
          borderRadius: 20,
          padding: 28,
          position: "relative",
          overflow: "hidden",
          boxShadow: `0 10px 40px #fb923c30`,
          transition: "all 0.3s"
        }}>
          <div style={{ position: "absolute", top: -30, right: -30, width: 120, height: 120, background: `radial-gradient(circle, #fb923c15, transparent)`, borderRadius: "50%" }} />
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
            <div style={{ width: 52, height: 52, borderRadius: 14, background: `#fb923c20`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24 }}>🎯</div>
            <div style={{ fontSize: 10, color: "#374151", letterSpacing: 2, textTransform: "uppercase", fontWeight: 600 }}>Ticket Médio</div>
          </div>
          <div style={{ fontFamily: "'Inter', sans-serif", fontWeight: 800, fontSize: 32, color: "#b91c1c", letterSpacing: -1.2, marginBottom: 8 }}>
            {privado ? "••••••" : fmt(vendasFiltradas.length > 0 ? totalFaturamento / vendasFiltradas.length : 0)}
          </div>
          <div style={{ fontSize: 11, color: "#374151", fontWeight: 500 }}>Por venda no período</div>
        </div>

        {/* Online vs Física */}
        <div className="tfx-fade-in-delay-3 tfx-card-hover" style={{ 
          background: C.bgElevated,
          border: `2px solid ${C.info}25`,
          borderRadius: 20,
          padding: 28,
          position: "relative",
          overflow: "hidden",
          boxShadow: `0 10px 40px ${C.info}30`,
          transition: "all 0.3s"
        }}>
          <div style={{ position: "absolute", top: -30, right: -30, width: 120, height: 120, background: `radial-gradient(circle, ${C.info}15, transparent)`, borderRadius: "50%" }} />
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
            <div style={{ width: 52, height: 52, borderRadius: 14, background: `${C.info}20`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24 }}>📊</div>
            <div style={{ fontSize: 10, color: "#374151", letterSpacing: 2, textTransform: "uppercase", fontWeight: 600 }}>Físico + Online</div>
          </div>
          <div style={{ display: "flex", gap: 16, marginTop: 4 }}>
            <div style={{ flex: 1 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                <div style={{ width: 10, height: 10, borderRadius: 2, background: C.accent }} />
                <span style={{ fontSize: 9, color: "#374151", letterSpacing: 1 }}>ONLINE</span>
              </div>
              <div style={{ fontSize: 20, fontWeight: 800, color: C.accent, letterSpacing: -0.8 }}>{privado ? "••••••" : fmt(onlineVendas)}</div>
              <div style={{ fontSize: 9, color: "#374151", marginTop: 2 }}>{onlineQtd} vendas</div>
            </div>
            <div style={{ width: 1, background: `${C.border}50` }} />
            <div style={{ flex: 1 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                <div style={{ width: 10, height: 10, borderRadius: 2, background: "#374151" }} />
                <span style={{ fontSize: 9, color: "#374151", letterSpacing: 1 }}>FÍSICA</span>
              </div>
              <div style={{ fontSize: 20, fontWeight: 800, color: "#374151", letterSpacing: -0.8 }}>{privado ? "••••••" : fmt(fisicaVendas)}</div>
              <div style={{ fontSize: 9, color: "#374151", marginTop: 2 }}>{fisicaQtd} vendas</div>
            </div>
          </div>
        </div>
      </div>

      {/* ── KPIs UPSELL ── */}
      {(() => {
        const upsells = data.vendas_upsell || [];
        const idsComUpsell = new Set(upsells.map(u => u.venda_id));
        const vendasComUpsell = vendasFiltradas.filter(v => idsComUpsell.has(v.id));
        const vendasSemUpsell = vendasFiltradas.filter(v => !idsComUpsell.has(v.id));
        const totalVendas = vendasFiltradas.length;
        const qtdComUpsell = vendasComUpsell.length;
        const qtdSemUpsell = vendasSemUpsell.length;
        const pctCom = totalVendas > 0 ? (qtdComUpsell / totalVendas * 100) : 0;
        const pctSem = totalVendas > 0 ? (qtdSemUpsell / totalVendas * 100) : 0;
        const receitaUpsell = upsells.reduce((s, u) => s + (Number(u.valor) * Number(u.quantidade || 1)), 0);
        const valorMedioUpsell = upsells.length > 0 ? receitaUpsell / upsells.length : 0;
        // eslint-disable-next-line no-unused-vars
        const ticketCom = qtdComUpsell > 0 ? vendasComUpsell.reduce((s, v) => s + (v.valor * v.qtd), 0) / qtdComUpsell : 0;
        // eslint-disable-next-line no-unused-vars
        const ticketSem = qtdSemUpsell > 0 ? vendasSemUpsell.reduce((s, v) => s + (v.valor * v.qtd), 0) / qtdSemUpsell : 0;
        const topProdutos = Object.entries(
          upsells.reduce((acc, u) => { const nome = (u.produto_nome || "").replace(" [upsell]", "").trim(); acc[nome] = (acc[nome] || 0) + 1; return acc; }, {})
        ).sort((a, b) => b[1] - a[1]).slice(0, 3);

        if (upsells.length === 0 && vendasFiltradas.length === 0) return null;

        return (
          <div style={{ marginBottom: 32 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
              <span style={{ fontSize: 18 }}>🎯</span>
              <span style={{ fontSize: 13, fontWeight: 700, color: "#111827", letterSpacing: -0.3 }}>Upsell</span>
              <span style={{ fontSize: 11, color: "#374151" }}>— Produtos adicionais vendidos</span>
            </div>

            {/* Linha 1: 4 cards */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16, marginBottom: 16 }}>

              {/* Card 1: Valor médio do upsell */}
              <div style={{ ...styles.kpiCard("#15803d"), padding: 20 }}>
                <div style={{ fontSize: 9, color: "#374151", letterSpacing: 2, textTransform: "uppercase", marginBottom: 8 }}>Valor Médio Upsell</div>
                <div style={{ fontSize: 26, fontWeight: 800, color: "#15803d" }}>{privado ? "••••••" : fmt(valorMedioUpsell)}</div>
                <div style={{ fontSize: 11, color: "#374151", marginTop: 4 }}>Total: {privado ? "••••••" : fmt(receitaUpsell)}</div>
                <div style={{ fontSize: 11, color: "#374151" }}>{upsells.length} upsell{upsells.length !== 1 ? "s" : ""} registrado{upsells.length !== 1 ? "s" : ""}</div>
              </div>

              {/* Card 2: % com e sem upsell */}
              <div style={{ ...styles.kpiCard(C.accent), padding: 20 }}>
                <div style={{ fontSize: 9, color: "#374151", letterSpacing: 2, textTransform: "uppercase", marginBottom: 12 }}>Conversão Upsell</div>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                  <span style={{ fontSize: 11, color: C.accent }}>✅ Com upsell</span>
                  <span style={{ fontSize: 14, fontWeight: 800, color: C.accent }}>{pctCom.toFixed(1)}%</span>
                </div>
                <div style={{ height: 6, borderRadius: 3, background: C.border, marginBottom: 10 }}>
                  <div style={{ height: 6, borderRadius: 3, background: C.accent, width: `${Math.min(pctCom, 100)}%` }} />
                </div>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span style={{ fontSize: 11, color: "#374151" }}>❌ Sem upsell</span>
                  <span style={{ fontSize: 14, fontWeight: 800, color: "#374151" }}>{pctSem.toFixed(1)}%</span>
                </div>
                <div style={{ height: 6, borderRadius: 3, background: C.border, marginTop: 6 }}>
                  <div style={{ height: 6, borderRadius: 3, background: C.muted, width: `${Math.min(pctSem, 100)}%` }} />
                </div>
                <div style={{ fontSize: 10, color: "#374151", marginTop: 8 }}>{qtdComUpsell} de {totalVendas} vendas</div>
              </div>

              {/* Card 3: Top produto */}
              <div style={{ ...styles.kpiCard(C.gold), padding: 20 }}>
                <div style={{ fontSize: 9, color: "#374151", letterSpacing: 2, textTransform: "uppercase", marginBottom: 10 }}>Top Upsells</div>
                {topProdutos.length > 0 ? topProdutos.map(([nome, qtd], i) => (
                  <div key={nome} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                    <span style={{ fontSize: 12, color: "#111827", fontWeight: i === 0 ? 700 : 400 }}>
                      {i === 0 ? "🥇" : i === 1 ? "🥈" : "🥉"} {nome}
                    </span>
                    <span style={{ fontSize: 12, color: C.gold, fontWeight: 700 }}>{qtd}x</span>
                  </div>
                )) : <div style={{ fontSize: 11, color: "#374151" }}>Sem dados</div>}
              </div>

              {/* Card 4: Ticket médio com vs sem upsell (baseado nos itens) */}
              {(() => {
                const isUpsellItem = (nome) => (nome || "").toLowerCase().includes("upsell");
                const calcTicket = (vendas) => {
                  if (vendas.length === 0) return 0;
                  const total = vendas.reduce((s, v) => {
                    const itensV = v.itens || [];
                    const valorBase = itensV.length > 0
                      ? itensV.filter(i => !isUpsellItem(i.produto)).reduce((si, i) => si + (Number(i.valor) || 0) * (Number(i.qtd) || 1), 0)
                      : (v.valor * v.qtd);
                    return s + valorBase;
                  }, 0);
                  return total / vendas.length;
                };
                const calcTicketTotal = (vendas) => {
                  if (vendas.length === 0) return 0;
                  return vendas.reduce((s, v) => s + (v.valor * v.qtd), 0) / vendas.length;
                };
                const ticketAtual = calcTicketTotal(vendasFiltradas);
                const ticketBaseReal = calcTicket(vendasFiltradas);
                const adicaoUpsell = ticketAtual - ticketBaseReal;
                const pctAdicao = ticketBaseReal > 0 ? (adicaoUpsell / ticketBaseReal * 100) : 0;

                return (
                  <div style={{ ...styles.kpiCard("#6b7280"), padding: 20 }}>
                    <div style={{ fontSize: 9, color: "#374151", letterSpacing: 2, textTransform: "uppercase", marginBottom: 12 }}>Ticket Médio</div>
                    <div style={{ marginBottom: 10 }}>
                      <div style={{ fontSize: 10, color: "#15803d", marginBottom: 4 }}>🎯 Com upsell (atual)</div>
                      <div style={{ fontSize: 22, fontWeight: 800, color: "#15803d" }}>{privado ? "••••••" : fmt(ticketAtual)}</div>
                    </div>
                    <div style={{ borderTop: "1px solid #e5e7eb", paddingTop: 10, marginBottom: 8 }}>
                      <div style={{ fontSize: 10, color: "#374151", marginBottom: 4 }}>📦 Sem upsell (base)</div>
                      <div style={{ fontSize: 18, fontWeight: 700, color: "#374151" }}>{privado ? "••••••" : fmt(ticketBaseReal)}</div>
                    </div>
                    {adicaoUpsell > 0 && (
                      <div style={{ background: "#22c55e15", borderRadius: 8, padding: "6px 10px" }}>
                        <span style={{ fontSize: 11, color: "#15803d", fontWeight: 700 }}>
                          +{fmt(adicaoUpsell)} por venda (+{pctAdicao.toFixed(1)}%)
                        </span>
                      </div>
                    )}
                  </div>
                );
              })()}

            </div>
          </div>
        );
      })()}

      <div style={{ 
        background: C.bgElevated,
        border: `2px solid ${C.border}30`,
        borderRadius: 20,
        padding: 28,
        boxShadow: "0 4px 30px rgba(0,0,0,0.3)"
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ 
              width: 44, 
              height: 44, 
              borderRadius: 12, 
              background: `linear-gradient(135deg, ${C.accent}, ${C.accentMid})`, 
              display: "flex", 
              alignItems: "center", 
              justifyContent: "center",
              fontSize: 20,
              boxShadow: `0 4px 16px ${C.accent}40`
            }}>📊</div>
            <div>
              <div style={{ fontWeight: 800, fontSize: 18, color: "#111827", letterSpacing: -0.5 }}>Histórico de Vendas</div>
              <div style={{ fontSize: 11, color: "#374151", marginTop: 2 }}>{vendasOrdenadas.length} vendas encontradas</div>
            </div>
            {selecionados.length > 0 && (
              <button onClick={deletarSelecionados} style={{ ...styles.btn("secondary"), fontSize: 10, padding: "4px 10px", color: C.warn, borderColor: C.warn }}>
                🗑️ Deletar {selecionados.length}
              </button>
            )}
          </div>
          <button onClick={() => setSortDir(d => d === "desc" ? "asc" : "desc")} style={{ ...styles.btn("secondary"), display: "flex", alignItems: "center", gap: 6, fontSize: 10 }}>
            Data {sortDir === "desc" ? "↓ Mais recente" : "↑ Mais antiga"}
          </button>
        </div>
        <table style={{ ...styles.table, borderCollapse: "separate", borderSpacing: 0 }}>
          <thead>
            <tr style={{ 
              background: `linear-gradient(90deg, ${C.surface}, ${C.bgElevated})`,
              borderBottom: `2px solid ${C.accent}40`
            }}>
              <th style={{ ...styles.th, width: 32, padding: "10px 12px" }}><input type="checkbox" onChange={toggleAll} checked={selecionados.length === vendasOrdenadas.length && vendasOrdenadas.length > 0} /></th>
              {["Data", "Produto", "Cliente", "Canal", "Vendedor", "Qtd", "Receita", "Lucro", "Garantia", ""].map((h) => (
                <th key={h} style={{ ...styles.th, padding: "10px 14px", fontSize: 9, letterSpacing: 1.5, borderBottom: `2px solid ${C.accent}30` }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {vendasOrdenadas.slice(0, limiteVendas).map((v, idx) => {
              const lucro = (v.valor - v.custo) * v.qtd - (v.taxa_maquininha || 0);
              const receita = v.valor * v.qtd;
              const margem = receita > 0 ? (lucro / receita * 100).toFixed(0) : 0;
              return (
                <tr key={v.id} draggable
                  onDragStart={() => setDraggedVenda(v)}
                  onDragEnter={() => draggedVenda && draggedVenda.id !== v.id && setDragOverVenda(v)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={async (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (!draggedVenda || draggedVenda.id === v.id) {
                      setDraggedVenda(null);
                      setDragOverVenda(null);
                      return;
                    }
                    const targetIdx = vendasOrdenadas.findIndex(x => x.id === v.id);
                    let novaOrdem;
                    if (targetIdx === 0) {
                      const menorOrdem = Math.min(...vendasOrdenadas.map(x => x.ordem || 999999));
                      novaOrdem = menorOrdem - 1000;
                    } else {
                      const anterior = vendasOrdenadas[targetIdx - 1];
                      const atual = vendasOrdenadas[targetIdx];
                      const ordemAnt = anterior?.ordem || 0;
                      const ordemAtual = atual?.ordem || 999999;
                      novaOrdem = (ordemAnt + ordemAtual) / 2;
                    }
                    setData(d => ({
                      ...d,
                      vendas: d.vendas.map(x =>
                        x.id === draggedVenda.id ? { ...x, ordem: novaOrdem } : x
                      )
                    }));
                    setDraggedVenda(null);
                    setDragOverVenda(null);
                    await sb.update("vendas", draggedVenda.id, { ordem: novaOrdem });
                  }}
                  onDragEnd={() => { setDraggedVenda(null); setDragOverVenda(null); }}
                  style={{ background: selecionados.includes(v.id) ? `${C.accent}15` : idx % 2 === 0 ? "transparent" : `${C.surface}50`, transition: "all 0.3s", borderRadius: 8, opacity: draggedVenda?.id === v.id ? 0.3 : 1, cursor: draggedVenda?.id === v.id ? 'grabbing' : 'grab', borderTop: dragOverVenda?.id === v.id ? `3px solid ${C.accent}` : 'none', transform: draggedVenda?.id === v.id ? 'scale(0.98)' : 'scale(1)' }}
                  onMouseEnter={e => {
                    if (!draggedVenda) {
                      e.currentTarget.style.background = `${C.accent}12`;
                      e.currentTarget.style.transform = "translateY(-2px)";
                      e.currentTarget.style.boxShadow = `0 4px 12px ${C.accent}20`;
                    }
                  }}
                  onMouseLeave={e => {
                    if (!draggedVenda) {
                      e.currentTarget.style.background = selecionados.includes(v.id) ? `${C.accent}15` : idx % 2 === 0 ? "transparent" : `${C.surface}50`;
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow = "none";
                    }
                  }}>
                  <td style={{ ...styles.td, padding: "10px 12px", borderBottom: "1px solid #e5e7eb" }}>
                    <input type="checkbox" checked={selecionados.includes(v.id)} onChange={() => toggleSel(v.id)} />
                  </td>
                  <td style={{ ...styles.td, padding: "10px 14px", borderBottom: "1px solid #e5e7eb" }}>
                    <div style={{ fontSize: 11, color: "#374151" }}>{v.data}</div>
                  </td>
                  <td style={{ ...styles.td, padding: "10px 14px", borderBottom: "1px solid #e5e7eb", maxWidth: 200 }}>
                    <div style={{ fontWeight: 600, fontSize: 12, color: "#111827", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{v.produto}</div>
                  </td>
                  <td style={{ ...styles.td, padding: "10px 14px", borderBottom: "1px solid #e5e7eb" }}>
                    {v.cliente ? <span style={{ fontSize: 11, color: "#111827", background: C.bgElevated, padding: "2px 8px", borderRadius: 4, border: "1px solid #e5e7eb" }}>{v.cliente}</span> : <span style={{ color: "#374151", fontSize: 10 }}>—</span>}
                  </td>
                  <td style={{ ...styles.td, padding: "10px 14px", borderBottom: "1px solid #e5e7eb" }}>
                    <span style={{ ...styles.badge(v.canal === "Online" ? C.accent : C.gold), fontSize: 9, padding: "2px 8px" }}>{v.canal}</span>
                  </td>
                  <td style={{ ...styles.td, padding: "10px 14px", borderBottom: "1px solid #e5e7eb" }}>
                    {v.vendedor ? <span style={{ fontSize: 11, color: "#111827", background: C.bgElevated, padding: "2px 8px", borderRadius: 4, border: "1px solid #e5e7eb" }}>{v.vendedor}</span> : <span style={{ color: "#374151", fontSize: 10 }}>—</span>}
                  </td>
                  <td style={{ ...styles.td, padding: "10px 14px", borderBottom: "1px solid #e5e7eb", textAlign: "center" }}>
                    <span style={{ fontSize: 12, color: "#374151" }}>{privado ? "••" : v.qtd}</span>
                  </td>
                  <td style={{ ...styles.td, padding: "10px 14px", borderBottom: "1px solid #e5e7eb" }}>
                    <div style={{ fontWeight: 700, fontSize: 12, color: C.accent }}>{privado ? "••••" : fmt(receita)}</div>
                  </td>
                  <td style={{ ...styles.td, padding: "10px 14px", borderBottom: "1px solid #e5e7eb" }}>
                    <div style={{ fontWeight: 700, fontSize: 12, color: lucro >= 0 ? C.gold : C.warn }}>{privado ? "••••" : fmt(lucro)}</div>
                    {!privado && <div style={{ fontSize: 9, color: "#374151" }}>{margem}% margem</div>}
                  </td>
                  <td style={{ ...styles.td, padding: "10px 14px", borderBottom: "1px solid #e5e7eb" }}>
                    {v.garantia_meses > 0 ? (() => {
                      const hoje = new Date();
                      const fim = new Date(String(v.garantia_fim || "").slice(0, 10) + "T23:59:59");
                      const ativa = hoje <= fim;
                      const dataFormatada = fmtDataBR(v.garantia_fim);
                      return (
                        <div style={{ display: "flex", alignItems: "center", gap: 4, flexDirection: "column" }}>
                          <span style={{ fontSize: 9, background: ativa ? "#22c55e20" : C.muted+"20", color: ativa ? "#15803d" : C.muted, padding: "3px 10px", borderRadius: 6, fontWeight: 600, whiteSpace: "nowrap" }}>
                            {ativa ? "✓" : "✕"} {v.garantia_meses} {v.garantia_meses === 1 ? "mês" : "meses"}
                          </span>
                          <span style={{ fontSize: 9, color: "#374151", whiteSpace: "nowrap" }}>até {dataFormatada}</span>
                        </div>
                      );
                    })() : <span style={{ fontSize: 10, color: "#374151" }}>—</span>}
                  </td>
                  <td style={{ ...styles.td, padding: "8px 10px", borderBottom: "1px solid #e5e7eb" }}>
                    <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                      <button onClick={() => abrirModalEtiqueta(v)} title="Etiqueta" style={{ background: "none", border: "none", color: "#6b7280", cursor: "pointer", fontSize: 14, padding: 2 }}>🏷️</button>
                      <button onClick={() => { setEditando(v); setEditandoOriginal(JSON.parse(JSON.stringify(v))); }} title="Editar" style={{ background: "none", border: "none", color: C.accent, cursor: "pointer", fontSize: 14, padding: 2 }}>✎</button>
                      <button onClick={() => gerarReciboPDF(v, configLoja)} title="Gerar Recibo PDF" style={{ background: "none", border: "none", color: "#10b981", cursor: "pointer", fontSize: 14, padding: 2 }}>📄</button>
                      <button onClick={() => handleDelete(v.id)} title="Deletar" style={{ background: "none", border: "none", color: C.warn, cursor: "pointer", fontSize: 14, padding: 2 }}>✕</button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {vendasOrdenadas.length > limiteVendas && (
          <div style={{ textAlign: "center", padding: "16px 12px", display: "flex", gap: 10, justifyContent: "center", alignItems: "center", flexWrap: "wrap" }}>
            <span style={{ fontSize: 11, color: "#6b7280" }}>Mostrando {limiteVendas} de {vendasOrdenadas.length} vendas</span>
            <button onClick={() => setLimiteVendas(n => n + 30)} style={{ ...styles.btn("secondary"), fontSize: 11 }}>Mostrar mais 30</button>
            <button onClick={() => setLimiteVendas(vendasOrdenadas.length)} style={{ ...styles.btn("secondary"), fontSize: 11 }}>Mostrar todas</button>
          </div>
        )}
      </div>
      <>
      {/* Modal Manda Bem */}
      {modalMandaBem && (
        <div style={{ position: "fixed", inset: 0, background: "#00000080", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={() => setModalMandaBem(null)}>
          <div style={{ background: "#f0f2f5", borderRadius: 16, padding: 28, width: 500, maxHeight: "80vh", overflowY: "auto" }} onClick={e => e.stopPropagation()}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 28 }}>
              <div style={{ fontSize: 18, fontWeight: 800, color: "#111827", letterSpacing: -0.5 }}>📦 Gerar Etiqueta Manda Bem</div>
              <button onClick={() => setModalMandaBem(null)} style={{ background: "none", border: "none", color: "#374151", cursor: "pointer", fontSize: 18 }}>✕</button>
            </div>
            <div style={{ marginBottom: 16, padding: 12, background: C.bgElevated, borderRadius: 8, border: "1px solid #e5e7eb" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <div style={{ fontSize: 11, color: "#374151", marginBottom: 4 }}>Cliente</div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: "#111827" }}>{modalMandaBem.cliente || 'Sem cliente'}</div>
                  <div style={{ fontSize: 11, color: "#374151", marginTop: 4 }}>Produto: {modalMandaBem.produto}</div>
                </div>
                <button onClick={() => setModalCadastroCliente(true)} style={{ background: C.accent, color: C.bg, border: "none", padding: "6px 12px", borderRadius: 6, fontSize: 11, cursor: "pointer", fontWeight: 600 }}>➕ Cadastrar</button>
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
              <div><label style={styles.label}>Peso (kg)</label><input style={styles.input} type="number" step="0.1" value={formMB.peso} onChange={e => setFormMB({...formMB, peso: e.target.value})} /></div>
              <div><label style={styles.label}>Tipo Envio</label><select style={styles.input} value={formMB.forma_envio} onChange={e => setFormMB({...formMB, forma_envio: e.target.value})}><option value="PAC">PAC</option><option value="SEDEX">SEDEX</option><option value="PACMINI">PAC MINI</option></select></div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 16 }}>
              <div><label style={styles.label}>Altura (cm)</label><input style={styles.input} type="number" value={formMB.altura} onChange={e => setFormMB({...formMB, altura: e.target.value})} /></div>
              <div><label style={styles.label}>Largura (cm)</label><input style={styles.input} type="number" value={formMB.largura} onChange={e => setFormMB({...formMB, largura: e.target.value})} /></div>
              <div><label style={styles.label}>Comprimento (cm)</label><input style={styles.input} type="number" value={formMB.comprimento} onChange={e => setFormMB({...formMB, comprimento: e.target.value})} /></div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 28 }}>
              <div><label style={styles.label}>Valor da Nota (R$)</label><input style={styles.input} type="number" step="0.01" placeholder="Opcional" value={formMB.valor_nota} onChange={e => setFormMB({...formMB, valor_nota: e.target.value})} /></div>
              <div><label style={styles.label}>Nº da Nota</label><input style={styles.input} type="text" placeholder="Opcional" value={formMB.numero_nota} onChange={e => setFormMB({...formMB, numero_nota: e.target.value})} /></div>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={() => gerarEtiquetaMandaBem(modalMandaBem)} disabled={gerandoEtiqueta} style={{ ...styles.btn("primary"), flex: 1, opacity: gerandoEtiqueta ? 0.6 : 1 }}>{gerandoEtiqueta ? "Gerando..." : "🚀 Gerar Etiqueta"}</button>
              <button onClick={() => setModalMandaBem(null)} style={styles.btn("secondary")}>Cancelar</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Cadastro Rápido Cliente */}
      {modalCadastroCliente && (
        <div style={{ position: "fixed", inset: 0, background: "#00000090", zIndex: 1001, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={() => setModalCadastroCliente(false)}>
          <div style={{ background: "#f0f2f5", borderRadius: 16, padding: 28, width: 500, maxHeight: "80vh", overflowY: "auto" }} onClick={e => e.stopPropagation()}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 28 }}>
              <div style={{ fontSize: 18, fontWeight: 800, color: "#111827", letterSpacing: -0.5 }}>➕ Cadastrar Cliente</div>
              <button onClick={() => setModalCadastroCliente(false)} style={{ background: "none", border: "none", color: "#374151", cursor: "pointer", fontSize: 18 }}>✕</button>
            </div>
            <div style={{ display: "grid", gap: 16 }}>
              <div><label style={styles.label}>Nome *</label><input style={styles.input} value={formNovoCliente.nome} onChange={e => setFormNovoCliente({...formNovoCliente, nome: e.target.value})} /></div>
              <div><label style={styles.label}>Telefone</label><input style={styles.input} value={formNovoCliente.telefone} onChange={e => setFormNovoCliente({...formNovoCliente, telefone: e.target.value})} placeholder="(92) 99999-9999" /></div>
              <div><label style={styles.label}>CEP *</label><input style={styles.input} value={formNovoCliente.cep} onChange={e => { setFormNovoCliente({...formNovoCliente, cep: e.target.value}); if(e.target.value.replace(/\D/g, '').length === 8) buscarCEPVenda(e.target.value); }} placeholder="69000-000" /></div>
              <div><label style={styles.label}>Endereço *</label><input style={styles.input} value={formNovoCliente.endereco} onChange={e => setFormNovoCliente({...formNovoCliente, endereco: e.target.value})} /></div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: 16 }}>
                <div><label style={styles.label}>Número *</label><input style={styles.input} value={formNovoCliente.numero} onChange={e => setFormNovoCliente({...formNovoCliente, numero: e.target.value})} /></div>
                <div><label style={styles.label}>Complemento</label><input style={styles.input} value={formNovoCliente.complemento} onChange={e => setFormNovoCliente({...formNovoCliente, complemento: e.target.value})} /></div>
              </div>
              <div><label style={styles.label}>Bairro *</label><input style={styles.input} value={formNovoCliente.bairro} onChange={e => setFormNovoCliente({...formNovoCliente, bairro: e.target.value})} /></div>
              <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 16 }}>
                <div><label style={styles.label}>Cidade *</label><input style={styles.input} value={formNovoCliente.cidade} onChange={e => setFormNovoCliente({...formNovoCliente, cidade: e.target.value})} /></div>
                <div><label style={styles.label}>UF *</label><input style={styles.input} value={formNovoCliente.estado} onChange={e => setFormNovoCliente({...formNovoCliente, estado: e.target.value.toUpperCase()})} maxLength="2" placeholder="AM" /></div>
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 20 }}>
              <button onClick={salvarNovoCliente} style={{ ...styles.btn("primary"), flex: 1 }}>✅ Salvar Cliente</button>
              <button onClick={() => setModalCadastroCliente(false)} style={styles.btn("secondary")}>Cancelar</button>
            </div>
          </div>
        </div>
      )}

      

      {modalEtiqueta && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }} onClick={() => setModalEtiqueta(null)}>
          <div style={{ background: C.cardBg, borderRadius: 16, padding: 32, width: '90%', maxWidth: 400 }} onClick={e => e.stopPropagation()}>
            <h3 style={{ fontSize: 20, fontWeight: 700, marginBottom: 24 }}>🏷️ Etiqueta de Entrega</h3>
            <div style={{ marginBottom: 16 }}><label style={{ display: 'block', fontSize: 12, marginBottom: 6 }}>Nome</label><input type="text" value={formEtiqueta.nome} onChange={e => setFormEtiqueta({ ...formEtiqueta, nome: e.target.value })} style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: 8 }} /></div>
            <div style={{ marginBottom: 16 }}><label style={{ display: 'block', fontSize: 12, marginBottom: 6 }}>Bairro</label><input type="text" value={formEtiqueta.bairro} onChange={e => setFormEtiqueta({ ...formEtiqueta, bairro: e.target.value })} style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: 8 }} /></div>
            <div style={{ marginBottom: 16 }}><label style={{ display: 'block', fontSize: 12, marginBottom: 6 }}>Pagamento</label><select value={formEtiqueta.pagamento} onChange={e => setFormEtiqueta({ ...formEtiqueta, pagamento: e.target.value })} style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: 8 }}><option>Dinheiro</option><option>Pix</option><option>Cartao Debito</option><option>Cartao Credito</option></select></div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 24 }}><div><label style={{ display: 'block', fontSize: 12, marginBottom: 6 }}>Valor</label><input type="text" value={formEtiqueta.valor} onChange={e => setFormEtiqueta({ ...formEtiqueta, valor: e.target.value })} style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: 8 }} /></div><div><label style={{ display: 'block', fontSize: 12, marginBottom: 6 }}>Horario</label><input type="text" value={formEtiqueta.horario} onChange={e => setFormEtiqueta({ ...formEtiqueta, horario: e.target.value })} style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: 8 }} /></div></div>
            <div style={{ display: 'flex', gap: 12 }}><button onClick={() => setModalEtiqueta(null)} style={{ ...styles.btn('secondary'), flex: 1 }}>Cancelar</button><button onClick={imprimirEtiqueta} disabled={imprimindo} style={{ ...styles.btn('primary'), flex: 1 }}>{imprimindo ? 'Imprimindo...' : '🖨️ Imprimir'}</button></div>
          </div>
        </div>
      )}
</>
      </div>





  );
}

// ── GASTOS PAGE ───────────────────────────────────────────────
function Gastos({ data, setData, privado, showToast }) {
  const [selGastos, setSelGastos] = useState([]);
  const toggleSelG = (id) => setSelGastos(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
  const [form, setForm] = useState({ categoria: "Imposto", subcategoria: "", valor: "", tipo: "loja", parcelas: "1", data_gasto: hojeManaus() });
  const [showForm, setShowForm] = useState(false);
  const [salvandoGasto, setSalvandoGasto] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [projecaoModal, setProjecaoModal] = useState(null); // { mes, ano, label }
  const [buscaGastos, setBuscaGastos] = useState("");
  const [limiteGastos, setLimiteGastos] = useState(30);
  const [filtro, setFiltro] = useState("todos");
  const [sortDirGastos, setSortDirGastos] = useState("desc");
  const { mesFiltro, periodoRapido: periodoRapidoG, dataInicio: dataInicioG, setDataInicio: setDataInicioG, dataFim: dataFimG, setDataFim: setDataFimG, showFiltro: showFiltroG, setShowFiltro: setShowFiltroG, aplicarPeriodo: aplicarPeriodoRapidoG, selecionarMes: selecionarMesGastos, filtrarLista: filtrarListaGastos, labelPeriodo: labelPeriodoGastos } = useFiltro("tfx_gastos");

  // Inclui meses passados E futuros (para parcelas)
  const mesesDisponiveis = [...new Set(data.gastos.map(g => getMes(g.data)).filter(Boolean))].sort().reverse();
  // eslint-disable-next-line no-unused-vars
  const gastosPorMes = filtrarListaGastos(data.gastos);
  // Pausar/reativar TODOS os lançamentos do mesmo gasto fixo (não só a linha clicada)
  const togglePausarRecorrencia = async (descricao, ativar) => {
    const linhas = data.gastos.filter(x => 
      x.recorrente === true && 
      (x.descricao === descricao || x.subcategoria === descricao)
    );
    if (linhas.length === 0) return 0;
    for (const linha of linhas) {
      await sb.update("gastos", linha.id, { recorrente_ativo: ativar });
    }
    setData(d => ({ 
      ...d, 
      gastos: d.gastos.map(g => 
        linhas.some(l => l.id === g.id) ? { ...g, recorrente_ativo: ativar } : g
      ) 
    }));
    return linhas.length;
  };

  const cancelarRecorrencia = async (g) => {
    const desc = g.descricao || g.subcategoria;
    const ativar = g.recorrente_ativo === false;
    const linhas = data.gastos.filter(x => x.recorrente === true && (x.descricao === desc || x.subcategoria === desc));
    const msg = ativar 
      ? `Reativar "${desc}"?\n\nVai reativar ${linhas.length} lançamento(s).` 
      : `Pausar "${desc}"?\n\nIsso vai pausar ${linhas.length} lançamento(s) e parar de criar nos próximos meses.\nO histórico será mantido.`;
    if (!window.confirm(msg)) return;
    const n = await togglePausarRecorrencia(desc, ativar);
    if (showToast) showToast(`${ativar ? 'Reativado' : 'Pausado'} (${n} lançamento${n > 1 ? 's' : ''})!`);
  };

  // Categorias customizáveis
  const categoriasBase = ["Imposto", "Tráfego Pago", "Operacional", "Pessoal"];
  const categoriasCustom = (data.categorias_gastos || []).map(c => c.nome);
  const todasCategorias = [...new Set([...categoriasBase, ...categoriasCustom])];

  const [showGerenciarCats, setShowGerenciarCats] = useState(false);
  const [novaCategoria, setNovaCategoria] = useState("");
  const [salvandoCat, setSalvandoCat] = useState(false);

  const adicionarCategoria = async () => {
    if (!novaCategoria.trim()) return;
    if (todasCategorias.includes(novaCategoria.trim())) return alert("Categoria já existe!");
    setSalvandoCat(true);
    try {
      await sb.insert("categorias_gastos", { nome: novaCategoria.trim(), tenant_id: getTenant() });
      const atualizadas = await sb.get("categorias_gastos");
      setData(d => ({ ...d, categorias_gastos: atualizadas || [] }));
      setNovaCategoria("");
      if (showToast) showToast("Categoria adicionada!");
    } catch(e) { alert("Erro: " + e.message); }
    setSalvandoCat(false);
  };

  const removerCategoria = async (cat) => {
    if (categoriasBase.includes(cat)) return alert("Não é possível remover categorias padrão!");
    if (!window.confirm(`Remover categoria "${cat}"?`)) return;
    const catObj = (data.categorias_gastos || []).find(c => c.nome === cat);
    if (catObj) {
      await sb.remove("categorias_gastos", catObj.id);
      setData(d => ({ ...d, categorias_gastos: d.categorias_gastos.filter(c => c.id !== catObj.id) }));
    }
  };

  const gastosPorMesTipo = (filtro === "todos" ? [...gastosPorMes] : gastosPorMes.filter((g) => g.tipo === filtro));
  const gastosBuscados = buscaGastos ? gastosPorMesTipo.filter(g => (g.subcategoria||g.descricao||'').toLowerCase().includes(buscaGastos.toLowerCase()) || (g.categoria||'').toLowerCase().includes(buscaGastos.toLowerCase())) : gastosPorMesTipo;
  const allGastos = [...gastosBuscados].sort((a, b) => {
    const dateA = new Date(a.data || '1970-01-01');
    const dateB = new Date(b.data || '1970-01-01');
    return sortDirGastos === 'desc' ? dateB - dateA : dateA - dateB;
  });

  const catColors = { Imposto: C.warn, "Tráfego Pago": "#b91c1c", Operacional: C.gold, Pessoal: "#6b7280" };

  const serieParcelas = (g) => {
    const p = parseParcela(g.descricao || g.subcategoria);
    if (!p) return [];
    return data.gastos.filter(x => {
      const q = parseParcela(x.descricao || x.subcategoria);
      return q && q.base === p.base && q.total === p.total && x.categoria === g.categoria
        && (x.tipo || "loja") === (g.tipo || "loja") && Number(x.valor) === Number(g.valor);
    });
  };

  const apagarGastos = async (ids) => {
    await Promise.all(ids.map(i => sb.remove("gastos", i)));
    setData(d => ({ ...d, gastos: d.gastos.filter(x => !ids.includes(x.id)) }));
  };

  const handleDelete = async (id) => {
    const g = data.gastos.find(x => x.id === id);
    const serie = g ? serieParcelas(g) : [];
    if (serie.length > 1) { setExcluirParc({ g, serie }); return; }
    if (!window.confirm("Deletar este gasto?")) return;
    await apagarGastos([id]);
  };

  const confirmarExclusaoParcelas = async (modo) => {
    const { g, serie } = excluirParc;
    const nAtual = parseParcela(g.descricao || g.subcategoria).n;
    let ids = [g.id];
    if (modo === "seguintes") ids = serie.filter(x => parseParcela(x.descricao || x.subcategoria).n >= nAtual).map(x => x.id);
    if (modo === "todas") ids = serie.map(x => x.id);
    setExcluirParc(null);
    await apagarGastos(ids);
    if (showToast) showToast(ids.length + " lancamento(s) removido(s)");
  };

  const handleDuplicarGasto = async (g) => {
    const novaData = somarMeses(g.data, 1);
    const row = {
      descricao: g.descricao || g.subcategoria,
      categoria: g.categoria,
      tipo: g.tipo || "loja",
      valor: g.valor,
      data: novaData,
      recorrente: false
    };
    const result = await sb.insert("gastos", row);
    const inserted = Array.isArray(result) ? result[0] : result;
    if (!inserted || inserted.code) return alert("Erro ao duplicar gasto.");
    const novo = { ...inserted, subcategoria: inserted.descricao };
    setData(d => ({ ...d, gastos: [...d.gastos, novo] }));
    if (showToast) showToast(`Gasto duplicado para ${novaData.slice(0,7)}!`);
  };

  const [editando, setEditando] = useState(null);
  const [excluirParc, setExcluirParc] = useState(null);
  const [draggedGasto, setDraggedGasto] = useState(null);
  const [dragOverGasto, setDragOverGasto] = useState(null);

  const handleEdit = async (form) => {
    const row = { descricao: form.subcategoria, categoria: form.categoria, tipo: form.tipo, valor: Number(form.valor) };
    
    // Se for gasto recorrente, atualizar TODOS os lançamentos com mesma subcategoria
    if (editando.recorrente && form.dia_vencimento) {
      const gastosRelacionados = data.gastos.filter(g => 
        g.recorrente && 
        (g.subcategoria === editando.subcategoria || g.descricao === editando.subcategoria)
      );
      
      // Atualizar todos no banco
      for (const g of gastosRelacionados) {
        await sb.update("gastos", g.id, { 
          ...row, 
          dia_vencimento: parseInt(form.dia_vencimento) 
        });
      }
      
      // Atualizar state local
      setData(d => ({ 
        ...d, 
        gastos: d.gastos.map(g => 
          gastosRelacionados.some(gr => gr.id === g.id) 
            ? { ...g, ...row, subcategoria: row.descricao, dia_vencimento: parseInt(form.dia_vencimento) } 
            : g
        ) 
      }));
    } else {
      // Gasto único - atualizar só ele
      await sb.update("gastos", editando.id, row);
      setData(d => ({ ...d, gastos: d.gastos.map(g => g.id === editando.id ? { ...g, ...row, subcategoria: row.descricao } : g) }));
    }
    
    setEditando(null);
    if (showToast) showToast("Gasto atualizado!");
  };

  const handleAdd = async () => {
    if (salvandoGasto) return;
    if (!form.subcategoria) { alert("Preencha a Subcategoria / Descrição!"); return; }
    if (!form.valor) { alert("Preencha o Valor!"); return; }
    setSalvandoGasto(true);
    const nParcelas = Number(form.parcelas) || 1;
    const dataBase = form.data_gasto || hojeManaus();
    const valorBruto = form.categoria === "Tráfego Pago" ? Number(form.valor) * fatorTrafego(data.caixa_config) : Number(form.valor);
    const valorParcela = valorBruto / nParcelas;
    const novos = [];
    for (let p = 0; p < nParcelas; p++) {
      const dataP = somarMeses(dataBase, p);
      const descP = nParcelas > 1 ? `${form.subcategoria} (${p+1}/${nParcelas})` : form.subcategoria;
      const row = { data: dataP, descricao: descP, categoria: form.categoria, tipo: form.tipo, valor: valorParcela, recorrente: form.recorrente || false, dia_vencimento: form.recorrente && form.dia_vencimento ? parseInt(form.dia_vencimento) : null };
      const ins = await sb.insert("gastos", row);
      const inserted = Array.isArray(ins) ? ins[0] : ins;
      novos.push({ ...inserted, subcategoria: inserted?.descricao || descP });
    }
    setData((d) => ({ ...d, gastos: [...d.gastos, ...novos] }));
    setSalvandoGasto(false);
    setForm({ categoria: "Imposto", subcategoria: "", valor: "", tipo: "loja", parcelas: "1", data_gasto: hojeManaus() });
    setShowForm(false);
    // Auto-lançar no caixa
    for (const n of novos) {
      const catCaixa = n.categoria === "Tráfego Pago" ? "trafego" : "gastos_loja";
      await autoLancarCaixa("saida", n.subcategoria || n.descricao || n.categoria, n.valor, n.data || hojeManaus(), catCaixa, data.caixa_config);
    }
    if (showToast) showToast("Gasto salvo!");
  };

  return (
    <div>
      {editando && <EditModal title="Editar Gasto" values={editando} fields={[
        { key: "subcategoria", label: "Descrição" },
        { key: "valor", label: "Valor (R$)", type: "number" },
        { key: "categoria", label: "Categoria", options: ["Imposto", "Tráfego Pago", "Operacional", "Pessoal", "Entrega / Frete", "Uber Loja", "ICMS", "Prejuízo"] },
        { key: "tipo", label: "Tipo", options: ["loja", "pessoal"] },
        { key: "data", label: "Data", type: "date" },
        ...(editando.recorrente ? [{ key: "dia_vencimento", label: "Dia Vencimento (1-31)", type: "number" }] : []),
      ]} onSave={handleEdit} onClose={() => setEditando(null)} />}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 28 }}>
        <div>
          <div style={styles.pageTitle}>Gestão de Gastos</div>
          <div style={styles.pageSub}>Loja + Pessoal</div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <button onClick={() => setShowFiltroG(!showFiltroG)} style={{ ...styles.btn("secondary"), fontSize: 11, padding: "6px 12px" }}>
            📅 {labelPeriodoGastos()} ▾
          </button>
          <input style={{ ...styles.input, width: 160, fontSize: 11 }} placeholder="🔍 Buscar..." value={buscaGastos} onChange={e => setBuscaGastos(e.target.value)} />
          <button style={styles.btn("primary")} onClick={() => setShowForm(!showForm)}>+ Novo Gasto</button>
        </div>
      </div>

      {showFiltroG && (
        <div style={{ ...styles.card, marginBottom: 16, padding: 16 }}>
          <div style={{ fontSize: 11, color: "#374151", marginBottom: 10, letterSpacing: 1 }}>PERÍODO RÁPIDO</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 14 }}>
            {[["todos","Todos"],["hoje","Hoje"],["ontem","Ontem"],["7d","7 dias"],["15d","15 dias"],["30d","30 dias"],["90d","90 dias"]].map(([v,l]) => (
              <button key={v} onClick={() => aplicarPeriodoRapidoG(v)} style={{ ...styles.btn(periodoRapidoG === v && mesFiltro === "todos" ? "primary" : "secondary"), fontSize: 11, padding: "5px 12px" }}>{l}</button>
            ))}
          </div>
          <div style={{ fontSize: 11, color: "#374151", marginBottom: 10, letterSpacing: 1 }}>POR MÊS</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 14 }}>
            {mesesDisponiveis.map(m => (
              <button key={m} onClick={() => selecionarMesGastos(m)} style={{ ...styles.btn(mesFiltro === m ? "primary" : "secondary"), fontSize: 11, padding: "5px 12px" }}>{MESES[parseInt(m.slice(5,7))-1] + "/" + m.slice(2,4)}</button>
            ))}
          </div>
          <div style={{ fontSize: 11, color: "#374151", marginBottom: 10, letterSpacing: 1 }}>PERÍODO CUSTOMIZADO</div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <input type="date" style={{ ...styles.input, width: "auto", fontSize: 12 }} value={dataInicioG} onChange={e => setDataInicioG(e.target.value)} />
            <span style={{ color: "#374151" }}>→</span>
            <input type="date" style={{ ...styles.input, width: "auto", fontSize: 12 }} value={dataFimG} onChange={e => setDataFimG(e.target.value)} />
            <button onClick={() => setShowFiltroG(false)} style={{ ...styles.btn("primary"), fontSize: 11 }}>Aplicar</button>
          </div>
        </div>
      )}

      {showForm && (
        <div style={{ ...styles.card, marginBottom: 20, borderColor: C.warn + "40" }}>
          <div style={{ fontWeight: 800, fontSize: 20, color: C.warn, letterSpacing: -0.8, marginBottom: 24, display: "flex", alignItems: "center", gap: 12 }}>
            <span style={{ fontSize: 24 }}>💸</span> Registrar Gasto
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 14 }}>
            <div>
              <label style={styles.label}>Categoria</label>
              <select style={styles.input} value={form.categoria} onChange={(e) => setForm({ ...form, categoria: e.target.value })}>
                <option>Imposto</option><option>Tráfego Pago</option><option>Operacional</option><option>Pessoal</option><option>Entrega / Frete</option><option>Uber Loja</option><option>ICMS</option><option>Prejuízo</option><option>Cartão de Crédito</option>
              </select>
            </div>
            <div>
              <label style={styles.label}>Subcategoria / Descrição</label>
              <input style={styles.input} list="lista-descricoes-gasto" value={form.subcategoria} onChange={(e) => setForm({ ...form, subcategoria: e.target.value })} placeholder="Ex: Google Ads, Motoboy..." autoComplete="off" />
              <datalist id="lista-descricoes-gasto">
                {[...new Set(data.gastos.map(g => g.descricao || g.subcategoria).filter(Boolean))].map(d => <option key={d} value={d} />)}
              </datalist>
            </div>
            <div>
              <label style={styles.label}>Valor Total (R$){form.categoria === "Tráfego Pago" && <span style={{ color: C.gold, fontSize: 10, marginLeft: 6 }}>+{fmtAliquota(data.caixa_config)}% imposto aplicado automaticamente</span>}</label>
              <input style={styles.input} type="number" value={form.valor} onChange={(e) => setForm({ ...form, valor: e.target.value })} />
              {form.categoria === "Tráfego Pago" && form.valor && <div style={{ fontSize: 11, color: C.gold, marginTop: 4 }}>Total com imposto: R$ {(Number(form.valor) * fatorTrafego(data.caixa_config)).toFixed(2)}</div>}
            </div>
            <div>
              <label style={styles.label}>Tipo</label>
              <select style={styles.input} value={form.tipo} onChange={(e) => setForm({ ...form, tipo: e.target.value })}>
                <option value="loja">Loja</option><option value="pessoal">Pessoal</option>
              </select>
            </div>
            <div>
              <label style={styles.label}>Data</label>
              <input style={styles.input} type="date" value={form.data_gasto} onChange={(e) => setForm({ ...form, data_gasto: e.target.value })} />
            </div>
            <div>
              <label style={styles.label}>Parcelas</label>
              <select style={styles.input} value={form.parcelas} onChange={(e) => setForm({ ...form, parcelas: e.target.value })}>
                {[1,2,3,4,5,6,7,8,9,10,11,12].map(n => <option key={n} value={n}>{n}x{n > 1 ? ` — ${form.valor ? "R$ " + (Number(form.valor)/n).toFixed(2) + "/mês" : ""}` : ""}</option>)}
              </select>
            </div>
            {Number(form.parcelas) > 1 && (
              <div style={{ gridColumn: "span 2" }}>
                <label style={styles.label}>📅 Lançamento</label>
                <div style={{ ...styles.input, fontSize: 12, color: C.gold, display: "flex", alignItems: "center" }}>
                  {Number(form.parcelas)}x de R$ {form.valor ? (Number(form.valor)/Number(form.parcelas)).toFixed(2) : "0,00"} — lançado automaticamente nos próximos meses
                </div>
              </div>
            )}

          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button style={{...styles.btn("primary"), opacity: salvandoGasto ? 0.6 : 1}} onClick={handleAdd} disabled={salvandoGasto}>{salvandoGasto ? "Salvando..." : "Salvar"}</button>
            <button style={styles.btn("secondary")} onClick={() => setShowForm(false)}>Cancelar</button>
          </div>
        </div>
      )}

      {/* Filtro */}
      <div style={{ display: "flex", gap: 8, marginBottom: 28 }}>
        {[["todos", "Todos"], ["loja", "Loja"], ["pessoal", "Pessoal"]].map(([v, l]) => (
          <button key={v} onClick={() => setFiltro(v)} style={{
            ...styles.btn(filtro === v ? "primary" : "secondary"),
            fontSize: 12,
            fontWeight: 600,
            padding: "8px 16px",
            borderRadius: 10,
            background: filtro === v ? `${C.warn}` : C.bgElevated,
            border: `1px solid ${filtro === v ? C.warn : C.border}40`,
            transition: "all 0.2s"
          }}>{l}</button>
        ))}
      </div>

      {/* KPIs + Pizza */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 16, marginBottom: 28 }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 20 }}>
          {/* Total Gastos */}
          <div className="tfx-fade-in-delay-0 tfx-card-hover" style={{ 
            background: C.bgElevated,
            border: `2px solid ${C.warn}25`,
            borderRadius: 20,
            padding: 28,
            position: "relative",
            overflow: "hidden",
            boxShadow: `0 10px 40px ${C.warn}30`,
            transition: "all 0.3s"
          }}>
            <div style={{ position: "absolute", top: -30, right: -30, width: 120, height: 120, background: `radial-gradient(circle, ${C.warn}15, transparent)`, borderRadius: "50%" }} />
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
              <div style={{ width: 52, height: 52, borderRadius: 14, background: `${C.warn}20`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24 }}>💸</div>
              <div style={{ fontSize: 10, color: "#374151", letterSpacing: 2, textTransform: "uppercase", fontWeight: 600 }}>Total Gastos</div>
            </div>
            <div style={{ fontFamily: "'Inter', sans-serif", fontWeight: 800, fontSize: 32, color: C.warn, letterSpacing: -1.2, marginBottom: 8 }}>
              {privado ? "••••••" : fmt(allGastos.reduce((s,g) => s + g.valor, 0))}
            </div>
            <div style={{ fontSize: 11, color: "#374151", fontWeight: 500 }}>{allGastos.length} gastos no período</div>
          </div>

          {/* Gastos Fixos */}
          <div className="tfx-fade-in-delay-1 tfx-card-hover" style={{ 
            background: C.bgElevated,
            border: `2px solid #fb923c25`,
            borderRadius: 20,
            padding: 28,
            position: "relative",
            overflow: "hidden",
            boxShadow: `0 10px 40px #fb923c30`,
            transition: "all 0.3s"
          }}>
            <div style={{ position: "absolute", top: -30, right: -30, width: 120, height: 120, background: `radial-gradient(circle, #fb923c15, transparent)`, borderRadius: "50%" }} />
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
              <div style={{ width: 52, height: 52, borderRadius: 14, background: `#fb923c20`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24 }}>📌</div>
              <div style={{ fontSize: 10, color: "#374151", letterSpacing: 2, textTransform: "uppercase", fontWeight: 600 }}>Gastos Fixos</div>
            </div>
            <div style={{ fontFamily: "'Inter', sans-serif", fontWeight: 800, fontSize: 32, color: "#b91c1c", letterSpacing: -1.2, marginBottom: 8 }}>
              {privado ? "••••••" : fmt(allGastos.filter(g => g.recorrente).reduce((s,g) => s + g.valor, 0))}
            </div>
            <div style={{ fontSize: 11, color: "#374151", fontWeight: 500 }}>{allGastos.filter(g => g.recorrente).length} recorrentes</div>
          </div>

          {/* Gastos Variáveis */}
          <div className="tfx-fade-in-delay-2 tfx-card-hover" style={{ 
            background: C.bgElevated,
            border: `2px solid #22c55e25`,
            borderRadius: 20,
            padding: 28,
            position: "relative",
            overflow: "hidden",
            boxShadow: `0 10px 40px #22c55e30`,
            transition: "all 0.3s"
          }}>
            <div style={{ position: "absolute", top: -30, right: -30, width: 120, height: 120, background: `radial-gradient(circle, #22c55e15, transparent)`, borderRadius: "50%" }} />
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
              <div style={{ width: 52, height: 52, borderRadius: 14, background: `#22c55e20`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24 }}>📊</div>
              <div style={{ fontSize: 10, color: "#374151", letterSpacing: 2, textTransform: "uppercase", fontWeight: 600 }}>Gastos Variáveis</div>
            </div>
            <div style={{ fontFamily: "'Inter', sans-serif", fontWeight: 800, fontSize: 32, color: "#15803d", letterSpacing: -1.2, marginBottom: 8 }}>
              {privado ? "••••••" : fmt(allGastos.filter(g => !g.recorrente).reduce((s,g) => s + g.valor, 0))}
            </div>
            <div style={{ fontSize: 11, color: "#374151", fontWeight: 500 }}>{allGastos.filter(g => !g.recorrente).length} eventuais</div>
          </div>
        </div>
        <div style={{ ...styles.card, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minWidth: 200, padding: 20 }}>
          <div style={styles.sectionTitle}>Distribuição</div>
          <div style={{ marginTop: 12 }}>
            <PizzaChart data={Object.entries(catColors).map(([label, color]) => ({ label, color, value: allGastos.filter(g=>g.categoria===label).reduce((s,g)=>s+g.valor,0) })).filter(d=>d.value>0)} size={120} />
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 5, marginTop: 12 }}>
            {Object.entries(catColors).map(([label, color]) => {
              const v = allGastos.filter(g=>g.categoria===label).reduce((s,g)=>s+g.valor,0);
              const tot = allGastos.reduce((s,g)=>s+g.valor,0);
              if (v === 0) return null;
              return <div key={label} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <div style={{ width: 7, height: 7, borderRadius: 2, background: color }} />
                <span style={{ fontSize: 10, color: "#374151" }}>{label}</span>
                <span style={{ fontSize: 10, color, fontWeight: 700, marginLeft: "auto" }}>{pct(v/tot)}</span>
              </div>;
            })}
          </div>
        </div>
      </div>

      <div style={{ 
        background: C.bgElevated,
        border: `2px solid ${C.border}30`,
        borderRadius: 20,
        padding: 28,
        boxShadow: "0 4px 30px rgba(0,0,0,0.3)"
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <div style={{ fontWeight: 800, fontSize: 18, color: "#111827", letterSpacing: -0.5 }}>Detalhamento de Gastos</div>
          <button onClick={() => setSortDirGastos(d => d === "desc" ? "asc" : "desc")} style={{ ...styles.btn("secondary"), display: "flex", alignItems: "center", gap: 6, fontSize: 10 }}>
            Data {sortDirGastos === "desc" ? "↓ Mais recente" : "↑ Mais antiga"}
          </button>
          <div style={{ fontWeight: 800, fontSize: 16, color: C.warn }}>{privado ? "••••••" : fmt(allGastos.reduce((s,g)=>s+g.valor,0))}</div>
        </div>
        {selGastos.length > 0 && (
          <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
            <button onClick={async () => {
              if (!window.confirm(`Deletar ${selGastos.length} gastos?`)) return;
              for (const id of selGastos) { await sb.remove("gastos", id); }
              setData(d => ({ ...d, gastos: d.gastos.filter(g => !selGastos.includes(g.id)) }));
              setSelGastos([]);
            }} style={{ ...styles.btn("secondary"), fontSize: 10, padding: "4px 10px", color: C.warn, borderColor: C.warn }}>
              🗑️ Deletar {selGastos.length}
            </button>
            <button onClick={async () => {
              const gastosSel = data.gastos.filter(g => selGastos.includes(g.id));
              let count = 0;
              for (const g of gastosSel) {
                const novaData = somarMeses(g.data, 1);
                const row = { descricao: g.descricao || g.subcategoria, categoria: g.categoria, tipo: g.tipo || "loja", valor: g.valor, data: novaData, recorrente: false };
                const result = await sb.insert("gastos", row);
                const inserted = Array.isArray(result) ? result[0] : result;
                if (inserted && !inserted.code) {
                  const novo = { ...inserted, subcategoria: inserted.descricao };
                  setData(d => ({ ...d, gastos: [...d.gastos, novo] }));
                  count++;
                }
              }
              setSelGastos([]);
              if (showToast) showToast(`${count} gasto(s) duplicado(s) pro mês seguinte!`);
            }} style={{ ...styles.btn("secondary"), fontSize: 10, padding: "4px 10px", color: C.gold, borderColor: C.gold }}>
              📋 Duplicar {selGastos.length} pro mês seguinte
            </button>
          </div>
        )}
        {/* Gerenciar categorias */}
        <div style={{ marginBottom: 12 }}>
          <button onClick={() => setShowGerenciarCats(s => !s)} style={{ background: "none", border: "none", color: "#374151", cursor: "pointer", fontSize: 11 }}>
            ⚙️ {showGerenciarCats ? "Fechar" : "Gerenciar"} categorias
          </button>
          {showGerenciarCats && (
            <div style={{ ...styles.card, marginTop: 8, padding: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: "#111827", marginBottom: 10 }}>Categorias de Gastos</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
                {todasCategorias.map(cat => (
                  <div key={cat} style={{ display: "flex", alignItems: "center", gap: 4, background: "#f0f2f5", border: "1px solid #e5e7eb", borderRadius: 6, padding: "4px 10px", fontSize: 11 }}>
                    <span style={{ color: "#111827" }}>{cat}</span>
                    {!categoriasBase.includes(cat) && (
                      <button onClick={() => removerCategoria(cat)} style={{ background: "none", border: "none", color: C.warn, cursor: "pointer", fontSize: 12, padding: 0, marginLeft: 4 }}>✕</button>
                    )}
                    {categoriasBase.includes(cat) && <span style={{ fontSize: 9, color: "#374151", marginLeft: 4 }}>padrão</span>}
                  </div>
                ))}
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <input style={{ ...styles.input, flex: 1, fontSize: 12 }} value={novaCategoria} onChange={e => setNovaCategoria(e.target.value)} placeholder="Nova categoria (ex: Embalagens, Frete...)" onKeyDown={e => e.key === "Enter" && adicionarCategoria()} />
                <button onClick={adicionarCategoria} disabled={salvandoCat} style={{ ...styles.btn("primary"), fontSize: 11, padding: "0 14px" }}>{salvandoCat ? "..." : "+ Adicionar"}</button>
              </div>
            </div>
          )}
        </div>

        <table style={{ ...styles.table, borderCollapse: "separate", borderSpacing: 0 }}>
          <thead>
            <tr style={{ 
              background: `linear-gradient(90deg, ${C.surface}, ${C.bgElevated})`,
              borderBottom: `2px solid ${C.accent}40`
            }}>
              <th style={{ ...styles.th, width: 32, padding: "10px 12px" }}><input type="checkbox" onChange={() => setSelGastos(s => s.length === allGastos.length ? [] : allGastos.map(g => g.id))} checked={selGastos.length === allGastos.length && allGastos.length > 0} /></th>
              {["Data", "Categoria", "Descrição", "Tipo", "Valor", ""].map((h) => <th key={h} style={{ ...styles.th, padding: "10px 14px", fontSize: 9, letterSpacing: 1.5, borderBottom: `2px solid ${C.accent}30` }}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {allGastos.slice(0, limiteGastos).map((g, idx) => (
              <tr 
                key={g.id} 
                draggable
                onDragStart={(e) => {
                  setDraggedGasto(g);
                  e.dataTransfer.effectAllowed = 'move';
                }}
                onDragEnter={() => {
                  if (draggedGasto && draggedGasto.id !== g.id) {
                    setDragOverGasto(g);
                  }
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.dataTransfer.dropEffect = 'move';
                }}
                onDrop={async (e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  
                  if (!draggedGasto || draggedGasto.id === g.id) {
                    setDraggedGasto(null);
                    setDragOverGasto(null);
                    return;
                  }
                  
                  const targetIdx = allGastos.findIndex(x => x.id === g.id);
                  
                  // Usar timestamp negativo pra garantir que vai pra posição certa
                  // Quanto MENOR o número, mais pra cima
                  let novaOrdem;
                  if (targetIdx === 0) {
                    // Primeira posição: timestamp menor que TODOS
                    const menorOrdem = Math.min(...allGastos.map(x => x.ordem || 999999));
                    novaOrdem = menorOrdem - 1000;
                  } else {
                    // Entre duas linhas
                    const anterior = allGastos[targetIdx - 1];
                    const atual = allGastos[targetIdx];
                    const ordemAnt = anterior?.ordem || 0;
                    const ordemAtual = atual?.ordem || 999999;
                    novaOrdem = (ordemAnt + ordemAtual) / 2;
                  }
                  
                  // Atualizar UI instantaneamente
                  setData(d => ({ 
                    ...d, 
                    gastos: d.gastos.map(x => 
                      x.id === draggedGasto.id ? { ...x, ordem: novaOrdem } : x
                    )
                  }));
                  
                  setDraggedGasto(null);
                  setDragOverGasto(null);
                  
                  // Salvar só o item movido
                  console.log("🔍 Salvando ordem:", draggedGasto.id, "→", novaOrdem);
                  const resultado = await sb.update("gastos", draggedGasto.id, { ordem: novaOrdem });
                  console.log("✅ Resultado:", resultado);
                }}
                onDragEnd={() => {
                  setDraggedGasto(null);
                  setDragOverGasto(null);
                }}
                style={{ 
                  background: selGastos.includes(g.id) ? "#f9fafb" : idx % 2 === 0 ? "transparent" : C.surface+"80", 
                  transition: "all 0.2s",
                  opacity: draggedGasto?.id === g.id ? 0.3 : 1,
                  cursor: draggedGasto?.id === g.id ? 'grabbing' : 'grab',
                  borderTop: dragOverGasto?.id === g.id ? `3px solid ${C.accent}` : 'none',
                  transform: draggedGasto?.id === g.id ? 'scale(0.98)' : 'scale(1)'
                }}
                onMouseEnter={e => !draggedGasto && (e.currentTarget.style.background = "#fafafa")}
                onMouseLeave={e => !draggedGasto && (e.currentTarget.style.background = selGastos.includes(g.id) ? "#f9fafb" : idx % 2 === 0 ? "transparent" : C.surface+"80")}>
                <td style={{ ...styles.td, padding: "10px 12px", borderBottom: "1px solid #e5e7eb" }}><input type="checkbox" checked={selGastos.includes(g.id)} onChange={() => toggleSelG(g.id)} /></td>
                <td style={{ ...styles.td, padding: "10px 14px", borderBottom: "1px solid #e5e7eb", color: "#374151", fontSize: 11 }}>{g.data}</td>
                <td style={{ ...styles.td, padding: "10px 14px", borderBottom: "1px solid #e5e7eb" }}><span style={{ ...styles.badge(catColors[g.categoria] || C.muted), fontSize: 9, padding: "2px 8px" }}>{g.categoria}</span></td>
                <td style={{ ...styles.td, padding: "10px 14px", borderBottom: "1px solid #e5e7eb", color: "#111827" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <input type="checkbox" checked={!!g.pago} title={g.pago ? "Marcar como não pago" : "Marcar como pago"} onChange={async () => {
                        await sb.update("gastos", g.id, { pago: !g.pago });
                        setData(d => ({ ...d, gastos: d.gastos.map(x => x.id === g.id ? { ...x, pago: !x.pago } : x) }));
                      }} style={{ width: 14, height: 14, cursor: "pointer", accentColor: C.accent }} />
                    <span style={{ textDecoration: g.pago ? "line-through" : "none", opacity: g.pago ? 0.5 : 1 }}>{g.subcategoria}</span>
                    {g.recorrente && <span style={{ background: g.recorrente_ativo === false ? C.warn+"20" : C.accent+"20", color: g.recorrente_ativo === false ? C.warn : C.accent, fontSize: 9, padding: "1px 6px", borderRadius: 4, letterSpacing: 1 }}>{g.recorrente_ativo === false ? "⏸ PAUSADO" : "🔄 FIXO"}</span>}
                    {g.recorrente && !g.pago && g.dia_vencimento && (() => { const hoje = new Date(); const diaHoje = hoje.getDate(); return diaHoje >= g.dia_vencimento ? <span style={{ background: C.warn+"25", color: C.warn, fontSize: 9, padding: "1px 6px", borderRadius: 4, letterSpacing: 1, marginLeft: 2 }}>⚠️ VENCIDA</span> : null; })()}
                  </div>
                </td>
                <td style={{ ...styles.td, padding: "10px 14px", borderBottom: "1px solid #e5e7eb" }}><span style={{ ...styles.badge(g.tipo === "loja" ? C.accent : C.gold), fontSize: 9, padding: "2px 8px" }}>{g.tipo}</span></td>
                <td style={{ ...styles.td, padding: "10px 14px", borderBottom: "1px solid #e5e7eb", color: C.warn, fontWeight: 700 }}>{privado ? "••••" : fmt(g.valor)}</td>
                <td style={{ ...styles.td, padding: "8px 10px", borderBottom: "1px solid #e5e7eb" }}>
                  <button onClick={() => setEditando({ ...g, subcategoria: g.subcategoria || g.descricao })} style={{ background: "none", border: "none", color: C.accent, cursor: "pointer", fontSize: 14, opacity: 0.7, marginRight: 6 }}>✏️</button>
                  <button onClick={() => handleDuplicarGasto(g)} title="Duplicar pro mês seguinte" style={{ background: "none", border: "none", color: C.gold, cursor: "pointer", fontSize: 14, opacity: 0.8, marginRight: 6 }}>📋</button>
                  {g.recorrente && <button onClick={() => cancelarRecorrencia(g)} title={g.recorrente_ativo === false ? "Reativar recorrência" : "Cancelar recorrência"} style={{ background: "none", border: "none", color: g.recorrente_ativo === false ? C.accent : C.warn, cursor: "pointer", fontSize: 14, opacity: 0.8, marginRight: 6 }}>{g.recorrente_ativo === false ? "▶️" : "⏸"}</button>}
                  <>
                  <button onClick={() => handleDelete(g.id)} style={{ background: "none", border: "none", color: C.warn, cursor: "pointer", fontSize: 14, opacity: 1 }}>✕</button>
                  {excluirParc && excluirParc.g.id === g.id && (() => {
                    const pa = parseParcela(g.descricao || g.subcategoria);
                    const serie = excluirParc.serie;
                    const seguintes = serie.filter(x => parseParcela(x.descricao || x.subcategoria).n >= pa.n);
                    const passadas = serie.filter(x => String(x.data).slice(0, 10) < hojeManaus()).length;
                    const bt = { display: "block", width: "100%", textAlign: "left", padding: "10px 12px", marginTop: 8, borderRadius: 8, border: "1px solid #d1d5db", background: "#f9fafb", color: "#111827", cursor: "pointer", fontSize: 13 };
                    return (
                      <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 10000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }} onClick={() => setExcluirParc(null)}>
                        <div style={{ background: "#ffffff", color: "#111827", borderRadius: 12, padding: 20, width: "100%", maxWidth: 420, textAlign: "left" }} onClick={e => e.stopPropagation()}>
                          <div style={{ fontWeight: 700, fontSize: 16 }}>Excluir parcela</div>
                          <div style={{ fontSize: 13, color: "#4b5563", marginTop: 4 }}>{pa.base} · parcela {pa.n}/{pa.total} · {serie.length} lancamentos na serie</div>
                          <button style={bt} onClick={() => confirmarExclusaoParcelas("uma")}>🗓️ So esta parcela ({pa.n}/{pa.total})</button>
                          <button style={bt} onClick={() => confirmarExclusaoParcelas("seguintes")}>➡️ Esta e as proximas ({seguintes.length})</button>
                          <button style={{ ...bt, borderColor: "#fca5a5", background: "#fef2f2", color: "#991b1b" }} onClick={() => confirmarExclusaoParcelas("todas")}>🗑️ Todas as parcelas ({serie.length}){passadas > 0 ? " — inclui " + passadas + " de meses passados" : ""}</button>
                          <button style={{ ...bt, textAlign: "center", background: "#fff" }} onClick={() => setExcluirParc(null)}>Cancelar</button>
                        </div>
                      </div>
                    );
                  })()}
                  </>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {allGastos.length > limiteGastos && (
          <div style={{ textAlign: "center", padding: "16px 12px", display: "flex", gap: 10, justifyContent: "center", alignItems: "center", flexWrap: "wrap" }}>
            <span style={{ fontSize: 11, color: "#6b7280" }}>Mostrando {limiteGastos} de {allGastos.length} gastos</span>
            <button onClick={() => setLimiteGastos(n => n + 30)} style={{ ...styles.btn("secondary"), fontSize: 11 }}>Mostrar mais 30</button>
            <button onClick={() => setLimiteGastos(allGastos.length)} style={{ ...styles.btn("secondary"), fontSize: 11 }}>Mostrar todas</button>
          </div>
        )}
      </div>



    </div>
  );
}

// ── ESTOQUE PAGE ──────────────────────────────────────────────
// Mes (YYYY-MM) relativo ao mes atual de Manaus. So aritmetica: sem estouro de dia e sem fuso.
function mesRelativo(delta) {
  const [a, m] = hojeManaus().slice(0, 7).split("-").map(Number);
  const idx = a * 12 + (m - 1) + delta;
  return Math.floor(idx / 12) + "-" + String(idx % 12 + 1).padStart(2, "0");
}

// Soma n meses a uma data YYYY-MM-DD, limitando ao ultimo dia do mes (31/jan + 1 = 28/fev).
function somarMeses(dataStr, n) {
  const [a, m, d] = String(dataStr).slice(0, 10).split("-").map(Number);
  const idx = a * 12 + (m - 1) + n;
  const ano = Math.floor(idx / 12), mes = idx % 12;
  const ultimo = new Date(Date.UTC(ano, mes + 1, 0)).getUTCDate();
  return ano + "-" + String(mes + 1).padStart(2, "0") + "-" + String(Math.min(d, ultimo)).padStart(2, "0");
}

// Reconhece gasto parcelado pela descricao "Nome (2/5)".
function parseParcela(desc) {
  const m = String(desc || "").match(/^(.*)\s\((\d+)\/(\d+)\)$/);
  return m ? { base: m[1], n: Number(m[2]), total: Number(m[3]) } : null;
}

// Formata 'YYYY-MM-DD' (ou ISO) como DD/MM/AAAA sem converter fuso (evita mostrar 1 dia antes).
// tfx-visual-1
function fmtDataBR(str) {
  const p = String(str || "").slice(0, 10).split("-");
  return p.length === 3 && p[0].length === 4 ? p[2] + "/" + p[1] + "/" + p[0] : "";
}

// Ajusta a quantidade de uma cor do produto (delta negativo baixa, positivo devolve).
// Retorna o novo array de cores, ou null se o produto nao usa essa cor.
function ajustarCores(prod, cor, delta) {
  if (!cor || !prod || !Array.isArray(prod.cores) || prod.cores.length === 0) return null;
  let achou = false;
  const novas = prod.cores.map(c => {
    if ((c.cor || "").toLowerCase() === String(cor).toLowerCase()) { achou = true; return { ...c, qtd: Math.max(0, (Number(c.qtd) || 0) + delta) }; }
    return c;
  });
  return achou ? novas : null;
}

// Soma (sinal=1) ou desconta (sinal=-1) as cores de um item de compra no produto.
function aplicarCoresCompra(prod, entradas, sinal) {
  if (!prod || !Array.isArray(entradas) || entradas.length === 0) return null;
  const base = Array.isArray(prod.cores) ? prod.cores.map(c => ({ ...c })) : [];
  entradas.forEach(e => {
    const q = (Number(e.qtd) || 0) * sinal;
    if (!e.cor || q === 0) return;
    const c = base.find(x => (x.cor || "").toLowerCase() === String(e.cor).toLowerCase());
    if (c) c.qtd = Math.max(0, (Number(c.qtd) || 0) + q);
    else if (q > 0) base.push({ cor: e.cor, qtd: q });
  });
  return base;
}

// ── MODAL DE CORES (variações do produto) ─────────────────────
function CoresModal({ produto, onSave, onClose }) {
  const [linhas, setLinhas] = useState(() => (Array.isArray(produto.cores) ? produto.cores.map(c => ({ cor: c.cor || "", qtd: String(c.qtd ?? 0) })) : []));
  const [salvando, setSalvando] = useState(false);
  const soma = linhas.reduce((s, l) => s + (Number(l.qtd) || 0), 0);
  const nomes = linhas.map(l => l.cor.trim().toLowerCase()).filter(Boolean);
  const duplicada = nomes.length !== new Set(nomes).size;
  const upd = (i, k, v) => setLinhas(ls => ls.map((l, j) => j === i ? { ...l, [k]: v } : l));
  const salvar = async () => {
    if (duplicada) return;
    setSalvando(true);
    const limpas = linhas.map(l => ({ cor: l.cor.trim(), qtd: Math.max(0, parseInt(l.qtd, 10) || 0) })).filter(l => l.cor);
    await onSave(limpas);
    setSalvando(false);
  };
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.5)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ background: "#ffffff", border: "1px solid #e5e7eb", borderRadius: 16, padding: 24, width: 440, maxWidth: "95vw", maxHeight: "90vh", overflowY: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.25)" }}>
        <div style={{ fontWeight: 700, fontSize: 15, color: "#111827", marginBottom: 4 }}>🎨 Cores — {produto.produto}</div>
        <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 16 }}>
          {linhas.length === 0 ? "Sem cores: o produto é controlado só pelo total." : "O estoque total do produto passa a ser a soma das cores."}
        </div>
        <datalist id="cores-sugestoes">
          {["Preto", "Prata", "Rosa", "Azul", "Verde", "Branco", "Dourado", "Cinza", "Vermelho", "Roxo", "Laranja", "Rose Gold"].map(c => <option key={c} value={c} />)}
        </datalist>
        {linhas.map((l, i) => (
          <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 90px 28px", gap: 8, marginBottom: 8 }}>
            <input style={styles.input} list="cores-sugestoes" placeholder="Cor (ex.: Preto)" value={l.cor} onChange={e => upd(i, "cor", e.target.value)} />
            <input style={styles.input} type="number" min="0" placeholder="Qtd" value={l.qtd} onChange={e => upd(i, "qtd", e.target.value)} />
            <button onClick={() => setLinhas(ls => ls.filter((_, j) => j !== i))} style={{ background: "none", border: "none", color: "#ef4444", cursor: "pointer", fontSize: 16 }}>✕</button>
          </div>
        ))}
        <button onClick={() => setLinhas(ls => [...ls, { cor: "", qtd: "0" }])} style={{ ...styles.btn("secondary"), fontSize: 11, padding: "6px 12px", marginTop: 4 }}>+ Cor</button>
        {duplicada && <div style={{ fontSize: 11, color: "#ef4444", marginTop: 8 }}>Há cores repetidas. Junte-as numa linha só.</div>}
        {linhas.length > 0 && <div style={{ fontSize: 12, color: "#111827", marginTop: 14, fontWeight: 600 }}>Total: {soma} un.</div>}
        <div style={{ display: "flex", gap: 8, marginTop: 18 }}>
          <button style={{ ...styles.btn("primary"), opacity: duplicada || salvando ? 0.5 : 1 }} disabled={duplicada || salvando} onClick={salvar}>{salvando ? "Salvando..." : "💾 Salvar"}</button>
          <button style={styles.btn("secondary")} onClick={onClose}>Cancelar</button>
        </div>
      </div>
    </div>
  );
}

function Estoque({ data, setData, privado, showToast }) {
  const [draggedEstoque, setDraggedEstoque] = useState(null);
  const [dragOverEstoque, setDragOverEstoque] = useState(null);
  const [selEstoque, setSelEstoque] = useState([]);
  const toggleSelE = (id) => setSelEstoque(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
  const [abaEstoque, setAbaEstoque] = useState("produtos"); // "produtos" | "kits"
  const [form, setForm] = useState({ produto: "", sku: "", categoria: "", estoque: "", minimo: "", custo: "", preco: "", ncm: "" });
  const [showForm, setShowForm] = useState(false);
  const [salvandoProduto, setSalvandoProduto] = useState(false);
  const [catFiltro, setCatFiltro] = useState("todas");
  const [viewMode, setViewMode] = useState("tabela"); // "tabela" | "cards"
  // eslint-disable-next-line
  const [buscaEstoque, setBuscaEstoque] = useState("");

  const valorEstoque = data.estoque.reduce((s, p) => s + p.estoque * p.custo, 0);
  const valorVenda = data.estoque.reduce((s, p) => s + p.estoque * p.preco, 0);

  const handleDelete = async (id) => {
    if (!window.confirm("Deletar este produto?")) return;
    await sb.remove("estoque", id);
    setData(d => ({ ...d, estoque: d.estoque.filter(p => p.id !== id) }));
  };
  const abaixoMinimo = data.estoque.filter((p) => p.estoque <= p.minimo).length;

  const [editandoEstoque, setEditandoEstoque] = useState(null);
  const [coresModal, setCoresModal] = useState(null);
  const salvarCores = async (cores) => {
    const p = coresModal;
    const patch = cores.length > 0 ? { cores, qtd: cores.reduce((t, c) => t + c.qtd, 0) } : { cores: [] };
    await sb.update("estoque", p.id, patch);
    setData(d => ({ ...d, estoque: d.estoque.map(x => x.id === p.id ? { ...x, ...patch, ...(patch.qtd !== undefined ? { estoque: patch.qtd } : {}) } : x) }));
    setCoresModal(null);
    if (showToast) showToast(cores.length ? "Cores salvas!" : "Cores removidas");
  };

  const handleEditEstoque = async (form) => {
    const row = { nome: form.produto, sku: form.sku || "", categoria: form.categoria || "", qtd: Number(form.estoque) || 0, minimo: Number(form.minimo) || 0, preco_custo: Number(form.custo) || 0, preco_venda: Number(form.preco) || 0, ncm: form.ncm || "" };
    await sb.update("estoque", editandoEstoque.id, row);
    setData(d => ({ ...d, estoque: d.estoque.map(p => p.id === editandoEstoque.id ? { ...p, ...row, produto: row.nome, estoque: row.qtd, custo: row.preco_custo, preco: row.preco_venda, ncm: row.ncm } : p) }));
    setEditandoEstoque(null);
  };

  const handleAdd = async () => {
    if (salvandoProduto) return;
    if (!form.produto) return alert("Preencha o nome do produto!");
    setSalvandoProduto(true);
    try {
      const row = { nome: form.produto, sku: form.sku || "", categoria: form.categoria || "", qtd: Number(form.estoque) || 0, minimo: Number(form.minimo) || 0, preco_custo: Number(form.custo) || 0, preco_venda: Number(form.preco) || 0, ncm: form.ncm || "" };
      const result = await sb.insert("estoque", row);
      const inserted = Array.isArray(result) ? result[0] : result;
      if (!inserted || inserted.code) return alert("Erro ao salvar: " + JSON.stringify(inserted));
      const novo = { ...inserted, produto: inserted.nome || "", estoque: Number(inserted.qtd) || 0, custo: Number(inserted.preco_custo) || 0, preco: Number(inserted.preco_venda) || 0, minimo: Number(inserted.minimo) || 0, ncm: inserted.ncm || "" };
      setData((d) => ({ ...d, estoque: [...d.estoque, novo] }));
      setSalvandoProduto(false);
      setForm({ produto: "", sku: "", categoria: "", estoque: "", minimo: "", custo: "", preco: "", ncm: "" });
      setShowForm(false);
      if (showToast) showToast("Produto salvo!");
    } catch(e) {
      setSalvandoProduto(false);
      alert("Erro: " + e.message);
    }
  };

  return (
    <div>
      {coresModal && <CoresModal produto={coresModal} onSave={salvarCores} onClose={() => setCoresModal(null)} />}
      {editandoEstoque && <EditModal title="Editar Produto" values={{ ...editandoEstoque, produto: editandoEstoque.produto, estoque: editandoEstoque.estoque, custo: editandoEstoque.custo, preco: editandoEstoque.preco }} fields={[
        { key: "produto", label: "Nome do Produto" },
        { key: "sku", label: "SKU / Código" },
        { key: "categoria", label: "Categoria" },
        { key: "estoque", label: "Qtd em Estoque", type: "number" },
        { key: "minimo", label: "Estoque Mínimo", type: "number" },
        { key: "custo", label: "Custo (R$)", type: "number" },
        { key: "preco", label: "Preço de Venda (R$)", type: "number" },
        { key: "ncm", label: "NCM (Código Fiscal)" },
      ]} onSave={handleEditEstoque} onClose={() => setEditandoEstoque(null)} />}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 28 }}>
        <div>
          <div style={styles.pageTitle}>Controle de Estoque</div>
          <div style={styles.pageSub}>Produtos cadastrados — atualizado hoje</div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          {abaEstoque === "produtos" && <input style={{ ...styles.input, width: 160, fontSize: 11 }} placeholder="🔍 Buscar produto..." value={buscaEstoque} onChange={e => setBuscaEstoque(e.target.value)} />}
          {abaEstoque === "produtos" && <button style={styles.btn("primary")} onClick={() => setShowForm(!showForm)}>+ Novo Produto</button>}
        </div>
      </div>
      <div style={{ display: "flex", gap: 8, marginBottom: 28 }}>
        <button onClick={() => setAbaEstoque("produtos")} style={{ ...styles.btn(abaEstoque === "produtos" ? "primary" : "secondary"), fontSize: 12 }}>📦 Produtos</button>
        <button onClick={() => setAbaEstoque("kits")} style={{ ...styles.btn(abaEstoque === "kits" ? "primary" : "secondary"), fontSize: 12 }}>🎁 Kits & Combos</button>
      </div>
      {abaEstoque === "kits" && <Kits data={data} setData={setData} />}
      {abaEstoque === "produtos" && <div>

      {/* KPIs ESTOQUE */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 20, marginBottom: 28 }}>
        {/* Valor em Estoque */}
        <div className="tfx-fade-in-delay-0 tfx-card-hover" style={{ 
          background: C.bgElevated,
          border: `2px solid #8b5cf625`,
          borderRadius: 20,
          padding: 24,
          position: "relative",
          overflow: "hidden",
          boxShadow: `0 10px 40px #8b5cf630`,
          transition: "all 0.3s"
        }}>
          <div style={{ position: "absolute", top: -30, right: -30, width: 120, height: 120, background: `radial-gradient(circle, #8b5cf615, transparent)`, borderRadius: "50%" }} />
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
            <div style={{ width: 48, height: 48, borderRadius: 12, background: `#8b5cf620`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}>💰</div>
            <div style={{ fontSize: 9, color: "#374151", letterSpacing: 2, textTransform: "uppercase", fontWeight: 600 }}>Valor em Estoque</div>
          </div>
          <div style={{ fontFamily: "'Inter', sans-serif", fontWeight: 800, fontSize: 28, color: "#6b7280", letterSpacing: -1.2 }}>
            {privado ? "••••••" : fmt(valorEstoque)}
          </div>
        </div>

        {/* Valor de Venda */}
        <div className="tfx-fade-in-delay-1 tfx-card-hover" style={{ 
          background: C.bgElevated,
          border: `2px solid #22c55e25`,
          borderRadius: 20,
          padding: 24,
          position: "relative",
          overflow: "hidden",
          boxShadow: `0 10px 40px #22c55e30`,
          transition: "all 0.3s"
        }}>
          <div style={{ position: "absolute", top: -30, right: -30, width: 120, height: 120, background: `radial-gradient(circle, #22c55e15, transparent)`, borderRadius: "50%" }} />
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
            <div style={{ width: 48, height: 48, borderRadius: 12, background: `#22c55e20`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}>💵</div>
            <div style={{ fontSize: 9, color: "#374151", letterSpacing: 2, textTransform: "uppercase", fontWeight: 600 }}>Valor de Venda</div>
          </div>
          <div style={{ fontFamily: "'Inter', sans-serif", fontWeight: 800, fontSize: 28, color: "#15803d", letterSpacing: -1.2 }}>
            {privado ? "••••••" : fmt(valorVenda)}
          </div>
        </div>

        {/* Produtos Abaixo do Mínimo */}
        <div className="tfx-fade-in-delay-2 tfx-card-hover" style={{ 
          background: C.bgElevated,
          border: `2px solid ${C.warn}25`,
          borderRadius: 20,
          padding: 24,
          position: "relative",
          overflow: "hidden",
          boxShadow: `0 10px 40px ${C.warn}30`,
          transition: "all 0.3s"
        }}>
          <div style={{ position: "absolute", top: -30, right: -30, width: 120, height: 120, background: `radial-gradient(circle, ${C.warn}15, transparent)`, borderRadius: "50%" }} />
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
            <div style={{ width: 48, height: 48, borderRadius: 12, background: `${C.warn}20`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}>⚠️</div>
            <div style={{ fontSize: 9, color: "#374151", letterSpacing: 2, textTransform: "uppercase", fontWeight: 600 }}>Abaixo do Mínimo</div>
          </div>
          <div style={{ fontFamily: "'Inter', sans-serif", fontWeight: 800, fontSize: 28, color: C.warn, letterSpacing: -1.2 }}>
            {abaixoMinimo} produto{abaixoMinimo !== 1 ? 's' : ''}
          </div>
        </div>

        {/* Total de SKUs */}
        <div className="tfx-fade-in-delay-3 tfx-card-hover" style={{ 
          background: C.bgElevated,
          border: `2px solid ${C.info}25`,
          borderRadius: 20,
          padding: 24,
          position: "relative",
          overflow: "hidden",
          boxShadow: `0 10px 40px ${C.info}30`,
          transition: "all 0.3s"
        }}>
          <div style={{ position: "absolute", top: -30, right: -30, width: 120, height: 120, background: `radial-gradient(circle, ${C.info}15, transparent)`, borderRadius: "50%" }} />
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
            <div style={{ width: 48, height: 48, borderRadius: 12, background: `${C.info}20`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}>📊</div>
            <div style={{ fontSize: 9, color: "#374151", letterSpacing: 2, textTransform: "uppercase", fontWeight: 600 }}>Produtos cadastrados</div>
          </div>
          <div style={{ fontFamily: "'Inter', sans-serif", fontWeight: 800, fontSize: 28, color: C.info, letterSpacing: -1.2 }}>
            {data.estoque.length}
          </div>
          <div style={{ fontSize: 10, color: "#374151", marginTop: 8 }}>
            Produtos cadastrados
          </div>
        </div>
      </div>

      {/* Filtro por categoria + modo de visualização */}
      {(() => {
        const cats = [...new Set((data.estoque || []).map(p => p.categoria).filter(Boolean))].sort();
        return (
          <div style={{ marginBottom: 16 }}>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 10 }}>
              <button onClick={() => setCatFiltro("todas")} style={{
              ...styles.btn("secondary"),
              fontSize: 12,
              fontWeight: 600,
              padding: "8px 16px",
              borderRadius: 10,
              background: catFiltro === "todas" ? `#8b5cf6` : C.bgElevated,
              border: `1px solid ${catFiltro === "todas" ? "#6b7280" : C.border}40`,
              transition: "all 0.2s"
            }}>Todas</button>
              {cats.map(c => (
                <button key={c} onClick={() => setCatFiltro(c)} style={{
                ...styles.btn("secondary"),
                fontSize: 12,
                fontWeight: 600,
                padding: "8px 16px",
                borderRadius: 10,
                background: catFiltro === c ? `#8b5cf6` : C.bgElevated,
                border: `1px solid ${catFiltro === c ? "#6b7280" : C.border}40`,
                transition: "all 0.2s"
              }}>{c}</button>
              ))}
              <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
                <button onClick={() => setViewMode("tabela")} style={{ ...styles.btn("secondary"), fontSize: 10, padding: "4px 10px", background: viewMode === "tabela" ? C.accent+"20" : "transparent", color: viewMode === "tabela" ? C.accent : C.muted }}>☰ Tabela</button>
                <button onClick={() => setViewMode("cards")} style={{ ...styles.btn("secondary"), fontSize: 10, padding: "4px 10px", background: viewMode === "cards" ? C.accent+"20" : "transparent", color: viewMode === "cards" ? C.accent : C.muted }}>⊞ Cards</button>
              </div>
            </div>
          </div>
        );
      })()}

      {showForm && (
        <div style={{ ...styles.card, marginBottom: 20, borderColor: C.info + "40" }}>
          <div style={styles.sectionTitle}>Cadastrar Produto</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 14 }}>
            {[
              { key: "produto", label: "Nome do Produto" },
              { key: "sku", label: "SKU / Código" },
              { key: "categoria", label: "Categoria", isSelect: true },
              { key: "estoque", label: "Qtd em Estoque", type: "number" },
              { key: "minimo", label: "Estoque Mínimo", type: "number" },
              { key: "custo", label: "Custo (R$)", type: "number" },
              { key: "preco", label: "Preço de Venda (R$)", type: "number" },
              { key: "ncm", label: "NCM (Código Fiscal)" },
            ].map((f) => (
              <div key={f.key}>
                <label style={styles.label}>{f.label}</label>
                <input style={styles.input} type={f.type || "text"} value={form[f.key]} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} />
              </div>
            ))}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button style={{...styles.btn("primary"), opacity: salvandoProduto ? 0.6 : 1}} onClick={handleAdd} disabled={salvandoProduto}>{salvandoProduto ? "Salvando..." : "Salvar Produto"}</button>
            <button style={styles.btn("secondary")} onClick={() => setShowForm(false)}>Cancelar</button>
          </div>
        </div>
      )}

      <div style={styles.card}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
          <div style={styles.sectionTitle}>Produtos em Estoque</div>
          {selEstoque.length > 0 && (
            <button onClick={async () => {
              if (!window.confirm(`Deletar ${selEstoque.length} produtos?`)) return;
              for (const id of selEstoque) { await sb.remove("estoque", id); }
              setData(d => ({ ...d, estoque: d.estoque.filter(p => !selEstoque.includes(p.id)) }));
              setSelEstoque([]);
            }} style={{ ...styles.btn("secondary"), fontSize: 10, padding: "4px 10px", color: C.warn, borderColor: C.warn }}>
              🗑️ Deletar {selEstoque.length}
            </button>
          )}
        </div>
        {viewMode === "cards" && (() => {
          const cats = catFiltro === "todas"
            ? [...new Set((data.estoque || []).map(p => p.categoria).filter(Boolean))].sort()
            : [catFiltro];
          const semCat = catFiltro === "todas" ? (data.estoque || []).filter(p => !p.categoria) : [];
          return (
            <div>
              {cats.map(cat => {
                const prods = (data.estoque || []).filter(p => p.categoria === cat && (buscaEstoque ? (p.produto||'').toLowerCase().includes(buscaEstoque.toLowerCase()) : true));
                if (prods.length === 0) return null;
                const valorCusto = prods.reduce((s, p) => s + (p.custo || 0) * (p.estoque || 0), 0);
                const criticos = prods.filter(p => p.estoque > 0 && p.estoque <= p.minimo).length;
                const esgotados = prods.filter(p => p.estoque === 0).length;
                return (
                  <div key={cat} style={{ marginBottom: 28 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12, paddingBottom: 8, borderBottom: "1px solid #e5e7eb" }}>
                      <div style={{ fontWeight: 700, fontSize: 14, color: "#111827" }}>{cat}</div>
                      <div style={{ fontSize: 10, color: "#374151" }}>{prods.length} produto(s)</div>
                      {criticos > 0 && <span style={{ fontSize: 9, background: C.warn+"20", color: C.warn, padding: "2px 8px", borderRadius: 4 }}>⚠️ {criticos} crítico(s)</span>}
                      {esgotados > 0 && <span style={{ fontSize: 9, background: "#FF3B3020", color: "#FF3B30", padding: "2px 8px", borderRadius: 4 }}>❌ {esgotados} esgotado(s)</span>}
                      <div style={{ marginLeft: "auto", fontSize: 11, color: "#374151" }}>Valor: <span style={{ color: C.accent, fontWeight: 700 }}>{privado ? "••••••" : fmt(valorCusto)}</span></div>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(190px, 1fr))", gap: 10 }}>
                      {prods.map(p => {
                        const status = p.estoque === 0 ? "Esgotado" : p.estoque <= p.minimo ? "Crítico" : "OK";
                        const statusColor = status === "OK" ? C.accent : status === "Crítico" ? C.warn : "#FF3B30";
                        const margem = p.preco > 0 ? ((p.preco - p.custo) / p.preco * 100).toFixed(0) : 0;
                        return (
                          <div key={p.id} style={{ background: C.card, border: `1px solid ${status !== "OK" ? statusColor+"50" : C.border}`, borderRadius: 16, padding: 14, position: "relative" }}>
                            <div style={{ position: "absolute", top: 8, right: 8, display: "flex", gap: 2 }}>
                              <button onClick={() => setCoresModal({ ...p })} title="Cores" style={{ background: "none", border: "none", cursor: "pointer", fontSize: 12 }}>🎨</button>
                              <button onClick={() => setEditandoEstoque({ ...p })} style={{ background: "none", border: "none", color: C.accent, cursor: "pointer", fontSize: 12 }}>✎</button>
                              <button onClick={() => handleDelete(p.id)} style={{ background: "none", border: "none", color: C.warn, cursor: "pointer", fontSize: 12 }}>✕</button>
                            </div>
                            <div style={{ fontWeight: 700, fontSize: 12, color: "#111827", marginBottom: 10, paddingRight: 36, lineHeight: 1.3 }}>{p.produto}</div>
                            {Array.isArray(p.cores) && p.cores.length > 0 && (() => {
                              const somaC = p.cores.reduce((t, c) => t + (Number(c.qtd) || 0), 0);
                              const dif = (Number(p.estoque) || 0) - somaC;
                              return (
                                <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 8 }}>
                                  {p.cores.map(c => <span key={c.cor} style={{ fontSize: 9, background: "#f3f4f6", color: "#374151", padding: "2px 6px", borderRadius: 4 }}>{c.cor} {c.qtd}</span>)}
                                  {dif !== 0 && <span style={{ fontSize: 9, background: "#fef3c7", color: "#b45309", padding: "2px 6px", borderRadius: 4 }}>{dif > 0 ? "+" + dif + " sem cor" : dif + " a conferir"}</span>}
                                </div>
                              );
                            })()}
                            <div style={{ display: "flex", alignItems: "baseline", gap: 6, marginBottom: 8 }}>
                              <span style={{ fontSize: 28, fontWeight: 900, color: statusColor, lineHeight: 1 }}>{p.estoque}</span>
                              <div>
                                <div style={{ fontSize: 9, color: "#374151" }}>unidades</div>
                                <div style={{ fontSize: 9, color: "#374151" }}>mín: {p.minimo}</div>
                              </div>
                            </div>
                            <span style={{ fontSize: 9, background: statusColor+"20", color: statusColor, padding: "2px 8px", borderRadius: 4, fontWeight: 600 }}>{status}</span>
                            <div style={{ borderTop: "1px solid #e5e7eb", paddingTop: 8, marginTop: 8 }}>
                              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, marginBottom: 2 }}>
                                <span style={{ color: "#374151" }}>Custo</span><span style={{ color: C.warn }}>{privado ? "••••" : fmt(p.custo)}</span>
                              </div>
                              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, marginBottom: 2 }}>
                                <span style={{ color: "#374151" }}>Preço</span><span style={{ color: C.accent, fontWeight: 700 }}>{privado ? "••••" : fmt(p.preco)}</span>
                              </div>
                              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10 }}>
                                <span style={{ color: "#374151" }}>Margem</span><span style={{ color: C.gold, fontWeight: 700 }}>{margem}%</span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
              {semCat.length > 0 && (
                <div style={{ marginBottom: 24 }}>
                  <div style={{ fontWeight: 700, fontSize: 14, color: "#374151", marginBottom: 12 }}>Sem categoria</div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(190px, 1fr))", gap: 10 }}>
                    {semCat.map(p => (
                      <div key={p.id} style={{ background: C.card, border: "1px solid #e5e7eb", borderRadius: 16, padding: 14 }}>
                        <div style={{ fontWeight: 700, fontSize: 12, color: "#111827", marginBottom: 6 }}>{p.produto}</div>
                        <div style={{ fontSize: 24, fontWeight: 900, color: "#374151", marginBottom: 6 }}>{p.estoque}</div>
                        <div style={{ display: "flex", gap: 4 }}>
                          <button onClick={() => setCoresModal({ ...p })} title="Cores" style={{ background: "none", border: "none", cursor: "pointer", fontSize: 12 }}>🎨</button>
                              <button onClick={() => setEditandoEstoque({ ...p })} style={{ background: "none", border: "none", color: C.accent, cursor: "pointer", fontSize: 12 }}>✎</button>
                          <button onClick={() => handleDelete(p.id)} style={{ background: "none", border: "none", color: C.warn, cursor: "pointer", fontSize: 12 }}>✕</button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })()}

        {viewMode === "tabela" && <table style={{ ...styles.table, borderCollapse: "separate", borderSpacing: 0 }}>
          <thead>
            <tr style={{ 
              background: `linear-gradient(90deg, ${C.surface}, ${C.bgElevated})`,
              borderBottom: `2px solid ${C.accent}40`
            }}>
              <th style={{ ...styles.th, width: 32, padding: "10px 12px" }}><input type="checkbox" onChange={() => setSelEstoque(s => s.length === data.estoque.length ? [] : data.estoque.map(p => p.id))} checked={selEstoque.length === data.estoque.length && data.estoque.length > 0} /></th>
              {["Produto", ...((data.estoque || []).some(x => x.sku) ? ["SKU"] : []), "Categoria", "Estoque", "Mín.", "Status", "Custo", "Preço", "Margem", ""].map((h) => <th key={h} style={{ ...styles.th, padding: "10px 14px", fontSize: 9, letterSpacing: 1.5, borderBottom: `2px solid ${C.accent}30` }}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {(buscaEstoque ? data.estoque.filter(p => (p.produto||'').toLowerCase().includes(buscaEstoque.toLowerCase()) || (p.categoria||'').toLowerCase().includes(buscaEstoque.toLowerCase()) || (p.sku||'').toLowerCase().includes(buscaEstoque.toLowerCase())) : data.estoque).filter(p => catFiltro === "todas" || p.categoria === catFiltro).sort((a, b) => (a.ordem || 999) - (b.ordem || 999)).map((p, idx) => {
              const status = p.estoque === 0 ? "Esgotado" : p.estoque <= p.minimo ? "Crítico" : "OK";
              const statusColor = status === "OK" ? C.accent : status === "Crítico" ? C.warn : "#FF3B30";
              const margem = p.preco > 0 ? (p.preco - p.custo) / p.preco : 0;
              const estoqueList = (buscaEstoque ? data.estoque.filter(x => (x.produto||'').toLowerCase().includes(buscaEstoque.toLowerCase()) || (x.categoria||'').toLowerCase().includes(buscaEstoque.toLowerCase()) || (x.sku||'').toLowerCase().includes(buscaEstoque.toLowerCase())) : data.estoque).filter(x => catFiltro === "todas" || x.categoria === catFiltro).sort((a, b) => (a.ordem || 999) - (b.ordem || 999));
              return (
                <tr key={p.id} draggable
                  onDragStart={() => setDraggedEstoque(p)}
                  onDragEnter={() => draggedEstoque && draggedEstoque.id !== p.id && setDragOverEstoque(p)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={async (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (!draggedEstoque || draggedEstoque.id === p.id) {
                      setDraggedEstoque(null);
                      setDragOverEstoque(null);
                      return;
                    }
                    const targetIdx = estoqueList.findIndex(x => x.id === p.id);
                    const draggedIdx = estoqueList.findIndex(x => x.id === draggedEstoque.id);
                    
                    // Renumerar TODA a lista para evitar conflitos
                    const reordered = [...estoqueList];
                    const [movedItem] = reordered.splice(draggedIdx, 1);
                    reordered.splice(targetIdx, 0, movedItem);
                    
                    // Atualizar UI instantaneamente
                    const updates = {};
                    reordered.forEach((item, idx) => {
                      updates[item.id] = idx * 1000;
                    });
                    
                    setData(d => ({
                      ...d,
                      estoque: d.estoque.map(x =>
                        updates[x.id] !== undefined ? { ...x, ordem: updates[x.id] } : x
                      )
                    }));
                    
                    setDraggedEstoque(null);
                    setDragOverEstoque(null);
                    
                    // Salvar TODAS as ordens de uma vez
                    Promise.all(
                      Object.entries(updates).map(([id, ordem]) =>
                        sb.update("estoque", id, { ordem })
                      )
                    );
                  }}
                  onDragEnd={() => { setDraggedEstoque(null); setDragOverEstoque(null); }}
                  style={{ background: selEstoque.includes(p.id) ? "#f9fafb" : C.surface, transition: "all 0.2s", opacity: draggedEstoque?.id === p.id ? 0.3 : 1, cursor: draggedEstoque?.id === p.id ? 'grabbing' : 'grab', borderTop: dragOverEstoque?.id === p.id ? `3px solid ${C.accent}` : 'none', transform: draggedEstoque?.id === p.id ? 'scale(0.98)' : 'scale(1)' }}
>
                  <td style={{ ...styles.td, padding: "10px 12px", borderBottom: "1px solid #e5e7eb" }}><input type="checkbox" checked={selEstoque.includes(p.id)} onChange={() => toggleSelE(p.id)} /></td>
                  <td style={{ ...styles.td, padding: "10px 14px", borderBottom: "1px solid #e5e7eb", fontWeight: 600, color: "#111827" }}>{p.produto}</td>
                  {(data.estoque || []).some(x => x.sku) && <td style={{ ...styles.td, padding: "10px 14px", borderBottom: "1px solid #e5e7eb", color: "#374151", fontSize: 11 }}>{p.sku || "—"}</td>}
                  <td style={{ ...styles.td, padding: "10px 14px", borderBottom: "1px solid #e5e7eb", color: "#374151" }}>{p.categoria || "—"}</td>
                  <td style={{ ...styles.td, padding: "10px 14px", borderBottom: "1px solid #e5e7eb", fontWeight: 700, color: status === "OK" ? "#111827" : C.warn, fontSize: 14 }}>{p.estoque}</td>
                  <td style={{ ...styles.td, padding: "10px 14px", borderBottom: "1px solid #e5e7eb", color: "#374151" }}>{p.minimo}</td>
                  <td style={{ ...styles.td, padding: "10px 14px", borderBottom: "1px solid #e5e7eb" }}><span style={{ ...styles.badge(statusColor), fontSize: 9, padding: "2px 8px" }}>{status}</span></td>
                  <td style={{ ...styles.td, padding: "10px 14px", borderBottom: "1px solid #e5e7eb", color: C.warn }}>{privado ? "••••" : fmt(p.custo)}</td>
                  <td style={{ ...styles.td, padding: "10px 14px", borderBottom: "1px solid #e5e7eb", color: C.accent, fontWeight: 700 }}>{privado ? "••••" : (Number(p.preco) > 0 ? fmt(p.preco) : "—")}</td>
                  <td style={{ ...styles.td, padding: "10px 14px", borderBottom: "1px solid #e5e7eb", color: C.gold, fontWeight: 700 }}>{Number(p.preco) > 0 ? pct(margem) : "—"}</td>
                  <td style={{ ...styles.td, padding: "8px 10px", borderBottom: "1px solid #e5e7eb" }}>
                    <div style={{ display: "flex", gap: 4 }}>
                      <button onClick={() => setCoresModal({ ...p })} title="Cores" style={{ background: "none", border: "none", cursor: "pointer", fontSize: 12 }}>🎨</button>
                              <button onClick={() => setEditandoEstoque({ ...p })} style={{ background: "none", border: "none", color: C.accent, cursor: "pointer", fontSize: 14, padding: 2 }}>✎</button>
                      <button onClick={() => handleDelete(p.id)} style={{ background: "none", border: "none", color: C.warn, cursor: "pointer", fontSize: 14, padding: 2 }}>✕</button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>}
      </div>
      </div>}
    </div>
  );
}

// ── COMPRAS PAGE ──────────────────────────────────────────────
function Compras({ data, setData, privado, showToast }) {
  const [showAnalise, setShowAnalise] = useState(false);
  const [comBrindesC, setComBrindesC] = useState(false);
  const [semanasCobertura, setSemanasCobertura] = useState(3);
  const [draggedCompra, setDraggedCompra] = useState(null);
  const [dragOverCompra, setDragOverCompra] = useState(null);
  const [editFormItens, setEditFormItens] = useState([]);
  const keyRef = useRef(Date.now());
  const emptyItemC = () => ({ produto: "", qtd: "1", custo: "", _key: ++keyRef.current });
  const fornecedoresSalvos = [...new Set(data.compras.map(c => c.fornecedor).filter(Boolean))];
  const [fornecedor, setFornecedor] = useState("");
  const [statusPedido, setStatusPedido] = useState("Pendente");
  const [dataPedido, setDataPedido] = useState(hojeManaus());
  const [itensC, setItensC] = useState([emptyItemC()]);
  const [showForm, setShowForm] = useState(false);
  const [salvandoCompra, setSalvandoCompra] = useState(false);
  const [buscaCompras, setBuscaCompras] = useState("");
  const [mesFiltroC, setMesFiltroC] = useState("todos");
  const [sortDirCompras, setSortDirCompras] = useState("desc");
  const mesesC = [...new Set(data.compras.map(c => c.data?.slice(0,7)).filter(Boolean))].sort().reverse();
  const comprasPorMes = mesFiltroC === "todos" ? data.compras : data.compras.filter(c => c.data?.startsWith(mesFiltroC));
  const comprasComBusca = buscaCompras ? comprasPorMes.filter(c => (c.fornecedor||'').toLowerCase().includes(buscaCompras.toLowerCase()) || (c.produto||'').toLowerCase().includes(buscaCompras.toLowerCase())) : comprasPorMes;
  const comprasFiltradas = [...comprasComBusca].sort((a, b) => {
    const dateA = new Date(a.data || '1970-01-01');
    const dateB = new Date(b.data || '1970-01-01');
    return sortDirCompras === 'desc' ? dateB - dateA : dateA - dateB;
  });
  const [selCompras, setSelCompras] = useState([]);
  const [expandidosC, setExpandidosC] = useState([]);
  const toggleSelC = (id) => setSelCompras(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
  const toggleAllCompras = () => setSelCompras(s => s.length === comprasFiltradas.length ? [] : comprasFiltradas.map(c => c.id));
  const deletarSelCompras = async () => {
    if (!selCompras.length) return;
    if (!window.confirm(`Deletar ${selCompras.length} pedidos selecionados?`)) return;
    for (const id of selCompras) { await sb.remove("compras", id); }
    setData(d => ({ ...d, compras: d.compras.filter(c => !selCompras.includes(c.id)) }));
    setSelCompras([]);
  };

  const addItemC = () => {
    console.log("🔍 Clicou adicionar produto. Itens atuais:", itensC.length);
    const novoItem = emptyItemC();
    console.log("🔍 Novo item criado:", novoItem);
    setItensC(i => {
      const updated = [...i, novoItem];
      console.log("🔍 Itens após adicionar:", updated);
      return updated;
    });
  };
  const removeItemC = (idx) => setItensC(i => i.filter((_, j) => j !== idx));
  const updateItemC = (idx, key, val) => setItensC(i => i.map((it, j) => j === idx ? {...it, [key]: val} : it));
  const prodComCores = (nome) => { const pr = (data.estoque || []).find(x => x.produto === nome); return pr && Array.isArray(pr.cores) && pr.cores.length > 0 ? pr : null; };
  const aplicarQtdCor = (it, cor, val) => {
    const base = Array.isArray(it.cores) ? it.cores : [];
    const cores = base.some(x => x.cor === cor) ? base.map(x => x.cor === cor ? { ...x, qtd: val } : x) : [...base, { cor, qtd: val }];
    return { ...it, cores, qtd: String(cores.reduce((t, x) => t + (Number(x.qtd) || 0), 0)) };
  };
  const temCorPreenchida = (it) => Array.isArray(it.cores) && it.cores.some(x => Number(x.qtd) > 0);

  // eslint-disable-next-line
  const totalCompras = data.compras.reduce((s, c) => s + (Number(c.total) || Number(c.valor) || 0), 0);
  // eslint-disable-next-line
  const recebido = data.compras.filter((c) => c.status === "Recebido").reduce((s, c) => s + (Number(c.total) || Number(c.valor) || 0), 0);
  // eslint-disable-next-line
  const aCaminho = data.compras.filter((c) => c.status === "A caminho").reduce((s, c) => s + (Number(c.total) || Number(c.valor) || 0), 0);
  // eslint-disable-next-line
  const pendente = data.compras.filter((c) => c.status === "Pendente").reduce((s, c) => s + (Number(c.total) || Number(c.valor) || 0), 0);

  const handleDelete = async (id) => {
    if (!window.confirm("Deletar este pedido?")) return;
    await sb.remove("compras", id);
    setData(d => ({ ...d, compras: d.compras.filter(c => c.id !== id) }));
  };

  const handleAdd = async () => {
    if (salvandoCompra) return;
    if (!fornecedor) return alert("Preencha o fornecedor!");
    setSalvandoCompra(true);
    const validos = itensC.filter(i => i.produto && i.qtd && i.custo);
    if (validos.length === 0) return alert("Preencha pelo menos um produto!");
    const descricao = validos.map(i => `${i.qtd}x ${i.produto}`).join(" + ");
    const totalValor = validos.reduce((s, i) => s + Number(i.qtd) * Number(i.custo), 0);
    const totalQtd = validos.reduce((s, i) => s + Number(i.qtd), 0);
    const itensData = validos.map(i => ({ produto: i.produto, qtd: Number(i.qtd), custo: Number(i.custo), ...(Array.isArray(i.cores) && i.cores.length ? { cores: i.cores.map(c => ({ cor: c.cor, qtd: Number(c.qtd) || 0 })).filter(c => c.qtd > 0) } : {}) }));
    const custoMedio = totalQtd > 0 ? totalValor / totalQtd : 0;
    const row = { data: dataPedido, fornecedor, descricao, valor: totalValor, qtd: totalQtd, custo: custoMedio, status: statusPedido, itens: itensData };
    const result = await sb.insert("compras", row);
    const inserted = Array.isArray(result) ? result[0] : result;
    const novo = { ...inserted, produto: inserted.descricao, qtd: totalQtd, total: inserted.valor, custo: custoMedio, itens: itensData };

    // Lança compra no caixa automaticamente
    await autoLancarCaixa("saida", descricao, totalValor, dataPedido, "compras_fornecedores", data.caixa_config);

    // Se já criou como Recebido, dar entrada no estoque
    if (statusPedido === "Recebido") {
      setData(d => {
        let estoqueAtual = [...d.estoque];
        for (const item of itensData) {
          const idx = estoqueAtual.findIndex(p => p.produto?.toLowerCase() === item.produto?.toLowerCase());
          if (idx >= 0) {
            const novaQtd = (estoqueAtual[idx].estoque || 0) + Number(item.qtd);
            const ncCompra = aplicarCoresCompra(estoqueAtual[idx], item.cores, 1);
            estoqueAtual[idx] = { ...estoqueAtual[idx], estoque: novaQtd, qtd: novaQtd, ...(ncCompra ? { cores: ncCompra } : {}) };
            sb.update("estoque", estoqueAtual[idx].id, { qtd: novaQtd, ...(ncCompra ? { cores: ncCompra } : {}) });
          }
        }
        return { ...d, compras: [...d.compras, novo], estoque: estoqueAtual };
      });
    } else {
      setData(d => ({ ...d, compras: [...d.compras, novo] }));
    }
    setSalvandoCompra(false);
    setItensC([emptyItemC()]);
    setFornecedor("");
    setShowForm(false);
    if (showToast) showToast("Pedido salvo!");
  };

  const [editandoCompra, setEditandoCompra] = useState(null);
  const [viewModeC, setViewModeC] = useState("tabela");
  const [filtroFornecedor, setFiltroFornecedor] = useState("todos");
  const handleEditStatus = async (id, novoStatus) => {
    const compra = data.compras.find(c => c.id === id);
    const itensCompra = compra?.itens || (compra?.produto ? [{ produto: compra.produto, qtd: compra.qtd || 1, custo: compra.custo || 0 }] : []);

    if (novoStatus === "Recebido" && compra?.status !== "Recebido") {
      // Salvar custo anterior de cada item no estoque ANTES de alterar
      const custoAnterior = {};
      data.estoque.forEach(p => { custoAnterior[p.produto?.toLowerCase()] = p.custo; });
      await sb.update("compras", id, { status: novoStatus, custo_anterior: custoAnterior });
    } else {
      await sb.update("compras", id, { status: novoStatus });
    }

    setData(d => {
      let estoqueAtual = [...d.estoque];

      if (novoStatus === "Recebido" && compra?.status !== "Recebido") {
        for (const item of itensCompra) {
          const idx = estoqueAtual.findIndex(p => p.produto?.toLowerCase() === item.produto?.toLowerCase());
          if (idx >= 0) {
            const qtdAtual = estoqueAtual[idx].estoque || 0;
            const custoAtual = estoqueAtual[idx].custo || 0;
            const qtdNova = Number(item.qtd);
            const custoNovo = Number(item.custo) || custoAtual;
            const novaQtd = qtdAtual + qtdNova;
            const novoCusto = novaQtd > 0 ? ((qtdAtual * custoAtual) + (qtdNova * custoNovo)) / novaQtd : custoNovo;
            const ncRec = aplicarCoresCompra(estoqueAtual[idx], item.cores, 1);
            estoqueAtual[idx] = { ...estoqueAtual[idx], estoque: novaQtd, qtd: novaQtd, custo: novoCusto, ...(ncRec ? { cores: ncRec } : {}) };
            sb.update("estoque", estoqueAtual[idx].id, { qtd: novaQtd, preco_custo: novoCusto, ...(ncRec ? { cores: ncRec } : {}) });
          }
        }
      }

      if (compra?.status === "Recebido" && novoStatus !== "Recebido") {
        const custoAnterior = compra?.custo_anterior || {};
        for (const item of itensCompra) {
          const idx = estoqueAtual.findIndex(p => p.produto?.toLowerCase() === item.produto?.toLowerCase());
          if (idx >= 0) {
            const novaQtd = Math.max(0, (estoqueAtual[idx].estoque || 0) - Number(item.qtd));
            // Restaurar custo anterior se disponível
            const custoRestaurado = custoAnterior[item.produto?.toLowerCase()] || estoqueAtual[idx].custo;
            const ncDes = aplicarCoresCompra(estoqueAtual[idx], item.cores, -1);
            estoqueAtual[idx] = { ...estoqueAtual[idx], estoque: novaQtd, qtd: novaQtd, custo: custoRestaurado, ...(ncDes ? { cores: ncDes } : {}) };
            sb.update("estoque", estoqueAtual[idx].id, { qtd: novaQtd, preco_custo: custoRestaurado, ...(ncDes ? { cores: ncDes } : {}) });
          }
        }
      }

      return { ...d, compras: d.compras.map(c => c.id === id ? { ...c, status: novoStatus, custo_anterior: novoStatus === "Recebido" ? Object.fromEntries(d.estoque.map(p => [p.produto?.toLowerCase(), p.custo])) : c.custo_anterior } : c), estoque: estoqueAtual };
    });
  };
  const handleEditCompra = async (form) => {
    const validos = (form.itens || []).filter(i => i.produto && i.qtd);
    const totalQtd = validos.reduce((s, i) => s + Number(i.qtd), 0);
    const totalValor = validos.reduce((s, i) => s + Number(i.custo) * Number(i.qtd), 0);
    const descricao = validos.length > 0 ? validos.map(i => `${i.qtd}x ${i.produto}`).join(" + ") : form.produto;
    const row = {
      fornecedor: form.fornecedor,
      descricao,
      status: form.status,
      data: form.data,
      qtd: totalQtd || editandoCompra.qtd,
      valor: totalValor || editandoCompra.valor,
      custo: totalQtd > 0 ? totalValor / totalQtd : editandoCompra.custo,
      itens: validos.map(i => ({ produto: i.produto, qtd: Number(i.qtd), custo: Number(i.custo) || 0, ...(Array.isArray(i.cores) && i.cores.length ? { cores: i.cores.map(c => ({ cor: c.cor, qtd: Number(c.qtd) || 0 })).filter(c => c.qtd > 0) } : {}) })),
    };
    await sb.update("compras", editandoCompra.id, row);
    setData(d => ({ ...d, compras: d.compras.map(c => c.id === editandoCompra.id ? { ...c, ...row, produto: row.descricao, total: row.valor } : c) }));
    setEditandoCompra(null);
    if (showToast) showToast("Pedido atualizado!");
  };

  const statusColor = { Recebido: C.accent, "A caminho": C.gold, Pendente: C.muted };


  // Analise de compra
  const hoje = hojeManaus();
  const d7 = new Date(hoje + "T12:00:00"); d7.setDate(d7.getDate() - 7);
  const d30 = new Date(hoje + "T12:00:00"); d30.setDate(d30.getDate() - 30);
  const data7s = d7.toISOString().slice(0,10);
  const data30s = d30.toISOString().slice(0,10);
  const vendas7c = data.vendas.filter(v => v.data >= data7s && v.data <= hoje);
  const vendas30c = data.vendas.filter(v => v.data >= data30s && v.data <= hoje);
  function contarItens(vendas) {
    var counts = {};
    var brindePP = {};
    for (var v of vendas) {
      var itens = Array.isArray(v.itens) && v.itens.length > 0 ? v.itens : [{ produto: v.descricao||'', qtd: v.qtd||1, valor: v.valor||0, custo: v.custo||0 }];
      var principais = itens.filter(it => Number(it.valor||0) > 0);
      var brindes = itens.filter(it => Number(it.valor||0) === 0);
      var qtdPrinc = principais.reduce((s,it) => s + Number(it.qtd||1), 0);
      for (var it of itens) {
        var p = it.produto||''; if (!p) continue;
        if (!counts[p]) counts[p] = { total: 0, brinde: 0 };
        counts[p].total += Number(it.qtd||1);
        if (it.cor) { counts[p].cores = counts[p].cores || {}; counts[p].cores[it.cor] = (counts[p].cores[it.cor] || 0) + Number(it.qtd||1); }
        if (Number(it.valor||0) === 0) counts[p].brinde += Number(it.qtd||1);
      }
      if (qtdPrinc > 0) for (var b of brindes) {
        if (!brindePP[b.produto]) brindePP[b.produto] = { qtd: 0, princ: 0 };
        brindePP[b.produto].qtd += Number(b.qtd||1);
        brindePP[b.produto].princ += qtdPrinc;
      }
    }
    for (var prod of Object.keys(brindePP)) {
      if (!counts[prod]) counts[prod] = { total: 0, brinde: 0 };
      counts[prod].taxaBrinde = brindePP[prod].princ > 0 ? brindePP[prod].qtd / brindePP[prod].princ : 0;
      counts[prod].totalPrinc = brindePP[prod].princ;
    }
    return counts;
  }
  const v7c = contarItens(vendas7c);
  const v30c = contarItens(vendas30c);
  const aCaminhoC = {};
  const aCaminhoCor = {};
  data.compras.filter(c => c.status === 'A caminho').forEach(c => {
    (Array.isArray(c.itens) ? c.itens : []).forEach(it => {
      if (it.produto && it.qtd) aCaminhoC[it.produto] = (aCaminhoC[it.produto] || 0) + Number(it.qtd);
      if (it.produto && Array.isArray(it.cores)) it.cores.forEach(cc => { const k = it.produto + '||' + String(cc.cor).toLowerCase(); aCaminhoCor[k] = (aCaminhoCor[k] || 0) + Number(cc.qtd || 0); });
    });
  });
  const analiseCompra = data.estoque.map(p => {
    var dados7 = v7c[p.nome] || { total: 0 };
    var dados30 = v30c[p.nome] || { total: 0 };
    var ritmo7 = dados7.total / 7;
    var ritmo30 = dados30.total / 30;
    var taxaBrinde = dados7.taxaBrinde || dados30.taxaBrinde || 0;
    var isBrinde = Number(p.preco_venda) === 0 && taxaBrinde > 0;
    var ritmoAtual = isBrinde && dados7.totalPrinc > 0 ? (dados7.totalPrinc/7)*taxaBrinde : ritmo7*0.7 + ritmo30*0.3;
    var acaminho = aCaminhoC[p.nome] || 0;
    var qtdTotal = Number(p.qtd) + acaminho;
    var diasRest = ritmoAtual > 0 ? Math.floor(qtdTotal / ritmoAtual) : 999;
    var precisaPara21 = Math.ceil(ritmoAtual * 7 * semanasCobertura);
    var minimo = p.nome.toLowerCase().includes('pulseira') ? 100 : p.nome.toLowerCase().includes('fone') ? 20 : 5;
    var sugerir = Math.max(0, precisaPara21 - qtdTotal);
    if (sugerir > 0 && sugerir < minimo) sugerir = minimo;
    var custoCompra = sugerir * Number(p.preco_custo);
    var porCor = null;
    var corSemHist = false;
    if (Array.isArray(p.cores) && p.cores.length > 0 && !isBrinde) {
      var c7 = dados7.cores || {}, c30 = dados30.cores || {};
      var temHist = Object.keys(c7).length > 0 || Object.keys(c30).length > 0;
      corSemHist = !temHist;
      porCor = p.cores.map(cc => {
        var r7 = (Number(c7[cc.cor]) || 0) / 7, r30 = (Number(c30[cc.cor]) || 0) / 30;
        var rc = temHist ? (r7 * 0.7 + r30 * 0.3) : (ritmoAtual / p.cores.length);
        var ac = aCaminhoCor[p.nome + '||' + String(cc.cor).toLowerCase()] || 0;
        var q = Number(cc.qtd) || 0;
        var need = Math.max(0, Math.ceil(rc * 7 * semanasCobertura) - q - ac);
        return { cor: cc.cor, qtd: q, acaminho: ac, ritmoSem: rc * 7, sugerir: need, dias: rc > 0 ? Math.floor((q + ac) / rc) : 999 };
      });
      sugerir = porCor.reduce((t, x) => t + x.sugerir, 0);
      if (sugerir > 0 && sugerir < minimo) {
        var topo = porCor.slice().sort((a, b) => b.ritmoSem - a.ritmoSem)[0];
        topo.sugerir += minimo - sugerir;
        sugerir = minimo;
      }
      custoCompra = sugerir * Number(p.preco_custo);
      var vivos = porCor.filter(x => x.dias < 999);
      if (vivos.length) diasRest = Math.min.apply(null, vivos.map(x => x.dias));
    }
    return { porCor, corSemHist, nome: p.nome, qtd: Number(p.qtd), acaminho, ritmo7sem: ritmo7*7, ritmo30sem: ritmo30*7, diasRest, sugerir, custoCompra, custo: Number(p.preco_custo), venda: Number(p.preco_venda), isBrinde };
  }).filter(p => p.ritmo7sem > 0 || p.ritmo30sem > 0).sort((a,b) => a.diasRest - b.diasRest);
  const totalSugerido = analiseCompra.reduce((s,p) => s + p.custoCompra, 0);
  const rankCores = (() => {
    const acc = {};
    Object.values(v30c).forEach(x => { Object.entries(x.cores || {}).forEach(([cor, q]) => { const k = cor.toLowerCase(); acc[k] = acc[k] || { cor, q: 0 }; acc[k].q += q; }); });
    return Object.values(acc).sort((a, b) => b.q - a.q);
  })();

  return (
    <div>
      {/* Analise de Compra */}
      <div style={{ background: '#ffffff', border: '1px solid #e5e7eb', borderRadius: 12, marginBottom: 20 }}>
        <div onClick={() => setShowAnalise(s => !s)} style={{ padding: '14px 20px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>Analise de Compra — Sugestao para 3 semanas</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontSize: 12, color: totalSugerido > 10000 ? '#b91c1c' : totalSugerido < 5000 ? '#d97706' : '#15803d', fontWeight: 600 }}>Total sugerido: {privado ? '••••' : fmt(totalSugerido)}</span>
            <span style={{ fontSize: 14 }}>{showAnalise ? '▲' : '▼'}</span>
          </div>
        </div>
        {showAnalise && (
          <div style={{ padding: '0 20px 16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 12, flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 11, color: '#6b7280' }}>Cobertura:</span>
                {[1,2,3,4,6,8].map(s => (
                  <button key={s} onClick={() => setSemanasCobertura(s)} style={{ ...styles.btn(semanasCobertura === s ? 'primary' : 'secondary'), fontSize: 11, padding: '4px 10px' }}>{s}sem</button>
                ))}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 11, color: '#6b7280' }}>Com brindes</span>
              <div onClick={() => setComBrindesC(b => !b)} style={{ width: 36, height: 20, borderRadius: 10, background: comBrindesC ? '#111827' : '#e5e7eb', cursor: 'pointer', position: 'relative' }}>
                <div style={{ position: 'absolute', top: 2, left: comBrindesC ? 18 : 2, width: 16, height: 16, borderRadius: '50%', background: '#fff', transition: 'left 0.2s' }} />
              </div>
              </div>
            </div>
            {rankCores.length > 0 && (
              <div style={{ fontSize: 11, color: '#374151', marginBottom: 10, display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
                <span style={{ color: '#6b7280' }}>Cores que mais saem (30 dias):</span>
                {rankCores.slice(0, 6).map((x, i) => <span key={x.cor} style={{ background: '#f3f4f6', padding: '2px 8px', borderRadius: 4 }}>{i + 1}º {x.cor} · {x.q} un</span>)}
              </div>
            )}
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                <thead><tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                  {['Produto','Estoque','A caminho','7d/sem','30d/sem','Dura','Sugerir','Custo'].map(h => <th key={h} style={{ textAlign:'left', padding:'6px 8px', fontSize:9, color:'#9ca3af', letterSpacing:1.5, textTransform:'uppercase', fontWeight:700 }}>{h}</th>)}
                </tr></thead>
                <tbody>
                  {analiseCompra.filter(p => comBrindesC || !p.isBrinde || p.sugerir > 0).map((p,i) => {
                    const urgencia = p.diasRest <= 7 ? '#b91c1c' : p.diasRest <= 14 ? '#d97706' : '#15803d';
                    return (
                      <tr key={i} style={{ borderBottom: '1px solid #f3f4f6' }}>
                        <td style={{ padding:'8px', fontWeight:600, color:'#111827' }}>{p.nome}{p.isBrinde ? ' 🎁' : ''}
                          {p.porCor && (
                            <div style={{ display:'flex', flexWrap:'wrap', gap:4, marginTop:4 }}>
                              {p.porCor.map(c => <span key={c.cor} style={{ fontSize:9, fontWeight:400, background:'#f3f4f6', color:'#374151', padding:'1px 6px', borderRadius:4 }}>{c.cor} {c.qtd}{c.dias < 999 ? ' · ' + c.dias + 'd' : ''}</span>)}
                            </div>
                          )}
                          {p.porCor && p.corSemHist && <div style={{ fontSize:9, fontWeight:400, color:'#b45309', marginTop:2 }}>sem vendas por cor ainda: divisão igual</div>}
                        </td>
                        <td style={{ padding:'8px', color:'#374151' }}>{p.qtd}</td>
                        <td style={{ padding:'8px', color: p.acaminho > 0 ? '#15803d' : '#9ca3af' }}>{p.acaminho > 0 ? '+' + p.acaminho : '—'}</td>
                        <td style={{ padding:'8px', color:'#374151' }}>{p.ritmo7sem.toFixed(1)}</td>
                        <td style={{ padding:'8px', color:'#374151' }}>{p.ritmo30sem.toFixed(1)}</td>
                        <td style={{ padding:'8px', fontWeight:700, color:urgencia }}>{p.diasRest < 999 ? p.diasRest + 'd' : 'OK'}</td>
                        <td style={{ padding:'8px', color: p.sugerir > 0 ? '#111827' : '#9ca3af', fontWeight: p.sugerir > 0 ? 600 : 400 }}>{p.sugerir > 0 ? p.sugerir + ' un' : '—'}
                          {p.porCor && p.sugerir > 0 && <div style={{ fontSize:10, fontWeight:400, color:'#6b7280' }}>{p.porCor.filter(c => c.sugerir > 0).map(c => c.cor + ' ' + c.sugerir).join(' · ')}</div>}
                        </td>
                        <td style={{ padding:'8px', color: p.sugerir > 0 ? '#b91c1c' : '#9ca3af' }}>{p.sugerir > 0 && !privado ? fmt(p.custoCompra) : '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {editandoCompra && (() => {
        const itensInicial = editandoCompra.itens && editandoCompra.itens.length > 0
          ? editandoCompra.itens.map(i => ({ produto: i.produto, qtd: String(i.qtd), custo: String(i.custo), cores: Array.isArray(i.cores) ? i.cores.map(c => ({ cor: c.cor, qtd: String(c.qtd) })) : [], _key: Math.random() }))
          : [{ produto: editandoCompra.produto || "", qtd: String(editandoCompra.qtd || 1), custo: String(editandoCompra.custo || ""), _key: Math.random() }];
        
        // Inicializar editFormItens se vazio
        if (editFormItens.length === 0) {
          setEditFormItens(itensInicial);
        }
        
        let formC = { 
          fornecedor: editandoCompra.fornecedor || "", 
          status: editandoCompra.status || "Pendente", 
          data: editandoCompra.data || hojeManaus(), 
          itens: editFormItens.length > 0 ? editFormItens : itensInicial 
        };
        return (
          <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.5)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", top: 0, left: 0, right: 0, bottom: 0 }}>
            <div style={{ background: C.card, border: `1px solid ${C.accent}40`, borderRadius: 16, padding: 28, width: 620, maxWidth: "95vw", maxHeight: "90vh", overflowY: "auto" }}>
              <div style={{ fontWeight: 700, fontSize: 16, color: C.accent, marginBottom: 28 }}>✏️ Editar Pedido</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 16 }}>
                <div><label style={styles.label}>Fornecedor</label><input style={styles.input} type="text" defaultValue={formC.fornecedor} onChange={e => formC.fornecedor = e.target.value} /></div>
                <div><label style={styles.label}>Status</label><select style={styles.input} defaultValue={formC.status} onChange={e => formC.status = e.target.value}><option>Pendente</option><option>A caminho</option><option>Recebido</option><option>Cancelado</option></select></div>
                <div><label style={styles.label}>Data</label><input style={styles.input} type="date" defaultValue={formC.data} onChange={e => formC.data = e.target.value} /></div>
              </div>
              <div style={{ fontSize: 11, color: "#374151", marginBottom: 8, letterSpacing: 1 }}>ITENS DO PEDIDO</div>
              <div style={{ background: C.bgElevated, borderRadius: 8, padding: 12, marginBottom: 16 }}>
                <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 24px", gap: 8, marginBottom: 6 }}>
                  {["Produto", "Qtd", "Custo Unit. (R$)", ""].map(h => <div key={h} style={{ fontSize: 9, color: "#374151", letterSpacing: 1 }}>{h}</div>)}
                </div>
                {formC.itens.map((it, idx) => (
                  <div key={it._key || idx} style={{ marginBottom: 8 }}>
                  <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 24px", gap: 8, marginBottom: 4 }}>
                    <select style={styles.input} value={it.produto} onChange={e => setEditFormItens(formC.itens.map((item, i) => i === idx ? {...item, produto: e.target.value, cores: []} : item))}>
                      <option value="">— Selecionar —</option>
                      {(data.estoque || []).map(p => <option key={p.id} value={p.produto}>{p.produto}</option>)}
                      {it.produto && !data.estoque.find(p => p.produto === it.produto) && <option value={it.produto}>{it.produto}</option>}
                    </select>
                    <input style={styles.input} type="number" value={it.qtd} disabled={temCorPreenchida(it)} onChange={e => setEditFormItens(formC.itens.map((item, i) => i === idx ? {...item, qtd: e.target.value} : item))} placeholder="1" />
                    <input style={styles.input} type="number" value={it.custo} onChange={e => setEditFormItens(formC.itens.map((item, i) => i === idx ? {...item, custo: e.target.value} : item))} placeholder="0,00" />
                    {formC.itens.length > 1
                      ? <button onClick={() => setEditFormItens(formC.itens.filter((_,j) => j !== idx))} style={{ background: "none", border: "none", color: C.warn, cursor: "pointer", fontSize: 16 }}>✕</button>
                      : <div />}
                  </div>
                  {(() => {
                    const pc = prodComCores(it.produto);
                    if (!pc) return null;
                    return (
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
                        <span style={{ fontSize: 10, color: "#6b7280" }}>Quantidade por cor:</span>
                        {pc.cores.map(c => {
                          const atual = (Array.isArray(it.cores) ? it.cores : []).find(x => x.cor === c.cor);
                          return (
                            <label key={c.cor} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, color: "#374151" }}>
                              {c.cor}
                              <input type="number" min="0" placeholder="0" style={{ ...styles.input, width: 64, padding: "4px 6px" }} value={atual ? atual.qtd : ""} onChange={e => setEditFormItens(formC.itens.map((item, i) => i === idx ? aplicarQtdCor(item, c.cor, e.target.value) : item))} />
                            </label>
                          );
                        })}
                      </div>
                    );
                  })()}
                  </div>
                ))}
                <button onClick={() => setEditFormItens([...formC.itens, { produto: "", qtd: "1", custo: "", _key: Math.random() }])} style={{ ...styles.btn("secondary"), fontSize: 11, marginTop: 4 }}>+ Item</button>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button style={styles.btn("primary")} onClick={() => { handleEditCompra(formC); setEditFormItens([]); }}>💾 Salvar</button>
                <button style={styles.btn("secondary")} onClick={() => { setEditandoCompra(null); setEditFormItens([]); }}>Cancelar</button>
              </div>
            </div>
          </div>
        );
      })()}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 28 }}>
        <div>
          <div style={styles.pageTitle}>Compras & Fornecedores</div>
          <div style={styles.pageSub}>Pedidos de reposição</div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <select style={{ ...styles.input, width: "auto", fontSize: 11 }} value={mesFiltroC} onChange={e => setMesFiltroC(e.target.value)}>
            <option value="todos">Todos os meses</option>
            {mesesC.map(m => <option key={m} value={m}>{MESES[parseInt(m.slice(5,7))-1] + "/" + m.slice(2,4)}</option>)}
          </select>
          <input style={{ ...styles.input, width: 160, fontSize: 11 }} placeholder="🔍 Buscar..." value={buscaCompras} onChange={e => setBuscaCompras(e.target.value)} />
          <button style={styles.btn("primary")} onClick={() => setShowForm(!showForm)}>+ Novo Pedido</button>
        </div>
      </div>

      {/* KPIs COMPRAS */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 20, marginBottom: 28 }}>
        {/* Total Investido */}
        <div className="tfx-fade-in-delay-0 tfx-card-hover" style={{ 
          background: C.bgElevated,
          border: `2px solid ${C.gold}25`,
          borderRadius: 20,
          padding: 24,
          position: "relative",
          overflow: "hidden",
          boxShadow: `0 10px 40px ${C.gold}30`,
          transition: "all 0.3s"
        }}>
          <div style={{ position: "absolute", top: -30, right: -30, width: 120, height: 120, background: `radial-gradient(circle, ${C.gold}15, transparent)`, borderRadius: "50%" }} />
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
            <div style={{ width: 48, height: 48, borderRadius: 12, background: `${C.gold}20`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}>💰</div>
            <div style={{ fontSize: 9, color: "#374151", letterSpacing: 2, textTransform: "uppercase", fontWeight: 600 }}>Total Investido</div>
          </div>
          <div style={{ fontFamily: "'Inter', sans-serif", fontWeight: 800, fontSize: 28, color: C.gold, letterSpacing: -1.2 }}>
            {privado ? "••••••" : fmt(comprasFiltradas.reduce((s,c) => s + c.total, 0))}
          </div>
        </div>

        {/* Recebido */}
        <div className="tfx-fade-in-delay-1 tfx-card-hover" style={{ 
          background: C.bgElevated,
          border: `2px solid #22c55e25`,
          borderRadius: 20,
          padding: 24,
          position: "relative",
          overflow: "hidden",
          boxShadow: `0 10px 40px #22c55e30`,
          transition: "all 0.3s"
        }}>
          <div style={{ position: "absolute", top: -30, right: -30, width: 120, height: 120, background: `radial-gradient(circle, #22c55e15, transparent)`, borderRadius: "50%" }} />
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
            <div style={{ width: 48, height: 48, borderRadius: 12, background: `#22c55e20`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}>✅</div>
            <div style={{ fontSize: 9, color: "#374151", letterSpacing: 2, textTransform: "uppercase", fontWeight: 600 }}>Recebido</div>
          </div>
          <div style={{ fontFamily: "'Inter', sans-serif", fontWeight: 800, fontSize: 28, color: "#15803d", letterSpacing: -1.2 }}>
            {privado ? "••••••" : fmt(comprasFiltradas.filter(c => c.status === "Recebido").reduce((s,c) => s + c.total, 0))}
          </div>
        </div>

        {/* A Caminho */}
        <div className="tfx-fade-in-delay-2 tfx-card-hover" style={{ 
          background: C.bgElevated,
          border: `2px solid #3b82f625`,
          borderRadius: 20,
          padding: 24,
          position: "relative",
          overflow: "hidden",
          boxShadow: `0 10px 40px #3b82f630`,
          transition: "all 0.3s"
        }}>
          <div style={{ position: "absolute", top: -30, right: -30, width: 120, height: 120, background: `radial-gradient(circle, #3b82f615, transparent)`, borderRadius: "50%" }} />
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
            <div style={{ width: 48, height: 48, borderRadius: 12, background: `#3b82f620`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}>🚚</div>
            <div style={{ fontSize: 9, color: "#374151", letterSpacing: 2, textTransform: "uppercase", fontWeight: 600 }}>A Caminho</div>
          </div>
          <div style={{ fontFamily: "'Inter', sans-serif", fontWeight: 800, fontSize: 28, color: "#374151", letterSpacing: -1.2 }}>
            {privado ? "••••••" : fmt(comprasFiltradas.filter(c => c.status === "A caminho").reduce((s,c) => s + c.total, 0))}
          </div>
        </div>

        {/* Pendente */}
        <div className="tfx-fade-in-delay-3 tfx-card-hover" style={{ 
          background: C.bgElevated,
          border: `2px solid ${C.warn}25`,
          borderRadius: 20,
          padding: 24,
          position: "relative",
          overflow: "hidden",
          boxShadow: `0 10px 40px ${C.warn}30`,
          transition: "all 0.3s"
        }}>
          <div style={{ position: "absolute", top: -30, right: -30, width: 120, height: 120, background: `radial-gradient(circle, ${C.warn}15, transparent)`, borderRadius: "50%" }} />
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
            <div style={{ width: 48, height: 48, borderRadius: 12, background: `${C.warn}20`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}>⏳</div>
            <div style={{ fontSize: 9, color: "#374151", letterSpacing: 2, textTransform: "uppercase", fontWeight: 600 }}>Pendente</div>
          </div>
          <div style={{ fontFamily: "'Inter', sans-serif", fontWeight: 800, fontSize: 28, color: C.warn, letterSpacing: -1.2 }}>
            {privado ? "••••••" : fmt(comprasFiltradas.filter(c => c.status === "Pendente").reduce((s,c) => s + c.total, 0))}
          </div>
        </div>
      </div>

      {showForm && (
        <div style={{ 
          background: C.bgElevated,
          border: `2px solid ${C.gold}30`,
          borderRadius: 20,
          padding: 32,
          marginBottom: 24,
          boxShadow: `0 10px 40px ${C.gold}20`
        }}>
          <div style={{ fontWeight: 800, fontSize: 20, color: C.gold, letterSpacing: -0.8, marginBottom: 24, display: "flex", alignItems: "center", gap: 12 }}>
            <span style={{ fontSize: 24 }}>📦</span> Registrar Pedido
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 16 }}>
            <div>
              <label style={styles.label}>Fornecedor</label>
              <input style={styles.input} list="lista-fornecedores" value={fornecedor} onChange={e => setFornecedor(e.target.value)} placeholder="Nome do fornecedor" autoComplete="off" />
              <datalist id="lista-fornecedores">
                {fornecedoresSalvos.map(f => <option key={f} value={f}>{f}</option>)}
              </datalist>
            </div>
            <div>
              <label style={styles.label}>Status</label>
              <select style={styles.input} value={statusPedido} onChange={e => setStatusPedido(e.target.value)}>
                <option>Pendente</option><option>A caminho</option><option>Recebido</option><option>Cancelado</option>
              </select>
            </div>
            <div>
              <label style={styles.label}>Data</label>
              <input style={styles.input} type="date" value={dataPedido} onChange={e => setDataPedido(e.target.value)} />
            </div>
          </div>
          <div style={{ marginBottom: 8 }}>
            <div style={{ display: "grid", gridTemplateColumns: "3fr 1fr 1fr 32px", gap: 8, marginBottom: 6 }}>
              {["Produto / Descrição","Qtd","Custo Unit. (R$)",""].map((h,i) => (
                <div key={i} style={{ fontSize: 9, color: "#374151", letterSpacing: 1, textTransform: "uppercase" }}>{h}</div>
              ))}
            </div>
            {itensC.map((item, idx) => (
              <div key={item._key ?? idx} style={{ marginBottom: 8 }}>
              <div style={{ display: "grid", gridTemplateColumns: "3fr 1fr 1fr 32px", gap: 8, marginBottom: 4 }}>
                <select style={{ ...styles.input, color: item.produto ? "#111827" : "#6b7280" }} value={item.produto} onChange={e => {
                  const nome = e.target.value;
                  const prod = data.estoque.find(p => p.produto === nome);
                  updateItemC(idx, "produto", nome);
                  updateItemC(idx, "cores", []);
                  if (prod) updateItemC(idx, "custo", String(prod.custo));
                }}>
                  <option value="">-- Selecionar produto --</option>
                  {(data.estoque || []).map(p => (
                    <option key={p.id} value={p.produto}>{p.produto} (estoque: {p.estoque} un)</option>
                  ))}
                </select>
                <input style={styles.input} type="number" placeholder="1" value={item.qtd} disabled={temCorPreenchida(item)} title={temCorPreenchida(item) ? "Soma das cores" : ""} onChange={e => updateItemC(idx,"qtd",e.target.value)} />
                <input style={styles.input} type="number" placeholder="0,00" value={item.custo} onChange={e => updateItemC(idx,"custo",e.target.value)} />
                <button onClick={() => removeItemC(idx)} style={{ background:"none", border:"none", color: itensC.length > 1 ? C.warn : C.border, cursor: itensC.length > 1 ? "pointer" : "default", fontSize:16 }} disabled={itensC.length === 1} title={itensC.length === 1 ? "Mínimo 1 item" : "Remover item"}>✕</button>
              </div>
              {(() => {
                const pc = prodComCores(item.produto);
                if (!pc) return null;
                return (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
                    <span style={{ fontSize: 10, color: "#6b7280" }}>Quantidade por cor:</span>
                    {pc.cores.map(c => {
                      const atual = (Array.isArray(item.cores) ? item.cores : []).find(x => x.cor === c.cor);
                      return (
                        <label key={c.cor} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, color: "#374151" }}>
                          {c.cor}
                          <input type="number" min="0" placeholder="0" style={{ ...styles.input, width: 64, padding: "4px 6px" }} value={atual ? atual.qtd : ""} onChange={e => setItensC(its => its.map((it, j) => j === idx ? aplicarQtdCor(it, c.cor, e.target.value) : it))} />
                        </label>
                      );
                    })}
                  </div>
                );
              })()}
              </div>
            ))}
            <button onClick={addItemC} style={{ ...styles.btn("secondary"), fontSize: 11, padding: "6px 12px", marginTop: 4 }}>+ Adicionar produto</button>
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
            <button style={{
              ...styles.btn("primary"),
              opacity: salvandoCompra ? 0.6 : 1,
              fontSize: 15,
              fontWeight: 700,
              padding: "14px 28px",
              borderRadius: 12,
              background: salvandoCompra ? C.muted : `linear-gradient(135deg, ${C.gold}, #fbbf24)`,
              boxShadow: salvandoCompra ? "none" : `0 4px 20px ${C.gold}40`,
              border: "none"
            }} onClick={handleAdd} disabled={salvandoCompra}>
              {salvandoCompra ? "⏳ Salvando..." : "✅ Salvar Pedido"}
            </button>
            <button style={styles.btn("secondary")} onClick={() => { setShowForm(false); setItensC([emptyItemC()]); setFornecedor(""); }}>Cancelar</button>
          </div>
        </div>
      )}


      {/* Filtro fornecedor + toggle view */}
      {(() => {
        const fornecedores = [...new Set(comprasFiltradas.map(c => c.fornecedor).filter(Boolean))].sort();
        return (
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 16 }}>
            <button onClick={() => setFiltroFornecedor("todos")} style={{
              ...styles.btn("secondary"),
              fontSize: 12,
              fontWeight: 600,
              padding: "8px 16px",
              borderRadius: 10,
              background: filtroFornecedor === "todos" ? `${C.warn}` : C.bgElevated,
              border: `1px solid ${filtroFornecedor === "todos" ? C.warn : C.border}40`,
              transition: "all 0.2s"
            }}>Todos</button>
            {fornecedores.map(f => (
              <button key={f} onClick={() => setFiltroFornecedor(f)} style={{
              ...styles.btn("secondary"),
              fontSize: 12,
              fontWeight: 600,
              padding: "8px 16px",
              borderRadius: 10,
              background: filtroFornecedor === f ? `${C.warn}` : C.bgElevated,
              border: `1px solid ${filtroFornecedor === f ? C.warn : C.border}40`,
              transition: "all 0.2s"
            }}>{f}</button>
            ))}
            <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
              <button onClick={() => setViewModeC("tabela")} style={{ ...styles.btn("secondary"), fontSize: 10, padding: "4px 10px", background: viewModeC === "tabela" ? C.accent+"20" : "transparent", color: viewModeC === "tabela" ? C.accent : C.muted }}>☰ Tabela</button>
              <button onClick={() => setViewModeC("cards")} style={{ ...styles.btn("secondary"), fontSize: 10, padding: "4px 10px", background: viewModeC === "cards" ? C.accent+"20" : "transparent", color: viewModeC === "cards" ? C.accent : C.muted }}>⊞ Cards</button>
            </div>
          </div>
        );
      })()}

      {/* Modo Cards */}
      {viewModeC === "cards" && (() => {
        const lista = comprasFiltradas.filter(c => filtroFornecedor === "todos" || c.fornecedor === filtroFornecedor);
        const fornecedores = filtroFornecedor === "todos"
          ? [...new Set(lista.map(c => c.fornecedor).filter(Boolean))].sort()
          : [filtroFornecedor];
        return (
          <div>
            {fornecedores.map(forn => {
              const pedidos = lista.filter(c => c.fornecedor === forn).sort((a, b) => {
                if (a.ordem !== undefined && b.ordem !== undefined) return a.ordem - b.ordem;
                if (a.ordem !== undefined) return -1;
                if (b.ordem !== undefined) return 1;
                return (b.data || "").localeCompare(a.data || "");
              });
              const totalInv = pedidos.reduce((s, c) => s + (Number(c.total) || Number(c.valor) || 0), 0);
              const pendentes = pedidos.filter(c => c.status === "Pendente" || c.status === "A caminho").length;
              return (
                <div key={forn} style={{ marginBottom: 24 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12, paddingBottom: 8, borderBottom: "1px solid #e5e7eb" }}>
                    <div style={{ fontWeight: 700, fontSize: 14, color: "#111827" }}>{forn}</div>
                    <div style={{ fontSize: 10, color: "#374151" }}>{pedidos.length} pedido(s)</div>
                    {pendentes > 0 && <span style={{ fontSize: 9, background: C.gold+"20", color: C.gold, padding: "2px 8px", borderRadius: 4 }}>⏳ {pendentes} em aberto</span>}
                    <div style={{ marginLeft: "auto", fontSize: 11, color: "#374151" }}>Total: <span style={{ color: C.accent, fontWeight: 700 }}>{privado ? "••••••" : fmt(totalInv)}</span></div>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 10 }}>
                    {pedidos.map(c => {
                      const itensP = c.itens && c.itens.length > 0 ? c.itens : (c.produto ? [{ produto: c.produto, qtd: c.qtd || 1, custo: c.custo || 0 }] : []);
                      const total = Number(c.total) || Number(c.valor) || 0;
                      const sc = statusColor[c.status] || C.muted;
                      return (
                        <div key={c.id} style={{ background: C.card, border: `1px solid ${sc}30`, borderRadius: 16, padding: 14, position: "relative" }}>
                          <div style={{ position: "absolute", top: 8, right: 8, display: "flex", gap: 2 }}>
                            <button onClick={() => setEditandoCompra({ ...c })} style={{ background: "none", border: "none", color: C.accent, cursor: "pointer", fontSize: 12 }}>✎</button>
                            <button onClick={() => handleDelete(c.id)} style={{ background: "none", border: "none", color: C.warn, cursor: "pointer", fontSize: 12 }}>✕</button>
                          </div>
                          <div style={{ fontSize: 9, color: "#374151", marginBottom: 4 }}>{c.data}</div>
                          <div style={{ fontWeight: 600, fontSize: 12, color: "#111827", marginBottom: 8, paddingRight: 36 }}>{c.produto}</div>
                          <span style={{ fontSize: 9, background: sc+"20", color: sc, padding: "2px 8px", borderRadius: 4, fontWeight: 600, marginBottom: 8, display: "inline-block" }}>{c.status}</span>
                          <div style={{ borderTop: "1px solid #e5e7eb", paddingTop: 8, marginTop: 8 }}>
                            {itensP.slice(0, 3).map((it, i) => (
                              <div key={i} style={{ display: "flex", justifyContent: "space-between", fontSize: 10, marginBottom: 2 }}>
                                <span style={{ color: "#374151", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 120 }}>{it.qtd}x {it.produto}</span>
                                <span style={{ color: "#111827" }}>{privado ? "••••" : fmt(Number(it.custo) * Number(it.qtd))}</span>
                              </div>
                            ))}
                            {itensP.length > 3 && <div style={{ fontSize: 9, color: "#374151" }}>+{itensP.length - 3} item(s)</div>}
                            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, fontWeight: 700, marginTop: 6, paddingTop: 6, borderTop: "1px solid #f3f4f6" }}>
                              <span style={{ color: "#374151" }}>Total</span>
                              <span style={{ color: C.gold }}>{privado ? "••••••" : fmt(total)}</span>
                            </div>
                          </div>
                          <div style={{ marginTop: 8 }}>
                            <select value={c.status} onChange={e => handleEditStatus(c.id, e.target.value)}
                              style={{ background: sc+"15", border: `1px solid ${sc}40`, color: sc, borderRadius: 6, padding: "3px 8px", fontSize: 10, cursor: "pointer", fontFamily: "'Inter', sans-serif", width: "100%" }}>
                              <option>Pendente</option><option>A caminho</option><option>Recebido</option><option>Cancelado</option>
                            </select>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        );
      })()}

      {viewModeC === "tabela" && <div style={{ 
        background: C.bgElevated,
        border: `2px solid ${C.border}30`,
        borderRadius: 20,
        padding: 28,
        boxShadow: "0 4px 30px rgba(0,0,0,0.3)"
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
          <div style={{ fontWeight: 800, fontSize: 18, color: "#111827", letterSpacing: -0.5 }}>Histórico de Pedidos</div>
          <button onClick={() => setSortDirCompras(d => d === "desc" ? "asc" : "desc")} style={{ ...styles.btn("secondary"), display: "flex", alignItems: "center", gap: 6, fontSize: 10 }}>
            Data {sortDirCompras === "desc" ? "↓ Mais recente" : "↑ Mais antiga"}
          </button>
          {selCompras.length > 0 && (
            <button onClick={deletarSelCompras} style={{ ...styles.btn("secondary"), fontSize: 10, padding: "4px 10px", color: C.warn, borderColor: C.warn }}>
              🗑️ Deletar {selCompras.length}
            </button>
          )}
        </div>
        <table style={styles.table}>
          <thead>
            <tr style={{ 
              background: `linear-gradient(90deg, ${C.surface}, ${C.bgElevated})`,
              borderBottom: `2px solid ${C.gold}40`
            }}>
              <th style={styles.th}><input type="checkbox" onChange={toggleAllCompras} checked={selCompras.length === comprasFiltradas.length && comprasFiltradas.length > 0} /></th>
              {["Data", "Fornecedor", "Produto", "Qtd", "Total", "Status", ""].map((h) => <th key={h} style={{ ...styles.th, padding: "10px 14px", fontSize: 9, letterSpacing: 1.5, borderBottom: `2px solid ${C.accent}30` }}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {comprasFiltradas.filter(c => filtroFornecedor === "todos" || c.fornecedor === filtroFornecedor).map((c, idx) => {
              const itensP = c.itens && c.itens.length > 0 ? c.itens : (c.produto ? [{ produto: c.produto, qtd: c.qtd || 1, custo: c.custo || 0 }] : []);
              const expandido = expandidosC.includes(c.id);
              const comprasList = comprasFiltradas.filter(x => filtroFornecedor === "todos" || x.fornecedor === filtroFornecedor);
              return (
                <>
                  <tr key={c.id} draggable
                    onDragStart={() => setDraggedCompra(c)}
                    onDragEnter={() => draggedCompra && draggedCompra.id !== c.id && setDragOverCompra(c)}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={async (e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (!draggedCompra || draggedCompra.id === c.id) {
                        setDraggedCompra(null);
                        setDragOverCompra(null);
                        return;
                      }
                      const targetIdx = comprasList.findIndex(x => x.id === c.id);
                      const draggedIdx = comprasList.findIndex(x => x.id === draggedCompra.id);
                      
                      // Renumerar TODA a lista
                      const reordered = [...comprasList];
                      const [movedItem] = reordered.splice(draggedIdx, 1);
                      reordered.splice(targetIdx, 0, movedItem);
                      
                      const updates = {};
                      reordered.forEach((item, idx) => {
                        updates[item.id] = idx * 1000;
                      });
                      
                      setData(d => ({
                        ...d,
                        compras: d.compras.map(x =>
                          updates[x.id] !== undefined ? { ...x, ordem: updates[x.id] } : x
                        )
                      }));
                      
                      setDraggedCompra(null);
                      setDragOverCompra(null);
                      
                      Promise.all(
                        Object.entries(updates).map(([id, ordem]) =>
                          sb.update("compras", id, { ordem })
                        )
                      );
                    }}
                    onDragEnd={() => { setDraggedCompra(null); setDragOverCompra(null); }}
                    style={{ background: selCompras.includes(c.id) ? "#f9fafb" : idx % 2 === 0 ? "transparent" : C.surface+"80", transition: "all 0.2s", opacity: draggedCompra?.id === c.id ? 0.3 : 1, cursor: draggedCompra?.id === c.id ? 'grabbing' : 'grab', borderTop: dragOverCompra?.id === c.id ? `3px solid ${C.accent}` : 'none', transform: draggedCompra?.id === c.id ? 'scale(0.98)' : 'scale(1)' }}
                    onMouseEnter={e => !draggedCompra && (e.currentTarget.style.background = "#fafafa")}
                    onMouseLeave={e => !draggedCompra && (e.currentTarget.style.background = selCompras.includes(c.id) ? "#f9fafb" : idx % 2 === 0 ? "transparent" : C.surface+"80")}>
                    <td style={{ ...styles.td, padding: "10px 12px", borderBottom: "1px solid #e5e7eb" }}><input type="checkbox" checked={selCompras.includes(c.id)} onChange={() => toggleSelC(c.id)} /></td>
                    <td style={{ ...styles.td, padding: "10px 14px", borderBottom: "1px solid #e5e7eb", color: "#374151", fontSize: 11 }}>{c.data}</td>
                    <td style={{ ...styles.td, padding: "10px 14px", borderBottom: "1px solid #e5e7eb", fontWeight: 600, color: "#111827" }}>{c.fornecedor}</td>
                    <td style={{ ...styles.td, padding: "10px 14px", borderBottom: "1px solid #e5e7eb" }}>
                      <button onClick={() => setExpandidosC(e => e.includes(c.id) ? e.filter(x => x !== c.id) : [...e, c.id])}
                        style={{ background: "none", border: "none", color: C.accent, cursor: "pointer", fontSize: 12, textAlign: "left", padding: 0 }}>
                        {itensP.length > 1 ? (expandido ? "▼ " : "▶ ") : ""}{c.produto}
                      </button>
                    </td>
                    <td style={{ ...styles.td, padding: "10px 14px", borderBottom: "1px solid #e5e7eb", color: "#374151" }}>{c.qtd}</td>
                    <td style={{ ...styles.td, padding: "10px 14px", borderBottom: "1px solid #e5e7eb", color: C.gold, fontWeight: 700 }}>{privado ? "••••" : fmt(Number(c.total) || Number(c.valor) || 0)}</td>
                    <td style={{ ...styles.td, padding: "10px 14px", borderBottom: "1px solid #e5e7eb" }}>
                      <select value={c.status} onChange={e => handleEditStatus(c.id, e.target.value)}
                        style={{ background: (statusColor[c.status] || C.muted) + "20", border: "1px solid " + (statusColor[c.status] || C.muted) + "50", color: statusColor[c.status] || C.muted, borderRadius: 6, padding: "3px 8px", fontSize: 10, cursor: "pointer", fontFamily: "'Inter', sans-serif" }}>
                        <option>Pendente</option><option>A caminho</option><option>Recebido</option><option>Cancelado</option>
                      </select>
                    </td>
                    <td style={{ ...styles.td, padding: "8px 10px", borderBottom: "1px solid #e5e7eb" }}>
                      <div style={{ display: "flex", gap: 4 }}>
                        <button onClick={() => setEditandoCompra({ ...c, produto: c.produto })} style={{ background: "none", border: "none", color: C.accent, cursor: "pointer", fontSize: 14, padding: 2 }}>✎</button>
                        <button onClick={() => handleDelete(c.id)} style={{ background: "none", border: "none", color: C.warn, cursor: "pointer", fontSize: 14, padding: 2 }}>✕</button>
                      </div>
                    </td>
                  </tr>
                  {expandido && itensP.map((it, i) => (
                    <tr key={c.id + "_" + i} style={{ background: C.surface }}>
                      <td style={{ ...styles.td, padding: "8px 12px", borderBottom: "1px solid #f3f4f6" }}></td>
                      <td style={{ ...styles.td, padding: "8px 14px", borderBottom: "1px solid #f3f4f6" }}></td>
                      <td style={{ ...styles.td, padding: "8px 14px", borderBottom: "1px solid #f3f4f6" }}></td>
                      <td style={{ ...styles.td, padding: "8px 14px", borderBottom: "1px solid #f3f4f6", color: "#374151", fontSize: 11, paddingLeft: 24 }}>↳ {it.produto}</td>
                      <td style={{ ...styles.td, padding: "8px 14px", borderBottom: "1px solid #f3f4f6", color: "#374151", fontSize: 11 }}>{it.qtd} un</td>
                      <td style={{ ...styles.td, padding: "8px 14px", borderBottom: "1px solid #f3f4f6", color: "#374151", fontSize: 11 }}>{privado ? "••••" : fmt(Number(it.custo) * Number(it.qtd))}</td>
                      <td style={{ ...styles.td, padding: "8px 14px", borderBottom: "1px solid #f3f4f6" }}></td>
                      <td style={{ ...styles.td, padding: "8px 14px", borderBottom: "1px solid #f3f4f6" }}></td>
                    </tr>
                  ))}
                </>
              );
            })}
          </tbody>
        </table>
      </div>}
    </div>
  );
}




// ── TROCAS & DEVOLUÇÕES ───────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════
// COMPONENTE TROCAS & DEVOLUÇÕES — VERSÃO 2
// Substitui o componente atual (linhas 5286 até o final dele).
// 
// MUDANÇAS PRINCIPAIS:
//   1. Campo "Produto NOVO" (o que o cliente leva pra casa)
//   2. Motivo agora é decisivo:
//      - Tamanho / Arrependimento → produto devolvido VOLTA pro estoque
//      - Defeito / Outro → produto devolvido NÃO volta
//   3. Quando motivo=defeito → cria entrada em "Devoluções ao Fornecedor"
//      automaticamente (fornecedor: "A definir", editável depois)
//   4. Diferença de preço opcional (entrada/saída no caixa)
//   5. Reembolso REMOVIDO — usa o fluxo de exclusão de venda
//   6. Substituição do fornecedor abre modal: você escolhe qual produto entrou
//      no estoque (pode ser diferente do original)
//   7. Bug do delete corrigido: usa o retorno {ok:true/false} do sb.remove
// ═══════════════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════════════
// COMPONENTE TROCAS & DEVOLUÇÕES — VERSÃO 2
// Substitui o componente atual (linhas 5286 até o final dele).
// 
// MUDANÇAS PRINCIPAIS:
//   1. Campo "Produto NOVO" (o que o cliente leva pra casa)
//   2. Motivo agora é decisivo:
//      - Tamanho / Arrependimento → produto devolvido VOLTA pro estoque
//      - Defeito / Outro → produto devolvido NÃO volta
//   3. Quando motivo=defeito → cria entrada em "Devoluções ao Fornecedor"
//      automaticamente (fornecedor: "A definir", editável depois)
//   4. Diferença de preço opcional (entrada/saída no caixa)
//   5. Reembolso REMOVIDO — usa o fluxo de exclusão de venda
//   6. Substituição do fornecedor abre modal: você escolhe qual produto entrou
//      no estoque (pode ser diferente do original)
//   7. Bug do delete corrigido: usa o retorno {ok:true/false} do sb.remove
// ═══════════════════════════════════════════════════════════════════

function TrocasDevolucoes({ data, setData, privado, showToast }) {
  const [aba, setAba] = useState("trocas");
  const [showFormTroca, setShowFormTroca] = useState(false);
  const [showFormDev, setShowFormDev] = useState(false);

  const [formTroca, setFormTroca] = useState({
    data: hojeManaus(),
    cliente: "",
    produto: "",            // produto devolvido pelo cliente
    produto_novo: "",       // produto que o cliente leva (se ação=trocar)
    motivo: "tamanho",      // tamanho | defeito | arrependimento | outro
    acao: "trocar",         // trocar | credito
    diferenca: "",          // R$ extra (positivo cliente paga, negativo loja paga)
    observacao: ""
  });

  const [formDev, setFormDev] = useState({
    data_envio: hojeManaus(),
    fornecedor: "",
    produto: "",
    qtd: "1",
    motivo: "",
    observacao: ""
  });

  const trocasClientes = data?.trocas_clientes || [];
  const devolucoesFornecedor = data?.devolucoes_fornecedor || [];

  // ─────────────────────────────────────────────────────────────
  // HELPERS
  // ─────────────────────────────────────────────────────────────
  const motivoLabel = (m) => ({
    defeito: "Defeito",
    tamanho: "Tamanho/Cor",
    arrependimento: "Arrependimento",
    outro: "Outro"
  }[m] || m);

  const acaoLabel = (a) => ({
    trocar: "Trocar por outro",
    credito: "Crédito loja"
  }[a] || a);

  // motivos que devolvem o produto pro estoque
  const motivoDevolveEstoque = (m) => m === "tamanho" || m === "arrependimento";

  // motivos que vão pra devolução ao fornecedor
  const motivoVaiPraFornecedor = (m) => m === "defeito";

  const acharProdutoEstoque = (nome) => {
    if (!nome) return null;
    return data.estoque.find(p => p.produto?.toLowerCase() === nome.toLowerCase());
  };

  // ─────────────────────────────────────────────────────────────
  // CRIAR TROCA
  // ─────────────────────────────────────────────────────────────
  const handleAddTroca = async () => {
    // validações básicas
    if (!formTroca.cliente.trim()) return alert("Preencha o nome do cliente.");
    if (!formTroca.produto) return alert("Selecione o produto devolvido.");
    if (formTroca.acao === "trocar" && !formTroca.produto_novo) {
      return alert("Selecione o produto novo que o cliente vai levar.");
    }

    const prodDevolvido = acharProdutoEstoque(formTroca.produto);
    const prodNovo = formTroca.acao === "trocar"
      ? acharProdutoEstoque(formTroca.produto_novo)
      : null;

    // valida estoque do produto novo (não pode trocar por algo zerado)
    if (prodNovo && (prodNovo.estoque || 0) <= 0) {
      const confirmar = window.confirm(
        `O produto "${prodNovo.produto}" está com estoque zerado.\n` +
        `Continuar mesmo assim? (estoque ficará negativo)`
      );
      if (!confirmar) return;
    }

    const diferenca = Number(formTroca.diferenca) || 0;

    // monta linha da troca
    const row = {
      data: formTroca.data,
      cliente: formTroca.cliente.trim(),
      produto: formTroca.produto,
      produto_novo: formTroca.acao === "trocar" ? formTroca.produto_novo : null,
      motivo: formTroca.motivo,
      acao: formTroca.acao,
      valor: diferenca,
      status: motivoVaiPraFornecedor(formTroca.motivo) ? "aguardando_fornecedor" : "concluida",
      observacao: formTroca.observacao || ""
    };

    let inserted;
    try {
      const result = await sb.insert("trocas_clientes", row);
      inserted = Array.isArray(result) ? result[0] : result;
      if (!inserted || !inserted.id) {
        return alert("Erro ao salvar troca: resposta inválida do servidor.");
      }
    } catch (e) {
      return alert("Erro ao salvar troca: " + (e.message || "desconhecido"));
    }

    // ─── ATUALIZAR ESTOQUE ───
    let estoqueAtualizado = [...(data.estoque || [])];

    // produto devolvido: volta pro estoque SE motivo permitir
    if (prodDevolvido && motivoDevolveEstoque(formTroca.motivo)) {
      const novaQtd = (prodDevolvido.estoque || 0) + 1;
      try {
        await sb.update("estoque", prodDevolvido.id, { qtd: novaQtd });
        estoqueAtualizado = estoqueAtualizado.map(p =>
          p.id === prodDevolvido.id ? { ...p, estoque: novaQtd, qtd: novaQtd } : p
        );
      } catch (e) {
        console.error("Falha ao devolver produto ao estoque:", e);
      }
    }

    // produto novo: sai do estoque SE ação=trocar
    if (prodNovo && formTroca.acao === "trocar") {
      const novaQtd = Math.max(0, (prodNovo.estoque || 0) - 1);
      try {
        await sb.update("estoque", prodNovo.id, { qtd: novaQtd });
        estoqueAtualizado = estoqueAtualizado.map(p =>
          p.id === prodNovo.id ? { ...p, estoque: novaQtd, qtd: novaQtd } : p
        );
      } catch (e) {
        console.error("Falha ao descontar produto novo do estoque:", e);
      }
    }

    // ─── CAIXA ───
    // Diferença de valor: entrada se positivo, saída se negativo
    if (diferenca > 0) {
      try {
        await autoLancarCaixa(
          "entrada",
          `Diferença troca — ${formTroca.cliente}`,
          diferenca,
          formTroca.data,
          "operacional",
          data.caixa_config
        );
      } catch (e) { console.error(e); }
    } else if (diferenca < 0) {
      try {
        await autoLancarCaixa(
          "saida",
          `Diferença troca — ${formTroca.cliente}`,
          Math.abs(diferenca),
          formTroca.data,
          "operacional",
          data.caixa_config
        );
      } catch (e) { console.error(e); }
    }

    // ─── DEVOLUÇÃO AO FORNECEDOR (auto, se defeito) ───
    let novaDevolucao = null;
    if (motivoVaiPraFornecedor(formTroca.motivo)) {
      const rowDev = {
        data_envio: formTroca.data,
        fornecedor: "A definir",
        produto: formTroca.produto,
        qtd: 1,
        motivo: `Troca cliente: ${formTroca.cliente} — ${motivoLabel(formTroca.motivo)}`,
        status: "pendente",
        observacao: `Gerado automaticamente da troca #${inserted.id}. ${formTroca.observacao || ""}`.trim()
      };
      try {
        const devResult = await sb.insert("devolucoes_fornecedor", rowDev);
        novaDevolucao = Array.isArray(devResult) ? devResult[0] : devResult;
      } catch (e) {
        console.error("Falha ao criar devolução automática:", e);
      }
    }

    // ─── ATUALIZAR ESTADO LOCAL ───
    setData(d => ({
      ...d,
      estoque: estoqueAtualizado,
      trocas_clientes: [...(d.trocas_clientes || []), inserted],
      devolucoes_fornecedor: novaDevolucao
        ? [...(d.devolucoes_fornecedor || []), novaDevolucao]
        : (d.devolucoes_fornecedor || [])
    }));

    // reset form
    setShowFormTroca(false);
    setFormTroca({
      data: hojeManaus(),
      cliente: "",
      produto: "",
      produto_novo: "",
      motivo: "tamanho",
      acao: "trocar",
      diferenca: "",
      observacao: ""
    });

    if (showToast) {
      const msg = novaDevolucao
        ? "Troca registrada e devolução criada na aba Fornecedor!"
        : "Troca registrada!";
      showToast(msg);
    }
  };

  // ─────────────────────────────────────────────────────────────
  // CRIAR DEVOLUÇÃO MANUAL AO FORNECEDOR
  // ─────────────────────────────────────────────────────────────
  const handleAddDevolucao = async () => {
    if (!formDev.fornecedor.trim()) return alert("Preencha o fornecedor.");
    if (!formDev.produto) return alert("Selecione o produto.");

    const row = {
      data_envio: formDev.data_envio,
      fornecedor: formDev.fornecedor.trim(),
      produto: formDev.produto,
      qtd: Number(formDev.qtd) || 1,
      motivo: formDev.motivo || "",
      status: "pendente",
      observacao: formDev.observacao || ""
    };

    try {
      const result = await sb.insert("devolucoes_fornecedor", row);
      const inserted = Array.isArray(result) ? result[0] : result;
      if (!inserted || !inserted.id) return alert("Erro ao salvar.");
      setData(d => ({
        ...d,
        devolucoes_fornecedor: [...(d.devolucoes_fornecedor || []), inserted]
      }));
    } catch (e) {
      return alert("Erro: " + e.message);
    }

    setShowFormDev(false);
    setFormDev({
      data_envio: hojeManaus(),
      fornecedor: "",
      produto: "",
      qtd: "1",
      motivo: "",
      observacao: ""
    });
    if (showToast) showToast("Devolução registrada!");
  };

  // ─────────────────────────────────────────────────────────────
  // ATUALIZAR STATUS DA DEVOLUÇÃO + INLINE EDIT FORNECEDOR
  // Quando muda pra "recebido_substituicao", abre modal pedindo
  // qual produto o fornecedor mandou (pode ser diferente do original)
  // Quando muda pra "recebido_credito", abre modal pedindo o valor
  // ─────────────────────────────────────────────────────────────
  const [showModalSubstituicao, setShowModalSubstituicao] = useState(null); // {dev} ou null
  const [produtoSubstituicao, setProdutoSubstituicao] = useState("");
  const [qtdSubstituicao, setQtdSubstituicao] = useState("1");

  const [showModalCredito, setShowModalCredito] = useState(null); // {dev} ou null
  const [valorCredito, setValorCredito] = useState("");
  const [obsCredito, setObsCredito] = useState("");

  const handleUpdateStatusDev = async (id, novoStatus) => {
    const dev = devolucoesFornecedor.find(d => d.id === id);
    if (!dev) return;

    // Caso especial: substituição precisa perguntar qual produto entrou
    if (novoStatus === "recebido_substituicao") {
      setProdutoSubstituicao(dev.produto || ""); // pre-preenche com o original
      setQtdSubstituicao(String(dev.qtd || 1));
      setShowModalSubstituicao({ dev });
      return;
    }

    // Caso especial: crédito precisa perguntar o valor
    if (novoStatus === "recebido_credito") {
      setValorCredito(String(dev.valor_credito || ""));
      setObsCredito("");
      setShowModalCredito({ dev });
      return;
    }

    const updates = { status: novoStatus };

    try {
      await sb.update("devolucoes_fornecedor", id, updates);
    } catch (e) {
      return alert("Erro ao atualizar: " + e.message);
    }

    setData(d => ({
      ...d,
      devolucoes_fornecedor: d.devolucoes_fornecedor.map(dv =>
        dv.id === id ? { ...dv, ...updates } : dv
      )
    }));

    if (showToast) showToast("Status atualizado!");
  };

  const confirmarSubstituicao = async () => {
    const ctx = showModalSubstituicao;
    if (!ctx) return;
    const { dev } = ctx;
    if (!produtoSubstituicao) return alert("Selecione o produto recebido.");

    const qtd = Number(qtdSubstituicao) || 1;
    if (qtd <= 0) return alert("Quantidade inválida.");

    const updates = {
      status: "recebido_substituicao",
      data_retorno: hojeManaus(),
      // anota no observacao qual produto entrou (se diferente do original)
      observacao: produtoSubstituicao !== dev.produto
        ? `${dev.observacao || ""} | Substituído por: ${produtoSubstituicao} (${qtd}x)`.trim()
        : (dev.observacao || "")
    };

    try {
      await sb.update("devolucoes_fornecedor", dev.id, updates);
    } catch (e) {
      return alert("Erro ao atualizar: " + e.message);
    }

    // adiciona produto recebido no estoque
    let estoqueAtualizado = [...(data.estoque || [])];
    const prodEstoque = acharProdutoEstoque(produtoSubstituicao);
    if (prodEstoque) {
      const novaQtd = (prodEstoque.estoque || 0) + qtd;
      try {
        await sb.update("estoque", prodEstoque.id, { qtd: novaQtd });
        estoqueAtualizado = estoqueAtualizado.map(p =>
          p.id === prodEstoque.id ? { ...p, estoque: novaQtd, qtd: novaQtd } : p
        );
      } catch (e) { console.error(e); }
    }

    setData(d => ({
      ...d,
      estoque: estoqueAtualizado,
      devolucoes_fornecedor: d.devolucoes_fornecedor.map(dv =>
        dv.id === dev.id ? { ...dv, ...updates } : dv
      )
    }));

    setShowModalSubstituicao(null);
    setProdutoSubstituicao("");
    setQtdSubstituicao("1");

    if (showToast) showToast(`${qtd}x ${produtoSubstituicao} adicionado ao estoque!`);
  };

  const confirmarCredito = async () => {
    const ctx = showModalCredito;
    if (!ctx) return;
    const { dev } = ctx;

    const valor = Number(valorCredito) || 0;
    if (valor <= 0) return alert("Valor do crédito deve ser maior que zero.");

    const obsExtra = obsCredito.trim()
      ? ` | Crédito R$ ${valor.toFixed(2)}: ${obsCredito.trim()}`
      : ` | Crédito recebido R$ ${valor.toFixed(2)}`;

    const updates = {
      status: "recebido_credito",
      data_retorno: hojeManaus(),
      valor_credito: valor,
      observacao: ((dev.observacao || "") + obsExtra).trim()
    };

    try {
      await sb.update("devolucoes_fornecedor", dev.id, updates);
    } catch (e) {
      return alert("Erro ao atualizar: " + e.message);
    }

    setData(d => ({
      ...d,
      devolucoes_fornecedor: d.devolucoes_fornecedor.map(dv =>
        dv.id === dev.id ? { ...dv, ...updates } : dv
      )
    }));

    setShowModalCredito(null);
    setValorCredito("");
    setObsCredito("");

    if (showToast) showToast(`Crédito de R$ ${valor.toFixed(2)} registrado!`);
  };

  const handleEditFornecedorDev = async (id) => {
    const dev = devolucoesFornecedor.find(d => d.id === id);
    if (!dev) return;
    const novoForn = window.prompt("Fornecedor:", dev.fornecedor || "");
    if (novoForn === null) return; // cancelou
    const limpo = novoForn.trim();
    if (!limpo) return alert("Fornecedor não pode ser vazio.");
    try {
      await sb.update("devolucoes_fornecedor", id, { fornecedor: limpo });
      setData(d => ({
        ...d,
        devolucoes_fornecedor: d.devolucoes_fornecedor.map(dv =>
          dv.id === id ? { ...dv, fornecedor: limpo } : dv
        )
      }));
      if (showToast) showToast("Fornecedor atualizado!");
    } catch (e) {
      alert("Erro: " + e.message);
    }
  };

  // ─────────────────────────────────────────────────────────────
  // DELETAR
  // ─────────────────────────────────────────────────────────────
  const handleDeleteTroca = async (id) => {
    const troca = trocasClientes.find(t => t.id === id);
    if (!troca) return;

    const reverteEstoqueDevolvido = troca.produto && motivoDevolveEstoque(troca.motivo);
    const reverteEstoqueNovo = troca.produto_novo && troca.acao === "trocar";

    const msgPartes = [];
    if (reverteEstoqueDevolvido) msgPartes.push(`• ${troca.produto} sai do estoque (-1)`);
    if (reverteEstoqueNovo) msgPartes.push(`• ${troca.produto_novo} volta ao estoque (+1)`);

    const msg = msgPartes.length > 0
      ? `Deletar esta troca? Reversões:\n${msgPartes.join("\n")}`
      : "Deletar esta troca?";

    if (!window.confirm(msg)) return;

    const result = await sb.remove("trocas_clientes", id);
    if (!result.ok) return; // sb.remove já mostra alert

    let estoqueAtualizado = [...(data.estoque || [])];

    // reverter produto devolvido (se tinha voltado ao estoque, agora sai)
    if (reverteEstoqueDevolvido) {
      const p = acharProdutoEstoque(troca.produto);
      if (p) {
        const novaQtd = Math.max(0, (p.estoque || 0) - 1);
        try {
          await sb.update("estoque", p.id, { qtd: novaQtd });
          estoqueAtualizado = estoqueAtualizado.map(x =>
            x.id === p.id ? { ...x, estoque: novaQtd, qtd: novaQtd } : x
          );
        } catch (e) { console.error(e); }
      }
    }

    // reverter produto novo (se tinha saído, volta)
    if (reverteEstoqueNovo) {
      const p = acharProdutoEstoque(troca.produto_novo);
      if (p) {
        const novaQtd = (p.estoque || 0) + 1;
        try {
          await sb.update("estoque", p.id, { qtd: novaQtd });
          estoqueAtualizado = estoqueAtualizado.map(x =>
            x.id === p.id ? { ...x, estoque: novaQtd, qtd: novaQtd } : x
          );
        } catch (e) { console.error(e); }
      }
    }

    setData(d => ({
      ...d,
      estoque: estoqueAtualizado,
      trocas_clientes: d.trocas_clientes.filter(t => t.id !== id)
    }));

    if (showToast) showToast("Troca deletada!");
  };

  const handleDeleteDev = async (id) => {
    const dev = devolucoesFornecedor.find(d => d.id === id);
    if (!dev) return;

    const reverteEstoque = dev.status === "recebido_substituicao";
    const msg = reverteEstoque
      ? `Deletar? O produto ${dev.produto} (${dev.qtd}x) será removido do estoque.`
      : "Deletar esta devolução?";

    if (!window.confirm(msg)) return;

    const result = await sb.remove("devolucoes_fornecedor", id);
    if (!result.ok) return;

    let estoqueAtualizado = [...(data.estoque || [])];
    if (reverteEstoque) {
      const p = acharProdutoEstoque(dev.produto);
      if (p) {
        const novaQtd = Math.max(0, (p.estoque || 0) - (dev.qtd || 1));
        try {
          await sb.update("estoque", p.id, { qtd: novaQtd });
          estoqueAtualizado = estoqueAtualizado.map(x =>
            x.id === p.id ? { ...x, estoque: novaQtd, qtd: novaQtd } : x
          );
        } catch (e) { console.error(e); }
      }
    }

    setData(d => ({
      ...d,
      estoque: estoqueAtualizado,
      devolucoes_fornecedor: d.devolucoes_fornecedor.filter(dv => dv.id !== id)
    }));

    if (showToast) showToast("Devolução deletada!");
  };

  // ═══════════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════════

  const estoqueOrdenado = [...(data.estoque || [])].sort((a, b) =>
    (a.produto || "").localeCompare(b.produto || "")
  );

  const statusBadgeColor = (s) => {
    if (s === "concluida" || s === "recebido_credito" || s === "recebido_substituicao") return C.accent;
    if (s === "aguardando_fornecedor" || s === "pendente") return C.gold;
    if (s === "enviado") return "#374151";
    return C.muted;
  };

  const statusLabel = (s) => ({
    concluida: "Concluída",
    aguardando_fornecedor: "Aguardando fornec.",
    pendente: "Pendente",
    enviado: "Enviado",
    recebido_credito: "Crédito recebido",
    recebido_substituicao: "Substituído"
  }[s] || s);

  // ─────────────────────────────────────────────────────────────
  // KPIs DE DEVOLUÇÕES (capital parado/em trânsito)
  // ─────────────────────────────────────────────────────────────
  const calcKPI = (statusFiltro) => {
    return devolucoesFornecedor
      .filter(d => d.status === statusFiltro)
      .reduce((acc, d) => {
        const prod = acharProdutoEstoque(d.produto);
        const custo = Number(prod?.custo) || 0;
        const venda = Number(prod?.preco) || 0;
        const qtd = Number(d.qtd) || 1;
        return {
          custo: acc.custo + (custo * qtd),
          venda: acc.venda + (venda * qtd),
          itens: acc.itens + qtd
        };
      }, { custo: 0, venda: 0, itens: 0 });
  };

  const kpiPendente = calcKPI("pendente");
  const kpiEnviado = calcKPI("enviado");
  const totalCusto = kpiPendente.custo + kpiEnviado.custo;
  const totalVenda = kpiPendente.venda + kpiEnviado.venda;
  const margemRisco = totalVenda - totalCusto;
  const totalItens = kpiPendente.itens + kpiEnviado.itens;

  const fmtBRL = (v) => "R$ " + Number(v || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 28 }}>
        <div>
          <div style={styles.pageTitle}>Trocas & Devoluções</div>
          <div style={styles.pageSub}>Gestão de produtos devolvidos e trocas</div>
        </div>
      </div>

      {/* ═════════════════ KPIs DE CAPITAL EM DEVOLUÇÕES ═════════════════ */}
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(4, 1fr)",
        gap: 16,
        marginBottom: 28
      }}>
        {[
          {
            label: "Parado na loja",
            sub: `${kpiPendente.itens} ${kpiPendente.itens === 1 ? "item" : "itens"} aguardando envio`,
            value: privado ? "••••••" : fmtBRL(kpiPendente.custo),
            icon: "📦",
            color: C.gold
          },
          {
            label: "Com fornecedor",
            sub: `${kpiEnviado.itens} ${kpiEnviado.itens === 1 ? "item" : "itens"} em trânsito/análise`,
            value: privado ? "••••••" : fmtBRL(kpiEnviado.custo),
            icon: "🚚",
            color: "#374151"
          },
          {
            label: "Receita potencial perdida",
            sub: `${totalItens} ${totalItens === 1 ? "produto fora" : "produtos fora"} da prateleira`,
            value: privado ? "••••••" : fmtBRL(totalVenda),
            icon: "💸",
            color: C.warn
          },
          {
            label: "Margem em risco",
            sub: "Lucro que deixa de ser realizado",
            value: privado ? "••••••" : fmtBRL(margemRisco),
            icon: "⚠️",
            color: margemRisco > 0 ? C.warn : C.muted
          }
        ].map((k, idx) => (
          <div key={k.label} style={{
            background: C.bgElevated,
            border: `1px solid rgba(0,0,0,0.08)`,
            borderRadius: 16,
            padding: 20,
            position: "relative",
            overflow: "hidden",
            boxShadow: `0 1px 2px rgba(0,0,0,0.06), 0 0 0 1px rgba(0,0,0,0.05)`
          }}>
            <div style={{
              position: "absolute", top: -20, right: -20, width: 80, height: 80,
              background: `radial-gradient(circle, ${k.color}15, transparent)`, borderRadius: "50%"
            }} />
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
              <div style={{
                width: 36, height: 36, borderRadius: 10, background: `${k.color}20`,
                display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16
              }}>{k.icon}</div>
              <div style={{ fontSize: 9, color: "#374151", letterSpacing: 1.5, textTransform: "uppercase", fontWeight: 600 }}>
                {k.label}
              </div>
            </div>
            <div style={{
              fontFamily: "'Inter', sans-serif", fontWeight: 800, fontSize: 22,
              color: k.color, letterSpacing: -0.8, marginBottom: 4
            }}>{k.value}</div>
            <div style={{ fontSize: 10, color: "#374151" }}>{k.sub}</div>
          </div>
        ))}
      </div>

      <div style={{ display: "flex", gap: 8, marginBottom: 28 }}>
        <button onClick={() => setAba("trocas")} style={{ ...styles.btn(aba === "trocas" ? "primary" : "secondary"), fontSize: 12 }}>
          🔄 Trocas de Clientes ({trocasClientes.length})
        </button>
        <button onClick={() => setAba("fornecedor")} style={{ ...styles.btn(aba === "fornecedor" ? "primary" : "secondary"), fontSize: 12 }}>
          📦 Devoluções ao Fornecedor ({devolucoesFornecedor.length})
        </button>
      </div>

      {/* ═════════════════ ABA TROCAS ═════════════════ */}
      {aba === "trocas" && (
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 20 }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#111827" }}>Trocas de Clientes</div>
            <button onClick={() => setShowFormTroca(!showFormTroca)} style={styles.btn("primary")}>
              {showFormTroca ? "Cancelar" : "+ Nova Troca"}
            </button>
          </div>

          {showFormTroca && (
            <div style={{ background: C.card, border: "1px solid #e5e7eb", borderRadius: 16, padding: 24, marginBottom: 20 }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
                {/* Linha 1 */}
                <div>
                  <label style={styles.label}>Data</label>
                  <input type="date" style={styles.input} value={formTroca.data}
                    onChange={e => setFormTroca({ ...formTroca, data: e.target.value })} />
                </div>
                <div>
                  <label style={styles.label}>Cliente</label>
                  <input style={styles.input} value={formTroca.cliente}
                    onChange={e => setFormTroca({ ...formTroca, cliente: e.target.value })}
                    placeholder="Nome do cliente" />
                </div>

                {/* Linha 2 */}
                <div>
                  <label style={styles.label}>Produto Devolvido</label>
                  <select style={styles.input} value={formTroca.produto}
                    onChange={e => setFormTroca({ ...formTroca, produto: e.target.value })}>
                    <option value="">Selecione...</option>
                    {estoqueOrdenado.map(p => (
                      <option key={p.id} value={p.produto}>
                        {p.produto} (estoque: {p.estoque || 0})
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={styles.label}>Motivo</label>
                  <select style={styles.input} value={formTroca.motivo}
                    onChange={e => setFormTroca({ ...formTroca, motivo: e.target.value })}>
                    <option value="tamanho">Tamanho/Cor (volta ao estoque)</option>
                    <option value="arrependimento">Arrependimento (volta ao estoque)</option>
                    <option value="defeito">Defeito (vai pro fornecedor)</option>
                    <option value="outro">Outro (não volta)</option>
                  </select>
                </div>

                {/* Linha 3 */}
                <div>
                  <label style={styles.label}>Ação</label>
                  <select style={styles.input} value={formTroca.acao}
                    onChange={e => setFormTroca({ ...formTroca, acao: e.target.value })}>
                    <option value="trocar">Trocar por outro produto</option>
                    <option value="credito">Crédito loja</option>
                  </select>
                </div>
                {formTroca.acao === "trocar" && (
                  <div>
                    <label style={styles.label}>Produto Novo (vai pro cliente)</label>
                    <select style={styles.input} value={formTroca.produto_novo}
                      onChange={e => setFormTroca({ ...formTroca, produto_novo: e.target.value })}>
                      <option value="">Selecione...</option>
                      {estoqueOrdenado.map(p => (
                        <option key={p.id} value={p.produto}>
                          {p.produto} (estoque: {p.estoque || 0})
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Diferença só aparece se trocar */}
                {formTroca.acao === "trocar" && (
                  <div>
                    <label style={styles.label}>Diferença R$ (opcional)</label>
                    <input type="number" step="0.01" style={styles.input} value={formTroca.diferenca}
                      onChange={e => setFormTroca({ ...formTroca, diferenca: e.target.value })}
                      placeholder="Positivo: cliente paga · Negativo: você paga" />
                  </div>
                )}

                <div style={{ gridColumn: "1 / -1" }}>
                  <label style={styles.label}>Observação</label>
                  <input style={styles.input} value={formTroca.observacao}
                    onChange={e => setFormTroca({ ...formTroca, observacao: e.target.value })} />
                </div>
              </div>

              {/* Aviso resumo do que vai acontecer */}
              <div style={{
                background: `${C.gold}15`,
                border: `1px solid ${C.gold}40`,
                borderRadius: 8,
                padding: "10px 14px",
                marginBottom: 12,
                fontSize: 11,
                color: "#374151",
                lineHeight: 1.6
              }}>
                <div style={{ fontWeight: 700, color: C.gold, marginBottom: 4 }}>Ao salvar:</div>
                {formTroca.produto && motivoDevolveEstoque(formTroca.motivo) && (
                  <div>• {formTroca.produto} <strong style={{ color: C.accent }}>+1</strong> no estoque</div>
                )}
                {formTroca.produto && motivoVaiPraFornecedor(formTroca.motivo) && (
                  <div>• {formTroca.produto} → Devoluções ao Fornecedor (status: pendente)</div>
                )}
                {formTroca.acao === "trocar" && formTroca.produto_novo && (
                  <div>• {formTroca.produto_novo} <strong style={{ color: C.warn }}>−1</strong> do estoque</div>
                )}
                {Number(formTroca.diferenca) > 0 && (
                  <div>• Entrada no caixa: R$ {Number(formTroca.diferenca).toFixed(2)}</div>
                )}
                {Number(formTroca.diferenca) < 0 && (
                  <div>• Saída no caixa: R$ {Math.abs(Number(formTroca.diferenca)).toFixed(2)}</div>
                )}
              </div>

              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={handleAddTroca} style={styles.btn("primary")}>Salvar</button>
                <button onClick={() => setShowFormTroca(false)} style={styles.btn("secondary")}>Cancelar</button>
              </div>
            </div>
          )}

          {/* Tabela trocas */}
          <div style={{
            background: C.bgElevated,
            border: `2px solid ${C.border}30`,
            borderRadius: 20,
            padding: 28
          }}>
            <table style={styles.table}>
              <thead>
                <tr style={{ borderBottom: `2px solid ${C.accent}40` }}>
                  <th style={{ ...styles.th, padding: "10px 14px", fontSize: 9, letterSpacing: 1.5 }}>DATA</th>
                  <th style={{ ...styles.th, padding: "10px 14px", fontSize: 9, letterSpacing: 1.5 }}>CLIENTE</th>
                  <th style={{ ...styles.th, padding: "10px 14px", fontSize: 9, letterSpacing: 1.5 }}>DEVOLVIDO</th>
                  <th style={{ ...styles.th, padding: "10px 14px", fontSize: 9, letterSpacing: 1.5 }}>NOVO</th>
                  <th style={{ ...styles.th, padding: "10px 14px", fontSize: 9, letterSpacing: 1.5 }}>MOTIVO</th>
                  <th style={{ ...styles.th, padding: "10px 14px", fontSize: 9, letterSpacing: 1.5 }}>AÇÃO</th>
                  <th style={{ ...styles.th, padding: "10px 14px", fontSize: 9, letterSpacing: 1.5 }}>DIFER.</th>
                  <th style={{ ...styles.th, padding: "10px 14px", fontSize: 9, letterSpacing: 1.5 }}>STATUS</th>
                  <th style={{ ...styles.th, padding: "10px 14px", fontSize: 9, letterSpacing: 1.5 }}></th>
                </tr>
              </thead>
              <tbody>
                {trocasClientes.length === 0 && (
                  <tr><td colSpan={9} style={{ ...styles.td, padding: 20, textAlign: "center", color: "#374151" }}>
                    Nenhuma troca registrada ainda.
                  </td></tr>
                )}
                {[...trocasClientes].sort((a, b) => (b.data || "").localeCompare(a.data || "")).map(t => (
                  <tr key={t.id}>
                    <td style={{ ...styles.td, padding: "10px 14px", color: "#374151", fontSize: 11 }}>{t.data}</td>
                    <td style={styles.td}>{t.cliente}</td>
                    <td style={styles.td}>{t.produto}</td>
                    <td style={styles.td}>{t.produto_novo || "—"}</td>
                    <td style={styles.td}>{motivoLabel(t.motivo)}</td>
                    <td style={styles.td}>{acaoLabel(t.acao)}</td>
                    <td style={styles.td}>
                      {Number(t.valor) !== 0 && !privado
                        ? `R$ ${Number(t.valor).toFixed(2)}`
                        : "—"}
                    </td>
                    <td style={styles.td}>
                      <span style={styles.badge(statusBadgeColor(t.status))}>{statusLabel(t.status)}</span>
                    </td>
                    <td style={styles.td}>
                      <button onClick={() => handleDeleteTroca(t.id)}
                        style={{ background: "none", border: "none", color: C.warn, cursor: "pointer", fontSize: 14 }}>✕</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ═════════════════ ABA DEVOLUÇÕES ═════════════════ */}
      {aba === "fornecedor" && (
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 20 }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#111827" }}>Devoluções ao Fornecedor</div>
            <button onClick={() => setShowFormDev(!showFormDev)} style={styles.btn("primary")}>
              {showFormDev ? "Cancelar" : "+ Nova Devolução"}
            </button>
          </div>

          {showFormDev && (
            <div style={{ background: C.card, border: "1px solid #e5e7eb", borderRadius: 16, padding: 24, marginBottom: 20 }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
                <div>
                  <label style={styles.label}>Data Envio</label>
                  <input type="date" style={styles.input} value={formDev.data_envio}
                    onChange={e => setFormDev({ ...formDev, data_envio: e.target.value })} />
                </div>
                <div>
                  <label style={styles.label}>Fornecedor</label>
                  <input style={styles.input} value={formDev.fornecedor}
                    onChange={e => setFormDev({ ...formDev, fornecedor: e.target.value })} />
                </div>
                <div>
                  <label style={styles.label}>Produto</label>
                  <select style={styles.input} value={formDev.produto}
                    onChange={e => setFormDev({ ...formDev, produto: e.target.value })}>
                    <option value="">Selecione...</option>
                    {estoqueOrdenado.map(p => (
                      <option key={p.id} value={p.produto}>{p.produto}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={styles.label}>Quantidade</label>
                  <input type="number" min="1" style={styles.input} value={formDev.qtd}
                    onChange={e => setFormDev({ ...formDev, qtd: e.target.value })} />
                </div>
                <div style={{ gridColumn: "1 / -1" }}>
                  <label style={styles.label}>Motivo</label>
                  <input style={styles.input} value={formDev.motivo}
                    onChange={e => setFormDev({ ...formDev, motivo: e.target.value })}
                    placeholder="Defeito de fabricação, lote errado, etc." />
                </div>
                <div style={{ gridColumn: "1 / -1" }}>
                  <label style={styles.label}>Observação</label>
                  <input style={styles.input} value={formDev.observacao}
                    onChange={e => setFormDev({ ...formDev, observacao: e.target.value })} />
                </div>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={handleAddDevolucao} style={styles.btn("primary")}>Salvar</button>
                <button onClick={() => setShowFormDev(false)} style={styles.btn("secondary")}>Cancelar</button>
              </div>
            </div>
          )}

          {/* Tabela devoluções */}
          <div style={{
            background: C.bgElevated,
            border: `2px solid ${C.border}30`,
            borderRadius: 20,
            padding: 28
          }}>
            <table style={styles.table}>
              <thead>
                <tr style={{ borderBottom: `2px solid ${C.accent}40` }}>
                  <th style={{ ...styles.th, padding: "10px 14px", fontSize: 9, letterSpacing: 1.5 }}>ENVIO</th>
                  <th style={{ ...styles.th, padding: "10px 14px", fontSize: 9, letterSpacing: 1.5 }}>RETORNO</th>
                  <th style={{ ...styles.th, padding: "10px 14px", fontSize: 9, letterSpacing: 1.5 }}>FORNECEDOR</th>
                  <th style={{ ...styles.th, padding: "10px 14px", fontSize: 9, letterSpacing: 1.5 }}>PRODUTO</th>
                  <th style={{ ...styles.th, padding: "10px 14px", fontSize: 9, letterSpacing: 1.5 }}>QTD</th>
                  <th style={{ ...styles.th, padding: "10px 14px", fontSize: 9, letterSpacing: 1.5 }}>STATUS</th>
                  <th style={{ ...styles.th, padding: "10px 14px", fontSize: 9, letterSpacing: 1.5 }}>AÇÕES</th>
                </tr>
              </thead>
              <tbody>
                {devolucoesFornecedor.length === 0 && (
                  <tr><td colSpan={7} style={{ ...styles.td, padding: 20, textAlign: "center", color: "#374151" }}>
                    Nenhuma devolução registrada ainda.
                  </td></tr>
                )}
                {[...devolucoesFornecedor].sort((a, b) => (b.data_envio || "").localeCompare(a.data_envio || "")).map(d => (
                  <tr key={d.id}>
                    <td style={{ ...styles.td, padding: "10px 14px", color: "#374151", fontSize: 11 }}>{d.data_envio}</td>
                    <td style={{ ...styles.td, padding: "10px 14px", color: "#374151", fontSize: 11 }}>{d.data_retorno || "—"}</td>
                    <td style={styles.td}>
                      {d.fornecedor === "A definir" ? (
                        <button onClick={() => handleEditFornecedorDev(d.id)}
                          style={{ background: `${C.gold}20`, border: `1px solid ${C.gold}`, color: C.gold, padding: "2px 8px", borderRadius: 6, fontSize: 10, cursor: "pointer" }}>
                          ⚠️ Definir
                        </button>
                      ) : (
                        <span onClick={() => handleEditFornecedorDev(d.id)}
                          style={{ cursor: "pointer", textDecoration: "underline", textDecorationStyle: "dotted" }}
                          title="Clique para editar">
                          {d.fornecedor}
                        </span>
                      )}
                    </td>
                    <td style={styles.td}>{d.produto}</td>
                    <td style={styles.td}>{d.qtd}</td>
                    <td style={styles.td}>
                      <span style={styles.badge(statusBadgeColor(d.status))}>{statusLabel(d.status)}</span>
                    </td>
                    <td style={styles.td}>
                      <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                        <select value={d.status || "pendente"}
                          onChange={e => handleUpdateStatusDev(d.id, e.target.value)}
                          style={{ ...styles.input, fontSize: 10, padding: "4px 6px", width: "auto" }}>
                          <option value="pendente">Pendente</option>
                          <option value="enviado">Enviado</option>
                          <option value="recebido_credito">Crédito recebido</option>
                          <option value="recebido_substituicao">Substituído (entra estoque)</option>
                        </select>
                        <button onClick={() => handleDeleteDev(d.id)}
                          style={{ background: "none", border: "none", color: C.warn, cursor: "pointer", fontSize: 14 }}>✕</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ═════════════════ MODAL: SUBSTITUIÇÃO RECEBIDA ═════════════════ */}
      {showModalSubstituicao && (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(15,23,42,0.5)",
          display: "flex", alignItems: "center", justifyContent: "center",
          zIndex: 1000, padding: 20
        }}>
          <div style={{
            background: C.card, border: "1px solid #e5e7eb", borderRadius: 16,
            padding: 28, maxWidth: 500, width: "100%"
          }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#111827", marginBottom: 6 }}>
              Substituição recebida do fornecedor
            </div>
            <div style={{ fontSize: 11, color: "#374151", marginBottom: 18 }}>
              Devolução original: <strong>{showModalSubstituicao.dev.produto}</strong> ({showModalSubstituicao.dev.qtd}x)
              {" — "}{showModalSubstituicao.dev.fornecedor}
            </div>

            <div style={{ marginBottom: 12 }}>
              <label style={styles.label}>Produto recebido (entra no estoque)</label>
              <select style={styles.input} value={produtoSubstituicao}
                onChange={e => setProdutoSubstituicao(e.target.value)}>
                <option value="">Selecione...</option>
                {estoqueOrdenado.map(p => (
                  <option key={p.id} value={p.produto}>
                    {p.produto} (estoque atual: {p.estoque || 0})
                  </option>
                ))}
              </select>
              <div style={{ fontSize: 10, color: "#374151", marginTop: 4 }}>
                Pode ser o mesmo produto ou um modelo diferente que o fornecedor mandou no lugar.
              </div>
            </div>

            <div style={{ marginBottom: 18 }}>
              <label style={styles.label}>Quantidade recebida</label>
              <input type="number" min="1" style={styles.input} value={qtdSubstituicao}
                onChange={e => setQtdSubstituicao(e.target.value)} />
            </div>

            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button onClick={() => { setShowModalSubstituicao(null); setProdutoSubstituicao(""); setQtdSubstituicao("1"); }}
                style={styles.btn("secondary")}>Cancelar</button>
              <button onClick={confirmarSubstituicao} style={styles.btn("primary")}>
                Confirmar entrada no estoque
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═════════════════ MODAL: CRÉDITO RECEBIDO ═════════════════ */}
      {showModalCredito && (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(15,23,42,0.5)",
          display: "flex", alignItems: "center", justifyContent: "center",
          zIndex: 1000, padding: 20
        }}>
          <div style={{
            background: C.card, border: "1px solid #e5e7eb", borderRadius: 16,
            padding: 28, maxWidth: 500, width: "100%"
          }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#111827", marginBottom: 6 }}>
              💳 Crédito recebido do fornecedor
            </div>
            <div style={{ fontSize: 11, color: "#374151", marginBottom: 18 }}>
              Devolução: <strong>{showModalCredito.dev.produto}</strong> ({showModalCredito.dev.qtd}x)
              {" — "}{showModalCredito.dev.fornecedor}
            </div>

            <div style={{ marginBottom: 12 }}>
              <label style={styles.label}>Valor do crédito (R$)</label>
              <input type="number" step="0.01" min="0.01" style={styles.input} value={valorCredito}
                onChange={e => setValorCredito(e.target.value)}
                placeholder="0,00" autoFocus />
              <div style={{ fontSize: 10, color: "#374151", marginTop: 4 }}>
                Valor que o fornecedor te creditou para usar em compras futuras.
              </div>
            </div>

            <div style={{ marginBottom: 18 }}>
              <label style={styles.label}>Observação (opcional)</label>
              <input style={styles.input} value={obsCredito}
                onChange={e => setObsCredito(e.target.value)}
                placeholder="Ex: usar até dez/26, voucher #123, etc." />
            </div>

            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button onClick={() => { setShowModalCredito(null); setValorCredito(""); setObsCredito(""); }}
                style={styles.btn("secondary")}>Cancelar</button>
              <button onClick={confirmarCredito} style={styles.btn("primary")}>
                Confirmar crédito
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function CaixaDividas({ data, setData, privado, showToast }) {
  const fmt = (v) => "R$ " + Number(v || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const dividas = data.dividas || [];
  const [aba, setAba] = useState("caixa"); // caixa | visao | dividas | simulador
  const [showForm, setShowForm] = useState(false);
  const [editando, setEditando] = useState(null);
  const [salvando, setSalvando] = useState(false);
  const [pagamentoExtra, setPagamentoExtra] = useState("");
  const [showFormCaixa, setShowFormCaixa] = useState(false);
  const [showConfigCaixa, setShowConfigCaixa] = useState(false);
  const [formCaixa, setFormCaixa] = useState({ tipo: "entrada", categoria: "operacional", descricao: "", valor: "", data: hojeManaus() });
  const [formConfig, setFormConfig] = useState({ saldo_inicial: "", meta_emergencia: "", saldo_emergencia: "" });
  const [salvandoCaixa, setSalvandoCaixa] = useState(false);
  const [selCaixa, setSelCaixa] = useState([]);
  const [filtroCaixaTipo, setFiltroCaixaTipo] = useState("todos");
  const [filtroCaixaCat, setFiltroCaixaCat] = useState("todas");
  const [sortDirCaixa, setSortDirCaixa] = useState("desc");
  const [valorTrafegoManual, setValorTrafegoManual] = useState("");
  const [dataTrafegoManual, setDataTrafegoManual] = useState(hojeManaus());
  const [vendasAuto, setVendasAuto] = useState(true);
  const [gastosAuto, setGastosAuto] = useState(true);
  const [valorVendasManual, setValorVendasManual] = useState("");
  const [dataVendasManual, setDataVendasManual] = useState(hojeManaus());
  const [valorGastosManual, setValorGastosManual] = useState("");
  const [dataGastosManual, setDataGastosManual] = useState(hojeManaus());
  const [comprasAuto, setComprasAuto] = useState(true);
  const [valorComprasManual, setValorComprasManual] = useState("");
  const [dataComprasManual, setDataComprasManual] = useState(hojeManaus());
  
  // Carrega preferências de auto/manual do config ao montar
  useEffect(() => {
    const cfg = data.caixa_config || {};
    if (cfg.vendas_auto !== undefined) setVendasAuto(cfg.vendas_auto);
    if (cfg.gastos_auto !== undefined) setGastosAuto(cfg.gastos_auto);
    if (cfg.compras_auto !== undefined) setComprasAuto(cfg.compras_auto);
  }, [data.caixa_config]);

  const { filtrarLista: filtrarCaixa, aplicarPeriodo: aplicarPeriodoCaixa, selecionarMes: selecionarMesCaixa, periodoRapido: periodoRapidoCaixa, mesFiltro: mesFiltroC } = useFiltro("tfx_caixa"); // eslint-disable-line

  // Carrega preferências de auto/manual do config
  useEffect(() => {
    const cfg = data.caixa_config || {};
    if (cfg.vendas_auto !== undefined) setVendasAuto(cfg.vendas_auto);
    if (cfg.gastos_auto !== undefined) setGastosAuto(cfg.gastos_auto);
    if (cfg.compras_auto !== undefined) setComprasAuto(cfg.compras_auto);
  }, [data.caixa_config]);
  const emptyForm = { nome: "", tipo: "cartao", valor_total: "", parcela_mensal: "", parcelas_total: "", parcelas_pagas: "", juros_mes: "", data_inicio: hojeManaus(), data_vencimento: "1", observacao: "" };
  const [form, setForm] = useState(emptyForm);

  const totalDividas = dividas.reduce((s, d) => s + (Number(d.valor_total) - Number(d.valor_pago || 0)), 0);
  const totalParcelas = dividas.reduce((s, d) => s + Number(d.parcela_mensal || 0), 0);
  const mesAtual = hojeManaus().slice(0, 7);
  const lucroMes = (() => {
    const fat = (data.vendas || []).filter(v => v.data?.startsWith(mesAtual)).reduce((s, v) => s + v.valor * v.qtd, 0);
    const custo = (data.vendas || []).filter(v => v.data?.startsWith(mesAtual)).reduce((s, v) => s + (v.custo || 0) * v.qtd, 0);
    const gastos = (data.gastos || []).filter(g => g.data?.startsWith(mesAtual) && g.tipo === "loja").reduce((s, g) => s + g.valor, 0);
    return fat - custo - gastos;
  })();
  const sobra = lucroMes - totalParcelas;

  const salvar = async () => {
    setSalvando(true);
    try {
      const row = {
        tenant_id: getTenant(),
        nome: form.nome,
        tipo: form.tipo,
        valor_total: Number(form.valor_total) || 0,
        parcela_mensal: Number(form.parcela_mensal) || 0,
        parcelas_total: Number(form.parcelas_total) || 0,
        parcelas_pagas: Number(form.parcelas_pagas) || 0,
        juros_mes: Number(form.juros_mes) || 0,
        data_inicio: form.data_inicio,
        data_vencimento: Number(form.data_vencimento) || 1,
        observacao: form.observacao,
        valor_pago: (Number(form.parcelas_pagas) || 0) * (Number(form.parcela_mensal) || 0),
      };
      if (editando) {
        await sb.update("dividas", editando.id, row);
        setData(d => ({ ...d, dividas: d.dividas.map(x => x.id === editando.id ? { ...x, ...row } : x) }));
        if (showToast) showToast("Dívida atualizada!");
      } else {
        const res = await sb.insert("dividas", row);
        const inserted = Array.isArray(res) ? res[0] : res;
        setData(d => ({ ...d, dividas: [...d.dividas, inserted] }));
        if (showToast) showToast("Dívida cadastrada!");
      }
      setForm(emptyForm);
      setEditando(null);
      setShowForm(false);
    } catch(e) { alert("Erro: " + e.message); }
    setSalvando(false);
  };

  const pagarParcela = async (d) => {
    const novasPagas = Number(d.parcelas_pagas || 0) + 1;
    const novoValorPago = novasPagas * Number(d.parcela_mensal);
    await sb.update("dividas", d.id, { parcelas_pagas: novasPagas, valor_pago: novoValorPago });
    setData(dd => ({ ...dd, dividas: dd.dividas.map(x => x.id === d.id ? { ...x, parcelas_pagas: novasPagas, valor_pago: novoValorPago } : x) }));
    if (showToast) showToast("Parcela registrada!");
  };

  const deletar = async (id) => {
    if (!window.confirm("Excluir esta dívida?")) return;
    await sb.remove("dividas", id);
    setData(d => ({ ...d, dividas: d.dividas.filter(x => x.id !== id) }));
    if (showToast) showToast("Dívida removida!");
  };

  const tipoIcon = { cartao: "💳", emprestimo: "🏦", parcela: "📦", outro: "📋" };
  const tipoLabel = { cartao: "Cartão", emprestimo: "Empréstimo", parcela: "Parcelamento", outro: "Outro" };
  const tipoColor = { cartao: C.warn, emprestimo: "#E74C3C", parcela: C.gold, outro: C.muted };

  // Funções vendas/gastos auto/manual
  const toggleVendasAuto = async (auto) => {
    setVendasAuto(auto);
    try {
      const cfg = data.caixa_config || {};
      if (cfg.id) {
        await sb.update("caixa_config", cfg.id, { vendas_auto: auto });
      } else {
        const row = { tenant_id: getTenant(), vendas_auto: auto };
        const res = await sb.insert("caixa_config", row);
        setData(d => ({ ...d, caixa_config: Array.isArray(res) ? res[0] : res }));
      }
      setData(d => ({ ...d, caixa_config: { ...d.caixa_config, vendas_auto: auto } }));
    } catch(e) { console.error(e); }
  };
  const toggleGastosAuto = async (auto) => {
    setGastosAuto(auto);
    try {
      const cfg = data.caixa_config || {};
      if (cfg.id) {
        await sb.update("caixa_config", cfg.id, { gastos_auto: auto });
      } else {
        const row = { tenant_id: getTenant(), gastos_auto: auto };
        const res = await sb.insert("caixa_config", row);
        setData(d => ({ ...d, caixa_config: Array.isArray(res) ? res[0] : res }));
      }
      setData(d => ({ ...d, caixa_config: { ...d.caixa_config, gastos_auto: auto } }));
    } catch(e) { console.error(e); }
  };

  const lancarVendasManual = async () => {
    if (!valorVendasManual || !dataVendasManual) return alert("Preencha valor e data!");
    setSalvandoCaixa(true);
    try {
      const valorLancar = Number(valorVendasManual);
      const row = { tenant_id: getTenant(), tipo: "entrada", categoria: "vendas_loja", descricao: `Vendas do período (lançamento manual)`, valor: valorLancar, data: dataVendasManual };
      const res = await sb.insert("caixa_movimentos", row);
      const inserted = Array.isArray(res) ? res[0] : res;
      setData(d => ({ ...d, caixa_movimentos: [...(d.caixa_movimentos || []), inserted] }));
      if (showToast) showToast(`Vendas lançadas: ${fmt(valorLancar)}`);
      setValorVendasManual("");
    } catch(e) { alert("Erro: " + e.message); }
    setSalvandoCaixa(false);
  };

  const lancarGastosManual = async () => {
    if (!valorGastosManual || !dataGastosManual) return alert("Preencha valor e data!");
    setSalvandoCaixa(true);
    try {
      const valorLancar = Number(valorGastosManual);
      const row = { tenant_id: getTenant(), tipo: "saida", categoria: "gastos_loja", descricao: `Gastos do período (lançamento manual)`, valor: valorLancar, data: dataGastosManual };
      const res = await sb.insert("caixa_movimentos", row);
      const inserted = Array.isArray(res) ? res[0] : res;
      setData(d => ({ ...d, caixa_movimentos: [...(d.caixa_movimentos || []), inserted] }));
      if (showToast) showToast(`Gastos lançados: ${fmt(valorLancar)}`);
      setValorGastosManual("");
    } catch(e) { alert("Erro: " + e.message); }
    setSalvandoCaixa(false);
  };

  const lancarVendasAuto = async (totalVendasPeriodo, totalVendasJaLancado) => {
    setSalvandoCaixa(true);
    try {
      const diferenca = totalVendasPeriodo - totalVendasJaLancado;
      if (diferenca <= 0) {
        alert("Vendas já lançadas ou sem movimentação!");
        setSalvandoCaixa(false);
        return;
      }
      const row = { tenant_id: getTenant(), tipo: "entrada", categoria: "vendas_loja", descricao: `Vendas do período (automático)`, valor: diferenca, data: hojeManaus() };
      const res = await sb.insert("caixa_movimentos", row);
      const inserted = Array.isArray(res) ? res[0] : res;
      setData(d => ({ ...d, caixa_movimentos: [...(d.caixa_movimentos || []), inserted] }));
      if (showToast) showToast(`Vendas lançadas: ${fmt(diferenca)}`);
    } catch(e) { alert("Erro: " + e.message); }
    setSalvandoCaixa(false);
  };

  const lancarGastosAuto = async (totalGastosPeriodo, totalGastosJaLancado) => {
    setSalvandoCaixa(true);
    try {
      const diferenca = totalGastosPeriodo - totalGastosJaLancado;
      if (diferenca <= 0) {
        alert("Gastos já lançados ou sem movimentação!");
        setSalvandoCaixa(false);
        return;
      }
      const row = { tenant_id: getTenant(), tipo: "saida", categoria: "gastos_loja", descricao: `Gastos do período (automático)`, valor: diferenca, data: hojeManaus() };
      const res = await sb.insert("caixa_movimentos", row);
      const inserted = Array.isArray(res) ? res[0] : res;
      setData(d => ({ ...d, caixa_movimentos: [...(d.caixa_movimentos || []), inserted] }));
      if (showToast) showToast(`Gastos lançados: ${fmt(diferenca)}`);
    } catch(e) { alert("Erro: " + e.message); }
    setSalvandoCaixa(false);
  };

  const toggleComprasAuto = async (auto) => {
    setComprasAuto(auto);
    try {
      const cfg = data.caixa_config || {};
      if (cfg.id) {
        await sb.update("caixa_config", cfg.id, { compras_auto: auto });
      } else {
        const row = { tenant_id: getTenant(), compras_auto: auto };
        const res = await sb.insert("caixa_config", row);
        setData(d => ({ ...d, caixa_config: Array.isArray(res) ? res[0] : res }));
      }
      setData(d => ({ ...d, caixa_config: { ...d.caixa_config, compras_auto: auto } }));
    } catch(e) { console.error(e); }
  };

  const lancarComprasManual = async () => {
    if (!valorComprasManual || !dataComprasManual) return alert("Preencha valor e data!");
    setSalvandoCaixa(true);
    try {
      const valorLancar = Number(valorComprasManual);
      const row = { tenant_id: getTenant(), tipo: "saida", categoria: "compras_fornecedores", descricao: `Compras/Fornecedores (lançamento manual)`, valor: valorLancar, data: dataComprasManual };
      const res = await sb.insert("caixa_movimentos", row);
      const inserted = Array.isArray(res) ? res[0] : res;
      setData(d => ({ ...d, caixa_movimentos: [...(d.caixa_movimentos || []), inserted] }));
      if (showToast) showToast(`Compras lançadas: ${fmt(valorLancar)}`);
      setValorComprasManual("");
    } catch(e) { alert("Erro: " + e.message); }
    setSalvandoCaixa(false);
  };

  const lancarComprasAuto = async (totalComprasPeriodo, totalComprasJaLancado) => {
    setSalvandoCaixa(true);
    try {
      const diferenca = totalComprasPeriodo - totalComprasJaLancado;
      if (diferenca <= 0) {
        alert("Compras já lançadas ou sem movimentação!");
        setSalvandoCaixa(false);
        return;
      }
      const row = { tenant_id: getTenant(), tipo: "saida", categoria: "compras_fornecedores", descricao: `Compras/Fornecedores (automático)`, valor: diferenca, data: hojeManaus() };
      const res = await sb.insert("caixa_movimentos", row);
      const inserted = Array.isArray(res) ? res[0] : res;
      setData(d => ({ ...d, caixa_movimentos: [...(d.caixa_movimentos || []), inserted] }));
      if (showToast) showToast(`Compras lançadas: ${fmt(diferenca)}`);
    } catch(e) { alert("Erro: " + e.message); }
    setSalvandoCaixa(false);
  };

  // Simulador de saída
  const simularSaida = (extra = 0) => {
    const pagMensal = totalParcelas + Number(extra || 0);
    if (pagMensal <= 0 || totalDividas <= 0) return null;
    let saldo = totalDividas;
    let meses = 0;
    while (saldo > 0 && meses < 360) {
      const juros = dividas.reduce((s, d) => {
        const restante = Number(d.valor_total) - Number(d.valor_pago || 0);
        return s + restante * (Number(d.juros_mes || 0) / 100);
      }, 0);
      saldo = saldo + juros - pagMensal;
      meses++;
      if (saldo < 0) saldo = 0;
    }
    return meses;
  };

  return (
    <div style={{ padding: "0 0 40px" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24, flexWrap: "wrap", gap: 10 }}>
        <div>
          <div style={styles.pageTitle}>💳 Caixa & Dívidas</div>
          <div style={styles.pageSub}>Controle financeiro real — saiba exatamente onde você está</div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={() => setSortDirCaixa(d => d === "desc" ? "asc" : "desc")} style={{ ...styles.btn("secondary"), display: "flex", alignItems: "center", gap: 6, fontSize: 10 }}>
            Data {sortDirCaixa === "desc" ? "↓ Mais recente" : "↑ Mais antiga"}
          </button>
          <button onClick={() => { setShowForm(!showForm); setEditando(null); setForm(emptyForm); }} style={styles.btn("primary")}>+ Nova Dívida</button>
        </div>
      </div>

      {/* KPIs principais */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 20, marginBottom: 28 }}>
        {[
          { label: "Total em Dívidas", value: privado ? "••••••" : fmt(totalDividas), sub: `${dividas.length} dívida(s) ativa(s)`, color: C.warn, icon: "⚠️" },
          { label: "Parcelas/Mês", value: privado ? "••••••" : fmt(totalParcelas), sub: "Compromisso mensal fixo", color: "#E74C3C", icon: "📅" },
          { label: "Lucro Operacional do Mês", value: privado ? "••••••" : fmt(lucroMes), sub: mesAtual, color: lucroMes > 0 ? C.accent : C.warn, icon: "💰" },
          { label: sobra >= 0 ? "Sobra após dívidas" : "Déficit após dívidas", value: privado ? "••••••" : fmt(Math.abs(sobra)), sub: sobra >= 0 ? "✅ Positivo" : "⚠️ Atenção!", color: sobra >= 0 ? C.accent : C.warn, icon: sobra >= 0 ? "✅" : "🚨" },
        ].map((k, idx) => (
          <div key={k.label} className={`tfx-fade-in-delay-${idx}`} style={{ 
            background: C.bgElevated,
            border: `1px solid rgba(0,0,0,0.08)`,
            borderRadius: 20,
            padding: 24,
            position: "relative",
            overflow: "hidden",
            boxShadow: `0 1px 3px rgba(0,0,0,0.08), 0 0 0 1px rgba(0,0,0,0.06)`,
            transition: "all 0.3s"
          }}>
            <div style={{ position: "absolute", top: -30, right: -30, width: 120, height: 120, background: `radial-gradient(circle, ${k.color}15, transparent)`, borderRadius: "50%" }} />
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
              <div style={{ width: 48, height: 48, borderRadius: 12, background: `${k.color}20`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}>{k.icon}</div>
              <div style={{ fontSize: 9, color: "#374151", letterSpacing: 2, textTransform: "uppercase", fontWeight: 600 }}>{k.label}</div>
            </div>
            <div style={{ fontFamily: "'Inter', sans-serif", fontWeight: 800, fontSize: 28, color: k.color, letterSpacing: -1.2 }}>{k.value}</div>
            <div style={{ fontSize: 10, color: "#374151", marginTop: 8 }}>{k.sub}</div>
          </div>
        ))}
      </div>

      {/* Alerta se déficit */}
      {sobra < 0 && (
        <div style={{ background: "#E74C3C15", border: "1px solid #E74C3C40", borderRadius: 10, padding: "14px 18px", marginBottom: 20, display: "flex", gap: 12, alignItems: "center" }}>
          <span style={{ fontSize: 20 }}>🚨</span>
          <div>
            <div style={{ fontWeight: 700, fontSize: 14, color: "#E74C3C" }}>Atenção: suas parcelas superam o lucro este mês!</div>
            <div style={{ fontSize: 12, color: "#374151", marginTop: 2 }}>Você precisa de {fmt(Math.abs(sobra))} a mais este mês para cobrir todas as parcelas. Considere renegociar ou cortar gastos.</div>
          </div>
        </div>
      )}

      {/* Abas */}
      <div style={{ display: "flex", gap: 8, marginBottom: 28 }}>
        {[["caixa","💰 Extrato"],["visao","📊 Dívidas"],["dividas","📋 Parcelas"],["simulador","🧮 Simulador"]].map(([id, label]) => (
          <button key={id} onClick={() => setAba(id)} style={{ ...styles.btn(aba === id ? "primary" : "secondary"), fontSize: 12 }}>{label}</button>
        ))}
      </div>

      {/* Form nova dívida */}
      {showForm && (
        <div style={{ 
          ...styles.card,
          marginBottom: 24,
          background: C.bgElevated,
          border: "1px solid #e5e7eb"
        }}>
          <div style={{ 
            fontSize: 14,
            fontWeight: 700,
            color: "#111827",
            marginBottom: 20,
            paddingBottom: 12,
            borderBottom: `2px solid ${C.accent}30`
          }}>{editando ? "✏️ Editar Dívida" : "➕ Nova Dívida"}</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 12, marginBottom: 16 }}>
            <div style={{ gridColumn: "span 2" }}><label style={styles.label}>Nome da dívida</label><input style={styles.input} value={form.nome} onChange={e => setForm(f => ({ ...f, nome: e.target.value }))} placeholder="Ex: Cartão Nubank, Empréstimo Caixa" /></div>
            <div><label style={styles.label}>Tipo</label><select style={styles.input} value={form.tipo} onChange={e => setForm(f => ({ ...f, tipo: e.target.value }))}>
              <option value="cartao">💳 Cartão</option>
              <option value="emprestimo">🏦 Empréstimo</option>
              <option value="parcela">📦 Parcelamento</option>
              <option value="outro">📋 Outro</option>
            </select></div>
            <div><label style={styles.label}>Valor Total (R$)</label><input style={styles.input} type="number" value={form.valor_total} onChange={e => setForm(f => ({ ...f, valor_total: e.target.value }))} placeholder="0,00" /></div>
            <div><label style={styles.label}>Parcela Mensal (R$)</label><input style={styles.input} type="number" value={form.parcela_mensal} onChange={e => setForm(f => ({ ...f, parcela_mensal: e.target.value }))} placeholder="0,00" /></div>
            <div><label style={styles.label}>Total de Parcelas</label><input style={styles.input} type="number" value={form.parcelas_total} onChange={e => setForm(f => ({ ...f, parcelas_total: e.target.value }))} placeholder="12" /></div>
            <div><label style={styles.label}>Parcelas Pagas</label><input style={styles.input} type="number" value={form.parcelas_pagas} onChange={e => setForm(f => ({ ...f, parcelas_pagas: e.target.value }))} placeholder="0" /></div>
            <div><label style={styles.label}>Juros ao mês (%)</label><input style={styles.input} type="number" value={form.juros_mes} onChange={e => setForm(f => ({ ...f, juros_mes: e.target.value }))} placeholder="0" /></div>
            <div><label style={styles.label}>Dia vencimento</label><input style={styles.input} type="number" value={form.data_vencimento} onChange={e => setForm(f => ({ ...f, data_vencimento: e.target.value }))} placeholder="1" /></div>
            <div><label style={styles.label}>Data início</label><input style={styles.input} type="date" value={form.data_inicio} onChange={e => setForm(f => ({ ...f, data_inicio: e.target.value }))} /></div>
            <div style={{ gridColumn: "span 2" }}><label style={styles.label}>Observação</label><input style={styles.input} value={form.observacao} onChange={e => setForm(f => ({ ...f, observacao: e.target.value }))} placeholder="Ex: Negociei juros, vence todo dia 15..." /></div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={salvar} disabled={salvando} style={styles.btn("primary")}>{salvando ? "Salvando..." : "💾 Salvar"}</button>
            <button onClick={() => { setShowForm(false); setEditando(null); setForm(emptyForm); }} style={styles.btn("secondary")}>Cancelar</button>
          </div>
        </div>
      )}

      {/* ABA: CAIXA */}
      {aba === "caixa" && (() => {
        const movimentosFiltrados = filtrarCaixa(data.caixa_movimentos || [])
          .filter(m => filtroCaixaTipo === "todos" || m.tipo === filtroCaixaTipo)
          .filter(m => filtroCaixaCat === "todas" || m.categoria === filtroCaixaCat)
          .sort((a, b) => b.data?.localeCompare(a.data));
        const movimentos = [...movimentosFiltrados].sort((a, b) => {
          const dateA = new Date(a.data || '1970-01-01');
          const dateB = new Date(b.data || '1970-01-01');
          return sortDirCaixa === 'desc' ? dateB - dateA : dateA - dateB;
        });
        
        // Calcula vendas e gastos do período (respeitando modo auto/manual)
        // VENDAS
        const vendasPeriodo = vendasAuto ? (data.vendas || []).filter(v => {
          if (!v.data) return false;
          const mesVenda = v.data.slice(0, 7);
          return mesFiltroC ? mesVenda === mesFiltroC : true;
        }) : [];
        const totalVendasPeriodo = vendasPeriodo.reduce((s, v) => s + (v.valor * v.qtd), 0);
        const totalVendasJaLancado = movimentosFiltrados.filter(m => m.categoria === "vendas_loja").reduce((s, m) => s + Number(m.valor), 0);
        
        // GASTOS
        const gastosPeriodo = gastosAuto ? (data.gastos || []).filter(g => {
          if (!g.data) return false;
          const mesGasto = g.data.slice(0, 7);
          return mesFiltroC ? mesGasto === mesFiltroC : true;
        }) : [];
        const totalGastosPeriodo = gastosPeriodo.reduce((s, g) => s + g.valor, 0);
        const totalGastosJaLancado = movimentosFiltrados.filter(m => m.categoria === "gastos_loja").reduce((s, m) => s + Number(m.valor), 0);
        
        // COMPRAS
        const comprasPeriodo = comprasAuto ? (data.compras || []).filter(c => {
          if (!c.data_compra) return false;
          const mesCompra = c.data_compra.slice(0, 7);
          return mesFiltroC ? mesCompra === mesFiltroC : true;
        }) : [];
        const totalComprasPeriodo = comprasPeriodo.reduce((s, c) => s + (c.valor_total || 0), 0);
        const totalComprasJaLancado = movimentosFiltrados.filter(m => m.categoria === "compras_fornecedores").reduce((s, m) => s + Number(m.valor), 0);
        const categoriasExistentes = [...new Set((filtrarCaixa(data.caixa_movimentos || [])).map(m => m.categoria).filter(Boolean))];
        const config = data.caixa_config || {};
        const saldoInicial = Number(config.saldo_inicial || 0);
        const saldoEmergencia = Number(config.saldo_emergencia || 0);
        const metaEmergencia = Number(config.meta_emergencia || 0);
        const totalEntradas = movimentos.filter(m => m.tipo === "entrada").reduce((s, m) => s + Number(m.valor), 0);
        const totalSaidas = movimentos.filter(m => m.tipo === "saida").reduce((s, m) => s + Number(m.valor), 0);
        const saldoAtual = saldoInicial + totalEntradas - totalSaidas;
        const pctEmergencia = metaEmergencia > 0 ? Math.min((saldoEmergencia / metaEmergencia) * 100, 100) : 0;

        const salvarMovimento = async () => {
          setSalvandoCaixa(true);
          try {
            const row = { tenant_id: getTenant(), tipo: formCaixa.tipo, categoria: formCaixa.categoria, descricao: formCaixa.descricao, valor: Number(formCaixa.valor) || 0, data: formCaixa.data };
            const res = await sb.insert("caixa_movimentos", row);
            const inserted = Array.isArray(res) ? res[0] : res;
            setData(d => ({ ...d, caixa_movimentos: [...(d.caixa_movimentos || []), inserted] }));
            setFormCaixa({ tipo: "entrada", categoria: "operacional", descricao: "", valor: "", data: hojeManaus() });
            setShowFormCaixa(false);
            if (showToast) showToast("Movimento registrado!");
          } catch(e) { alert("Erro: " + e.message); }
          setSalvandoCaixa(false);
        };

        const salvarConfig = async () => {
          try {
            const row = { tenant_id: getTenant(), saldo_inicial: Number(formConfig.saldo_inicial) || 0, meta_emergencia: Number(formConfig.meta_emergencia) || 0, saldo_emergencia: Number(formConfig.saldo_emergencia) || 0 };
            if (config.id) {
              await fetch(`${API_BASE}?table=caixa_config&id=eq.${config.id}`, { method: "PATCH", headers: { Authorization: "Bearer " + getJWT(), "Content-Type": "application/json" }, body: JSON.stringify(row) });
            } else {
              await fetch(`${API_BASE}?table=caixa_config`, { method: "POST", headers: { Authorization: "Bearer " + getJWT(), "Content-Type": "application/json", Prefer: "return=representation" }, body: JSON.stringify(row) });
            }
            setData(d => ({ ...d, caixa_config: { ...d.caixa_config, ...row } }));
            setShowConfigCaixa(false);
            if (showToast) showToast("Configuração salva!");
          } catch(e) { alert("Erro: " + e.message); }
        };

        const deletarMovimento = async (id) => {
          if (!window.confirm("Excluir este movimento?")) return;
          await sb.remove("caixa_movimentos", id);
          setData(d => ({ ...d, caixa_movimentos: d.caixa_movimentos.filter(m => m.id !== id) }));
          if (showToast) showToast("Movimento removido!");
        };


        return (
          <div>
            {/* KPIs caixa */}
            <div style={styles.grid(4)}>
              {[
                { label: "Saldo em Caixa", icon: "🏦", value: privado ? "••••••" : fmt(saldoAtual), sub: `Saldo inicial: ${fmt(saldoInicial)}`, color: saldoAtual >= 0 ? C.accent : C.warn },
                { label: "Entradas", icon: "➕", value: privado ? "••••••" : fmt(totalEntradas), sub: `${movimentos.filter(m=>m.tipo==="entrada").length} movimento(s)`, color: C.accent },
                { label: "Saídas", icon: "➖", value: privado ? "••••••" : fmt(totalSaidas), sub: `${movimentos.filter(m=>m.tipo==="saida").length} movimento(s)`, color: C.warn },
                { label: "Reserva de Emergência", icon: "🛡️", value: privado ? "••••••" : fmt(saldoEmergencia), sub: metaEmergencia > 0 ? `Meta: ${fmt(metaEmergencia)}` : "Configure a meta", color: C.gold },
              ].map((k, idx) => (
                <div key={k.label} className={`tfx-fade-in-delay-${idx}`} style={{ 
                  background: C.bgElevated,
                  border: `1px solid rgba(0,0,0,0.08)`,
                  borderRadius: 20,
                  padding: 24,
                  textAlign: "center",
                  position: "relative",
                  overflow: "hidden",
                  boxShadow: `0 1px 3px rgba(0,0,0,0.08), 0 0 0 1px rgba(0,0,0,0.06)`,
                  transition: "all 0.3s"
                }}>
                  <div style={{ position: "absolute", top: -30, right: -30, width: 120, height: 120, background: `radial-gradient(circle, ${k.color}15, transparent)`, borderRadius: "50%" }} />
                  <div style={{ width: 48, height: 48, borderRadius: 12, background: `${k.color}20`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, margin: "0 auto 12px" }}>{k.icon}</div>
                  <div style={{ fontSize: 9, color: "#374151", letterSpacing: 2, textTransform: "uppercase", marginBottom: 8, fontWeight: 600 }}>{k.label}</div>
                  <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 28, fontWeight: 800, color: k.color, marginBottom: 4, letterSpacing: -1.2 }}>{k.value}</div>
                  <div style={{ fontSize: 10, color: "#374151" }}>{k.sub}</div>
                </div>
              ))}
            </div>

            {/* Barra emergência */}
            {metaEmergencia > 0 && (
              <div style={{ ...styles.card, background: C.bgElevated, border: "1px solid #e5e7eb", marginBottom: 28 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                  <div style={{ fontWeight: 700, fontSize: 14, color: "#111827" }}>🛡️ Reserva de Emergência</div>
                  <div style={{ fontSize: 12, color: C.gold }}>{pctEmergencia.toFixed(0)}% da meta</div>
                </div>
                <div style={{ background: C.border, borderRadius: 6, height: 12, overflow: "hidden", marginBottom: 8 }}>
                  <div style={{ width: `${pctEmergencia}%`, height: "100%", background: pctEmergencia >= 100 ? C.accent : C.gold, borderRadius: 6, transition: "width 0.5s" }} />
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "#374151" }}>
                  <span>{privado ? "••••••" : fmt(saldoEmergencia)} guardado</span>
                  <span>Meta: {privado ? "••••••" : fmt(metaEmergencia)}</span>
                </div>
                {pctEmergencia < 100 && <div style={{ fontSize: 11, color: "#374151", marginTop: 6 }}>Faltam {privado ? "••••••" : fmt(metaEmergencia - saldoEmergencia)} para completar a reserva.</div>}
              </div>
            )}

            {/* Filtros tipo e categoria */}
            <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap", marginBottom: 10 }}>
              {[["todos","Todos"],["entrada","▲ Entradas"],["saida","▼ Saídas"]].map(([v,l]) => (
                <button key={v} onClick={() => setFiltroCaixaTipo(v)} style={{ ...styles.btn("secondary"), fontSize: 10, padding: "4px 12px", background: filtroCaixaTipo === v ? C.accent+"20" : "transparent", borderColor: filtroCaixaTipo === v ? C.accent : C.border, color: filtroCaixaTipo === v ? C.accent : C.muted }}>{l}</button>
              ))}
              <div style={{ width: 1, height: 20, background: C.border }} />
              <button onClick={() => setFiltroCaixaCat("todas")} style={{ ...styles.btn("secondary"), fontSize: 10, padding: "4px 12px", background: filtroCaixaCat === "todas" ? C.accent+"20" : "transparent", borderColor: filtroCaixaCat === "todas" ? C.accent : C.border, color: filtroCaixaCat === "todas" ? C.accent : C.muted }}>Todas categorias</button>
              {categoriasExistentes.map(c => (
                <button key={c} onClick={() => setFiltroCaixaCat(c)} style={{ ...styles.btn("secondary"), fontSize: 10, padding: "4px 12px", background: filtroCaixaCat === c ? C.accent+"20" : "transparent", borderColor: filtroCaixaCat === c ? C.accent : C.border, color: filtroCaixaCat === c ? C.accent : C.muted }}>{c}</button>
              ))}
            </div>

            {/* Filtro período caixa */}
            <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap", marginBottom: 14 }}>
              <span style={{ fontSize: 10, color: "#374151" }}>Filtrar:</span>
              {[["hoje","Hoje"],["ontem","Ontem"],["7d","7d"],["15d","15d"],["30d","30d"],["90d","90d"]].map(([p,l]) => (
                <button key={p} onClick={() => aplicarPeriodoCaixa(p)} style={{ ...styles.btn("secondary"), fontSize: 10, padding: "4px 10px", background: periodoRapidoCaixa === p ? C.accent+"20" : "transparent", borderColor: periodoRapidoCaixa === p ? C.accent : C.border, color: periodoRapidoCaixa === p ? C.accent : C.muted }}>{l}</button>
              ))}
              <select style={{ ...styles.input, width: "auto", fontSize: 11, padding: "4px 10px" }} value={mesFiltroC} onChange={e => selecionarMesCaixa(e.target.value)}>
                <option value="todos">Todos</option>
                {Array.from({length: 12}, (_, i) => mesRelativo(-i)).map(m => (
                  <option key={m} value={m}>{MESES[parseInt(m.slice(5,7))-1] + "/" + m.slice(2,4)}</option>
                ))}
              </select>
            </div>

            {/* Ações */}
            <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
              <button onClick={() => { setFormCaixa({ tipo: "entrada", categoria: "operacional", descricao: "", valor: "", data: hojeManaus() }); setShowFormCaixa(!showFormCaixa); }} style={{ ...styles.btn("primary"), fontSize: 12 }}>+ Entrada</button>
              <button onClick={() => { setFormCaixa({ tipo: "saida", categoria: "operacional", descricao: "", valor: "", data: hojeManaus() }); setShowFormCaixa(!showFormCaixa); }} style={{ ...styles.btn("secondary"), fontSize: 12, color: C.warn, borderColor: C.warn }}>− Saída</button>


              <button onClick={() => { setFormConfig({ saldo_inicial: String(config.saldo_inicial || ""), meta_emergencia: String(config.meta_emergencia || ""), saldo_emergencia: String(config.saldo_emergencia || "") }); setShowConfigCaixa(!showConfigCaixa); }} style={{ ...styles.btn("secondary"), fontSize: 12 }}>⚙️ Configurar</button>
            </div>

            {/* Form config */}
            {showConfigCaixa && (
              <div style={{ ...styles.card, background: C.bgElevated, border: "1px solid #e5e7eb", marginBottom: 16, borderColor: C.gold + "40" }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: "#111827", marginBottom: 20, paddingBottom: 12, borderBottom: `2px solid ${C.accent}30` }}>⚙️ Configurar Caixa</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 16 }}>
                  <div><label style={styles.label}>Saldo Inicial (R$) <span style={{fontSize:9,color:C.muted}}>— valor em caixa agora</span></label><input style={styles.input} type="number" value={formConfig.saldo_inicial} onChange={e => setFormConfig(f => ({ ...f, saldo_inicial: e.target.value }))} placeholder="Ex: 5000" /></div>
                  <div><label style={styles.label}>Reserva de Emergência Atual (R$)</label><input style={styles.input} type="number" value={formConfig.saldo_emergencia} onChange={e => setFormConfig(f => ({ ...f, saldo_emergencia: e.target.value }))} placeholder="Ex: 2000" /></div>
                  <div><label style={styles.label}>Meta de Emergência (R$)</label><input style={styles.input} type="number" value={formConfig.meta_emergencia} onChange={e => setFormConfig(f => ({ ...f, meta_emergencia: e.target.value }))} placeholder="Ex: 10000" /></div>
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button onClick={salvarConfig} style={styles.btn("primary")}>💾 Salvar</button>
                  <button onClick={() => setShowConfigCaixa(false)} style={styles.btn("secondary")}>Cancelar</button>
                </div>
              </div>
            )}

            {/* Painel de Tráfego Pago */}
            {(() => {
              const trafegoAuto = config.trafego_auto !== false;
              const mesAtualSync = hojeManaus().slice(0, 7);
              const trafegoDoMes = (data.gastos || []).filter(g => g.data?.startsWith(mesAtualSync) && g.categoria === "Tráfego Pago");
              const totalTrafego = trafegoDoMes.reduce((s, g) => s + Number(g.valor), 0);

              const toggleTrafegoAuto = async (val) => {
                try {
                  const row = { trafego_auto: val };
                  if (config.id) {
                    await fetch(`${API_BASE}?table=caixa_config&id=eq.${config.id}`, { method: "PATCH", headers: { Authorization: "Bearer " + getJWT(), "Content-Type": "application/json" }, body: JSON.stringify(row) });
                  } else {
                    await fetch(`${API_BASE}?table=caixa_config`, { method: "POST", headers: { Authorization: "Bearer " + getJWT(), "Content-Type": "application/json", Prefer: "return=representation" }, body: JSON.stringify(row) });
                  }
                  setData(d => ({ ...d, caixa_config: { ...d.caixa_config, trafego_auto: val } }));
                  if (showToast) showToast(val ? "Tráfego: lançamento automático ativado!" : "Tráfego: lançamento manual ativado!");
                } catch(e) { alert("Erro: " + e.message); }
              };

              const lancarTrafegoManual = async () => {
                const valor = Number(valorTrafegoManual);
                if (!valor || valor <= 0) return alert("Informe um valor válido.");
                if (!dataTrafegoManual) return alert("Informe a data do pagamento.");
                setSalvandoCaixa(true);
                const row = { tenant_id: getTenant(), tipo: "saida", categoria: "operacional", descricao: `Fatura Tráfego Pago (${mesAtualSync})`, valor, data: dataTrafegoManual };
                const res = await sb.insert("caixa_movimentos", row);
                const inserted = Array.isArray(res) ? res[0] : res;
                setData(d => ({ ...d, caixa_movimentos: [...(d.caixa_movimentos || []), inserted] }));
                setValorTrafegoManual("");
                setDataTrafegoManual(hojeManaus());
                setSalvandoCaixa(false);
                if (showToast) showToast("Tráfego lançado no caixa!");
              };

              const lancarTrafegoAuto = async () => {
                if (trafegoDoMes.length === 0) return alert("Nenhum gasto de Tráfego Pago encontrado neste mês.");
                if (!window.confirm(`Lançar R$ ${totalTrafego.toLocaleString("pt-BR", {minimumFractionDigits:2})} de Tráfego Pago no caixa com a data de hoje?`)) return;
                setSalvandoCaixa(true);
                const row = { tenant_id: getTenant(), tipo: "saida", categoria: "operacional", descricao: `Fatura Tráfego Pago (${mesAtualSync})`, valor: totalTrafego, data: hojeManaus() };
                const res = await sb.insert("caixa_movimentos", row);
                const inserted = Array.isArray(res) ? res[0] : res;
                setData(d => ({ ...d, caixa_movimentos: [...(d.caixa_movimentos || []), inserted] }));
                setSalvandoCaixa(false);
                if (showToast) showToast("Tráfego lançado no caixa!");
              };

              return (
                <div style={{ ...styles.card, background: C.bgElevated, border: "1px solid #e5e7eb", marginBottom: 16, borderColor: C.info + "40" }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 14, color: "#111827" }}>📱 Tráfego Pago no Caixa</div>
                      <div style={{ fontSize: 11, color: "#374151", marginTop: 2 }}>Total do mês: <span style={{ color: C.info, fontWeight: 600 }}>{fmt(totalTrafego)}</span></div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <span style={{ fontSize: 11, color: "#374151" }}>Lançamento:</span>
                      <div style={{ display: "flex", gap: 4 }}>
                        <button onClick={() => toggleTrafegoAuto(true)} style={{ ...styles.btn(trafegoAuto ? "primary" : "secondary"), fontSize: 10, padding: "4px 12px" }}>🤖 Automático</button>
                        <button onClick={() => toggleTrafegoAuto(false)} style={{ ...styles.btn(!trafegoAuto ? "primary" : "secondary"), fontSize: 10, padding: "4px 12px" }}>✍️ Manual</button>
                      </div>
                    </div>
                  </div>

                  {trafegoAuto ? (
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <div style={{ fontSize: 11, color: "#374151", flex: 1 }}>O tráfego será lançado automaticamente ao sincronizar o mês. Ou clique para lançar agora.</div>
                      <button onClick={lancarTrafegoAuto} disabled={salvandoCaixa} style={{ ...styles.btn("primary"), fontSize: 11, padding: "6px 14px", whiteSpace: "nowrap" }}>⚡ Lançar agora</button>
                    </div>
                  ) : (
                    <div>
                      <div style={{ fontSize: 11, color: "#374151", marginBottom: 10 }}>Informe o valor real da fatura e a data do pagamento:</div>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: 10, alignItems: "end" }}>
                        <div>
                          <label style={styles.label}>Valor da fatura (R$)</label>
                          <input style={styles.input} type="number" value={valorTrafegoManual} onChange={e => setValorTrafegoManual(e.target.value)} placeholder={`Sugerido: ${fmt(totalTrafego)}`} />
                        </div>
                        <div>
                          <label style={styles.label}>Data do pagamento</label>
                          <input style={styles.input} type="date" value={dataTrafegoManual} onChange={e => setDataTrafegoManual(e.target.value)} />
                        </div>
                        <button onClick={lancarTrafegoManual} disabled={salvandoCaixa} style={{ ...styles.btn("primary"), fontSize: 11, padding: "10px 16px", whiteSpace: "nowrap" }}>💾 Lançar no caixa</button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Card Vendas da Loja no Caixa */}
            {(() => {
              const diferencaVendas = totalVendasPeriodo - totalVendasJaLancado;
              return (
                <div style={{ ...styles.card, background: C.bgElevated, border: "1px solid #e5e7eb", marginBottom: 16 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 14, color: "#111827" }}>🛒 Vendas da Loja no Caixa</div>
                      <div style={{ fontSize: 11, color: "#374151", marginTop: 2 }}>
                        Total período: <span style={{ color: C.accent, fontWeight: 600 }}>{fmt(totalVendasPeriodo)}</span> · 
                        Já lançado: <span style={{ color: C.info, fontWeight: 600 }}>{fmt(totalVendasJaLancado)}</span> · 
                        Diferença: <span style={{ color: diferencaVendas > 0 ? C.warn : C.muted, fontWeight: 600 }}>{fmt(diferencaVendas)}</span>
                      </div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <span style={{ fontSize: 11, color: "#374151" }}>Lançamento:</span>
                      <div style={{ display: "flex", gap: 4 }}>
                        <button onClick={() => toggleVendasAuto(true)} style={{ ...styles.btn(vendasAuto ? "primary" : "secondary"), fontSize: 10, padding: "4px 12px" }}>🤖 Automático</button>
                        <button onClick={() => toggleVendasAuto(false)} style={{ ...styles.btn(!vendasAuto ? "primary" : "secondary"), fontSize: 10, padding: "4px 12px" }}>✍️ Manual</button>
                      </div>
                    </div>
                  </div>

                  {vendasAuto ? (
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <div style={{ fontSize: 11, color: "#374151", flex: 1 }}>As vendas serão lançadas automaticamente. Ou clique para lançar a diferença agora.</div>
                      <button onClick={() => lancarVendasAuto(totalVendasPeriodo, totalVendasJaLancado)} disabled={salvandoCaixa || diferencaVendas <= 0} style={{ ...styles.btn("primary"), fontSize: 11, padding: "6px 14px", whiteSpace: "nowrap", opacity: diferencaVendas <= 0 ? 0.5 : 1 }}>⚡ Lançar agora ({fmt(diferencaVendas)})</button>
                    </div>
                  ) : (
                    <div>
                      <div style={{ fontSize: 11, color: "#374151", marginBottom: 10 }}>Informe o valor real recebido e a data:</div>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: 10, alignItems: "end" }}>
                        <div>
                          <label style={styles.label}>Valor recebido (R$)</label>
                          <input style={styles.input} type="number" value={valorVendasManual} onChange={e => setValorVendasManual(e.target.value)} placeholder={`Sugerido: ${fmt(diferencaVendas)}`} />
                        </div>
                        <div>
                          <label style={styles.label}>Data do recebimento</label>
                          <input style={styles.input} type="date" value={dataVendasManual} onChange={e => setDataVendasManual(e.target.value)} />
                        </div>
                        <button onClick={lancarVendasManual} disabled={salvandoCaixa} style={{ ...styles.btn("primary"), fontSize: 11, padding: "10px 16px", whiteSpace: "nowrap" }}>💾 Lançar no caixa</button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Card Gastos da Loja no Caixa */}
            {(() => {
              const diferencaGastos = totalGastosPeriodo - totalGastosJaLancado;
              return (
                <div style={{ ...styles.card, background: C.bgElevated, border: "1px solid #e5e7eb", marginBottom: 16 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 14, color: "#111827" }}>💸 Gastos da Loja no Caixa</div>
                      <div style={{ fontSize: 11, color: "#374151", marginTop: 2 }}>
                        Total período: <span style={{ color: C.warn, fontWeight: 600 }}>{fmt(totalGastosPeriodo)}</span> · 
                        Já lançado: <span style={{ color: C.info, fontWeight: 600 }}>{fmt(totalGastosJaLancado)}</span> · 
                        Diferença: <span style={{ color: diferencaGastos > 0 ? C.warn : C.muted, fontWeight: 600 }}>{fmt(diferencaGastos)}</span>
                      </div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <span style={{ fontSize: 11, color: "#374151" }}>Lançamento:</span>
                      <div style={{ display: "flex", gap: 4 }}>
                        <button onClick={() => toggleGastosAuto(true)} style={{ ...styles.btn(gastosAuto ? "primary" : "secondary"), fontSize: 10, padding: "4px 12px" }}>🤖 Automático</button>
                        <button onClick={() => toggleGastosAuto(false)} style={{ ...styles.btn(!gastosAuto ? "primary" : "secondary"), fontSize: 10, padding: "4px 12px" }}>✍️ Manual</button>
                      </div>
                    </div>
                  </div>

                  {gastosAuto ? (
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <div style={{ fontSize: 11, color: "#374151", flex: 1 }}>Os gastos serão lançados automaticamente. Ou clique para lançar a diferença agora.</div>
                      <button onClick={() => lancarGastosAuto(totalGastosPeriodo, totalGastosJaLancado)} disabled={salvandoCaixa || diferencaGastos <= 0} style={{ ...styles.btn("primary"), fontSize: 11, padding: "6px 14px", whiteSpace: "nowrap", opacity: diferencaGastos <= 0 ? 0.5 : 1 }}>⚡ Lançar agora ({fmt(diferencaGastos)})</button>
                    </div>
                  ) : (
                    <div>
                      <div style={{ fontSize: 11, color: "#374151", marginBottom: 10 }}>Informe o valor real pago e a data:</div>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: 10, alignItems: "end" }}>
                        <div>
                          <label style={styles.label}>Valor pago (R$)</label>
                          <input style={styles.input} type="number" value={valorGastosManual} onChange={e => setValorGastosManual(e.target.value)} placeholder={`Sugerido: ${fmt(diferencaGastos)}`} />
                        </div>
                        <div>
                          <label style={styles.label}>Data do pagamento</label>
                          <input style={styles.input} type="date" value={dataGastosManual} onChange={e => setDataGastosManual(e.target.value)} />
                        </div>
                        <button onClick={lancarGastosManual} disabled={salvandoCaixa} style={{ ...styles.btn("primary"), fontSize: 11, padding: "10px 16px", whiteSpace: "nowrap" }}>💾 Lançar no caixa</button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Card Compras/Fornecedores no Caixa */}
            {(() => {
              const diferencaCompras = totalComprasPeriodo - totalComprasJaLancado;
              return (
                <div style={{ ...styles.card, background: C.bgElevated, border: "1px solid #e5e7eb", marginBottom: 16 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 14, color: "#111827" }}>📦 Compras/Fornecedores no Caixa</div>
                      <div style={{ fontSize: 11, color: "#374151", marginTop: 2 }}>
                        Total período: <span style={{ color: C.gold, fontWeight: 600 }}>{fmt(totalComprasPeriodo)}</span> · 
                        Já lançado: <span style={{ color: C.info, fontWeight: 600 }}>{fmt(totalComprasJaLancado)}</span> · 
                        Diferença: <span style={{ color: diferencaCompras > 0 ? C.warn : C.muted, fontWeight: 600 }}>{fmt(diferencaCompras)}</span>
                      </div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <span style={{ fontSize: 11, color: "#374151" }}>Lançamento:</span>
                      <div style={{ display: "flex", gap: 4 }}>
                        <button onClick={() => toggleComprasAuto(true)} style={{ ...styles.btn(comprasAuto ? "primary" : "secondary"), fontSize: 10, padding: "4px 12px" }}>🤖 Automático</button>
                        <button onClick={() => toggleComprasAuto(false)} style={{ ...styles.btn(!comprasAuto ? "primary" : "secondary"), fontSize: 10, padding: "4px 12px" }}>✍️ Manual</button>
                      </div>
                    </div>
                  </div>

                  {comprasAuto ? (
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <div style={{ fontSize: 11, color: "#374151", flex: 1 }}>As compras serão lançadas automaticamente. Ou clique para lançar a diferença agora.</div>
                      <button onClick={() => lancarComprasAuto(totalComprasPeriodo, totalComprasJaLancado)} disabled={salvandoCaixa || diferencaCompras <= 0} style={{ ...styles.btn("primary"), fontSize: 11, padding: "6px 14px", whiteSpace: "nowrap", opacity: diferencaCompras <= 0 ? 0.5 : 1 }}>⚡ Lançar agora ({fmt(diferencaCompras)})</button>
                    </div>
                  ) : (
                    <div>
                      <div style={{ fontSize: 11, color: "#374151", marginBottom: 10 }}>Informe o valor real pago aos fornecedores e a data:</div>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: 10, alignItems: "end" }}>
                        <div>
                          <label style={styles.label}>Valor pago (R$)</label>
                          <input style={styles.input} type="number" value={valorComprasManual} onChange={e => setValorComprasManual(e.target.value)} placeholder={`Sugerido: ${fmt(diferencaCompras)}`} />
                        </div>
                        <div>
                          <label style={styles.label}>Data do pagamento</label>
                          <input style={styles.input} type="date" value={dataComprasManual} onChange={e => setDataComprasManual(e.target.value)} />
                        </div>
                        <button onClick={lancarComprasManual} disabled={salvandoCaixa} style={{ ...styles.btn("primary"), fontSize: 11, padding: "10px 16px", whiteSpace: "nowrap" }}>💾 Lançar no caixa</button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Form movimento */}
            {showFormCaixa && (
              <div style={{ ...styles.card, background: C.bgElevated, border: "1px solid #e5e7eb", marginBottom: 16, borderColor: formCaixa.tipo === "entrada" ? C.accent+"40" : C.warn+"40" }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: "#111827", marginBottom: 20, paddingBottom: 12, borderBottom: `2px solid ${C.accent}30` }}>{formCaixa.tipo === "entrada" ? "➕ Nova Entrada" : "➖ Nova Saída"}</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 12, marginBottom: 16 }}>
                  <div><label style={styles.label}>Tipo</label><select style={styles.input} value={formCaixa.tipo} onChange={e => setFormCaixa(f => ({ ...f, tipo: e.target.value }))}>
                    <option value="entrada">➕ Entrada</option>
                    <option value="saida">➖ Saída</option>
                  </select></div>
                  <div><label style={styles.label}>Categoria</label><select style={styles.input} value={formCaixa.categoria} onChange={e => setFormCaixa(f => ({ ...f, categoria: e.target.value }))}>
                    <option value="operacional">Operacional</option>
                    <option value="emergencia">Reserva Emergência</option>
                    <option value="pessoal">Pessoal</option>
                    <option value="investimento">Investimento</option>
                    <option value="outro">Outro</option>
                  </select></div>
                  <div style={{ gridColumn: "span 2" }}><label style={styles.label}>Descrição</label><input style={styles.input} value={formCaixa.descricao} onChange={e => setFormCaixa(f => ({ ...f, descricao: e.target.value }))} placeholder="Ex: Venda à vista, Pagamento fornecedor..." /></div>
                  <div><label style={styles.label}>Valor (R$)</label><input style={styles.input} type="number" value={formCaixa.valor} onChange={e => setFormCaixa(f => ({ ...f, valor: e.target.value }))} placeholder="0,00" /></div>
                  <div><label style={styles.label}>Data</label><input style={styles.input} type="date" value={formCaixa.data} onChange={e => setFormCaixa(f => ({ ...f, data: e.target.value }))} /></div>
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button onClick={salvarMovimento} disabled={salvandoCaixa} style={styles.btn("primary")}>{salvandoCaixa ? "Salvando..." : "💾 Salvar"}</button>
                  <button onClick={() => setShowFormCaixa(false)} style={styles.btn("secondary")}>Cancelar</button>
                </div>
              </div>
            )}

            {/* Extrato */}
            <div style={styles.card}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: "#111827", marginBottom: 20, paddingBottom: 12, borderBottom: `2px solid ${C.accent}30` }}>📋 Extrato de Movimentos</div>
                {selCaixa.length > 0 && (
                  <button onClick={async () => {
                    if (!window.confirm(`Deletar ${selCaixa.length} movimento(s)?`)) return;
                    for (const id of selCaixa) await sb.remove("caixa_movimentos", id);
                    setData(d => ({ ...d, caixa_movimentos: d.caixa_movimentos.filter(m => !selCaixa.includes(m.id)) }));
                    setSelCaixa([]);
                    if (showToast) showToast(`${selCaixa.length} movimento(s) deletado(s)!`);
                  }} style={{ ...styles.btn("secondary"), fontSize: 10, padding: "4px 10px", color: C.warn, borderColor: C.warn }}>
                    🗑️ Deletar {selCaixa.length}
                  </button>
                )}
              </div>
              {movimentos.length === 0 ? (
                <div style={{ textAlign: "center", padding: "30px 0", color: "#374151" }}>
                  <div style={{ fontSize: 32, marginBottom: 8 }}>💰</div>
                  <div>Nenhum movimento registrado. Clique em "+ Entrada" ou "− Saída" para começar.</div>
                </div>
              ) : (
                <table style={{ ...styles.table, borderCollapse: "separate", borderSpacing: 0 }}>
                  <thead>
            <tr style={{ 
              background: `linear-gradient(90deg, ${C.surface}, ${C.bgElevated})`,
              borderBottom: `2px solid ${C.accent}40`
            }}>
                      <th style={{ ...styles.th, width: 32, padding: "10px 12px" }}><input type="checkbox" onChange={() => setSelCaixa(s => s.length === movimentos.length ? [] : movimentos.map(m => m.id))} checked={selCaixa.length === movimentos.length && movimentos.length > 0} /></th>
                      {["Data", "Descrição", "Categoria", "Tipo", "Valor", ""].map(h => (
                        <th key={h} style={{ ...styles.th, padding: "10px 14px", fontSize: 9, letterSpacing: 1.5, borderBottom: `2px solid ${C.accent}30` }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {movimentos.map((m, idx) => (
                      <tr key={m.id} style={{ background: selCaixa.includes(m.id) ? "#f9fafb" : C.surface+"80", transition: "background 0.15s" }}
                        onMouseEnter={e => e.currentTarget.style.background = "#fafafa"}
                        onMouseLeave={e => e.currentTarget.style.background = selCaixa.includes(m.id) ? "#f9fafb" : C.surface+"80"}>
                        <td style={{ ...styles.td, padding: "10px 12px", borderBottom: "1px solid #e5e7eb" }}><input type="checkbox" checked={selCaixa.includes(m.id)} onChange={() => setSelCaixa(s => s.includes(m.id) ? s.filter(x => x !== m.id) : [...s, m.id])} /></td>
                        <td style={{ ...styles.td, padding: "10px 14px", borderBottom: "1px solid #e5e7eb", color: "#374151", fontSize: 11 }}>{m.data}</td>
                        <td style={{ ...styles.td, padding: "10px 14px", borderBottom: "1px solid #e5e7eb", color: "#111827", fontWeight: 500 }}>{m.descricao}</td>
                        <td style={{ ...styles.td, padding: "10px 14px", borderBottom: "1px solid #e5e7eb" }}><span style={{ fontSize: 9, background: C.bgElevated, color: "#374151", padding: "2px 8px", borderRadius: 4, border: "1px solid #e5e7eb" }}>{m.categoria}</span></td>
                        <td style={{ ...styles.td, padding: "10px 14px", borderBottom: "1px solid #e5e7eb" }}>
                          <span style={{ fontSize: 9, background: m.tipo === "entrada" ? C.accent+"20" : C.warn+"20", color: m.tipo === "entrada" ? C.accent : C.warn, padding: "2px 8px", borderRadius: 4, fontWeight: 600 }}>{m.tipo === "entrada" ? "▲ Entrada" : "▼ Saída"}</span>
                        </td>
                        <td style={{ ...styles.td, padding: "10px 14px", borderBottom: "1px solid #e5e7eb", fontWeight: 700, color: m.tipo === "entrada" ? C.accent : C.warn }}>{m.tipo === "entrada" ? "+" : "-"}{privado ? "••••" : fmt(m.valor)}</td>
                        <td style={{ ...styles.td, padding: "8px 10px", borderBottom: "1px solid #e5e7eb" }}>
                          <button onClick={() => deletarMovimento(m.id)} style={{ background: "none", border: "none", color: C.warn, cursor: "pointer", fontSize: 14 }}>✕</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {movimentos.length > 0 && (
                <div style={{ display: "flex", justifyContent: "space-between", padding: "12px 14px", background: C.bgElevated, borderRadius: 8, marginTop: 12 }}>
                  <span style={{ fontSize: 12, color: "#374151" }}>Saldo atual</span>
                  <span style={{ fontSize: 16, fontWeight: 800, color: saldoAtual >= 0 ? C.accent : C.warn }}>{privado ? "••••••" : fmt(saldoAtual)}</span>
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* ABA: VISÃO GERAL */}
      {aba === "visao" && (
        <div>
          {/* Barra de progresso geral */}
          <div style={{ ...styles.card, background: C.bgElevated, border: "1px solid #e5e7eb", marginBottom: 28 }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: "#111827", marginBottom: 16 }}>📊 Progresso Geral de Quitação</div>
            {dividas.length === 0 ? (
              <div style={{ textAlign: "center", padding: "30px 0", color: "#374151" }}>
                <div style={{ fontSize: 36, marginBottom: 8 }}>🎉</div>
                <div style={{ fontWeight: 600, color: C.accent }}>Nenhuma dívida cadastrada!</div>
                <div style={{ fontSize: 12, marginTop: 4 }}>Clique em "+ Nova Dívida" para começar a controlar.</div>
              </div>
            ) : (
              <div>
                {dividas.sort((a, b) => Number(b.juros_mes || 0) - Number(a.juros_mes || 0)).map(d => {
                  const restante = Number(d.valor_total) - Number(d.valor_pago || 0);
                  const pct = d.valor_total > 0 ? (Number(d.valor_pago || 0) / Number(d.valor_total)) * 100 : 0;
                  const parcelasRestantes = Number(d.parcelas_total || 0) - Number(d.parcelas_pagas || 0);
                  const cor = tipoColor[d.tipo] || C.muted;
                  return (
                    <div key={d.id} style={{ marginBottom: 16, padding: "14px 16px", background: C.bgElevated, borderRadius: 10, border: `1px solid ${cor}30` }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
                        <div>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <span style={{ fontSize: 16 }}>{tipoIcon[d.tipo] || "📋"}</span>
                            <span style={{ fontWeight: 700, fontSize: 14, color: "#111827" }}>{d.nome}</span>
                            <span style={{ fontSize: 9, background: cor+"20", color: cor, padding: "2px 8px", borderRadius: 4 }}>{tipoLabel[d.tipo]}</span>
                            {Number(d.juros_mes) > 0 && <span style={{ fontSize: 9, background: C.warn+"20", color: C.warn, padding: "2px 8px", borderRadius: 4 }}>{d.juros_mes}% a.m.</span>}
                          </div>
                          {d.observacao && <div style={{ fontSize: 11, color: "#374151", marginTop: 2 }}>{d.observacao}</div>}
                        </div>
                        <div style={{ textAlign: "right" }}>
                          <div style={{ fontSize: 16, fontWeight: 800, color: cor }}>{privado ? "••••••" : fmt(restante)}</div>
                          <div style={{ fontSize: 10, color: "#374151" }}>restante</div>
                        </div>
                      </div>
                      <div style={{ background: C.border, borderRadius: 4, height: 8, overflow: "hidden", marginBottom: 8 }}>
                        <div style={{ width: `${Math.min(pct, 100)}%`, height: "100%", background: pct >= 100 ? C.accent : cor, borderRadius: 4, transition: "width 0.5s" }} />
                      </div>
                      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: "#374151" }}>
                        <span>{pct.toFixed(0)}% quitado · {privado ? "••••" : fmt(d.parcela_mensal)}/mês</span>
                        <span>{parcelasRestantes > 0 ? `${parcelasRestantes} parcela(s) restante(s)` : "✅ Quitado!"}</span>
                      </div>
                      {parcelasRestantes > 0 && (
                        <div style={{ display: "flex", gap: 6, marginTop: 10 }}>
                          <button onClick={() => pagarParcela(d)} style={{ ...styles.btn("primary"), fontSize: 10, padding: "4px 12px" }}>✅ Registrar parcela paga</button>
                          <button onClick={() => { setEditando(d); setForm({ nome: d.nome, tipo: d.tipo, valor_total: String(d.valor_total), parcela_mensal: String(d.parcela_mensal), parcelas_total: String(d.parcelas_total), parcelas_pagas: String(d.parcelas_pagas), juros_mes: String(d.juros_mes || ""), data_inicio: d.data_inicio || hojeManaus(), data_vencimento: String(d.data_vencimento || 1), observacao: d.observacao || "" }); setShowForm(true); }} style={{ ...styles.btn("secondary"), fontSize: 10, padding: "4px 12px" }}>✎ Editar</button>
                          <button onClick={() => deletar(d.id)} style={{ ...styles.btn("secondary"), fontSize: 10, padding: "4px 12px", color: C.warn }}>✕ Excluir</button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Dica de prioridade */}
          {dividas.filter(d => Number(d.juros_mes) > 0).length > 0 && (
            <div style={{ ...styles.card, background: C.bgElevated, border: "1px solid #e5e7eb", borderColor: C.gold + "40" }}>
              <div style={{ fontWeight: 700, fontSize: 14, color: C.gold, marginBottom: 8 }}>💡 Estratégia recomendada: método avalanche</div>
              <div style={{ fontSize: 12, color: "#374151", marginBottom: 12 }}>Pague o mínimo em todas e coloque o dinheiro extra na dívida com maior juros primeiro. Isso economiza mais no longo prazo.</div>
              <div style={{ fontSize: 11, color: "#111827" }}>Prioridade de pagamento:</div>
              {[...dividas].sort((a, b) => Number(b.juros_mes || 0) - Number(a.juros_mes || 0)).map((d, i) => (
                <div key={d.id} style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8 }}>
                  <div style={{ width: 22, height: 22, borderRadius: "50%", background: i === 0 ? C.warn : C.surface, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, color: i === 0 ? "#fff" : C.muted }}>{i + 1}</div>
                  <div style={{ flex: 1, fontSize: 12, color: "#111827" }}>{d.nome}</div>
                  <div style={{ fontSize: 11, color: i === 0 ? C.warn : C.muted }}>{d.juros_mes || 0}% a.m.</div>
                  {i === 0 && <span style={{ fontSize: 9, background: C.warn+"20", color: C.warn, padding: "2px 6px", borderRadius: 4 }}>PAGAR PRIMEIRO</span>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ABA: MINHAS DÍVIDAS */}
      {aba === "dividas" && (
        <div style={styles.card}>
          {dividas.length === 0 ? (
            <div style={{ textAlign: "center", padding: "40px 0", color: "#374151" }}>
              <div style={{ fontSize: 36 }}>💳</div>
              <div style={{ fontWeight: 600, marginTop: 8 }}>Nenhuma dívida cadastrada</div>
            </div>
          ) : (
            <table style={{ ...styles.table, borderCollapse: "separate", borderSpacing: 0 }}>
              <thead>
            <tr style={{ 
              background: `linear-gradient(90deg, ${C.surface}, ${C.bgElevated})`,
              borderBottom: `2px solid ${C.accent}40`
            }}>
                  {["Dívida", "Tipo", "Total", "Pago", "Restante", "Parcela/Mês", "Parcelas", "Juros", "Vence dia", ""].map(h => (
                    <th key={h} style={{ ...styles.th, padding: "10px 14px", fontSize: 9, letterSpacing: 1.5, borderBottom: `2px solid ${C.accent}30` }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {dividas.map((d, idx) => {
                  const restante = Number(d.valor_total) - Number(d.valor_pago || 0);
                  const cor = tipoColor[d.tipo] || C.muted;
                  return (
                    <tr key={d.id} style={{ background: C.surface+"80", transition: "background 0.15s" }}
                      onMouseEnter={e => e.currentTarget.style.background = "#fafafa"}
                      onMouseLeave={e => e.currentTarget.style.background = C.surface+"80"}>
                      <td style={{ ...styles.td, padding: "10px 14px", borderBottom: "1px solid #e5e7eb", fontWeight: 600, color: "#111827" }}>{d.nome}</td>
                      <td style={{ ...styles.td, padding: "10px 14px", borderBottom: "1px solid #e5e7eb" }}><span style={{ fontSize: 9, background: cor+"20", color: cor, padding: "2px 8px", borderRadius: 4 }}>{tipoLabel[d.tipo]}</span></td>
                      <td style={{ ...styles.td, padding: "10px 14px", borderBottom: "1px solid #e5e7eb", color: "#374151" }}>{privado ? "••••" : fmt(d.valor_total)}</td>
                      <td style={{ ...styles.td, padding: "10px 14px", borderBottom: "1px solid #e5e7eb", color: C.accent }}>{privado ? "••••" : fmt(d.valor_pago || 0)}</td>
                      <td style={{ ...styles.td, padding: "10px 14px", borderBottom: "1px solid #e5e7eb", color: C.warn, fontWeight: 700 }}>{privado ? "••••" : fmt(restante)}</td>
                      <td style={{ ...styles.td, padding: "10px 14px", borderBottom: "1px solid #e5e7eb", color: C.gold }}>{privado ? "••••" : fmt(d.parcela_mensal)}</td>
                      <td style={{ ...styles.td, padding: "10px 14px", borderBottom: "1px solid #e5e7eb", color: "#374151" }}>{d.parcelas_pagas || 0}/{d.parcelas_total || "—"}</td>
                      <td style={{ ...styles.td, padding: "10px 14px", borderBottom: "1px solid #e5e7eb", color: Number(d.juros_mes) > 3 ? C.warn : C.muted }}>{d.juros_mes || 0}%</td>
                      <td style={{ ...styles.td, padding: "10px 14px", borderBottom: "1px solid #e5e7eb", color: "#374151" }}>dia {d.data_vencimento || "—"}</td>
                      <td style={{ ...styles.td, padding: "8px 10px", borderBottom: "1px solid #e5e7eb" }}>
                        <div style={{ display: "flex", gap: 4 }}>
                          <button onClick={() => pagarParcela(d)} title="Registrar parcela" style={{ background: "none", border: "none", color: C.accent, cursor: "pointer", fontSize: 13 }}>✅</button>
                          <button onClick={() => { setEditando(d); setForm({ nome: d.nome, tipo: d.tipo, valor_total: String(d.valor_total), parcela_mensal: String(d.parcela_mensal), parcelas_total: String(d.parcelas_total), parcelas_pagas: String(d.parcelas_pagas), juros_mes: String(d.juros_mes || ""), data_inicio: d.data_inicio || hojeManaus(), data_vencimento: String(d.data_vencimento || 1), observacao: d.observacao || "" }); setShowForm(true); }} style={{ background: "none", border: "none", color: C.accent, cursor: "pointer", fontSize: 13 }}>✎</button>
                          <button onClick={() => deletar(d.id)} style={{ background: "none", border: "none", color: C.warn, cursor: "pointer", fontSize: 14 }}>✕</button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* ABA: SIMULADOR */}
      {aba === "simulador" && (
        <div>
          <div style={styles.grid(2)}>
            <div style={styles.card}>
              <div style={{ fontWeight: 700, fontSize: 14, color: "#111827", marginBottom: 16 }}>🧮 Simulador de Saída das Dívidas</div>
              <div style={{ marginBottom: 16 }}>
                <label style={styles.label}>Pagamento extra por mês (R$)</label>
                <input style={styles.input} type="number" value={pagamentoExtra} onChange={e => setPagamentoExtra(e.target.value)} placeholder="Ex: 500" />
                <div style={{ fontSize: 10, color: "#374151", marginTop: 4 }}>Quanto a mais você consegue pagar além das parcelas mínimas?</div>
              </div>
              {(() => {
                const mesesSemExtra = simularSaida(0);
                const mesesComExtra = simularSaida(pagamentoExtra);
                const dataSemExtra = mesesSemExtra ? new Date(Date.now() + mesesSemExtra * 30 * 24 * 60 * 60 * 1000) : null;
                const dataComExtra = mesesComExtra ? new Date(Date.now() + mesesComExtra * 30 * 24 * 60 * 60 * 1000) : null;
                const mesesEconomizados = mesesSemExtra && mesesComExtra ? mesesSemExtra - mesesComExtra : 0;
                return (
                  <div>
                    <div style={{ background: C.bgElevated, borderRadius: 10, padding: 16, marginBottom: 12 }}>
                      <div style={{ fontSize: 10, color: "#374151", marginBottom: 4 }}>SEM PAGAMENTO EXTRA</div>
                      <div style={{ fontSize: 22, fontWeight: 800, color: C.warn }}>{mesesSemExtra ? `${mesesSemExtra} meses` : "—"}</div>
                      {dataSemExtra && <div style={{ fontSize: 11, color: "#374151" }}>Livre em {dataSemExtra.toLocaleDateString("pt-BR", { month: "long", year: "numeric" })}</div>}
                    </div>
                    {pagamentoExtra > 0 && (
                      <div style={{ background: C.accentDim, borderRadius: 10, padding: 16, border: `1px solid ${C.accent}30` }}>
                        <div style={{ fontSize: 10, color: "#374151", marginBottom: 4 }}>COM {fmt(pagamentoExtra)} EXTRA/MÊS</div>
                        <div style={{ fontSize: 22, fontWeight: 800, color: C.accent }}>{mesesComExtra ? `${mesesComExtra} meses` : "—"}</div>
                        {dataComExtra && <div style={{ fontSize: 11, color: "#374151" }}>Livre em {dataComExtra.toLocaleDateString("pt-BR", { month: "long", year: "numeric" })}</div>}
                        {mesesEconomizados > 0 && <div style={{ fontSize: 12, color: C.accent, marginTop: 8, fontWeight: 600 }}>🎉 {mesesEconomizados} meses a menos!</div>}
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>

            <div style={styles.card}>
              <div style={{ fontWeight: 700, fontSize: 14, color: "#111827", marginBottom: 16 }}>📅 Calendário de Vencimentos</div>
              {dividas.length === 0 ? (
                <div style={{ color: "#374151", fontSize: 12 }}>Nenhuma dívida cadastrada.</div>
              ) : (
                <div>
                  {[...dividas].sort((a, b) => Number(a.data_vencimento || 0) - Number(b.data_vencimento || 0)).map(d => {
                    const hoje = new Date(Date.now() - 4*60*60*1000).getDate();
                    const diasAteVencer = Number(d.data_vencimento || 1) - hoje;
                    const urgente = diasAteVencer >= 0 && diasAteVencer <= 5;
                    const vencido = diasAteVencer < 0;
                    return (
                      <div key={d.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: "1px solid #f3f4f6" }}>
                        <div style={{ width: 40, height: 40, borderRadius: 8, background: vencido ? C.warn+"20" : urgente ? C.gold+"20" : C.surface, display: "flex", alignItems: "center", justifyContent: "center" }}>
                          <span style={{ fontSize: 14, fontWeight: 800, color: vencido ? C.warn : urgente ? C.gold : C.muted }}>dia {d.data_vencimento || "—"}</span>
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 600, fontSize: 12, color: "#111827" }}>{d.nome}</div>
                          <div style={{ fontSize: 10, color: "#374151" }}>{privado ? "••••" : fmt(d.parcela_mensal)}/mês</div>
                        </div>
                        {vencido && <span style={{ fontSize: 9, background: C.warn+"20", color: C.warn, padding: "2px 8px", borderRadius: 4 }}>VENCIDO</span>}
                        {urgente && <span style={{ fontSize: 9, background: C.gold+"20", color: C.gold, padding: "2px 8px", borderRadius: 4 }}>EM {diasAteVencer}d</span>}
                      </div>
                    );
                  })}
                </div>
              )}

              <div style={{ marginTop: 16, padding: "12px 16px", background: C.bgElevated, borderRadius: 8, display: "flex", justifyContent: "space-between" }}>
                <span style={{ fontSize: 12, color: "#374151" }}>Total mensal</span>
                <span style={{ fontSize: 14, fontWeight: 800, color: C.warn }}>{privado ? "••••••" : fmt(totalParcelas)}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── KITS PAGE ─────────────────────────────────────────────────
function Kits({ data, setData }) {
  const [showForm, setShowForm] = useState(false);
  const [salvandoKit, setSalvandoKit] = useState(false);
  const [nomeKit, setNomeKit] = useState("");
  const [itensKit, setItensKit] = useState([{ produto: "", qtd: 1 }]);
  const [editando, setEditando] = useState(null);

  const produtosEstoque = data.estoque.map(p => p.produto).filter(Boolean);

  const addLinha = () => setItensKit(i => [...i, { produto: "", qtd: 1 }]);
  const removeLinha = (idx) => setItensKit(i => i.filter((_,j) => j !== idx));
  const updateLinha = (idx, key, val) => setItensKit(i => i.map((it,j) => j===idx ? {...it,[key]:val} : it));

  const calcValorKit = (itens) => itens.reduce((s, item) => {
    const p = data.estoque.find(e => e.produto === item.produto);
    return s + (p ? p.preco * item.qtd : 0);
  }, 0);

  const calcCustoKit = (itens) => itens.reduce((s, item) => {
    const p = data.estoque.find(e => e.produto === item.produto);
    return s + (p ? p.custo * item.qtd : 0);
  }, 0);

  const handleSave = async () => {
    if (salvandoKit) return;
    if (!nomeKit || itensKit.filter(i => i.produto).length === 0) return alert("Preencha o nome e pelo menos um produto!");
    setSalvandoKit(true);
    const itensValidos = itensKit.filter(i => i.produto);
    const valor = calcValorKit(itensValidos);
    const custo = calcCustoKit(itensValidos);
    if (editando) {
      await sb.update("kits", editando.id, { nome: nomeKit, itens: itensValidos, valor, custo });
      setData(d => ({ ...d, kits: d.kits.map(k => k.id === editando.id ? { ...k, nome: nomeKit, itens: itensValidos, valor, custo } : k) }));
    } else {
      const result = await sb.insert("kits", { nome: nomeKit, itens: itensValidos, valor, custo });
      const inserted = Array.isArray(result) ? result[0] : result;
      setData(d => ({ ...d, kits: [...(d.kits || []), { ...inserted, itens: itensValidos }] }));
    }
    setSalvandoKit(false);
    setNomeKit(""); setItensKit([{ produto: "", qtd: 1 }]); setShowForm(false); setEditando(null);
  };

  const handleEdit = (kit) => {
    setEditando(kit); setNomeKit(kit.nome); setItensKit(kit.itens); setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Deletar este kit?")) return;
    await sb.remove("kits", id);
    setData(d => ({ ...d, kits: d.kits.filter(k => k.id !== id) }));
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 28 }}>
        <div>
          <div style={styles.pageTitle}>Kits & Combos</div>
          <div style={styles.pageSub}>Combinações de produtos com valor automático</div>
        </div>
        <button style={styles.btn("primary")} onClick={() => { setShowForm(!showForm); setEditando(null); setNomeKit(""); setItensKit([{produto:"",qtd:1}]); }}>+ Novo Kit</button>
      </div>

      {showForm && (
        <div style={{ ...styles.card, background: C.bgElevated, border: "1px solid #e5e7eb", marginBottom: 20, borderColor: C.accent + "40" }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: "#111827", marginBottom: 20, paddingBottom: 12, borderBottom: `2px solid ${C.accent}30` }}>{editando ? "Editar Kit" : "Criar Kit / Combo"}</div>
          <div style={{ marginBottom: 16 }}>
            <label style={styles.label}>Nome do Kit</label>
            <input style={{ ...styles.input, maxWidth: 320 }} placeholder="ex: W11 Mini + 4 Pulseiras" value={nomeKit} onChange={e => setNomeKit(e.target.value)} />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "3fr 1fr 32px", gap: 8, marginBottom: 6 }}>
            {["Produto do Estoque", "Qtd", ""].map((h,i) => <div key={i} style={{ fontSize: 9, color: "#374151", letterSpacing: 1, textTransform: "uppercase" }}>{h}</div>)}
          </div>
          {itensKit.map((item, idx) => (
            <div key={idx} style={{ display: "grid", gridTemplateColumns: "3fr 1fr 32px", gap: 8, marginBottom: 8 }}>
              <select style={styles.input} value={item.produto} onChange={e => updateLinha(idx,"produto",e.target.value)}>
                <option value="">Selecione...</option>
                {produtosEstoque.map(p => <option key={p}>{p}</option>)}
              </select>
              <input style={styles.input} type="number" min="1" value={item.qtd} onChange={e => updateLinha(idx,"qtd",Number(e.target.value))} />
              {itensKit.length > 1 ? <button onClick={() => removeLinha(idx)} style={{ background:"none", border:"none", color:C.warn, cursor:"pointer", fontSize:16 }}>✕</button> : <div/>}
            </div>
          ))}
          <button onClick={addLinha} style={{ ...styles.btn("secondary"), fontSize: 11, padding: "6px 12px", marginBottom: 16 }}>+ Adicionar produto</button>

          <div style={{ display: "flex", gap: 16, marginBottom: 16 }}>
            <div style={styles.kpiCard(C.accent)}>
              <div style={styles.kpiLabel}>Valor do Kit</div>
              <div style={{ ...styles.kpiValue(C.accent), fontSize: 18 }}>{fmt(calcValorKit(itensKit))}</div>
              <div style={styles.kpiSub}>Soma dos preços</div>
            </div>
            <div style={styles.kpiCard(C.gold)}>
              <div style={styles.kpiLabel}>Custo do Kit</div>
              <div style={{ ...styles.kpiValue(C.gold), fontSize: 18 }}>{fmt(calcCustoKit(itensKit))}</div>
              <div style={styles.kpiSub}>Soma dos custos</div>
            </div>
          </div>

          <div style={{ display: "flex", gap: 8 }}>
            <button style={{...styles.btn("primary"), opacity: salvandoKit ? 0.6 : 1}} onClick={handleSave} disabled={salvandoKit}>{salvandoKit ? "Salvando..." : editando ? "Salvar alterações" : "Salvar Kit"}</button>
            <button style={styles.btn("secondary")} onClick={() => { setShowForm(false); setEditando(null); }}>Cancelar</button>
          </div>
        </div>
      )}

      {(data.kits || []).length === 0 && !showForm && (
        <div style={{ ...styles.card, background: C.bgElevated, border: "1px solid #e5e7eb", textAlign: "center", padding: 48 }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>📦</div>
          <div style={{ textAlign:"center", padding:"40px 20px", color:C.muted }}><div style={{fontSize:36,marginBottom:12}}>📦</div><div style={{fontSize:14,fontWeight:600,color:C.text,marginBottom:6}}>Nenhum kit criado</div><div style={{fontSize:12}}>Crie combinações de produtos e aumente seu ticket médio!</div></div>
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16 }}>
        {(data.kits || []).map(kit => {
          const margem = kit.valor > 0 ? (kit.valor - kit.custo) / kit.valor : 0;
          return (
            <div key={kit.id} style={{ ...styles.card, background: C.bgElevated, border: "1px solid #e5e7eb", position: "relative" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
                <div style={{ fontWeight: 700, color: "#111827", fontSize: 14 }}>📦 {kit.nome}</div>
                <div style={{ display: "flex", gap: 6 }}>
                  <button onClick={() => handleEdit(kit)} style={{ background:"none", border:"none", color:C.accent, cursor:"pointer", fontSize:14 }}>✎</button>
                  <button onClick={() => handleDelete(kit.id)} style={{ background:"none", border:"none", color:C.warn, cursor:"pointer", fontSize:14 }}>✕</button>
                </div>
              </div>
              <div style={{ marginBottom: 12 }}>
                {kit.itens.map((item, i) => (
                  <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", borderBottom: "1px solid #e5e7eb" }}>
                    <span style={{ fontSize: 12, color: "#374151" }}>{item.qtd}x {item.produto}</span>
                    <span style={{ fontSize: 12, color: C.accent }}>{fmt((data.estoque.find(e=>e.produto===item.produto)?.preco||0)*item.qtd)}</span>
                  </div>
                ))}
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <div style={{ fontSize: 9, color: "#374151" }}>VALOR TOTAL</div>
                  <div style={{ fontWeight: 800, fontSize: 18, color: C.accent }}>{fmt(kit.valor)}</div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontSize: 9, color: "#374151" }}>MARGEM</div>
                  <div style={{ fontWeight: 700, color: C.gold }}>{pct(margem)}</div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

      
}

// ── LOGIN ─────────────────────────────────────────────────────
function Login({ onLogin }) {
  const [form, setForm] = useState({ user: "", senha: "" });
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState("");
  const [mostrarSenha, setMostrarSenha] = useState(false);
  const [esqueceu, setEsqueceu] = useState(false);
  const [emailReset, setEmailReset] = useState("");
  const [enviandoReset, setEnviandoReset] = useState(false);

  const handleLogin = async () => {
    setCarregando(true);
    setErro("");
    // Verificar usuarios via API server-side (clientes SaaS + admin/vendedor)
    try {
      const r = await fetch("https://gestao-tfx-saas.vercel.app/api/resetar-senha", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "login", email: form.user, senha: form.senha })
      });
      const d = await r.json();
      if (r.status === 200 && d.ok) {
        const u = d.user;
        if (d.token) { try { sessionStorage.setItem("tfx_token", d.token); } catch(e){} }
        if (u.dias_trial) setErro(`⏳ Trial: ${u.dias_trial} dia(s) restante(s)`);
        setCarregando(false);
        onLogin({ user: u.email, nome: u.nome, perfil: u.perfil, tenant_id: u.tenant_id, status: u.status, trial_expira: u.trial_expira });
        return;
      }
      if (d.error === "TRIAL_EXPIRADO") { setCarregando(false); setErro("TRIAL_EXPIRADO"); return; }
      setCarregando(false); setErro(d.error || "Usuário ou senha incorretos"); return;
    } catch(e) {}
    setCarregando(false);
    setErro("Usuário ou senha incorretos");
  };

  if (erro === "TRIAL_EXPIRADO") return (
    <div style={{ background: "#f0f2f5", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div style={{ width: 480, background: C.card, border: "1px solid #e5e7eb", borderRadius: 24, overflow: "hidden", textAlign: "center" }}>
        {/* Header dourado */}
        <div style={{ background: "#fffbeb", borderBottom: `1px solid ${C.accent}30`, padding: "40px 40px 32px" }}>
          <div style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 64, height: 64, borderRadius: 18, background: C.accentDim, border: `1px solid ${C.accent}40`, marginBottom: 16, fontSize: 28 }}>⏳</div>
          <div style={{ fontFamily: "Inter, sans-serif", fontWeight: 900, fontSize: 26, color: "#111827", marginBottom: 8 }}>Seu trial expirou</div>
          <div style={{ fontSize: 14, color: "#374151", lineHeight: 1.7 }}>
            Seus 7 dias gratuitos chegaram ao fim.<br/>
            Assine agora e continue crescendo com o Gestão TFX.
          </div>
        </div>
        {/* Body */}
        <div style={{ padding: "32px 40px 40px" }}>
          {/* Benefícios */}
          <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 28, textAlign: "left" }}>
            {[
              "✅ Dashboard completo com DRE",
              "✅ Meta Ads integrado automaticamente",
              "✅ Controle de estoque, compras e funcionários",
              "✅ Metas e indicador de saúde do negócio",
            ].map(b => (
              <div key={b} style={{ fontSize: 12, color: "#374151", display: "flex", alignItems: "center", gap: 8 }}>{b}</div>
            ))}
          </div>
          {/* Preço */}
          <div style={{ background: C.accentDim, border: `1px solid ${C.accent}30`, borderRadius: 16, padding: "16px 20px", marginBottom: 28 }}>
            <div style={{ fontSize: 10, color: "#374151", letterSpacing: 2, textTransform: "uppercase", marginBottom: 4 }}>Plano Mensal</div>
            <div style={{ fontFamily: "Inter, sans-serif", fontWeight: 900, fontSize: 36, color: C.accent, letterSpacing: -1 }}>
              R$79<span style={{ fontSize: 14, fontWeight: 500, color: "#374151" }}>/mês</span>
            </div>
            <div style={{ fontSize: 11, color: "#374151", marginTop: 4 }}>Cancele quando quiser · Sem fidelidade</div>
          </div>
          <button onClick={async () => {
            try {
              const r = await fetch("https://gestao-tfx-saas.vercel.app/api/criar-assinatura", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ nome: form.user, email: form.user }) });
              const d = await r.json();
              if(d.init_point) window.location.href = d.init_point;
              else alert("Erro ao gerar link.");
            } catch(e) { alert("Erro de conexao."); }
          }} style={{ display: "block", width: "100%", background: C.accent, color: C.bg, padding: "14px", borderRadius: 16, fontWeight: 700, fontSize: 14, border: "none", cursor: "pointer", letterSpacing: 1, marginBottom: 12, boxShadow: `0 4px 20px ${C.accent}30` }}>
            ASSINAR AGORA →
          </button>
          <button onClick={() => setErro("")} style={{ background: "none", border: "none", color: "#374151", fontSize: 11, cursor: "pointer" }}>
            ← Voltar ao login
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div style={{ background: "#f0f2f5", minHeight: "100vh", display: "flex", alignItems: "stretch" }}>
      {/* Lado esquerdo — branding */}
      <div style={{
        flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
        background: "#111827",
        borderRight: "none", padding: 60,
        position: "relative", overflow: "hidden"
      }}>
        {/* Orbs decorativos */}
        <div style={{ position: "absolute", top: -80, left: -80, width: 300, height: 300, borderRadius: "50%", background: `radial-gradient(circle, ${C.accent}12 0%, transparent 70%)`, pointerEvents: "none" }} />
        <div style={{ position: "absolute", bottom: -60, right: -60, width: 250, height: 250, borderRadius: "50%", background: `radial-gradient(circle, ${C.accent}08 0%, transparent 70%)`, pointerEvents: "none" }} />

        <div style={{ position: "relative", zIndex: 1, maxWidth: 380, textAlign: "center" }}>
          {/* Logo */}
          <div style={{ marginBottom: 48 }}>
            <div style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 64, height: 64, borderRadius: 18, background: "#ffffff", marginBottom: 20, boxShadow: "0 8px 32px rgba(0,0,0,0.3)" }}>
              <span style={{ fontFamily: "Inter, sans-serif", fontWeight: 900, fontSize: 24, color: "#0f172a" }}>T</span>
            </div>
            <div style={{ fontFamily: "Inter, sans-serif", fontWeight: 900, fontSize: 36, color: "#ffffff", letterSpacing: -1.5, lineHeight: 1 }}>
              GESTÃO <span style={{ color: "#ffffff" }}>TFX</span>
            </div>
            <div style={{ fontSize: 11, color: "rgba(255,255,255,0.4)", letterSpacing: 3, marginTop: 8, textTransform: "uppercase" }}>Sistema de Controle</div>
          </div>

          {/* Features */}
          <div style={{ display: "flex", flexDirection: "column", gap: 16, textAlign: "left" }}>
            {[
              { icon: "📊", title: "Dashboard completo", desc: "Faturamento, lucro e gastos em tempo real" },
              { icon: "🎯", title: "Metas e saúde", desc: "Acompanhe seu crescimento com score inteligente" },
              { icon: "🔄", title: "Automações", desc: "Meta Ads, Bling e gastos fixos no piloto automático" },
            ].map(f => (
              <div key={f.title} style={{ display: "flex", gap: 14, alignItems: "flex-start", padding: "14px 16px", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12 }}>
                <span style={{ fontSize: 20 }}>{f.icon}</span>
                <div>
                  <div style={{ fontFamily: "Inter, sans-serif", fontWeight: 700, fontSize: 14, color: "#ffffff", marginBottom: 2 }}>{f.title}</div>
                  <div style={{ fontSize: 11, color: "rgba(255,255,255,0.5)", lineHeight: 1.5 }}>{f.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Lado direito — formulário */}
      <div style={{ width: 420, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "60px 48px", background: C.bg }}>
        <div style={{ width: "100%" }}>
          <div style={{ marginBottom: 36 }}>
            <div style={{ fontFamily: "Inter, sans-serif", fontWeight: 800, fontSize: 22, color: "#111827", marginBottom: 6 }}>Bem-vindo de volta</div>
            <div style={{ fontSize: 12, color: "#374151" }}>Entre com suas credenciais para acessar o sistema</div>
          </div>

          {!esqueceu ? (
            <>
              <div style={{ marginBottom: 16 }}>
                <label style={styles.label}>Usuário / Email</label>
                <input style={{ ...styles.input, fontSize: 14, padding: "11px 14px" }}
                  value={form.user} onChange={e => setForm({ ...form, user: e.target.value })}
                  onKeyDown={e => e.key === "Enter" && handleLogin()}
                  placeholder="seu@email.com" autoFocus />
              </div>
              <div style={{ marginBottom: 8 }}>
                <label style={styles.label}>Senha</label>
                <div style={{ position: "relative" }}>
                  <input style={{ ...styles.input, paddingRight: 44, fontSize: 14, padding: "11px 44px 11px 14px" }}
                    type={mostrarSenha ? "text" : "password"}
                    value={form.senha} onChange={e => setForm({ ...form, senha: e.target.value })}
                    onKeyDown={e => e.key === "Enter" && handleLogin()}
                    placeholder="••••••••" />
                  <button onClick={() => setMostrarSenha(v => !v)} style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: "#374151", cursor: "pointer", fontSize: 15, lineHeight: 1 }}>
                    {mostrarSenha ? "🙈" : "👁️"}
                  </button>
                </div>
              </div>

              <div style={{ textAlign: "right", marginBottom: 24 }}>
                <button onClick={() => setEsqueceu(true)} style={{ background: "none", border: "none", color: "#374151", fontSize: 11, cursor: "pointer" }}>
                  Esqueci minha senha
                </button>
              </div>

              {erro && (
                <div style={{ background: C.warnDim, border: `1px solid ${C.warn}40`, borderRadius: 10, padding: "10px 14px", fontSize: 12, color: C.warn, marginBottom: 16, display: "flex", alignItems: "center", gap: 8 }}>
                  <span>⚠️</span> {erro}
                </div>
              )}

              <button style={{ width: "100%", background: C.accent, color: C.bg, fontFamily: "Inter, sans-serif", fontWeight: 700, fontSize: 14, padding: "13px", borderRadius: 10, border: "none", cursor: "pointer", letterSpacing: 1, boxShadow: `0 4px 20px ${C.accent}30`, transition: "all 0.15s" }}
                onClick={handleLogin} disabled={carregando}>
                {carregando ? "VERIFICANDO..." : "ENTRAR →"}
              </button>

              <div style={{ textAlign: "center", marginTop: 28, fontSize: 11, color: "#374151" }}>
                Não tem conta? <a href="https://gestaotfx.com.br" style={{ color: C.accent, textDecoration: "none", fontWeight: 600 }}>Conheça o Gestão TFX</a>
              </div>
            </>
          ) : (
            <div>
              <div style={{ marginBottom: 24 }}>
                <div style={{ fontFamily: "Inter, sans-serif", fontWeight: 700, fontSize: 16, color: "#111827", marginBottom: 6 }}>Recuperar acesso</div>
                <div style={{ fontSize: 12, color: "#374151" }}>Digite seu email e enviaremos uma nova senha.</div>
              </div>
              <input style={{ ...styles.input, fontSize: 14, padding: "11px 14px", marginBottom: 12 }}
                type="email" value={emailReset} onChange={e => setEmailReset(e.target.value)}
                placeholder="seu@email.com" autoFocus />
              {erro && (
                <div style={{ background: C.accentDim, border: `1px solid ${C.accent}40`, borderRadius: 10, padding: "10px 14px", fontSize: 12, color: C.accent, marginBottom: 12 }}>
                  ✅ {erro}
                </div>
              )}
              <button onClick={async () => {
                if (!emailReset) return;
                setEnviandoReset(true);
                try {
                  await fetch("https://gestao-tfx-saas.vercel.app/api/resetar-senha", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ email: emailReset }) });
                  setErro("Se este email estiver cadastrado, você receberá uma nova senha em instantes.");
                  setEsqueceu(false);
                  setEmailReset("");
                } catch(e) {
                  setErro("Erro ao enviar. Tente novamente.");
                }
                setEnviandoReset(false);
              }} disabled={enviandoReset} style={{ width: "100%", background: C.accent, color: C.bg, fontFamily: "Inter, sans-serif", fontWeight: 700, fontSize: 14, padding: "13px", borderRadius: 10, border: "none", cursor: "pointer", marginBottom: 12 }}>
                {enviandoReset ? "Enviando..." : "Enviar nova senha →"}
              </button>
              <button onClick={() => { setEsqueceu(false); setEmailReset(""); setErro(""); }} style={{ background: "none", border: "none", color: "#374151", fontSize: 11, cursor: "pointer", width: "100%", textAlign: "center" }}>
                ← Voltar ao login
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── METAS ─────────────────────────────────────────────────────
const METAS_KEY = "tfx_metas";
function getMetas() {
  try { return JSON.parse(localStorage.getItem(METAS_KEY)) || {}; } catch { return {}; }
}
function saveMetas(m) {
  try { localStorage.setItem(METAS_KEY, JSON.stringify(m)); } catch {}
}

// ── RELATÓRIO DE VENDAS POR PRODUTO ───────────────────────────
function RelatorioVendas({ data, setPage, privado }) {
  const [limiteProdutos, setLimiteProdutos] = useState(30);
  const [mesFiltro, setMesFiltro] = useState("todos");
  const ultimos12Meses = Array.from({length: 12}, (_, i) => {
    return mesRelativo(-i);
  });
  const mesesDisponiveis = [...new Set([...ultimos12Meses, ...data.vendas.map(v => getMes(v.data))])].filter(Boolean).sort().reverse();
  const vendasFiltradas = mesFiltro === "todos" ? data.vendas : data.vendas.filter(v => getMes(v.data) === mesFiltro);

  // Agrupa por produto
  // tfx-por-produto: separa cada venda pelos itens (produto a produto)
  const porProduto = {};
  vendasFiltradas.forEach(v => {
    let its = v.itens;
    if (typeof its === "string") { try { its = JSON.parse(its); } catch { its = null; } }
    its = Array.isArray(its) ? its.filter(i => i && i.produto && Number(i.qtd) > 0) : [];
    const qtdVenda = Number(v.qtd) || 1;
    const receitaVenda = Number(v.valor) * qtdVenda;
    const taxa = Number(v.taxa_maquininha) || 0;
    if (its.length === 0) its = [{ produto: v.produto || "Sem nome", qtd: qtdVenda, valor: Number(v.valor), custo: Number(v.custo) || 0 }];
    // desconto e taxa ficam rateados: a soma dos itens bate com a venda
    const brutoItens = its.reduce((a, i) => a + (Number(i.valor) || 0) * Number(i.qtd), 0);
    its.forEach(i => {
      const q = Number(i.qtd);
      const brutoItem = (Number(i.valor) || 0) * q;
      const parte = brutoItens > 0 ? brutoItem / brutoItens : 1 / its.length;
      const receitaItem = receitaVenda * parte;
      const key = i.produto;
      if (!porProduto[key]) porProduto[key] = { produto: key, qtd: 0, receita: 0, lucro: 0, vendas: 0 };
      porProduto[key].qtd += q;
      porProduto[key].receita += receitaItem;
      porProduto[key].lucro += receitaItem - (Number(i.custo) || 0) * q - taxa * parte;
      porProduto[key].vendas += 1;
    });
  });

  const produtos = Object.values(porProduto).sort((a, b) => b.receita - a.receita);
  const totalReceita = produtos.reduce((s, p) => s + p.receita, 0);
  const topProduto = produtos[0];

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 28 }}>
        <div>
          <div style={styles.pageTitle}>Relatório por Produto</div>
          <div style={styles.pageSub}>Ranking de vendas e lucratividade</div>
        </div>
        <select style={{ ...styles.input, width: "auto", fontSize: 11 }} value={mesFiltro} onChange={e => setMesFiltro(e.target.value)}>
          <option value="todos">Todos os meses</option>
          {mesesDisponiveis.map(m => <option key={m} value={m}>{MESES[parseInt(m.slice(5,7))-1] + "/" + m.slice(2,4)}</option>)}
        </select>
      </div>

      {/* KPIs */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 20, marginBottom: 28 }}>
        {/* Total Faturado */}
        <div className="tfx-fade-in-delay-0 tfx-card-hover" style={{ 
          background: C.bgElevated,
          border: `2px solid ${C.accent}25`,
          borderRadius: 20,
          padding: 24,
          position: "relative",
          overflow: "hidden",
          boxShadow: `0 10px 40px ${C.accent}30`,
          transition: "all 0.3s"
        }}>
          <div style={{ position: "absolute", top: -30, right: -30, width: 120, height: 120, background: `radial-gradient(circle, ${C.accent}15, transparent)`, borderRadius: "50%" }} />
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
            <div style={{ width: 48, height: 48, borderRadius: 12, background: `${C.accent}20`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}>💰</div>
            <div style={{ fontSize: 9, color: "#374151", letterSpacing: 2, textTransform: "uppercase", fontWeight: 600 }}>Total Faturado</div>
          </div>
          <div style={{ fontFamily: "'Inter', sans-serif", fontWeight: 800, fontSize: 28, color: C.accent, letterSpacing: -1.2 }}>
            {privado ? "••••••" : fmt(totalReceita)}
          </div>
          <div style={{ fontSize: 10, color: "#374151", marginTop: 8 }}>{privado ? "•• vendas" : `${vendasFiltradas.length} vendas`}</div>
        </div>

        {/* Produtos Diferentes */}
        <div className="tfx-fade-in-delay-1 tfx-card-hover" style={{ 
          background: C.bgElevated,
          border: `2px solid ${C.gold}25`,
          borderRadius: 20,
          padding: 24,
          position: "relative",
          overflow: "hidden",
          boxShadow: `0 10px 40px ${C.gold}30`,
          transition: "all 0.3s"
        }}>
          <div style={{ position: "absolute", top: -30, right: -30, width: 120, height: 120, background: `radial-gradient(circle, ${C.gold}15, transparent)`, borderRadius: "50%" }} />
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
            <div style={{ width: 48, height: 48, borderRadius: 12, background: `${C.gold}20`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}>📦</div>
            <div style={{ fontSize: 9, color: "#374151", letterSpacing: 2, textTransform: "uppercase", fontWeight: 600 }}>Produtos Diferentes</div>
          </div>
          <div style={{ fontFamily: "'Inter', sans-serif", fontWeight: 800, fontSize: 28, color: C.gold, letterSpacing: -1.2 }}>
            {privado ? "••" : produtos.length}
          </div>
          <div style={{ fontSize: 10, color: "#374151", marginTop: 8 }}>produtos vendidos</div>
        </div>

        {/* Mais Vendido */}
        <div className="tfx-fade-in-delay-2 tfx-card-hover" style={{ 
          background: C.bgElevated,
          border: `2px solid ${C.info}25`,
          borderRadius: 20,
          padding: 24,
          position: "relative",
          overflow: "hidden",
          boxShadow: `0 10px 40px ${C.info}30`,
          transition: "all 0.3s"
        }}>
          <div style={{ position: "absolute", top: -30, right: -30, width: 120, height: 120, background: `radial-gradient(circle, ${C.info}15, transparent)`, borderRadius: "50%" }} />
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
            <div style={{ width: 48, height: 48, borderRadius: 12, background: `${C.info}20`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}>🏆</div>
            <div style={{ fontSize: 9, color: "#374151", letterSpacing: 2, textTransform: "uppercase", fontWeight: 600 }}>Mais Vendido</div>
          </div>
          <div style={{ fontFamily: "'Inter', sans-serif", fontWeight: 800, fontSize: topProduto?.produto && topProduto.produto.length > 15 ? 16 : 22, color: C.info, letterSpacing: -1.2, lineHeight: 1.2 }}>
            {topProduto?.produto || "—"}
          </div>
          <div style={{ fontSize: 10, color: "#374151", marginTop: 8 }}>{topProduto ? fmt(topProduto.receita) : "Sem dados"}</div>
        </div>

        {/* Maior Lucro */}
        <div className="tfx-fade-in-delay-3 tfx-card-hover" style={{ 
          background: C.bgElevated,
          border: `2px solid ${C.accent}25`,
          borderRadius: 20,
          padding: 24,
          position: "relative",
          overflow: "hidden",
          boxShadow: `0 10px 40px ${C.accent}30`,
          transition: "all 0.3s"
        }}>
          <div style={{ position: "absolute", top: -30, right: -30, width: 120, height: 120, background: `radial-gradient(circle, ${C.accent}15, transparent)`, borderRadius: "50%" }} />
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
            <div style={{ width: 48, height: 48, borderRadius: 12, background: `${C.accent}20`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}>💎</div>
            <div style={{ fontSize: 9, color: "#374151", letterSpacing: 2, textTransform: "uppercase", fontWeight: 600 }}>Maior Lucro</div>
          </div>
          <div style={{ fontFamily: "'Inter', sans-serif", fontWeight: 800, fontSize: 28, color: C.accent, letterSpacing: -1.2 }}>
            {privado ? "••••••" : topProduto ? fmt(Math.max(...produtos.map(p => p.lucro))) : "—"}
          </div>
          <div style={{ fontSize: 10, color: "#374151", marginTop: 8 }}>Produto mais lucrativo</div>
        </div>
      </div>

      <div style={{ 
        ...styles.card,
        background: C.bgElevated,
        border: "1px solid #e5e7eb"
      }}>
        <div style={{ 
          fontSize: 14, 
          fontWeight: 700, 
          color: "#111827", 
          marginBottom: 20,
          paddingBottom: 12,
          borderBottom: `2px solid ${C.accent}30`
        }}>📊 Ranking de Produtos</div>
        {produtos.length === 0 ? (
          <div style={{ textAlign:"center", padding:"40px 20px", color:C.muted }}><div style={{fontSize:36,marginBottom:12}}>📈</div><div style={{fontSize:14,fontWeight:600,color:C.text,marginBottom:6}}>Nenhuma venda ainda</div><div style={{fontSize:12}}>Registre sua primeira venda e comece a acompanhar seu crescimento!</div></div>
        ) : (
          <><table style={{ ...styles.table, borderCollapse: "separate", borderSpacing: 0 }}>
            <thead>
            <tr style={{ 
              background: `linear-gradient(90deg, ${C.surface}, ${C.bgElevated})`,
              borderBottom: `2px solid ${C.accent}40`
            }}>{["#", "Produto", "Qtd Vendida", "Receita", "Lucro", "Margem", "% do Total"].map(h => <th key={h} style={{ ...styles.th, padding: "10px 14px", fontSize: 9, letterSpacing: 1.5, borderBottom: `2px solid ${C.accent}30` }}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {produtos.slice(0, limiteProdutos).map((p, i) => {
                const margem = p.receita > 0 ? p.lucro / p.receita : 0;
                const share = totalReceita > 0 ? p.receita / totalReceita : 0;
                return (
                  <tr key={p.produto} style={{ background: C.surface, transition: "background 0.15s" }}
                    onMouseEnter={e => e.currentTarget.style.background = "#fafafa"}
                    onMouseLeave={e => e.currentTarget.style.background = C.surface}>
                    <td style={{ ...styles.td, padding: "10px 14px", borderBottom: "1px solid #e5e7eb", color: i === 0 ? C.gold : C.muted, fontWeight: 700 }}>#{i + 1}</td>
                    <td style={{ ...styles.td, padding: "10px 14px", borderBottom: "1px solid #e5e7eb", color: "#111827", fontWeight: 600 }}>{p.produto}</td>
                    <td style={{ ...styles.td, padding: "10px 14px", borderBottom: "1px solid #e5e7eb", color: "#374151" }}>{privado ? "••" : p.qtd}</td>
                    <td style={{ ...styles.td, padding: "10px 14px", borderBottom: "1px solid #e5e7eb", color: C.accent, fontWeight: 700 }}>{privado ? "••••" : fmt(p.receita)}</td>
                    <td style={{ ...styles.td, padding: "10px 14px", borderBottom: "1px solid #e5e7eb", color: C.gold, fontWeight: 700 }}>{privado ? "••••" : fmt(p.lucro)}</td>
                    <td style={{ ...styles.td, padding: "10px 14px", borderBottom: "1px solid #e5e7eb", color: margem > 0.3 ? C.accent : margem > 0.1 ? C.info : C.warn }}>{privado ? "••%" : pct(margem)}</td>
                    <td style={{ ...styles.td, padding: "10px 14px", borderBottom: "1px solid #e5e7eb" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <div style={{ flex: 1, background: C.border, borderRadius: 3, height: 5, overflow: "hidden" }}>
                          <div style={{ width: `${share * 100}%`, height: "100%", background: C.accent, borderRadius: 3 }} />
                        </div>
                        <span style={{ fontSize: 11, color: "#374151", width: 36 }}>{privado ? "••%" : pct(share)}</span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        {produtos.length > limiteProdutos && (
          <div style={{ textAlign: "center", padding: "16px 12px", display: "flex", gap: 10, justifyContent: "center", alignItems: "center", flexWrap: "wrap" }}>
            <span style={{ fontSize: 11, color: "#6b7280" }}>Mostrando {limiteProdutos} de {produtos.length} produtos</span>
            <button onClick={() => setLimiteProdutos(n => n + 30)} style={{ ...styles.btn("secondary"), fontSize: 11 }}>Mostrar mais 30</button>
            <button onClick={() => setLimiteProdutos(produtos.length)} style={{ ...styles.btn("secondary"), fontSize: 11 }}>Mostrar todas</button>
          </div>
        )}
        </>
        )}
      </div>
    </div>
  );
}


function Funcionarios({ data, setData, privado }) {
  const [showForm, setShowForm] = useState(false);
  const [salvandoFunc, setSalvandoFunc] = useState(false);
  const [form, setForm] = useState({ nome: "", cargo: "", salario: "", comissao_valor: "", data_entrada: hojeManaus() });
  const [editando, setEditando] = useState(null);
  const [mes, setMes] = useState(new Date().toISOString().slice(0,7));
  
  // Estados Adiantamentos
  const [showAdiant, setShowAdiant] = useState(false);
  const [formAdiant, setFormAdiant] = useState({ funcionario_id: "", funcionario_nome: "", valor: "", data: hojeManaus(), observacao: "" });
  const [salvandoAdiant, setSalvandoAdiant] = useState(false);

  const funcionarios = data.funcionarios || [];
  const vendas = data.vendas || [];
  const adiantamentos = data.adiantamentos || [];

  // Calcular comissão do mês por funcionário

  const handleSave = async () => {
    if (salvandoFunc) return;
    if (!form.nome) return alert("Nome obrigatório");
    setSalvandoFunc(true);
    const row = { nome: form.nome, cargo: form.cargo, salario: Number(form.salario) || 0, comissao_valor: Number(form.comissao_valor) || 0, data_entrada: form.data_entrada };
    if (editando) {
      await sb.update("funcionarios", editando.id, row);
      setData(d => ({ ...d, funcionarios: (d.funcionarios||[]).map(f => f.id === editando.id ? { ...f, ...row } : f) }));
    } else {
      const inserted = await sb.insert("funcionarios", row);
      const novo = Array.isArray(inserted) ? inserted[0] : inserted;
      setData(d => ({ ...d, funcionarios: [...(d.funcionarios||[]), novo] }));
    }
    setSalvandoFunc(false);
    setForm({ nome: "", cargo: "", salario: "", comissao_valor: "", data_entrada: hojeManaus() });
    setEditando(null); setShowForm(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Remover funcionário?")) return;
    await sb.remove("funcionarios", id);
    setData(d => ({ ...d, funcionarios: (d.funcionarios||[]).filter(f => f.id !== id) }));
  };

  const handleEdit = (f) => {
    setForm({ nome: f.nome, cargo: f.cargo || "", salario: f.salario || "", comissao_valor: f.comissao_valor || "", data_entrada: f.data_entrada || "" });
    setEditando(f); setShowForm(true);
  };

  const totalSalarios = funcionarios.reduce((s, f) => s + (f.salario || 0), 0);
  const totalComissoes = funcionarios.reduce((s, f) => s + (f.comissao_valor || 0), 0);
  const totalRH = totalSalarios + totalComissoes;
  
  // Adiantamentos do mês
  const adiantamentosMes = adiantamentos.filter(a => a.mes_referencia === mes);
  const totalAdiantado = adiantamentosMes.reduce((s, a) => s + (a.valor || 0), 0);
  const faltaPagar = totalRH - totalAdiantado;

  const mesesDisponiveis = [...new Set(vendas.map(v => v.data?.slice(0,7)).filter(Boolean))].sort().reverse();

  const handleSaveAdiant = async () => {
    if (salvandoAdiant) return;
    if (!formAdiant.funcionario_id || !formAdiant.valor) return alert("Funcionário e valor obrigatórios");
    setSalvandoAdiant(true);
    const row = { 
      funcionario_id: formAdiant.funcionario_id, 
      funcionario_nome: formAdiant.funcionario_nome,
      valor: Number(formAdiant.valor), 
      data: formAdiant.data, 
      mes_referencia: mes,
      observacao: formAdiant.observacao 
    };
    const inserted = await sb.insert("adiantamentos", row);
    const novo = Array.isArray(inserted) ? inserted[0] : inserted;
    setData(d => ({ ...d, adiantamentos: [...(d.adiantamentos||[]), novo] }));
    setSalvandoAdiant(false);
    setFormAdiant({ funcionario_id: "", funcionario_nome: "", valor: "", data: hojeManaus(), observacao: "" });
    setShowAdiant(false);
  };

  const handleDeleteAdiant = async (id) => {
    if (!window.confirm("Remover adiantamento?")) return;
    await sb.remove("adiantamentos", id);
    setData(d => ({ ...d, adiantamentos: (d.adiantamentos||[]).filter(a => a.id !== id) }));
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 28 }}>
        <div>
          <div style={styles.pageTitle}>Funcionários</div>
          <div style={styles.pageSub}>Salários, cargos e comissões</div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <select style={{ ...styles.input, width: "auto", fontSize: 11 }} value={mes} onChange={e => setMes(e.target.value)}>
            {mesesDisponiveis.map(m => <option key={m} value={m}>{MESES[parseInt(m.slice(5,7))-1] + "/" + m.slice(2,4)}</option>)}
          </select>
          <button style={styles.btn("primary")} onClick={() => { setForm({ nome: "", cargo: "", salario: "", comissao_valor: "", data_entrada: hojeManaus() }); setEditando(null); setShowForm(true); }}>+ Funcionário</button>
        </div>
      </div>

      {/* KPIs */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 20, marginBottom: 28 }}>
        {/* Total Salários */}
        <div className="tfx-fade-in-delay-0 tfx-card-hover" style={{ 
          background: C.bgElevated,
          border: `2px solid ${C.warn}25`,
          borderRadius: 20,
          padding: 24,
          position: "relative",
          overflow: "hidden",
          boxShadow: `0 10px 40px ${C.warn}30`,
          transition: "all 0.3s"
        }}>
          <div style={{ position: "absolute", top: -30, right: -30, width: 120, height: 120, background: `radial-gradient(circle, ${C.warn}15, transparent)`, borderRadius: "50%" }} />
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
            <div style={{ width: 48, height: 48, borderRadius: 12, background: `${C.warn}20`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}>💰</div>
            <div style={{ fontSize: 9, color: "#374151", letterSpacing: 2, textTransform: "uppercase", fontWeight: 600 }}>Total Salários</div>
          </div>
          <div style={{ fontFamily: "'Inter', sans-serif", fontWeight: 800, fontSize: 28, color: C.warn, letterSpacing: -1.2 }}>
            {privado ? "••••••" : fmt(totalSalarios)}
          </div>
        </div>

        {/* Total Comissões */}
        <div className="tfx-fade-in-delay-1 tfx-card-hover" style={{ 
          background: C.bgElevated,
          border: `2px solid ${C.gold}25`,
          borderRadius: 20,
          padding: 24,
          position: "relative",
          overflow: "hidden",
          boxShadow: `0 10px 40px ${C.gold}30`,
          transition: "all 0.3s"
        }}>
          <div style={{ position: "absolute", top: -30, right: -30, width: 120, height: 120, background: `radial-gradient(circle, ${C.gold}15, transparent)`, borderRadius: "50%" }} />
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
            <div style={{ width: 48, height: 48, borderRadius: 12, background: `${C.gold}20`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}>🎯</div>
            <div style={{ fontSize: 9, color: "#374151", letterSpacing: 2, textTransform: "uppercase", fontWeight: 600 }}>Total Comissões</div>
          </div>
          <div style={{ fontFamily: "'Inter', sans-serif", fontWeight: 800, fontSize: 28, color: C.gold, letterSpacing: -1.2 }}>
            {privado ? "••••••" : fmt(totalComissoes)}
          </div>
          <div style={{ fontSize: 10, color: "#374151", marginTop: 8 }}>Valores lançados manualmente</div>
        </div>

        {/* Custo Total RH */}
        <div className="tfx-fade-in-delay-2 tfx-card-hover" style={{ 
          background: C.bgElevated,
          border: `2px solid ${C.accent}25`,
          borderRadius: 20,
          padding: 24,
          position: "relative",
          overflow: "hidden",
          boxShadow: `0 10px 40px ${C.accent}30`,
          transition: "all 0.3s"
        }}>
          <div style={{ position: "absolute", top: -30, right: -30, width: 120, height: 120, background: `radial-gradient(circle, ${C.accent}15, transparent)`, borderRadius: "50%" }} />
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
            <div style={{ width: 48, height: 48, borderRadius: 12, background: `${C.accent}20`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}>👥</div>
            <div style={{ fontSize: 9, color: "#374151", letterSpacing: 2, textTransform: "uppercase", fontWeight: 600 }}>Custo Total RH</div>
          </div>
          <div style={{ fontFamily: "'Inter', sans-serif", fontWeight: 800, fontSize: 28, color: C.accent, letterSpacing: -1.2 }}>
            {privado ? "••••••" : fmt(totalRH)}
          </div>
        </div>

        {/* Falta Pagar */}
        <div className="tfx-fade-in-delay-3 tfx-card-hover" style={{ 
          background: C.bgElevated,
          border: `2px solid ${faltaPagar > 0 ? C.info : C.accent}25`,
          borderRadius: 20,
          padding: 24,
          position: "relative",
          overflow: "hidden",
          boxShadow: `0 10px 40px ${faltaPagar > 0 ? C.info : C.accent}30`,
          transition: "all 0.3s"
        }}>
          <div style={{ position: "absolute", top: -30, right: -30, width: 120, height: 120, background: `radial-gradient(circle, ${faltaPagar > 0 ? C.info : C.accent}15, transparent)`, borderRadius: "50%" }} />
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
            <div style={{ width: 48, height: 48, borderRadius: 12, background: `${faltaPagar > 0 ? C.info : C.accent}20`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}>💸</div>
            <div style={{ fontSize: 9, color: "#374151", letterSpacing: 2, textTransform: "uppercase", fontWeight: 600 }}>Falta Pagar</div>
          </div>
          <div style={{ fontFamily: "'Inter', sans-serif", fontWeight: 800, fontSize: 28, color: faltaPagar > 0 ? C.info : C.accent, letterSpacing: -1.2 }}>
            {privado ? "••••••" : fmt(faltaPagar)}
          </div>
          <div style={{ fontSize: 10, color: "#374151", marginTop: 8 }}>Salários - Adiantamentos</div>
        </div>
      </div>

      {/* Card Adiantamentos do Mês */}
      <div style={{ 
        ...styles.card,
        marginBottom: 24,
        background: C.bgElevated,
        border: "1px solid #e5e7eb"
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <div style={{ 
            fontSize: 14, 
            fontWeight: 700, 
            color: "#111827",
            paddingBottom: 12,
            borderBottom: `2px solid ${C.accent}30`,
            flex: 1
          }}>💰 Adiantamentos — {MESES[parseInt(mes.slice(5,7))-1] + "/" + mes.slice(2,4)}</div>
          <button onClick={() => setShowAdiant(true)} style={{ ...styles.btn("primary"), fontSize: 11, marginLeft: 12 }}>+ Adiantamento</button>
        </div>

        {adiantamentosMes.length === 0 && (
          <div style={{ textAlign: "center", padding: "40px 20px", color: "#374151" }}>
            <div style={{ fontSize: 32, marginBottom: 10 }}>💸</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: "#111827", marginBottom: 4 }}>Nenhum adiantamento</div>
            <div style={{ fontSize: 12 }}>Registre adiantamentos pagos aos funcionários neste mês</div>
          </div>
        )}

        {adiantamentosMes.length > 0 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {adiantamentosMes.map(a => (
              <div key={a.id} style={{ 
                display: "flex", 
                alignItems: "center", 
                gap: 16,
                padding: "14px 16px",
                background: C.bgElevated,
                border: "1px solid #e5e7eb",
                borderRadius: 12
              }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 13, color: "#111827", marginBottom: 4 }}>{a.funcionario_nome}</div>
                  <div style={{ fontSize: 11, color: "#374151" }}>{new Date(a.data + "T00:00:00").toLocaleDateString("pt-BR")} {a.observacao ? `· ${a.observacao}` : ""}</div>
                </div>
                <div style={{ fontWeight: 700, fontSize: 16, color: C.info }}>{privado ? "••••" : fmt(a.valor)}</div>
                <button onClick={() => handleDeleteAdiant(a.id)} style={{ background: "none", border: "none", color: C.warn, cursor: "pointer", fontSize: 16 }}>✕</button>
              </div>
            ))}
            <div style={{ 
              display: "flex", 
              justifyContent: "space-between", 
              alignItems: "center",
              padding: "12px 16px",
              borderTop: `2px solid ${C.accent}30`,
              marginTop: 8
            }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: "#374151", textTransform: "uppercase" }}>Total Adiantado</span>
              <span style={{ fontWeight: 800, fontSize: 18, color: C.info }}>{privado ? "••••••" : fmt(totalAdiantado)}</span>
            </div>
          </div>
        )}
      </div>

      {/* Modal Adiantamento */}
      {showAdiant && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.5)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", top: 0, left: 0, right: 0, bottom: 0 }}>
          <div style={{ background: C.card, border: `1px solid ${C.accent}40`, borderRadius: 16, padding: 28, width: 480, maxWidth: "90vw" }}>
            <div style={{ fontWeight: 700, fontSize: 16, color: C.accent, marginBottom: 24 }}>💰 Novo Adiantamento</div>
            <div style={{ marginBottom: 14 }}>
              <label style={styles.label}>Funcionário</label>
              <select style={styles.input} value={formAdiant.funcionario_id} onChange={e => {
                const f = funcionarios.find(x => x.id === e.target.value);
                setFormAdiant({...formAdiant, funcionario_id: e.target.value, funcionario_nome: f?.nome || ""});
              }}>
                <option value="">Selecione...</option>
                {funcionarios.map(f => <option key={f.id} value={f.id}>{f.nome}</option>)}
              </select>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
              <div><label style={styles.label}>Valor (R$)</label><input style={styles.input} type="number" value={formAdiant.valor} onChange={e => setFormAdiant({...formAdiant, valor: e.target.value})} placeholder="0,00" /></div>
              <div><label style={styles.label}>Data</label><input style={styles.input} type="date" value={formAdiant.data} onChange={e => setFormAdiant({...formAdiant, data: e.target.value})} /></div>
            </div>
            <div style={{ marginBottom: 24 }}>
              <label style={styles.label}>Observação (opcional)</label>
              <input style={styles.input} value={formAdiant.observacao} onChange={e => setFormAdiant({...formAdiant, observacao: e.target.value})} placeholder="Ex: Vale transporte" />
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button style={{...styles.btn("primary"), opacity: salvandoAdiant ? 0.6 : 1}} onClick={handleSaveAdiant} disabled={salvandoAdiant}>{salvandoAdiant ? "Salvando..." : "Salvar"}</button>
              <button style={styles.btn("secondary")} onClick={() => setShowAdiant(false)}>Cancelar</button>
            </div>
          </div>
        </div>
      )}

      {/* Ranking de Vendedores */}
      {(() => {
        const vendasMes = vendas.filter(v => v.data?.startsWith(mes) && v.vendedor);
        if (vendasMes.length === 0) return null;
        const ranking = funcionarios.map(f => {
          const vendasFunc = vendasMes.filter(v => v.vendedor === f.nome);
          const total = vendasFunc.reduce((s, v) => s + Number(v.valor) * Number(v.qtd || 1), 0);
          const qtd = vendasFunc.length;
          return { nome: f.nome, cargo: f.cargo, total, qtd };
        }).filter(f => f.qtd > 0).sort((a, b) => b.total - a.total);
        // vendas sem funcionário cadastrado
        const semFunc = vendasMes.filter(v => !funcionarios.find(f => f.nome === v.vendedor));
        const outrosMap = {};
        semFunc.forEach(v => { outrosMap[v.vendedor] = (outrosMap[v.vendedor] || { nome: v.vendedor, cargo: "—", total: 0, qtd: 0 }); outrosMap[v.vendedor].total += Number(v.valor) * Number(v.qtd || 1); outrosMap[v.vendedor].qtd++; });
        const todos = [...ranking, ...Object.values(outrosMap)].sort((a, b) => b.total - a.total);
        if (todos.length === 0) return null;
        const maxTotal = todos[0]?.total || 1;
        return (
          <div style={{ 
            ...styles.card,
            marginBottom: 24,
            background: C.bgElevated,
            border: "1px solid #e5e7eb"
          }}>
            <div style={{ 
              fontSize: 14, 
              fontWeight: 700, 
              color: "#111827", 
              marginBottom: 20,
              paddingBottom: 12,
              borderBottom: `2px solid ${C.accent}30`
            }}>🏆 Ranking de Vendedores — {MESES[parseInt(mes.slice(5,7))-1] + "/" + mes.slice(2,4)}</div>
            {todos.map((f, i) => (
              <div key={f.nome} style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
                <div style={{ width: 28, height: 28, borderRadius: "50%", background: i === 0 ? "#FFD700" : i === 1 ? "#C0C0C0" : i === 2 ? "#CD7F32" : C.surface, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 800, color: i < 3 ? "#080808" : C.muted }}>{i + 1}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                    <div>
                      <span style={{ fontWeight: 600, fontSize: 14, color: "#111827" }}>{f.nome}</span>
                      {f.cargo !== "—" && <span style={{ fontSize: 10, color: "#374151", marginLeft: 6 }}>{f.cargo}</span>}
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <span style={{ fontWeight: 700, fontSize: 14, color: i === 0 ? C.gold : C.accent }}>{privado ? "••••••" : fmt(f.total)}</span>
                      <span style={{ fontSize: 10, color: "#374151", marginLeft: 6 }}>{f.qtd} venda{f.qtd !== 1 ? "s" : ""}</span>
                    </div>
                  </div>
                  <div style={{ height: 4, background: C.bgElevated, borderRadius: 2, overflow: "hidden" }}>
                    <div style={{ height: "100%", width: (f.total / maxTotal * 100) + "%", background: i === 0 ? C.gold : C.accent, borderRadius: 2, transition: "width 0.6s ease" }} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        );
      })()}

      {/* Form */}
      {showForm && (
        <div style={{ ...styles.card, background: C.bgElevated, border: "1px solid #e5e7eb", marginBottom: 24, padding: 24 }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: C.accent, marginBottom: 16 }}>{editando ? "Editar Funcionário" : "Novo Funcionário"}</div>
          <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr 1fr", gap: 12, marginBottom: 16 }}>
            <div><label style={styles.label}>Nome</label><input style={styles.input} value={form.nome} onChange={e => setForm({...form, nome: e.target.value})} placeholder="Nome completo" /></div>
            <div><label style={styles.label}>Cargo</label><input style={styles.input} value={form.cargo} onChange={e => setForm({...form, cargo: e.target.value})} placeholder="Ex: Vendedor" /></div>
            <div><label style={styles.label}>Salário (R$)</label><input style={styles.input} type="number" value={form.salario} onChange={e => setForm({...form, salario: e.target.value})} placeholder="0,00" /></div>
            <div><label style={styles.label}>Comissão (R$)</label><input style={styles.input} type="number" value={form.comissao_valor} onChange={e => setForm({...form, comissao_valor: e.target.value})} placeholder="Ex: 200,00" /></div>
            <div><label style={styles.label}>Data Entrada</label><input style={styles.input} type="date" value={form.data_entrada} onChange={e => setForm({...form, data_entrada: e.target.value})} /></div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button style={{...styles.btn("primary"), opacity: salvandoFunc ? 0.6 : 1}} onClick={handleSave} disabled={salvandoFunc}>{salvandoFunc ? "Salvando..." : "Salvar"}</button>
            <button style={styles.btn("secondary")} onClick={() => { setShowForm(false); setEditando(null); }}>Cancelar</button>
          </div>
        </div>
      )}

      {/* Tabela */}
      <div style={styles.card}>
        <table style={{ ...styles.table, borderCollapse: "separate", borderSpacing: 0 }}>
          <thead>
            <tr style={{ 
              background: `linear-gradient(90deg, ${C.surface}, ${C.bgElevated})`,
              borderBottom: `2px solid ${C.accent}40`
            }}>{["Nome","Cargo","Salário","Comissão","Adiantado","Falta Pagar","Entrada",""].map(h => <th key={h} style={{ ...styles.th, padding: "10px 14px", fontSize: 9, letterSpacing: 1.5, borderBottom: `2px solid ${C.accent}30` }}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {funcionarios.length === 0 && (
              <tr><td colSpan={8} style={{ ...styles.td, textAlign:"center", padding:40 }}><div style={{fontSize:32,marginBottom:10}}>👥</div><div style={{color:C.text,fontWeight:600,fontSize:14,marginBottom:4}}>Nenhum funcionário</div><div style={{color:C.muted,fontSize:12}}>Adicione sua equipe para acompanhar os custos com pessoal.</div></td></tr>
            )}
            {funcionarios.map(f => {
              const comissaoMes = f.comissao_valor || 0;
              const totalMes = (f.salario || 0) + comissaoMes;
              const adiantadoFunc = adiantamentosMes.filter(a => a.funcionario_id === f.id).reduce((s, a) => s + (a.valor || 0), 0);
              const faltaPagarFunc = totalMes - adiantadoFunc;
              return (
                <tr key={f.id} style={{ background: C.surface }}>
                  <td style={{ ...styles.td, fontWeight: 600, color: "#111827" }}>{f.nome}</td>
                  <td style={{ ...styles.td, color: "#374151" }}>{f.cargo || "—"}</td>
                  <td style={{ ...styles.td, color: C.warn }}>{privado ? "••••" : fmt(f.salario || 0)}</td>
                  <td style={{ ...styles.td, color: C.gold }}>{privado ? "••••" : fmt(comissaoMes)}</td>
                  <td style={{ ...styles.td, color: C.info }}>{privado ? "••••" : fmt(adiantadoFunc)}</td>
                  <td style={{ ...styles.td, color: faltaPagarFunc > 0 ? C.accent : C.muted, fontWeight: 700 }}>{privado ? "••••" : fmt(faltaPagarFunc)}</td>
                  <td style={{ ...styles.td, color: "#374151" }}>{f.data_entrada || "—"}</td>
                  <td style={styles.td}>
                    <button onClick={() => handleEdit(f)} style={{ background: "none", border: "none", color: "#374151", cursor: "pointer", fontSize: 14, marginRight: 8 }}>✎</button>
                    <button onClick={() => handleDelete(f.id)} style={{ background: "none", border: "none", color: C.warn, cursor: "pointer", fontSize: 14 }}>✕</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}


function Backup({ data }) {
  const exportCSV = (lista, nome) => {
    if (!lista || lista.length === 0) return alert("Sem dados para exportar!");
    const keys = Object.keys(lista[0]).filter(k => !["id","created_at","itens","custo_anterior"].includes(k));
    const header = keys.join(";");
    const rows = lista.map(r => keys.map(k => String(r[k] ?? "").replace(/;/g,",")));
    const csv = [header, ...rows.map(r => r.join(";"))].join("\n");
    const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob);
    a.download = `${nome}_${hojeManaus()}.csv`; a.click();
  };
  const [status, setStatus] = useState("");
  const [baixando, setBaixando] = useState(false);

  const gerarBackup = () => {
    setBaixando(true);
    setStatus("Gerando backup...");
    try {
      const backup = {
        gerado_em: new Date().toISOString(),
        vendas: data.vendas || [],
        gastos: data.gastos || [],
        estoque: data.estoque || [],
        compras: data.compras || [],
      };
      const blob = new Blob([JSON.stringify(backup, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const hoje = new Date().toISOString().slice(0, 10);
      a.href = url;
      a.download = `backup_tfx_${hoje}.json`;
      a.click();
      URL.revokeObjectURL(url);
      setStatus(`✅ Backup baixado! ${backup.vendas.length} vendas, ${backup.gastos.length} gastos, ${backup.estoque.length} estoque, ${backup.compras.length} compras.`);
    } catch(e) {
      setStatus("❌ Erro ao gerar backup.");
    }
    setBaixando(false);
  };

  const verificarBackup = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const json = JSON.parse(ev.target.result);
        setStatus(`📂 Arquivo válido! Gerado em ${json.gerado_em ? new Date(json.gerado_em).toLocaleString("pt-BR") : "data desconhecida"}. ${json.vendas?.length || 0} vendas, ${json.gastos?.length || 0} gastos, ${json.estoque?.length || 0} estoque, ${json.compras?.length || 0} compras.`);
      } catch {
        setStatus("❌ Arquivo inválido.");
      }
    };
    reader.readAsText(file);
  };

  return (
    <div style={{ padding: "28px 24px" }}>
      <div style={{ marginBottom: 28 }}>
        <div style={styles.pageTitle}>Backup dos Dados</div>
        <div style={styles.pageSub}>Exporte e proteja suas informações</div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 20, marginBottom: 28 }}>
        {[
          { label: "Vendas", icon: "🛒", val: (data.vendas||[]).length, color: C.accent },
          { label: "Gastos", val: (data.gastos||[]).length, icon: "↘", color: C.warn },
          { label: "Estoque", val: (data.estoque||[]).length, icon: "▦", color: C.gold },
          { label: "Compras", val: (data.compras||[]).length, icon: "⊕", color: C.info },
        ].map((k, idx) => (
          <div key={k.label} className={`tfx-fade-in-delay-${idx}`} style={{ 
            background: C.bgElevated,
            border: `1px solid rgba(0,0,0,0.08)`,
            borderRadius: 20,
            padding: 24,
            position: "relative",
            overflow: "hidden",
            boxShadow: `0 1px 3px rgba(0,0,0,0.08), 0 0 0 1px rgba(0,0,0,0.06)`,
            transition: "all 0.3s"
          }}>
            <div style={{ position: "absolute", top: -30, right: -30, width: 120, height: 120, background: `radial-gradient(circle, ${k.color}15, transparent)`, borderRadius: "50%" }} />
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
              <div style={{ width: 48, height: 48, borderRadius: 12, background: `${k.color}20`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}>{k.icon}</div>
              <div style={{ fontSize: 9, color: "#374151", letterSpacing: 2, textTransform: "uppercase", fontWeight: 600 }}>{k.label}</div>
            </div>
            <div style={{ fontFamily: "'Inter', sans-serif", fontWeight: 800, fontSize: 28, color: k.color, letterSpacing: -1.2 }}>{k.val}</div>
            <div style={{ fontSize: 10, color: "#374151", marginTop: 8 }}>registros</div>
          </div>
        ))}
      </div>

      <div style={{ 
        background: C.bgElevated,
        border: "1px solid #e5e7eb",
        borderRadius: 16,
        padding: 28,
        marginBottom: 16
      }}>
        <div style={{ fontWeight: 700, fontSize: 14, color: "#111827", marginBottom: 8, paddingBottom: 8, borderBottom: `2px solid ${C.accent}30` }}>⬇ Exportar Backup</div>
        <div style={{ fontSize: 12, color: "#374151", marginBottom: 28 }}>Baixa um arquivo .json com todos os dados atuais do sistema.</div>
        <button onClick={gerarBackup} disabled={baixando}
          style={{ ...styles.btn("primary"), opacity: baixando ? 0.6 : 1, cursor: baixando ? "not-allowed" : "pointer" }}>
          {baixando ? "Gerando..." : "⬇ Baixar Backup Agora"}
        </button>
      </div>

      <div style={{ 
        background: C.bgElevated,
        border: "1px solid #e5e7eb",
        borderRadius: 16,
        padding: 28,
        marginBottom: 16
      }}>
        <div style={{ fontWeight: 700, fontSize: 14, color: "#111827", marginBottom: 8, paddingBottom: 8, borderBottom: `2px solid ${C.accent}30` }}>🔍 Verificar Backup</div>
        <div style={{ fontSize: 12, color: "#374151", marginBottom: 28 }}>Carregue um arquivo de backup para verificar seu conteúdo.</div>
        <input type="file" accept=".json" onChange={verificarBackup} style={{ fontSize: 12, color: "#374151", cursor: "pointer" }} />
      </div>

      <div style={{ 
        background: C.bgElevated,
        border: "1px solid #e5e7eb",
        borderRadius: 16,
        padding: 28
      }}>
        <div style={{ fontWeight: 700, fontSize: 14, color: "#111827", marginBottom: 8, paddingBottom: 8, borderBottom: `2px solid ${C.accent}30` }}>📊 Exportar CSV</div>
        <div style={{ fontSize: 12, color: "#374151", marginBottom: 28 }}>Exporta cada módulo separado em arquivo .csv (compatível com Excel e Planilhas).</div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button onClick={() => exportCSV(data.vendas, "vendas")} style={{ ...styles.btn("secondary"), fontSize: 12 }}>↗ Vendas</button>
          <button onClick={() => exportCSV(data.gastos, "gastos")} style={{ ...styles.btn("secondary"), fontSize: 12 }}>↘ Gastos</button>
          <button onClick={() => exportCSV(data.estoque, "estoque")} style={{ ...styles.btn("secondary"), fontSize: 12 }}>▦ Estoque</button>
          <button onClick={() => exportCSV(data.compras, "compras")} style={{ ...styles.btn("secondary"), fontSize: 12 }}>⊕ Compras</button>
        </div>
      </div>

      {status && (
        <div style={{ marginTop: 20, background: C.accent + "15", border: `1px solid ${C.accent}40`, borderRadius: 10, padding: "12px 16px", fontSize: 14, color: "#111827" }}>
          {status}
        </div>
      )}
    </div>
  );
}



function App() {
  const [logado, setLogado] = useState(() => {
    try {
      const u = JSON.parse(sessionStorage.getItem("tfx_user")) || null;
      if (u?.tenant_id) setTenant(u.tenant_id);
      return u;
    } catch { return null; }
  });
  const [page, setPageRaw] = useState(() => {
    const user = JSON.parse(sessionStorage.getItem("tfx_user") || "null");
    if (user?.perfil === "vendedor") return "vendas";
    return sessionStorage.getItem("tfx_page") || "dashboard";
  });
  const setPage = (p) => { sessionStorage.setItem("tfx_page", p); setPageRaw(p); };
  const [data, setData] = useState(initialData);
  const [loading, setLoading] = useState(true);
  const [metas, setMetas] = useState(getMetas);
  const [editMetas, setEditMetas] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(() => { try { return localStorage.getItem("tfx_darkmode") === "1"; } catch { return false; } });
  
  useEffect(() => {
    try { localStorage.setItem("tfx_darkmode", darkMode ? "1" : "0"); } catch {}
    if (darkMode) {
      document.documentElement.style.filter = "invert(1) hue-rotate(180deg)";
    } else {
      document.documentElement.style.filter = "";
    }
  }, [darkMode]);
  const [toast, setToast] = useState({ msg: "", tipo: "ok" });
  const showToast = (msg, tipo = "ok") => { setToast({ msg, tipo }); setTimeout(() => setToast({ msg: "", tipo: "ok" }), 2500); };
  const [showFabGasto, setShowFabGasto] = useState(false);
  const [fabForm, setFabForm] = useState({ categoria: "Operacional", descricao: "", valor: "", data_gasto: hojeManaus() });
  const [fabSalvando, setFabSalvando] = useState(false);
  const [privado, setPrivado] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(() => {
    try { return !localStorage.getItem("tfx_onboarding_visto"); } catch { return false; }
  });
  const fecharOnboarding = () => {
    try { localStorage.setItem("tfx_onboarding_visto", "1"); } catch {}
    setShowOnboarding(false);
  };
  const [metasForm, setMetasForm] = useState({ faturamento: "", lucro: "" });

  // Personalização
  const [nomeApp, setNomeApp] = useState(() => localStorage.getItem("tfx_nome") || "GESTÃO TFX");
  const [corAccent, setCorAccent] = useState(() => localStorage.getItem("tfx_cor") || "#111827");
  const [editandoNome, setEditandoNome] = useState(false);
  const [nomeTemp, setNomeTemp] = useState(nomeApp);
  const salvarPersonalizacao = () => {
    localStorage.setItem("tfx_nome", nomeTemp);
    localStorage.setItem("tfx_cor", corAccent);
    setNomeApp(nomeTemp);
    setEditandoNome(false);
  };

  const handleLogin = (user) => {
    sessionStorage.setItem("tfx_user", JSON.stringify(user));
    setTenant(user.tenant_id);
    setLogado(user);
    const paginaInicial = user.perfil === "vendedor" ? "vendas" : "dashboard";
    sessionStorage.setItem("tfx_page", paginaInicial);
    setPageRaw(paginaInicial);
  };
  const handleLogout = () => {
    sessionStorage.removeItem("tfx_user");
    sessionStorage.removeItem("tfx_page");
    setLogado(null);
    setPageRaw("dashboard");
  };

  // Carrega dados do Supabase ao iniciar
  useEffect(() => {
    if (!logado) { setLoading(false); return; }
    async function loadData() {
      try {
        const [vendas, gastos, estoque, compras, kits, funcionarios, adiantamentos, trocas_clientes, devolucoes_fornecedor, vendas_upsell_raw] = await Promise.all([
          sb.get("vendas"), sb.get("gastos"), sb.get("estoque"), sb.get("compras"), sb.get("kits"), sb.get("funcionarios"), sb.get("adiantamentos"), sb.get("trocas_clientes"), sb.get("devolucoes_fornecedor"), sb.get("vendas_upsell")
        ]);
        setData({
          vendas: (vendas || []).map(v => {
            const itensBase = typeof v.itens === "string" ? JSON.parse(v.itens) : (v.itens || []);
            const upsells = (vendas_upsell_raw || [])
              .filter(u => u.venda_id === v.id)
              .map(u => ({ produto: u.produto_nome + " [upsell]", qtd: u.quantidade, valor: u.valor, custo: u.custo || 0, upsell: true, upsell_id: u.id }));
            return { ...v, produto: v.descricao, itens: [...itensBase, ...upsells] };
          }),
          gastos: (gastos || []).map(g => ({ ...g, subcategoria: g.descricao })),
          estoque: (estoque || []).map(p => ({ ...p, produto: p.nome, estoque: p.qtd, custo: p.preco_custo, preco: p.preco_venda, minimo: p.minimo || 0, ncm: p.ncm || "" })),
          kits: (kits || []).map(k => ({ ...k, itens: typeof k.itens === "string" ? JSON.parse(k.itens) : k.itens })),
          compras: (compras || []).map(c => ({ ...c, produto: c.descricao, total: c.valor, itens: typeof c.itens === "string" ? JSON.parse(c.itens) : (c.itens || []), custo_anterior: typeof c.custo_anterior === "string" ? JSON.parse(c.custo_anterior) : (c.custo_anterior || {}) })),
          funcionarios: funcionarios || [],
          adiantamentos: adiantamentos || [],
          trocas_clientes: Array.isArray(trocas_clientes) ? trocas_clientes : [],
          vendas_upsell: (() => {
            if (!Array.isArray(vendas_upsell_raw)) return [];
            const vendaIdsSet = new Set((vendas || []).map(v => String(v.id)));
            return vendas_upsell_raw.filter(u => vendaIdsSet.has(String(u.venda_id)));
          })(),
          devolucoes_fornecedor: Array.isArray(devolucoes_fornecedor) ? devolucoes_fornecedor : [],
          indicadores: (await sb.get("indicadores")) || [],
          taxas_maquininha: (await sb.get("taxas_maquininha")) || [],
          clientes_loja: (await sb.get("clientes_loja")) || [],
          cupons: (await sb.get("cupons")) || [],

          categorias_gastos: (await sb.get("categorias_gastos")) || [],
          dividas: await sb.get("dividas"),
          caixa_movimentos: await sb.get("caixa_movimentos"),
          caixa_config: await (async () => {
            try {
              const r = await fetch(`${API_BASE}?table=caixa_config&limit=1`, {
                headers: { Authorization: "Bearer " + getJWT() }
              });
              const rows = await r.json();
              return rows?.[0] || null;
            } catch(e) { return null; }
          })(),
          config_loja: await (async () => {
            try {
              const r = await fetch(`${API_BASE}?table=clientes&select=nome_loja,logo_url,tema`, {
                headers: { Authorization: "Bearer " + getJWT() }
              });
              const rows = await r.json();
              const cfg = rows?.[0] || {};
              if (cfg.tema) aplicarTema(cfg.tema);
              return cfg;
            } catch(e) { return {}; }
          })(),
        });
      } catch(e) { console.error(e); }
      setLoading(false);
    }
    loadData();
  }, [logado]);

  // Atualização automática: 60s + ao voltar pra aba (só tabelas que mudam no dia a dia)
  useEffect(() => {
    if (!logado) return;
    let busy = false, ultimaAssinatura = null;
    const atualizar = async () => {
      if (busy || document.hidden) return;
      busy = true;
      try {
        const [vendas, upsell, gastos, estoque, indicadores, caixa_movimentos, dividas] = await Promise.all([
          sb.get("vendas"), sb.get("vendas_upsell"), sb.get("gastos"), sb.get("estoque"), sb.get("indicadores"), sb.get("caixa_movimentos"), sb.get("dividas")
        ]);
        if (![vendas, upsell, gastos, estoque, indicadores, caixa_movimentos, dividas].every(Array.isArray)) return;
        const assinatura = JSON.stringify([vendas, upsell, gastos, estoque, indicadores, caixa_movimentos, dividas]);
        if (assinatura === ultimaAssinatura) return;
        const primeira = ultimaAssinatura === null;
        ultimaAssinatura = assinatura;
        if (primeira) return; // 1a leitura so serve de referencia (dados ja vieram do loadData)
        const vendaIds = new Set(vendas.map(v => String(v.id)));
        setData(d => ({
          ...d,
          vendas: vendas.map(v => {
            const itensBase = typeof v.itens === "string" ? JSON.parse(v.itens) : (v.itens || []);
            const ups = upsell.filter(u => u.venda_id === v.id).map(u => ({ produto: u.produto_nome + " [upsell]", qtd: u.quantidade, valor: u.valor, custo: u.custo || 0, upsell: true, upsell_id: u.id }));
            return { ...v, produto: v.descricao, itens: [...itensBase, ...ups] };
          }),
          vendas_upsell: upsell.filter(u => vendaIds.has(String(u.venda_id))),
          gastos: gastos.map(g => ({ ...g, subcategoria: g.descricao })),
          estoque: estoque.map(p => ({ ...p, produto: p.nome, estoque: p.qtd, custo: p.preco_custo, preco: p.preco_venda, minimo: p.minimo || 0, ncm: p.ncm || "" })),
          indicadores, caixa_movimentos, dividas
        }));
      } catch(e) { /* mantem dados atuais */ }
      finally { busy = false; }
    };
    const t = setInterval(atualizar, 60000);
    const onVis = () => { if (!document.hidden) atualizar(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { clearInterval(t); document.removeEventListener("visibilitychange", onVis); };
  }, [logado]);


  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = FONT + `
      * { box-sizing: border-box; margin: 0; padding: 0; }
      ::-webkit-scrollbar { width: 4px; height: 4px; }
      ::-webkit-scrollbar-track { background: #080808; }
      ::-webkit-scrollbar-thumb { background: #2A2200; border-radius: 2px; }
      select option { background: #141414; color: #F0E8D0; }
      tr:hover td { background: rgba(255,255,255,0.015); }
      @media (max-width: 768px) {
        .tfx-sidebar { transform: translateX(-100%); position: fixed !important; z-index: 900; height: 100vh; transition: transform 0.25s ease; top: 0; }
        .tfx-sidebar.open { transform: translateX(0); box-shadow: 4px 0 24px rgba(0,0,0,0.5); }
        .tfx-overlay { display: none; position: fixed; inset: 0; background: rgba(0,0,0,0.5); z-index: 899; }
        .tfx-overlay.open { display: block; }
        .tfx-main { padding: 12px !important; padding-top: 68px !important; }
        .tfx-topbar { display: flex !important; }

        /* Grids responsivos */
        div[style*="repeat(4"] { grid-template-columns: 1fr 1fr !important; }
        div[style*="repeat(5"] { grid-template-columns: 1fr 1fr !important; }
        div[style*="repeat(5, 1fr)"] { grid-template-columns: 1fr 1fr !important; }
        div[style*="repeat(3"] { grid-template-columns: 1fr 1fr !important; }
        div[style*="1.4fr 1fr"] { grid-template-columns: 1fr !important; }
        div[style*="1fr 1fr 1fr"] { grid-template-columns: 1fr 1fr !important; }
        div[style*="1fr 1fr"] { grid-template-columns: 1fr 1fr !important; }
        div[style*="1fr auto"] { grid-template-columns: 1fr !important; }
        div[style*="2fr 1fr"] { grid-template-columns: 1fr !important; }
        div[style*="3fr 1fr"] { grid-template-columns: 1fr !important; }

        /* Tabelas com scroll horizontal */
        .tfx-table-wrap { overflow-x: auto !important; -webkit-overflow-scrolling: touch; }
        table { font-size: 11px !important; min-width: 600px; }
        table td, table th { padding: 6px 8px !important; white-space: nowrap; }

        /* Login responsivo */
        div[style*="height: 100vh"][style*="display: flex"] > div:first-child { display: none !important; }
        div[style*="height: 100vh"][style*="display: flex"] > div:last-child { width: 100% !important; padding: 40px 24px !important; }

        /* Modais responsivos */
        div[style*="width: 640px"] { width: 95vw !important; max-width: 95vw !important; }
        div[style*="width: 520px"] { width: 95vw !important; max-width: 95vw !important; }
        div[style*="width: 480px"] { width: 95vw !important; max-width: 95vw !important; }
        div[style*="width: 560px"] { width: 95vw !important; max-width: 95vw !important; }

        /* Modal edição de venda - grid de 4 colunas vira 2 */
        div[style*="grid-template-columns: 1fr 1fr 1fr 1fr"] { grid-template-columns: 1fr 1fr !important; }
        div[style*="grid-template-columns: 2fr 1fr 1fr 1fr"] { grid-template-columns: 1fr 1fr !important; }
        div[style*="grid-template-columns: repeat(4, 1fr)"] { grid-template-columns: 1fr 1fr !important; }

        /* Botoes de header nao empilham */
        div[style*="display: flex"][style*="gap: 8px"] { flex-wrap: wrap; }

        /* Resumo do dia - bottom sheet */
        div[style*="align-items: flex-end"] > div { border-radius: 16px 16px 0 0 !important; max-height: 85vh !important; overflow-y: auto !important; }

        /* FAB - lançar gasto */
        button[style*="bottom: 28"][style*="right: 24"] { bottom: 20px !important; right: 16px !important; }

        /* KPIs secundários - 2 por linha */
        div[style*="repeat(5, 1fr)"] { grid-template-columns: 1fr 1fr !important; }

        /* Texto menor em cards */
        div[style*="font-size: 26px"] { font-size: 20px !important; }
        div[style*="font-size: 24px"] { font-size: 18px !important; }
        div[style*="font-size: 22px"] { font-size: 17px !important; }
      }
      @media (min-width: 769px) {
        .tfx-topbar { display: none !important; }
        .tfx-overlay { display: none !important; }
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  // tfx-modais-1: Esc fecha o modal do topo, clique fora fecha modais sem formulario, botoes de salvar nao aceitam clique duplo
  useEffect(() => {
    const RE_FECHAR = /^(fechar|cancelar|voltar)\b/i;
    const RE_SALVAR = /^(💾\s*)?(salvar|registrar|confirmar|lançar|lancar|finalizar|emitir|gerar|excluir|apagar)/i;
    const overlayDoTopo = () => {
      let melhor = null, melhorZ = -1;
      document.querySelectorAll("body div").forEach(el => {
        if (!el.style || el.style.position !== "fixed") return;
        // tfx-modais-1c: reconhece o modal pelo botao Cancelar/Fechar/Voltar e pela camada (alguns overlays ficam dentro de linhas de tabela ou containers animados, entao o tamanho nao serve)
        const z = parseInt(getComputedStyle(el).zIndex, 10) || 0;
        if (z < 100 || z > 100000) return;
        if (!botaoFechar(el)) return;
        if (z >= melhorZ) { melhor = el; melhorZ = z; }
      });
      return melhor;
    };
    const botaoFechar = (ov) => [...ov.querySelectorAll("button")].find(b => !b.disabled && RE_FECHAR.test((b.textContent || "").trim()));
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      const ov = overlayDoTopo();
      const b = ov && botaoFechar(ov);
      if (b) { e.preventDefault(); b.click(); }
    };
    let downNoFundo = null;
    const onDown = (e) => { downNoFundo = e.target; };
    const onUp = (e) => {
      const alvo = e.target;
      if (!alvo || alvo !== downNoFundo || !alvo.style || alvo.style.position !== "fixed") return;
      if (alvo.querySelector("input, textarea, select")) return; // formulario: nao fecha por clique fora (evita perder dados)
      const b = botaoFechar(alvo);
      if (b) b.click();
    };
    const onClickSalvar = (e) => {
      const b = e.target && e.target.closest ? e.target.closest("button") : null;
      if (!b || b.disabled || !RE_SALVAR.test((b.textContent || "").trim())) return;
      if (!b.closest('div[style*="fixed"]')) return;
      setTimeout(() => {
        if (!b.isConnected) return;
        b.disabled = true; b.style.opacity = "0.6";
        setTimeout(() => { if (b.isConnected) { b.disabled = false; b.style.opacity = ""; } }, 1500);
      }, 0);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onDown, true);
    document.addEventListener("mouseup", onUp, true);
    document.addEventListener("click", onClickSalvar, true);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onDown, true);
      document.removeEventListener("mouseup", onUp, true);
      document.removeEventListener("click", onClickSalvar, true);
    };
  }, []);

  if (!logado) return <Login onLogin={handleLogin} />;





// ── SAÚDE FINANCEIRA ──────────────────────────────────────────
function SaudeFinanceira({ data, privado }) {
  const hoje = hojeManaus();
  const mesAtualReal = hoje.slice(0, 7);
  const [mesSel, setMesSel] = useState(() => localStorage.getItem('tfx_saude_mes') || mesAtualReal);
  const setMesSelPersist = (m) => { localStorage.setItem('tfx_saude_mes', m); setMesSel(m); };
  const mesAtual = mesSel;
  const isMesAtual = mesSel === mesAtualReal;
  const isFuturo = mesSel > mesAtualReal;

  // Meses historicos de vendas
  const meses = [...new Set(data.vendas.map(v => v.data ? v.data.slice(0, 7) : null).filter(Boolean))].sort();
  const ultimos12 = meses.slice(-12);

  // Faturamento por mes
  const fatPorMes = {};
  data.vendas.forEach(v => {
    const m = v.data ? v.data.slice(0, 7) : null;
    if (m) fatPorMes[m] = (fatPorMes[m] || 0) + Number(v.valor) * Number(v.qtd);
  });

  // CMV por mes
  const cmvPorMes = {};
  data.vendas.forEach(v => {
    const m = v.data ? v.data.slice(0, 7) : null;
    if (m) cmvPorMes[m] = (cmvPorMes[m] || 0) + Number(v.custo) * Number(v.qtd);
  });

  // Gastos por mes e tipo
  const gastosLojaPorMes = {};
  const gastosPessoalPorMes = {};
  const gastosDetalhe = {}; // {mes: [{desc, valor, data}]}
  data.gastos.forEach(g => {
    const m = g.data ? g.data.slice(0, 7) : null;
    if (!m) return;
    if (g.tipo === 'loja') gastosLojaPorMes[m] = (gastosLojaPorMes[m] || 0) + Number(g.valor);
    else gastosPessoalPorMes[m] = (gastosPessoalPorMes[m] || 0) + Number(g.valor);
    if (!gastosDetalhe[m]) gastosDetalhe[m] = [];
    gastosDetalhe[m].push({ desc: g.descricao || g.subcategoria || g.categoria, valor: Number(g.valor), tipo: g.tipo });
  });

  // Teto pessoal por mes (baseado na analise)
  const tetoPorMes = (data.caixa_config || {}).teto_por_mes || {};
  const teto = Number(tetoPorMes[mesSel] || (data.caixa_config || {}).teto_pessoal || 8000);

  // Projecao futura — gastos ja lancados
  const mesesFuturos = [...new Set(data.gastos
    .filter(g => g.data > hoje)
    .map(g => g.data.slice(0, 7))
  )].sort().slice(0, 12);

  const cmvPct = 0.32;
  const mesesFechados = ultimos12.filter(m => m < mesAtualReal).slice(-3);
  const fat3 = mesesFechados.map(m => fatPorMes[m] || 0).filter(v => v > 0);
  const fatMediaProj = fat3.reduce((s, v) => s + v, 0) / Math.max(fat3.length, 1);
  const diasNoMesSel = new Date(parseInt(mesSel.slice(0, 4)), parseInt(mesSel.slice(5, 7)), 0).getDate();
  const diaHoje = parseInt(hoje.slice(8, 10));
  const fatRealSel = fatPorMes[mesSel] || 0;
  const fatMesSel = isFuturo ? fatMediaProj : isMesAtual ? fatRealSel + fatMediaProj * Math.max(diasNoMesSel - diaHoje, 0) / diasNoMesSel : (fatRealSel || fatMediaProj);
  const gastosLojaSel = gastosLojaPorMes[mesSel] || 0;
  const gastosPessoalSel = gastosPessoalPorMes[mesSel] || 0;
  const cmvRealPctMes = fatRealSel > 0 ? (cmvPorMes[mesSel] || 0) / fatRealSel : cmvPct;
  const cmvMesSel = isMesAtual ? fatMesSel * cmvRealPctMes : (cmvPorMes[mesSel] || (fatMesSel * cmvPct));
  const lojaRefFechado = mesesFechados.length ? (gastosLojaPorMes[mesesFechados[mesesFechados.length - 1]] || 0) : 0;
  const lojaBaseEq = isMesAtual ? Math.max(gastosLojaSel, lojaRefFechado) : gastosLojaSel;
  const pontoEquilibrio = lojaBaseEq + teto + cmvMesSel;

  // Projecao de faturamento (media 3 meses)

  // Meses para analise (historico + futuro)
  const todosMeses = [...new Set([...ultimos12, ...mesesFuturos])].sort();

  // Resultado por mes
  const resultadoPorMes = todosMeses.map(m => {
    const fat = fatPorMes[m] || 0;
    const cmv = cmvPorMes[m] || fat * cmvPct;
    const loja = gastosLojaPorMes[m] || 0;
    const pessoal = gastosPessoalPorMes[m] || 0;
    const resultado = fat - cmv - loja - pessoal;
    const isFuturo = m > mesAtual;
    return { mes: m, fat, cmv, loja, pessoal, resultado, isFuturo };
  });

  const resultadoPorMesComProj = resultadoPorMes.map(r => {
    if (!r.isFuturo) return r;
    const fatProj = r.fat > 0 ? r.fat : fatMediaProj;
    const resultadoProj = fatProj - fatProj * cmvPct - r.loja - r.pessoal;
    return { ...r, fatProj, resultadoProj };
  });
  const voltaVerde = resultadoPorMesComProj.find(r => r.isFuturo && (r.resultadoProj || 0) > 0);

  // Parcelas futuras agrupadas por mes
  const parcelasFuturas = {};
  data.gastos
    .filter(g => g.data > mesAtual + '-01')
    .forEach(g => {
      const m = g.data.slice(0, 7);
      if (!parcelasFuturas[m]) parcelasFuturas[m] = { loja: 0, pessoal: 0, items: [] };
      if (g.tipo === 'pessoal') parcelasFuturas[m].pessoal += Number(g.valor);
      else parcelasFuturas[m].loja += Number(g.valor);
    });

  const [mesVis, setMesVis] = useState(mesAtual);
  const [editandoTeto, setEditandoTeto] = useState(false);
  const [novoTeto, setNovoTeto] = useState(String(teto));
  useEffect(() => { setNovoTeto(String(teto)); }, [mesSel, teto]); // eslint-disable-line react-hooks/exhaustive-deps
  const [simFat, setSimFat] = useState('');
  const [comBrindes, setComBrindes] = useState(false);
  const mesDetalhe = gastosDetalhe[mesVis] || [];
  const gastosPessoalMesAtual = gastosPessoalPorMes[mesAtual] || 0;
  const gastosPessoalAteHoje = data.gastos.filter(g => g.tipo !== 'loja' && g.data && g.data.slice(0, 7) === mesAtual && g.data <= hoje).reduce((s, g) => s + Number(g.valor), 0);
  const gastosPessoalAVencer = Math.max(gastosPessoalMesAtual - gastosPessoalAteHoje, 0);
  const pctTeto = Math.min((gastosPessoalMesAtual / teto) * 100, 100);
  const corTeto = pctTeto >= 100 ? '#b91c1c' : pctTeto >= 90 ? '#d97706' : '#15803d';

  const salvarTeto = async () => {
    const val = Number(novoTeto);
    if (!val || val < 0) return;
    const cfg = data.caixa_config || {};
    if (cfg.id) {
      const novoTetoPorMes = { ...(cfg.teto_por_mes || {}), [mesSel]: val };
      await fetch(API_BASE + '?table=caixa_config&id=eq.' + cfg.id, { method: 'PATCH', headers: { Authorization: 'Bearer ' + getJWT(), 'Content-Type': 'application/json' }, body: JSON.stringify({ teto_por_mes: novoTetoPorMes }) });
      setData(d => ({ ...d, caixa_config: { ...d.caixa_config, teto_por_mes: novoTetoPorMes } }));
    }
    setEditandoTeto(false);
  };

  // CMV por produto
  const cmvPP = {}; const fatPP = {}; const qtdPP = {}; const brindePP = {};
  data.vendas.filter(v => v.data && v.data >= (ultimos12[Math.max(0, ultimos12.length-3)] || '')).forEach(v => {
    const itens = Array.isArray(v.itens) && v.itens.length > 0 ? v.itens : [{ produto: v.descricao || '', qtd: v.qtd||1, valor: v.valor||0, custo: v.custo||0 }];
    const custoBrindes = itens.filter(it => Number(it.valor||0) === 0).reduce((s,it) => s + Number(it.custo||0)*Number(it.qtd||1), 0);
    const qtdPrinc = itens.filter(it => Number(it.valor||0) > 0).reduce((s,it) => s + Number(it.qtd||1), 0);
    itens.forEach(it => {
      const p = it.produto || ''; if (!p) return;
      if (!cmvPP[p]) { cmvPP[p]=0; fatPP[p]=0; qtdPP[p]=0; brindePP[p]=0; }
      cmvPP[p] += Number(it.custo||0)*Number(it.qtd||1);
      fatPP[p] += Number(it.valor||0)*Number(it.qtd||1);
      qtdPP[p] += Number(it.qtd||1);
      if (Number(it.valor||0) > 0 && qtdPrinc > 0) brindePP[p] += (custoBrindes/qtdPrinc)*Number(it.qtd||1);
    });
  });
  const produtosAnalise = Object.keys(fatPP).filter(p => fatPP[p]>0 && qtdPP[p]>=3).map(p => {
    const cmvFinal = comBrindes ? cmvPP[p]+(brindePP[p]||0) : cmvPP[p];
    return { nome:p, fat:fatPP[p], cmv:cmvFinal, brinde:brindePP[p]||0, qtd:qtdPP[p], margem:((fatPP[p]-cmvFinal)/fatPP[p]*100), ticket:fatPP[p]/qtdPP[p], custoMedio:cmvFinal/qtdPP[p] };
  }).sort((a,b) => b.fat-a.fat).slice(0,10);

  return (
    <div style={{ padding: 24, maxWidth: 1200 }}>
      <div style={{ marginBottom: 28, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div style={styles.pageTitle}>Saúde Financeira</div>
          <div style={styles.pageSub}>Panorama completo — histórico, projeção e alertas</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 11, color: '#6b7280' }}>Analisando:</span>
          <select value={mesSel} onChange={e => setMesSelPersist(e.target.value)} style={{ ...styles.input, width: 'auto', fontSize: 12, padding: '6px 12px' }}>
            {Array.from({length: 18}, (_, i) => {
              const [anoBase, mesBase] = mesAtualReal.split('-').map(Number);
              const idxMes = anoBase * 12 + (mesBase - 1) - 6 + i;
              const m = Math.floor(idxMes / 12) + '-' + String(idxMes % 12 + 1).padStart(2, '0');
              const label = MESES[parseInt(m.slice(5,7))-1] + '/' + m.slice(2,4);
              return <option key={m} value={m}>{label}{m === mesAtualReal ? ' (atual)' : m > mesAtualReal ? ' (futuro)' : ''}</option>;
            })}
          </select>
          {!isMesAtual && <button onClick={() => setMesSelPersist(mesAtualReal)} style={{ ...styles.btn('secondary'), fontSize: 11, padding: '6px 10px' }}>Hoje</button>}
        </div>
      </div>

      {/* Alerta teto pessoal */}
      {!isFuturo && pctTeto >= 70 && (
        <div style={{ background: pctTeto >= 90 ? '#fef2f2' : '#fffbeb', border: `1px solid ${corTeto}30`, borderRadius: 12, padding: '14px 20px', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 20 }}>{pctTeto >= 90 ? '🚨' : '⚠️'}</span>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: corTeto }}>
              {pctTeto >= 100 ? 'LIMITE ATINGIDO! Pare os gastos pessoais agora' : pctTeto >= 90 ? 'PARE! Limite pessoal quase atingido' : 'Atenção: gasto pessoal alto este mês'}
            </div>
            <div style={{ fontSize: 12, color: '#6b7280' }}>
              Gasto pessoal: {fmt(gastosPessoalMesAtual)} de {fmt(teto)} ({pctTeto.toFixed(0)}% do teto)
            </div>
          </div>
          <div style={{ marginLeft: 'auto', minWidth: 120 }}>
            <div style={{ height: 6, background: '#e5e7eb', borderRadius: 3, overflow: 'hidden' }}>
              <div style={{ height: '100%', width: pctTeto + '%', background: corTeto, borderRadius: 3, transition: 'width 0.5s' }} />
            </div>
          </div>
        </div>
      )}

      {/* Quanto posso gastar hoje */}
      {isMesAtual && (() => {
        const diasMes = new Date(parseInt(hoje.slice(0, 4)), parseInt(hoje.slice(5, 7)), 0).getDate();
        const diaAtual = parseInt(hoje.slice(8, 10));
        const diasRest = diasMes - diaAtual + 1; // conta o dia de hoje
        const dispMes = Math.max(0, teto - gastosPessoalMesAtual);
        const dispHoje = diasRest > 0 ? dispMes / diasRest : 0;
        const cor = dispHoje <= 0 ? '#b91c1c' : dispHoje < 100 ? '#d97706' : '#15803d';
        return (
          <div style={{ background: '#ffffff', border: '1px solid #e5e7eb', borderRadius: 12, padding: '14px 20px', marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ fontSize: 22 }}>💳</span>
              <div>
                <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>Pode gastar hoje</div>
                <div style={{ fontSize: 24, fontWeight: 800, color: cor }}>{privado ? '••••' : fmt(dispHoje)}</div>
                <div style={{ fontSize: 11, color: '#9ca3af' }}>pra não estourar o teto de {fmt(teto)}</div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 20 }}>
              <div style={{ textAlign: 'center' }}><div style={{ fontSize: 10, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>Já lançado até hoje</div><div style={{ fontSize: 16, fontWeight: 700, color: '#374151' }}>{privado ? '••••' : fmt(gastosPessoalAteHoje)}</div></div>
              <div style={{ textAlign: 'center' }}><div style={{ fontSize: 10, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>A vencer no mês</div><div style={{ fontSize: 16, fontWeight: 700, color: '#374151' }}>{privado ? '••••' : fmt(gastosPessoalAVencer)}</div></div>
              <div style={{ textAlign: 'center' }}><div style={{ fontSize: 10, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>Disponível</div><div style={{ fontSize: 16, fontWeight: 700, color: cor }}>{privado ? '••••' : fmt(dispMes)}</div></div>
              <div style={{ textAlign: 'center' }}><div style={{ fontSize: 10, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>Dias (com hoje)</div><div style={{ fontSize: 16, fontWeight: 700, color: '#374151' }}>{diasRest}</div></div>
            </div>
          </div>
        );
      })()}
      {/* KPIs topo */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 20 }}>
        {[
          { label: isFuturo ? 'Faturamento projetado' : isMesAtual ? 'Faturamento projetado' : 'Faturamento real', value: fmt(fatMesSel), sub: isFuturo ? 'media 3 meses fechados' : isMesAtual ? 'real ate hoje + restante na media' : MESES[parseInt(mesSel.slice(5,7))-1] + '/' + mesSel.slice(2,4), color: '#111827' },
          { label: 'Ponto de equilíbrio', value: fmt(pontoEquilibrio), sub: 'mínimo mensal', color: fatMesSel >= pontoEquilibrio ? '#15803d' : '#b91c1c' },
          { label: 'Teto pessoal', value: fmt(teto), sub: fmt(gastosPessoalSel) + ' usado', color: (gastosPessoalSel/teto) >= 0.9 ? '#b91c1c' : '#111827', onEdit: () => setEditandoTeto(true) },
          { label: 'Volta ao verde', value: voltaVerde ? MESES[parseInt(voltaVerde.mes.slice(5,7))-1] + '/' + voltaVerde.mes.slice(2,4) : 'Incerto', sub: voltaVerde ? fmt(voltaVerde.resultadoProj) + ' projetado' : 'Fat medio: ' + fmt(fatMediaProj), color: voltaVerde ? '#15803d' : '#b91c1c' },
        ].map((k, i) => (
          <div key={i} style={{ background: '#ffffff', border: '1px solid #e5e7eb', borderRadius: 12, padding: '16px 18px' }}>
            <div style={{ fontSize: 9, color: '#9ca3af', letterSpacing: 1.5, textTransform: 'uppercase', fontWeight: 700, marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>{k.label}{k.onEdit && <button onClick={k.onEdit} style={{ background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', fontSize: 11, padding: 0 }}>editar</button>}</div>
            <div style={{ fontSize: 20, fontWeight: 800, color: k.color, letterSpacing: -0.5, marginBottom: 4 }}>{privado ? '••••' : k.value}</div>
            <div style={{ fontSize: 10, color: '#9ca3af' }}>{k.sub}</div>
          </div>
        ))}
      </div>

      {/* Grafico principal */}
      <div style={{ background: '#ffffff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 20, marginBottom: 16 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Faturamento vs Gastos por Mês</span>
          <div style={{ display: 'flex', gap: 16, fontSize: 11 }}>
            {[['#2a78d6','Faturamento'],['#eb6834','Gastos Loja'],['#e87ba4','Pessoal'],['#15803d','Resultado']].map(([c,l]) => (
              <span key={l} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <span style={{ width: 10, height: 10, borderRadius: 2, background: c, display: 'inline-block' }} />
                {l}
              </span>
            ))}
          </div>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <div style={{ minWidth: 600, height: 280, display: 'flex', alignItems: 'flex-end', gap: 4, paddingBottom: 24, position: 'relative' }}>
            {/* Linha de equilibrio */}
            {resultadoPorMes.map((r, i) => {
              const maxVal = Math.max(...resultadoPorMes.map(x => x.fat || 0), pontoEquilibrio);
              const h = (v) => Math.max(0, (v / maxVal) * 240);
              return (
                <div key={r.mes} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, position: 'relative' }}>
                  {/* Barras */}
                  <div style={{ width: '100%', display: 'flex', gap: 1, alignItems: 'flex-end', height: 240 }}>
                    <div style={{ flex: 1, height: h(r.fat), background: r.isFuturo ? '#93c5fd' : '#2a78d6', borderRadius: '3px 3px 0 0', transition: 'height 0.3s' }} title={`Fat: ${fmt(r.fat)}`} />
                    <div style={{ flex: 1, height: h(r.loja), background: r.isFuturo ? '#fca5a5' : '#eb6834', borderRadius: '3px 3px 0 0' }} title={`Loja: ${fmt(r.loja)}`} />
                    <div style={{ flex: 1, height: h(r.pessoal), background: r.isFuturo ? '#f9a8d4' : '#e87ba4', borderRadius: '3px 3px 0 0' }} title={`Pessoal: ${fmt(r.pessoal)}`} />
                  </div>
                  {/* Label mes */}
                  <div style={{ fontSize: 9, color: '#374151', textAlign: 'center', position: 'absolute', bottom: 0, fontWeight: 700 }}>
                    {MESES[parseInt(r.mes.slice(5,7))-1].slice(0,3)}/{r.mes.slice(2,4)}
                  </div>
                  {/* Resultado */}
                  <div style={{ position: 'absolute', top: 240 - h(r.fat) - 16, fontSize: 8, color: r.resultado >= 0 ? '#15803d' : '#b91c1c', fontWeight: 700, whiteSpace: 'nowrap' }}>
                    {!privado && r.fat > 0 && (r.resultado >= 0 ? '+' : '') + (r.resultado/1000).toFixed(0) + 'k'}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 4 }}>Barras transparentes = meses futuros (parcelas já lançadas)</div>
      </div>

      {/* Simulador */}
      <div style={{ background: '#ffffff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 20, marginBottom: 16 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 12 }}>Simulador — E se eu faturar...</div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 200 }}>
            <span style={{ fontSize: 12, color: '#6b7280' }}>Faturamento:</span>
            <input type="number" value={simFat} onChange={e => setSimFat(e.target.value)} placeholder="Ex: 70000" style={{ ...styles.input, flex: 1 }} />
          </div>
          {[50000,60000,70000,80000].map(v => (
            <button key={v} onClick={() => setSimFat(String(v))} style={{ ...styles.btn('secondary'), fontSize: 11, padding: '6px 12px' }}>R${(v/1000).toFixed(0)}k</button>
          ))}
        </div>
        {simFat && Number(simFat) > 0 && (() => {
          const fat = Number(simFat);
          const cmvRealPct = fatPorMes[mesAtual] > 0 ? (cmvPorMes[mesAtual] || 0) / fatPorMes[mesAtual] : cmvPct;
          const cmv = fat * cmvRealPct;
          const lojaMes = gastosLojaSel || 0;
          const pessoalMes = gastosPessoalSel > 0 ? gastosPessoalSel : teto;
          const resultado = fat - cmv - lojaMes - pessoalMes;
          const margem = fat > 0 ? (resultado / fat * 100) : 0;
          const cor = resultado >= 0 ? '#15803d' : '#b91c1c';
          return (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
              {[
                { label: 'CMV estimado', value: fmt(cmv), sub: (cmvRealPct*100).toFixed(1) + '% do fat. (real)', color: '#b91c1c' },
                { label: 'Gastos loja', value: fmt(lojaMes), sub: 'mes atual', color: '#eb6834' },
                { label: 'Gasto pessoal', value: fmt(pessoalMes), sub: 'real do mes', color: '#e87ba4' },
                { label: 'Resultado', value: fmt(Math.abs(resultado)), sub: (resultado >= 0 ? 'positivo ' : 'negativo ') + margem.toFixed(1) + '%', color: cor },
              ].map((k, i) => (
                <div key={i} style={{ background: '#f9fafb', borderRadius: 10, padding: '12px 14px', border: '1px solid #e5e7eb' }}>
                  <div style={{ fontSize: 9, color: '#9ca3af', letterSpacing: 1.5, textTransform: 'uppercase', fontWeight: 700, marginBottom: 6 }}>{k.label}</div>
                  <div style={{ fontSize: 16, fontWeight: 800, color: k.color, marginBottom: 2 }}>{privado ? '••••' : k.value}</div>
                  <div style={{ fontSize: 10, color: '#9ca3af' }}>{k.sub}</div>
                </div>
              ))}
              <div style={{ gridColumn: '1 / -1', padding: '10px 14px', background: resultado >= 0 ? '#f0fdf4' : '#fef2f2', border: '1px solid ' + (resultado >= 0 ? '#bbf7d0' : '#fecaca'), borderRadius: 10, fontSize: 12, color: cor, fontWeight: 600 }}>
                {resultado >= 0 ? 'Com R$' + (fat/1000).toFixed(0) + 'k voce fecha no verde com +' + fmt(resultado) + '.' : 'Com R$' + (fat/1000).toFixed(0) + 'k o mes fecha no vermelho em ' + fmt(Math.abs(resultado)) + '.'}
              </div>
            </div>
          );
        })()}
      </div>

      {/* CMV por produto */}
      <div style={{ background: '#ffffff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 20, marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#374151' }}>Analise de Margem por Produto</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 11, color: '#6b7280' }}>Com brindes</span>
            <div onClick={() => setComBrindes(b => !b)} style={{ width: 36, height: 20, borderRadius: 10, background: comBrindes ? '#111827' : '#e5e7eb', cursor: 'pointer', position: 'relative', transition: 'background 0.2s' }}>
              <div style={{ position: 'absolute', top: 2, left: comBrindes ? 18 : 2, width: 16, height: 16, borderRadius: '50%', background: '#fff', transition: 'left 0.2s' }} />
            </div>
          </div>
        </div>
        <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 12 }}>Ultimos 3 meses — produtos com 3+ vendas</div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
            <thead><tr style={{ borderBottom: '2px solid #e5e7eb' }}>
              {['Produto','Qtd','Faturamento','CMV','Margem','Ticket','Custo Medio'].map(h => <th key={h} style={{ textAlign:'left', padding:'6px 10px', fontSize:9, color:'#9ca3af', letterSpacing:1.5, textTransform:'uppercase', fontWeight:700 }}>{h}</th>)}
            </tr></thead>
            <tbody>
              {produtosAnalise.map((p,i) => {
                const cm = p.margem >= 60 ? '#15803d' : p.margem >= 40 ? '#d97706' : '#b91c1c';
                return (
                  <tr key={i} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding:'8px 10px', fontWeight:600, color:'#111827' }}>{p.nome}</td>
                    <td style={{ padding:'8px 10px', color:'#374151' }}>{p.qtd}</td>
                    <td style={{ padding:'8px 10px', fontWeight:600, color:'#111827' }}>{privado ? '••••' : fmt(p.fat)}</td>
                    <td style={{ padding:'8px 10px', color:'#b91c1c' }}>{privado ? '••••' : fmt(p.cmv)}</td>
                    <td style={{ padding:'8px 10px' }}>
                      <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                        <div style={{ flex:1, height:6, background:'#f3f4f6', borderRadius:3, minWidth:60 }}>
                          <div style={{ height:'100%', width:Math.min(p.margem,100)+'%', background:cm, borderRadius:3 }} />
                        </div>
                        <span style={{ fontWeight:700, color:cm, minWidth:36 }}>{p.margem.toFixed(1)}%</span>
                      </div>
                    </td>
                    <td style={{ padding:'8px 10px', color:'#374151' }}>{privado ? '••••' : fmt(p.ticket)}</td>
                    <td style={{ padding:'8px 10px', color:'#6b7280' }}>{privado ? '••••' : fmt(p.custoMedio)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Linha do tempo de parcelas */}
      <div style={{ background: '#ffffff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 20, marginBottom: 16 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 16 }}>Compromissos Futuros — quando cada conta acaba</div>
        <div style={{ overflowX: 'auto' }}>
          <div style={{ display: 'flex', gap: 8, minWidth: 600, paddingBottom: 8 }}>
            {Object.keys(parcelasFuturas).slice(0, 12).map(m => {
              const p = parcelasFuturas[m];
              const total = p.loja + p.pessoal;
              const isVerde = total < 3000;
              return (
                <div key={m} style={{ flex: 1, minWidth: 80, background: isVerde ? '#f0fdf4' : total > 8000 ? '#fef2f2' : '#fffbeb', border: `1px solid ${isVerde ? '#bbf7d0' : total > 8000 ? '#fecaca' : '#fde68a'}`, borderRadius: 8, padding: 10, fontSize: 10 }}>
                  <div style={{ fontWeight: 700, color: '#374151', marginBottom: 4 }}>{MESES[parseInt(m.slice(5,7))-1].slice(0,3)}/{m.slice(2,4)}</div>
                  <div style={{ color: '#374151' }}>Loja: {fmt(p.loja)}</div>
                  <div style={{ color: '#374151' }}>Pessoal: {fmt(p.pessoal)}</div>
                  <div style={{ fontWeight: 700, color: isVerde ? '#15803d' : total > 8000 ? '#b91c1c' : '#d97706', marginTop: 4 }}>{fmt(total)}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Detalhe do mes selecionado */}
      <div style={{ background: '#ffffff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#374151' }}>Detalhe do Mês</div>
          <select value={mesVis} onChange={e => setMesVis(e.target.value)} style={{ ...styles.input, width: 'auto', fontSize: 11, padding: '4px 8px' }}>
            {todosMeses.map(m => <option key={m} value={m}>{MESES[parseInt(m.slice(5,7))-1] + '/' + m.slice(0,4)}</option>)}
          </select>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <div>
            <div style={{ fontSize: 10, color: '#9ca3af', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>Resumo</div>
            {(() => {
              const r = resultadoPorMes.find(x => x.mes === mesVis) || {};
              return [
                { l: 'Faturamento', v: r.fat || 0, c: '#111827' },
                { l: 'CMV', v: -(r.cmv || 0), c: '#b91c1c' },
                { l: 'Gastos Loja', v: -(r.loja || 0), c: '#eb6834' },
                { l: 'Gastos Pessoal', v: -(r.pessoal || 0), c: '#e87ba4' },
                { l: 'Resultado', v: r.resultado || 0, c: (r.resultado || 0) >= 0 ? '#15803d' : '#b91c1c', bold: true },
              ].map((item, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f3f4f6' }}>
                  <span style={{ fontSize: 12, color: '#374151', fontWeight: item.bold ? 700 : 400 }}>{item.l}</span>
                  <span style={{ fontSize: 12, fontWeight: 700, color: item.c }}>{privado ? '••••' : fmt(Math.abs(item.v))}{item.v < 0 ? ' ↑' : item.bold && item.v > 0 ? ' ✓' : ''}</span>
                </div>
              ));
            })()}
          </div>
          <div>
            <div style={{ fontSize: 10, color: '#9ca3af', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>Maiores Gastos</div>
            <div style={{ maxHeight: 200, overflowY: 'auto' }}>
              {mesDetalhe.sort((a, b) => b.valor - a.valor).slice(0, 10).map((g, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid #f3f4f6' }}>
                  <span style={{ fontSize: 11, color: '#374151' }}>{g.desc}</span>
                  <span style={{ fontSize: 11, fontWeight: 600, color: g.tipo === 'pessoal' ? '#e87ba4' : '#eb6834' }}>{privado ? '••••' : fmt(g.valor)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      {editandoTeto && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 16, padding: 28, width: 340, border: '1px solid #e5e7eb' }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#111827', marginBottom: 16 }}>Editar Teto Pessoal</div>
            <input type="number" value={novoTeto} onChange={e => setNovoTeto(e.target.value)} style={{ ...styles.input, marginBottom: 16 }} placeholder="Ex: 8000" />
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={salvarTeto} style={{ ...styles.btn('primary'), flex: 1 }}>Salvar</button>
              <button onClick={() => setEditandoTeto(false)} style={{ ...styles.btn('secondary'), flex: 1 }}>Cancelar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── INDICADORES PAGE ──────────────────────────────────────────
const ACESSORIOS_PADRAO = ["fone", "pulseira", "carregador", "cabo", "capa", "pelicula", "upsell", "airpods"];
function ehAcessorio(nome, lista) {
  const n = (nome || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
  return lista.some(w => { const s = (w || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/[.*+?^${}()|[\]\\]/g, "\\$&"); return s.length > 0 && new RegExp("\\b" + s + "(s|es)?\\b").test(n); });
}
function produtosDaVenda(v, lista) {
  const itens = Array.isArray(v.itens) ? v.itens : [];
  if (itens.length === 0) return Number(v.qtd || 0) > 0 ? Number(v.qtd) : 0;
  return itens.reduce((s, it) => (Number(it.valor || 0) > 0 && !ehAcessorio(it.produto, lista)) ? s + Number(it.qtd || 0) : s, 0);
}
function indicadoresDeVendas(vendas, lista) {
  const dias = {};
  for (const v of vendas || []) {
    if (!v.data) continue;
    const d = dias[v.data] || (dias[v.data] = { pedidos: 0, produtos: 0, faturamento: 0 });
    d.pedidos += 1;
    d.produtos += produtosDaVenda(v, lista);
    d.faturamento += Number(v.valor || 0) * Number(v.qtd || 1);
  }
  return dias;
}

function Indicadores({ data, setData, showToast }) {
  const hoje = hojeManaus();
  const [form, setForm] = useState({ data: hoje, leads: "", sem_resposta: "", pedidos: "", smartwatchs: "", faturamento: "", orcamento: "" });
  const [salvando, setSalvando] = useState(false);
  const [editandoId, setEditandoId] = useState(null);
  const { filtrarLista, aplicarPeriodo, selecionarMes, periodoRapido, mesFiltro } = useFiltro("tfx_indicadores"); // eslint-disable-line

  const [metaHoje, setMetaHoje] = useState(null); // gasto do Meta de hoje, ao vivo, já com imposto
  // Pedidos, produtos e faturamento vêm das vendas. Leads, S/R e orçamento vêm dos registros salvos.
  const cfgCaixa = data.caixa_config || {};
  const acessorios = Array.isArray(cfgCaixa.palavras_acessorio) ? cfgCaixa.palavras_acessorio : ACESSORIOS_PADRAO;
  const porDia = indicadoresDeVendas(data.vendas, acessorios);
  const guardados = {};
  (data.indicadores || []).forEach(i => { guardados[i.data] = i; });
  const linhasTodas = [...new Set([...Object.keys(porDia), ...Object.keys(guardados), ...(metaHoje > 0 ? [hoje] : [])])].map(dia => {
    const g = guardados[dia];
    const a = porDia[dia];
    return {
      id: g ? g.id : null,
      data: dia,
      orcamento: (dia === hoje && metaHoje !== null) ? metaHoje : (g ? g.orcamento : 0),
      aoVivo: dia === hoje && metaHoje !== null,
      leads: g ? g.leads : 0,
      sem_resposta: g ? g.sem_resposta : 0,
      pedidos: a ? a.pedidos : (g ? g.pedidos : 0),
      smartwatchs: a ? a.produtos : (g ? g.smartwatchs : 0),
      faturamento: a ? a.faturamento : (g ? g.faturamento : 0),
      manual: !a,
    };
  });
  const indicadores = filtrarLista(linhasTodas);
  const [editandoAcess, setEditandoAcess] = useState(false);
  const [textoAcess, setTextoAcess] = useState("");
  const salvarAcessorios = async () => {
    const lista = [...new Set(textoAcess.split(/[,;\n]/).map(s => s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim()).filter(Boolean))];
    try {
      if (cfgCaixa.id) {
        const r = await sb.update("caixa_config", cfgCaixa.id, { palavras_acessorio: lista });
        if (r && (r.error || r.code || r.message)) throw new Error(r.message || r.error || "falha ao salvar");
        setData(d => ({ ...d, caixa_config: { ...d.caixa_config, palavras_acessorio: lista } }));
      } else {
        const res = await sb.insert("caixa_config", { tenant_id: getTenant(), palavras_acessorio: lista });
        const novo = Array.isArray(res) ? res[0] : res;
        if (!novo || novo.code || !novo.id) throw new Error("não foi possível criar a configuração");
        setData(d => ({ ...d, caixa_config: novo }));
      }
      setEditandoAcess(false);
      if (showToast) showToast("Lista salva!");
    } catch (e) { alert("Erro ao salvar: " + e.message); }
  };
  const [editandoImp, setEditandoImp] = useState(false);
  const [textoImp, setTextoImp] = useState("");
  const salvarImposto = async () => {
    const t = String(textoImp).replace(",", ".").trim();
    const n = t === "" ? null : Number(t);
    if (n !== null && (!Number.isFinite(n) || n < 0 || n > 100)) { alert("Informe uma alíquota entre 0 e 100. Use 0 se a loja não paga imposto."); return; }
    try {
      if (cfgCaixa.id) {
        const r = await sb.update("caixa_config", cfgCaixa.id, { imposto_trafego: n });
        if (r && (r.error || r.code || r.message)) throw new Error(r.message || r.error || "falha ao salvar");
        setData(d => ({ ...d, caixa_config: { ...d.caixa_config, imposto_trafego: n } }));
      } else {
        const res = await sb.insert("caixa_config", { tenant_id: getTenant(), imposto_trafego: n });
        const novo = Array.isArray(res) ? res[0] : res;
        if (!novo || novo.code || !novo.id) throw new Error("não foi possível criar a configuração");
        setData(d => ({ ...d, caixa_config: novo }));
      }
      setEditandoImp(false);
      if (showToast) showToast("Alíquota salva!");
    } catch (e) { alert("Erro ao salvar: " + e.message); }
  };

  // Calcula métricas derivadas
  const calc = (row) => {
    const orc = parseFloat(row.orcamento || 0);
    const leads = parseInt(row.leads || 0);
    const pedidos = parseInt(row.pedidos || 0);
    const prod = parseInt(row.smartwatchs || 0);
    const fat = parseFloat(row.faturamento || 0);
    return {
      cpv: pedidos > 0 ? orc / pedidos : 0,
      cpp: prod > 0 ? orc / prod : 0,
      cpl: leads > 0 ? orc / leads : 0,
      taxa: leads > 0 ? (pedidos / leads) * 100 : 0,
      roi: orc > 0 ? fat / orc : 0,
      ticket: pedidos > 0 ? fat / pedidos : 0,
    };
  };

  // Totais do período
  const totais = indicadores.reduce((acc, r) => ({
    orcamento: acc.orcamento + parseFloat(r.orcamento || 0),
    leads: acc.leads + parseInt(r.leads || 0),
    sem_resposta: acc.sem_resposta + parseInt(r.sem_resposta || 0),
    pedidos: acc.pedidos + parseInt(r.pedidos || 0),
    smartwatchs: acc.smartwatchs + parseInt(r.smartwatchs || 0),
    faturamento: acc.faturamento + parseFloat(r.faturamento || 0),
  }), { orcamento: 0, leads: 0, sem_resposta: 0, pedidos: 0, smartwatchs: 0, faturamento: 0 });
  const linhasTrafego = indicadores.filter(r => parseFloat(r.orcamento || 0) > 0 || parseInt(r.leads || 0) > 0);
  const totTr = linhasTrafego.reduce((acc, r) => ({ orcamento: acc.orcamento + parseFloat(r.orcamento || 0), leads: acc.leads + parseInt(r.leads || 0), pedidos: acc.pedidos + parseInt(r.pedidos || 0), smartwatchs: acc.smartwatchs + parseInt(r.smartwatchs || 0), faturamento: acc.faturamento + parseFloat(r.faturamento || 0) }), { orcamento: 0, leads: 0, pedidos: 0, smartwatchs: 0, faturamento: 0 });
  const totaisCalc = { ...calc(totTr), ticket: totais.pedidos > 0 ? totais.faturamento / totais.pedidos : 0 };

  const buscarMetaParaDia = async (dataBusca) => {
    try {
      const r = await fetch(`https://gestao-tfx-saas.vercel.app/api/meta-ads?action=insights&tenant_id=${getTenant()}&since=${dataBusca}&until=${dataBusca}`);
      const d = await r.json();
      if (d.ok && d.data && !d.naoConectado) return { orcamento: Number(((parseFloat(d.data.spend) || 0) * fatorTrafego(cfgCaixa)).toFixed(2)), leads: d.data.clicks };
    } catch(e) {}
    return null;
  };

  const calcularFaturamentoDia = (novaData) => {
    const vendasDia = (data.vendas || []).filter(v => v.data === novaData);
    return vendasDia.reduce((s, v) => s + (Number(v.valor) * Number(v.qtd || 1)), 0);
  };

  const carregarMetaHoje = async () => {
    const m = await buscarMetaParaDia(hoje);
    if (m) setMetaHoje(m.orcamento);
  };
  useEffect(() => {
    carregarMetaHoje();
    const t = setInterval(carregarMetaHoje, 120000);
    return () => clearInterval(t);
  }, [cfgCaixa.imposto_trafego]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDataChange = async (novaData) => {
    setForm(f => ({ ...f, data: novaData }));
    const existente = (data.indicadores || []).find(i => i.data === novaData);
    if (existente) {
      setEditandoId(existente.id);
      setForm({ data: novaData, leads: existente.leads || "", sem_resposta: existente.sem_resposta || "", pedidos: existente.pedidos || "", smartwatchs: existente.smartwatchs || "", faturamento: existente.faturamento || "", orcamento: existente.orcamento || "" });
    } else {
      setEditandoId(null);
      const meta = await buscarMetaParaDia(novaData);
      const fatDia = calcularFaturamentoDia(novaData);
      setForm(f => ({ ...f, orcamento: meta?.orcamento || "", leads: meta?.leads || "", faturamento: fatDia > 0 ? fatDia.toFixed(2) : "" }));
    }
  };

  const salvar = async () => {
    setSalvando(true);
    try {
      const auto = porDia[form.data];
      const row = { tenant_id: getTenant(), data: form.data, leads: parseInt(form.leads || 0), sem_resposta: parseInt(form.sem_resposta || 0), pedidos: auto ? auto.pedidos : parseInt(form.pedidos || 0), smartwatchs: auto ? auto.produtos : parseInt(form.smartwatchs || 0), faturamento: auto ? parseFloat(auto.faturamento.toFixed(2)) : parseFloat(form.faturamento || 0), orcamento: parseFloat(form.orcamento || 0) };
      if (editandoId) {
        await sb.update("indicadores", editandoId, row);
        setData(d => ({ ...d, indicadores: d.indicadores.map(i => i.id === editandoId ? { ...i, ...row } : i) }));
      } else {
        await sb.insert("indicadores", row);
        const atualizados = await sb.get("indicadores");
        setData(d => ({ ...d, indicadores: atualizados || [] }));
        const novo = (atualizados || []).find(i => i.data === row.data && i.tenant_id === row.tenant_id);
        if (novo) setEditandoId(novo.id);
      }
      if (showToast) showToast("Indicadores salvos!");
    } catch(e) { alert("Erro: " + e.message); }
    setSalvando(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Remover leads e orçamento deste dia? As vendas não são afetadas.")) return;
    await sb.remove("indicadores", id);
    setData(d => ({ ...d, indicadores: d.indicadores.filter(i => i.id !== id) }));
  };

  const kpis = [
    { label: "Orçamento", value: privado ? "••••••" : fmt(totais.orcamento), color: C.warn },
    { label: "Leads", value: privado ? "••" : totais.leads, color: C.info },
    { label: "Pedidos", value: privado ? "••" : totais.pedidos, color: C.accent },
    { label: "Produtos", value: privado ? "••" : totais.smartwatchs, color: C.gold },
    { label: "Faturamento", icon: "💰", value: privado ? "••••••" : fmt(totais.faturamento), color: C.accent },
    { label: "CPV pedido", icon: "💳", value: privado ? "••••••" : totaisCalc.cpv > 0 ? fmt(totaisCalc.cpv) : "—", color: C.warn },
    { label: "CPV produto", icon: "💳", value: privado ? "••••••" : totaisCalc.cpp > 0 ? fmt(totaisCalc.cpp) : "—", color: C.warn },
    { label: "Custo/Lead", value: privado ? "••••••" : totaisCalc.cpl > 0 ? fmt(totaisCalc.cpl) : "—", color: C.info },
    { label: "Taxa Conv.", value: privado ? "••%" : totaisCalc.taxa > 0 ? totaisCalc.taxa.toFixed(1) + "%" : "—", color: C.gold },
    { label: "ROAS", value: privado ? "••x" : totaisCalc.roi > 0 ? totaisCalc.roi.toFixed(2) + "x" : "—", color: C.accent },
    { label: "Ticket Médio", icon: "🎫", value: privado ? "••••••" : totaisCalc.ticket > 0 ? fmt(totaisCalc.ticket) : "—", color: C.info },
  ];

  return (
    <div style={{ padding: "0 0 40px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 28 }}>
        <div>
          <div style={styles.pageTitle}>Indicadores de Tráfego</div>
          <div style={styles.pageSub}>Métricas de performance e conversão</div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={() => { setForm({ data: hoje, leads: "", sem_resposta: "", pedidos: "", smartwatchs: "", faturamento: "", orcamento: "" }); setEditandoId(null); handleDataChange(hoje); }} style={{ ...styles.btn("primary"), fontSize: 11 }}>+ Lançar Hoje</button>
        </div>
      </div>

      {/* Cards KPI */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: 12, marginBottom: 28 }}>
        {kpis.map((k, idx) => (
          <div key={k.label} className={`tfx-fade-in-delay-${idx % 3}`} style={{ gridColumn: idx === kpis.length - 1 && kpis.length % 6 === 5 ? "span 2" : undefined, minWidth: 0, boxSizing: "border-box", display: "flex", flexDirection: "column", justifyContent: "center", 
            background: C.bgElevated,
            border: `1px solid rgba(0,0,0,0.07)`,
            borderRadius: 16,
            padding: "16px 18px",
            position: "relative",
            overflow: "hidden",
            boxShadow: `0 4px 16px ${k.color}15`,
            transition: "all 0.3s"
          }}>
            <div style={{ position: "absolute", top: -20, right: -20, width: 60, height: 60, background: `radial-gradient(circle, ${k.color}10, transparent)`, borderRadius: "50%" }} />
            <div style={{ fontSize: 9, color: "#374151", letterSpacing: 1.5, textTransform: "uppercase", fontWeight: 600, marginBottom: 8 }}>{k.label}</div>
            <div style={{ fontFamily: "'Inter', sans-serif", fontWeight: 800, fontSize: 18, color: k.color, letterSpacing: -0.5 }}>{k.value}</div>
          </div>
        ))}
      </div>

      {/* Formulário */}
      <div style={{ 
        background: C.bgElevated,
        borderRadius: 16,
        padding: 20,
        marginBottom: 20,
        border: "1px solid #e5e7eb"
      }}>
        <div style={{ 
          fontSize: 14, 
          fontWeight: 700, 
          color: "#111827", 
          marginBottom: 16,
          paddingBottom: 12,
          borderBottom: `2px solid ${C.accent}30`
        }}>{editandoId ? "✏️ Editando Registro" : "➕ Novo Registro"}</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, alignItems: "start" }}>
          <div><label style={{ ...styles.label, display: "block", whiteSpace: "nowrap" }}>Data</label><input type="date" style={{ ...styles.input, width: "100%", boxSizing: "border-box" }} value={form.data} onChange={e => handleDataChange(e.target.value)} /></div>
          <div><label style={{ ...styles.label, display: "block", whiteSpace: "nowrap" }}>Orçamento (R$) <span title="Meta Ads automático, com imposto">🔌</span></label><input type="number" style={{ ...styles.input, width: "100%", boxSizing: "border-box" }} value={form.orcamento} onChange={e => setForm(f => ({...f, orcamento: e.target.value}))} placeholder="0.00" /></div>
          <div><label style={{ ...styles.label, display: "block", whiteSpace: "nowrap" }}>Leads</label><input type="number" style={{ ...styles.input, width: "100%", boxSizing: "border-box" }} value={form.leads} onChange={e => setForm(f => ({...f, leads: e.target.value}))} placeholder="0" /></div>
          <div><label style={{ ...styles.label, display: "block", whiteSpace: "nowrap" }}>Sem Resposta</label><input type="number" style={{ ...styles.input, width: "100%", boxSizing: "border-box" }} value={form.sem_resposta} onChange={e => setForm(f => ({...f, sem_resposta: e.target.value}))} placeholder="0" /></div>
          <div style={{ gridColumn: "1 / -1", background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 10, padding: "10px 14px", fontSize: 12, color: "#374151" }}>
            {porDia[form.data]
              ? <>Calculado das vendas de {form.data}: <b>{porDia[form.data].pedidos}</b> pedidos · <b>{porDia[form.data].produtos}</b> produtos · <b>{fmt(porDia[form.data].faturamento)}</b> de faturamento</>
              : "Sem vendas lançadas neste dia."}
            <div style={{ marginTop: 4, fontSize: 11, color: "#6b7280" }}>🔌 Orçamento puxado do Meta Ads automaticamente, já com imposto.</div>
          </div>
          
          
        </div>
        <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
          <button onClick={salvar} disabled={salvando} style={{ ...styles.btn("primary"), fontSize: 12 }}>{salvando ? "Salvando..." : editandoId ? "💾 Atualizar" : "💾 Salvar"}</button>
          {editandoId && <button onClick={() => { setEditandoId(null); setForm({ data: hoje, leads: "", sem_resposta: "", pedidos: "", smartwatchs: "", faturamento: "", orcamento: "" }); }} style={{ ...styles.btn("secondary"), fontSize: 12 }}>Cancelar</button>}
        </div>
      </div>

      {/* Filtro período */}
      <div style={{ marginBottom: 12, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        <span style={{ fontSize: 10, color: "#374151" }}>Filtrar:</span>
        {["hoje","ontem","7d","15d","30d","90d"].map(p => (
          <button key={p} onClick={() => aplicarPeriodo(p)} style={{ ...styles.btn("secondary"), fontSize: 10, padding: "4px 10px", background: periodoRapido === p ? C.accent+"20" : "transparent", borderColor: periodoRapido === p ? C.accent : C.border, color: periodoRapido === p ? C.accent : "#374151" }}>
            {p === "hoje" ? "Hoje" : p === "ontem" ? "Ontem" : p === "7d" ? "7d" : p === "15d" ? "15d" : p === "30d" ? "30d" : "90d"}
          </button>
        ))}
        <div style={{ width: 1, height: 20, background: C.border }} />
        <select style={{ ...styles.input, width: "auto", fontSize: 11, padding: "4px 10px" }} value={mesFiltro} onChange={e => selecionarMes(e.target.value)}>
          <option value="todos">Todos</option>
          {Array.from({length: 12}, (_, i) => mesRelativo(-i)).map(m => (
            <option key={m} value={m}>{MESES[parseInt(m.slice(5,7))-1] + "/" + m.slice(2,4)}</option>
          ))}
        </select>
      </div>

      <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 10, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        {editandoAcess ? (
          <>
            <span>Não contam como produto (separe por vírgula):</span>
            <input style={{ ...styles.input, flex: 1, minWidth: 220, fontSize: 12, padding: "6px 10px" }} value={textoAcess} onChange={e => setTextoAcess(e.target.value)} placeholder="fone, pulseira, carregador" />
            <button onClick={salvarAcessorios} style={{ ...styles.btn("primary"), fontSize: 11, padding: "6px 12px" }}>Salvar</button>
            <button onClick={() => setEditandoAcess(false)} style={{ ...styles.btn("secondary"), fontSize: 11, padding: "6px 12px" }}>Cancelar</button>
          </>
        ) : (
          <>
            <span>Não contam como produto: <b>{acessorios.length ? acessorios.join(", ") : "nenhum (todo item com valor conta)"}</b></span>
            <button onClick={() => { setTextoAcess(acessorios.join(", ")); setEditandoAcess(true); }} style={{ background: "none", border: "none", color: "#6b7280", cursor: "pointer", fontSize: 11, padding: 0, textDecoration: "underline" }}>editar</button>
          </>
        )}
      </div>
      <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 10, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        {editandoImp ? (
          <>
            <span>Imposto sobre o tráfego (%):</span>
            <input style={{ ...styles.input, width: 90, fontSize: 12, padding: "6px 10px" }} value={textoImp} onChange={e => setTextoImp(e.target.value)} placeholder="13.88" />
            <span>0 = sem imposto. Vazio = padrão 13.88. Vale só para lançamentos novos.</span>
            <button onClick={salvarImposto} style={{ ...styles.btn("primary"), fontSize: 11, padding: "6px 12px" }}>Salvar</button>
            <button onClick={() => setEditandoImp(false)} style={{ ...styles.btn("secondary"), fontSize: 11, padding: "6px 12px" }}>Cancelar</button>
          </>
        ) : (
          <>
            <span>Imposto sobre o tráfego: <b>{fmtAliquota(cfgCaixa)}%</b></span>
            <button onClick={() => { setTextoImp(cfgCaixa.imposto_trafego === null || cfgCaixa.imposto_trafego === undefined ? "" : String(cfgCaixa.imposto_trafego)); setEditandoImp(true); }} style={{ background: "none", border: "none", color: "#6b7280", cursor: "pointer", fontSize: 11, padding: 0, textDecoration: "underline" }}>editar</button>
          </>
        )}
      </div>
      {/* Tabela */}
      <div style={{ fontSize: 10, color: "#9ca3af", marginBottom: 8 }}>Pedidos, Produtos e Faturamento vêm das vendas. ROI, CPV, C/Lead e Conv% do período consideram só os dias com orçamento ou leads.</div>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
          <thead>
            <tr style={{ background: `linear-gradient(90deg, ${C.surface}, ${C.bgElevated})`, borderBottom: `2px solid ${C.accent}40` }}>
              {["Data", "Orçamento", "Leads", "S/R", "Pedidos", "Produtos", "Faturamento", "CPV pedido", "CPV produto", "C/Lead", "Conv%", "ROAS", "Ticket", ""].map(h => <th key={h} style={{ ...styles.th, padding: "10px 14px", fontSize: 9, letterSpacing: 1.5, borderBottom: `2px solid ${C.accent}30` }}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {indicadores.sort((a,b) => b.data.localeCompare(a.data)).map((r, idx) => {
              const c = calc(r);
              return (
                <tr key={r.data} style={{ background: C.surface, transition: "background 0.15s", cursor: "pointer" }}
                  onMouseEnter={e => e.currentTarget.style.background = "#fafafa"}
                  onMouseLeave={e => e.currentTarget.style.background = C.surface}
                  onClick={() => { if (r.id) { setEditandoId(r.id); setForm({ data: r.data, leads: r.leads || "", sem_resposta: r.sem_resposta || "", pedidos: r.pedidos || "", smartwatchs: r.smartwatchs || "", faturamento: r.faturamento || "", orcamento: r.orcamento || "" }); } else { handleDataChange(r.data); } }}>
                  <td style={{ ...styles.td, padding: "10px 14px", borderBottom: "1px solid #e5e7eb", color: "#374151", fontSize: 11 }}>{r.data}{r.manual ? <span title="Sem vendas no sistema: valor digitado antes" style={{ marginLeft: 6, fontSize: 9, color: "#9ca3af" }}>manual</span> : null}{r.aoVivo ? <span title="Gasto do Meta de hoje com imposto, atualizado a cada 2 minutos" style={{ marginLeft: 6, fontSize: 9, color: "#15803d" }}>ao vivo</span> : null}</td>
                  <td style={{ ...styles.td, color: C.warn }}>{privado ? "••••" : fmt(r.orcamento)}</td>
                  <td style={{ ...styles.td, borderBottom: "1px solid #e5e7eb", color: "#374151" }}>{privado ? "••" : r.leads}</td>
                  <td style={{ ...styles.td, borderBottom: "1px solid #e5e7eb", color: "#374151" }}>{privado ? "••" : r.sem_resposta}</td>
                  <td style={{ ...styles.td, borderBottom: "1px solid #e5e7eb", color: "#374151" }}>{privado ? "••" : r.pedidos}</td>
                  <td style={{ ...styles.td, color: C.gold }}>{privado ? "••" : r.smartwatchs}</td>
                  <td style={{ ...styles.td, color: C.accent }}>{privado ? "••••" : fmt(r.faturamento)}</td>
                  <td style={{ ...styles.td, borderBottom: "1px solid #e5e7eb", color: "#374151" }}>{privado ? "••••" : c.cpv > 0 ? fmt(c.cpv) : "—"}</td>
                  <td style={{ ...styles.td, borderBottom: "1px solid #e5e7eb", color: "#374151" }}>{privado ? "••••" : c.cpp > 0 ? fmt(c.cpp) : "—"}</td>
                  <td style={{ ...styles.td, borderBottom: "1px solid #e5e7eb", color: "#374151" }}>{privado ? "••••" : c.cpl > 0 ? fmt(c.cpl) : "—"}</td>
                  <td style={{ ...styles.td, borderBottom: "1px solid #e5e7eb", color: "#374151" }}>{privado ? "••%" : c.taxa > 0 ? c.taxa.toFixed(1) + "%" : "—"}</td>
                  <td style={{ ...styles.td, color: c.roi >= 3 ? C.accent : c.roi >= 1.5 ? C.gold : C.warn }}>{privado ? "••x" : c.roi > 0 ? c.roi.toFixed(2) + "x" : "—"}</td>
                  <td style={{ ...styles.td, borderBottom: "1px solid #e5e7eb", color: "#374151" }}>{privado ? "••••" : c.ticket > 0 ? fmt(c.ticket) : "—"}</td>
                  <td style={{ ...styles.td, borderBottom: "1px solid #e5e7eb" }}>{r.id ? <button onClick={e => { e.stopPropagation(); handleDelete(r.id); }} style={{ background: "none", border: "none", color: C.warn, cursor: "pointer", fontSize: 13 }}>✕</button> : null}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {indicadores.length === 0 && <div style={{ textAlign:"center", padding:"48px 20px" }}><div style={{fontSize:36,marginBottom:12}}>📊</div><div style={{fontSize:14,fontWeight:600,color:C.text,marginBottom:6}}>Nenhum indicador ainda</div><div style={{fontSize:12,color:C.muted}}>Clique em "+ Lançar Hoje" e comece a acompanhar seu faturamento diário.</div></div>}
      </div>
    </div>
  );
}


// ── CLIENTES LOJA PAGE ────────────────────────────────────────
function ClientesLoja({ data, setData, showToast }) {
  const [abaClientes, setAbaClientes] = useState(() => { try { return sessionStorage.getItem('tfx_aba_clientes') || 'lista'; } catch(e) { return 'lista'; } });
  const [buscaAnalise, setBuscaAnalise] = useState('');
  const [verTodosLtv, setVerTodosLtv] = useState(() => { try { return sessionStorage.getItem('tfx_ver_todos_ltv') === '1'; } catch(e) { return false; } });
  useEffect(() => { try { sessionStorage.setItem('tfx_aba_clientes', abaClientes); sessionStorage.setItem('tfx_ver_todos_ltv', verTodosLtv ? '1' : '0'); } catch(e) {} }, [abaClientes, verTodosLtv]);
  useEffect(() => { console.log('[ClientesLoja] montou'); return () => console.log('[ClientesLoja] desmontou'); }, []);
  const [identificando, setIdentificando] = useState(false);
  const salvarGenero = async (id, g) => {
    await sb.update("clientes_loja", id, { genero: g });
    setData(d => ({ ...d, clientes_loja: (d.clientes_loja || []).map(c => c.id === id ? { ...c, genero: g } : c) }));
  };
  const identificarGeneros = async () => {
    setIdentificando(true);
    const pend = (data.clientes_loja || []).filter(c => Number(c.num_pedidos || 0) > 0 && (!c.genero || c.genero === "desconhecido") && inferirGenero(c.nome) === "desconhecido");
    let ok = 0;
    for (const c of pend) {
      const g = await buscarGeneroIBGE(c.nome);
      if (g) { await salvarGenero(c.id, g); ok++; }
    }
    setIdentificando(false);
    if (showToast) showToast(ok + " de " + pend.length + " identificados");
  };
  const emptyForm = { nome: "", cpf_cnpj: "", email: "", telefone: "", cep: "", endereco: "", numero: "", complemento: "", bairro: "", cidade: "", estado: "", aniversario: "", observacoes: "" };
  const [form, setForm] = useState(emptyForm);
  const [editandoId, setEditandoId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [busca, setBusca] = useState("");
  const [salvando, setSalvando] = useState(false);
  const [showDisparo, setShowDisparo] = useState(false);
  const [disparoForm, setDisparoForm] = useState({ canal: "email", assunto: "", mensagem: "", link: "", link_texto: "", selecionados: [] });
  const [disparando, setDisparando] = useState(false);

  const clientes = (data.clientes_loja || []).filter(c =>
    !busca || c.nome?.toLowerCase().includes(busca.toLowerCase()) ||
    c.email?.toLowerCase().includes(busca.toLowerCase()) ||
    c.telefone?.includes(busca) || c.cpf_cnpj?.includes(busca)
  );

  const buscarCEP = async (cep) => {
    const c = cep.replace(/\D/g, "");
    if (c.length !== 8) return;
    try {
      const r = await fetch(`https://viacep.com.br/ws/${c}/json/`);
      const d = await r.json();
      if (!d.erro) setForm(f => ({ ...f, endereco: d.logradouro, bairro: d.bairro, cidade: d.localidade, estado: d.uf }));
    } catch(e) {}
  };

  const salvar = async () => {
    if (!form.nome) return alert("Nome é obrigatório!");
    setSalvando(true);
    try {
      const row = { ...form, aniversario: form.aniversario || null, tenant_id: getTenant() };
      if (editandoId) {
        const r = await sb.update("clientes_loja", editandoId, row);
        if (r && (r.error || r.code || r.message)) throw new Error(r.message || r.error || "falha ao salvar");
        setData(d => ({ ...d, clientes_loja: d.clientes_loja.map(c => c.id === editandoId ? { ...c, ...row } : c) }));
      } else {
        const res = await sb.insert("clientes_loja", row);
        const r0 = Array.isArray(res) ? res[0] : res;
        if (r0 && (r0.error || r0.code || r0.message)) throw new Error(r0.message || r0.error || "falha ao salvar");
        const atualizados = await sb.get("clientes_loja");
        setData(d => ({ ...d, clientes_loja: atualizados || [] }));
      }
      if (showToast) showToast(editandoId ? "Cliente atualizado!" : "Cliente cadastrado!");
      setForm(emptyForm);
      setEditandoId(null);
      setShowForm(false);
    } catch(e) { alert("Erro: " + e.message); }
    setSalvando(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Remover este cliente?")) return;
    await sb.remove("clientes_loja", id);
    setData(d => ({ ...d, clientes_loja: d.clientes_loja.filter(c => c.id !== id) }));
  };

  const editar = (c) => {
    setForm({ nome: c.nome || "", cpf_cnpj: c.cpf_cnpj || "", email: c.email || "", telefone: c.telefone || "", cep: c.cep || "", endereco: c.endereco || "", numero: c.numero || "", complemento: c.complemento || "", bairro: c.bairro || "", cidade: c.cidade || "", estado: c.estado || "", aniversario: c.aniversario || "", observacoes: c.observacoes || "" });
    setEditandoId(c.id);
    setShowForm(true);
    window.scrollTo(0, 0);
  };

  const toggleSelecionado = (id) => {
    setDisparoForm(f => ({ ...f, selecionados: f.selecionados.includes(id) ? f.selecionados.filter(x => x !== id) : [...f.selecionados, id] }));
  };

  const selecionarTodos = () => {
    setDisparoForm(f => ({ ...f, selecionados: f.selecionados.length === clientes.length ? [] : clientes.map(c => c.id) }));
  };

  const dispararMensagem = async () => {
    if (!disparoForm.mensagem) return alert("Preencha a mensagem!");
    if (disparoForm.selecionados.length === 0) return alert("Selecione pelo menos um cliente!");
    setDisparando(true);
    try {
      const destinatarios = clientes.filter(c => disparoForm.selecionados.includes(c.id));
      const r = await fetch("https://gestao-tfx-saas.vercel.app/api/disparar-mensagem", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ canal: disparoForm.canal, assunto: disparoForm.assunto, mensagem: disparoForm.mensagem, link: disparoForm.link, link_texto: disparoForm.link_texto, destinatarios, tenant_id: getTenant() })
      });
      const d = await r.json();
      if (d.ok) {
        if (showToast) showToast(`${d.enviados} mensagem(ns) enviada(s)!`);
        setShowDisparo(false);
        setDisparoForm({ canal: "email", assunto: "", mensagem: "", link: "", link_texto: "", selecionados: [] });
      } else alert("Erro: " + d.error);
    } catch(e) { alert("Erro: " + e.message); }
    setDisparando(false);
  };

  // Aniversariantes do mês
  const mesAtual = new Date().getMonth() + 1;
  const aniversariantes = (data.clientes_loja || []).filter(c => {
    if (!c.aniversario) return false;
    const mes = parseInt(c.aniversario.slice(5, 7));
    return mes === mesAtual;
  });


  // Análise de clientes — baseada no CADASTRO (clientes_loja), não nas vendas antigas
  const hoje = hojeManaus();
  const d30cli = new Date(hoje + "T12:00:00"); d30cli.setDate(d30cli.getDate() - 30);
  const data30cli = d30cli.toISOString().slice(0, 10);
  const diasDesde = (d) => d ? Math.floor((new Date(hoje + "T12:00:00") - new Date(d + "T12:00:00")) / 86400000) : null;
  const listaClientes = (data.clientes_loja || [])
    .filter(c => Number(c.num_pedidos || 0) > 0)
    .map(c => {
      const pedidos = Number(c.num_pedidos || 0);
      const totalC = Number(c.total_compras || 0);
      const genero = c.genero && c.genero !== 'desconhecido' ? c.genero : inferirGenero(c.nome);
      return { id: c.id, nome: c.nome || '', telefone: c.telefone || '', genero, pedidos, total: totalC, ultimaCompra: c.ultima_compra || null, recorrente: pedidos > 1, sumido: pedidos > 1 && !!c.ultima_compra && c.ultima_compra < data30cli, dias: diasDesde(c.ultima_compra), ticketMedio: totalC / pedidos };
    })
    .sort((x, y) => y.total - x.total);
  const buscaA = buscaAnalise.trim().toLowerCase();
  const buscaDig = buscaA.replace(/\D/g, '');
  const listaFiltrada = buscaA ? listaClientes.filter(c => c.nome.toLowerCase().includes(buscaA) || (buscaDig && c.telefone.replace(/\D/g, '').includes(buscaDig))) : listaClientes;
  const nClientes = listaClientes.length;
  const nRecorrentes = listaClientes.filter(c => c.recorrente).length;
  const nSumidos = listaClientes.filter(c => c.sumido).length;
  const nSemTel = listaClientes.filter(c => !c.telefone).length;
  const ltvMedio = nClientes > 0 ? listaClientes.reduce((s, c) => s + c.total, 0) / nClientes : 0;
  const nFem = listaClientes.filter(c => c.genero === 'feminino').length;
  const nMasc = listaClientes.filter(c => c.genero === 'masculino').length;
  const nNaoId = nClientes - nFem - nMasc;
  const pc = (n) => nClientes > 0 ? Math.round(n / nClientes * 100) : 0;

  return (
    <div style={{ padding: "0 0 40px" }}>
      {/* Tabs */}
      <div style={{ display: "flex", gap: 8, marginBottom: 20, padding: "0 24px" }}>
        {[['lista','Clientes'],['analise','Análise']].map(([id,label]) => (
          <button key={id} onClick={() => setAbaClientes(id)} style={{ ...styles.btn(abaClientes === id ? 'primary' : 'secondary'), fontSize: 12, padding: '8px 20px' }}>{label}</button>
        ))}
      </div>

      {abaClientes === 'analise' && (
        <div style={{ padding: "0 24px 40px" }}>
          <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
            <input style={{ ...styles.input, flex: 1 }} placeholder="Buscar cliente por nome ou telefone..." value={buscaAnalise} onChange={e => setBuscaAnalise(e.target.value)} />
            {nNaoId > 0 && <button onClick={identificarGeneros} disabled={identificando} style={{ ...styles.btn('secondary'), fontSize: 11, whiteSpace: 'nowrap', opacity: identificando ? 0.5 : 1 }}>{identificando ? 'Identificando...' : 'Identificar gênero (' + nNaoId + ')'}</button>}
          </div>
          {nClientes === 0 ? (
            <div style={{ background: '#ffffff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 28, textAlign: 'center', color: '#6b7280', fontSize: 13 }}>
              Ainda sem dados. A análise passa a contar a partir das próximas vendas com o cliente informado.
            </div>
          ) : (<>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 20 }}>
              {[
                { label: 'Clientes com compra', value: nClientes, sub: nSemTel > 0 ? nSemTel + ' sem telefone (podem estar misturados)' : 'todos com telefone' },
                { label: 'Recorrentes', value: nRecorrentes, sub: (nClientes - nRecorrentes) + ' com 1 compra', color: '#15803d' },
                { label: 'Sumidos +30d', value: nSumidos, sub: 'recorrentes que pararam', color: nSumidos > 0 ? '#b91c1c' : '#15803d' },
                { label: 'LTV médio', value: fmt(ltvMedio), sub: 'por cliente' },
              ].map((k, i) => (
                <div key={i} style={{ background: '#ffffff', border: '1px solid #e5e7eb', borderRadius: 12, padding: '16px 18px' }}>
                  <div style={{ fontSize: 9, color: '#9ca3af', letterSpacing: 1.5, textTransform: 'uppercase', fontWeight: 700, marginBottom: 8 }}>{k.label}</div>
                  <div style={{ fontSize: 22, fontWeight: 800, color: k.color || '#111827', marginBottom: 4 }}>{k.value}</div>
                  <div style={{ fontSize: 10, color: '#9ca3af' }}>{k.sub}</div>
                </div>
              ))}
            </div>

            <div style={{ background: '#ffffff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 20, marginBottom: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 12 }}>Gênero dos clientes</div>
              <div style={{ display: 'flex', height: 14, borderRadius: 7, overflow: 'hidden', background: '#f3f4f6', marginBottom: 12 }}>
                <div style={{ width: pc(nFem) + '%', background: '#e87ba4' }} />
                <div style={{ width: pc(nMasc) + '%', background: '#2a78d6' }} />
                <div style={{ width: pc(nNaoId) + '%', background: '#d1d5db' }} />
              </div>
              <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', fontSize: 12, color: '#374151' }}>
                {[['Feminino', '#e87ba4', nFem], ['Masculino', '#2a78d6', nMasc], ['Não identificado', '#d1d5db', nNaoId]].map(([l, c, n]) => (
                  <div key={l} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ width: 10, height: 10, borderRadius: 2, background: c, display: 'inline-block' }} />
                    <span>{l}: <b>{n}</b> ({pc(n)}%)</span>
                  </div>
                ))}
              </div>
            </div>

            <div style={{ background: '#ffffff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 20, marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#374151' }}>Clientes por LTV <span style={{ fontWeight: 400, color: '#9ca3af' }}>({verTodosLtv ? listaFiltrada.length : Math.min(30, listaFiltrada.length)} de {listaFiltrada.length})</span></div>
                {listaFiltrada.length > 30 && <button onClick={() => setVerTodosLtv(v => !v)} style={{ ...styles.btn("secondary"), fontSize: 11, padding: '4px 10px' }}>{verTodosLtv ? 'Mostrar top 30' : 'Mostrar todos (' + listaFiltrada.length + ')'}</button>}
              </div>
              {listaFiltrada.length === 0 ? (
                <div style={{ fontSize: 12, color: '#9ca3af' }}>Nenhum cliente encontrado.</div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                    <thead><tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                      {['Cliente', 'Gênero', 'Pedidos', 'LTV total', 'Ticket médio', 'Última compra', 'Status'].map(h => <th key={h} style={{ textAlign: 'left', padding: '6px 8px', fontSize: 9, color: '#9ca3af', letterSpacing: 1.5, textTransform: 'uppercase', fontWeight: 700 }}>{h}</th>)}
                    </tr></thead>
                    <tbody>
                      {(verTodosLtv ? listaFiltrada : listaFiltrada.slice(0, 30)).map(c => (
                        <tr key={c.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                          <td style={{ padding: '8px', fontWeight: 600, color: '#111827' }}>{c.nome}{c.telefone ? '' : ' *'}</td>
                          <td style={{ padding: '4px 8px' }}>
                            <select value={c.genero === 'feminino' || c.genero === 'masculino' ? c.genero : 'desconhecido'} onChange={e => salvarGenero(c.id, e.target.value)} style={{ ...styles.input, width: 'auto', fontSize: 12, padding: '2px 6px', color: c.genero === 'feminino' ? '#e87ba4' : c.genero === 'masculino' ? '#2a78d6' : '#9ca3af' }}>
                              <option value="desconhecido">?</option>
                              <option value="feminino">♀ Feminino</option>
                              <option value="masculino">♂ Masculino</option>
                            </select>
                          </td>
                          <td style={{ padding: '8px', color: '#374151' }}>{c.pedidos}</td>
                          <td style={{ padding: '8px', fontWeight: 700, color: '#111827' }}>{fmt(c.total)}</td>
                          <td style={{ padding: '8px', color: '#374151' }}>{fmt(c.ticketMedio)}</td>
                          <td style={{ padding: '8px', color: '#6b7280' }}>{c.ultimaCompra || '—'}</td>
                          <td style={{ padding: '8px' }}>
                            {c.sumido ? <span style={{ color: '#b91c1c', fontWeight: 600, fontSize: 10 }}>SUMIDO</span> : c.recorrente ? <span style={{ color: '#15803d', fontWeight: 600, fontSize: 10 }}>FIEL</span> : <span style={{ color: '#d97706', fontSize: 10 }}>NOVO</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 8 }}>* sem telefone cadastrado: agrupado só por nome.</div>
                </div>
              )}
            </div>

            {listaFiltrada.some(c => c.sumido) && (
              <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 12, padding: 20 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#b91c1c', marginBottom: 12 }}>Clientes sumidos — recorrentes sem comprar há mais de 30 dias</div>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                    <thead><tr style={{ borderBottom: '2px solid #fecaca' }}>
                      {['Cliente', 'Pedidos', 'LTV', 'Última compra', 'Dias sem comprar'].map(h => <th key={h} style={{ textAlign: 'left', padding: '6px 8px', fontSize: 9, color: '#b91c1c', letterSpacing: 1.5, textTransform: 'uppercase', fontWeight: 700 }}>{h}</th>)}
                    </tr></thead>
                    <tbody>
                      {listaFiltrada.filter(c => c.sumido).map(c => (
                        <tr key={c.id} style={{ borderBottom: '1px solid #fee2e2' }}>
                          <td style={{ padding: '8px', fontWeight: 600, color: '#111827' }}>{c.nome}</td>
                          <td style={{ padding: '8px', color: '#374151' }}>{c.pedidos}</td>
                          <td style={{ padding: '8px', fontWeight: 600, color: '#111827' }}>{fmt(c.total)}</td>
                          <td style={{ padding: '8px', color: '#6b7280' }}>{c.ultimaCompra}</td>
                          <td style={{ padding: '8px', fontWeight: 700, color: '#b91c1c' }}>{c.dias != null ? c.dias + ' dias' : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>)}
        </div>
      )}

      {abaClientes === 'lista' && (<>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 28 }}>
        <div>
          <div style={styles.pageTitle}>Clientes</div>
          <div style={styles.pageSub}>{(data.clientes_loja || []).length} cadastrados</div>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {aniversariantes.length > 0 && (
            <div style={{ background: C.gold + "20", border: `1px solid ${C.gold}40`, borderRadius: 8, padding: "6px 12px", fontSize: 11, color: C.gold }}>
              🎂 {aniversariantes.length} aniversariante(s) este mês
            </div>
          )}
          <button onClick={() => setShowDisparo(true)} style={{ ...styles.btn("secondary"), fontSize: 11 }}>📣 Disparar Mensagem</button>
          <button onClick={() => { setForm(emptyForm); setEditandoId(null); setShowForm(!showForm); }} style={{ ...styles.btn("primary"), fontSize: 11 }}>+ Novo Cliente</button>
        </div>
      </div>

      {/* Formulário */}
      {showForm && (
        <div style={{ 
          ...styles.card,
          marginBottom: 24,
          background: C.bgElevated,
          border: "1px solid #e5e7eb"
        }}>
          <div style={{ 
            fontSize: 14,
            fontWeight: 700,
            color: "#111827",
            marginBottom: 20,
            paddingBottom: 12,
            borderBottom: `2px solid ${C.accent}30`
          }}>{editandoId ? "✏️ Editar Cliente" : "➕ Novo Cliente"}</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 16 }}>
            <div><label style={styles.label}>Nome *</label><input style={styles.input} value={form.nome} onChange={e => setForm(f => ({...f, nome: e.target.value}))} placeholder="Nome completo" /></div>
            <div><label style={styles.label}>CPF/CNPJ</label><input style={styles.input} value={form.cpf_cnpj} onChange={e => setForm(f => ({...f, cpf_cnpj: e.target.value}))} placeholder="000.000.000-00" /></div>
            <div><label style={styles.label}>Email</label><input style={styles.input} value={form.email} onChange={e => setForm(f => ({...f, email: e.target.value}))} placeholder="email@exemplo.com" /></div>
            <div><label style={styles.label}>Telefone/WhatsApp</label><input style={styles.input} value={form.telefone} onChange={e => setForm(f => ({...f, telefone: e.target.value}))} placeholder="(92) 99999-9999" /></div>
            <div><label style={styles.label}>Aniversário</label><input type="date" style={styles.input} value={form.aniversario} onChange={e => setForm(f => ({...f, aniversario: e.target.value}))} /></div>
            <div><label style={styles.label}>CEP</label><input style={styles.input} value={form.cep} onChange={e => { setForm(f => ({...f, cep: e.target.value})); if (e.target.value.replace(/\D/g,"").length === 8) buscarCEP(e.target.value); }} placeholder="00000-000" /></div>
            <div><label style={styles.label}>Endereço</label><input style={styles.input} value={form.endereco} onChange={e => setForm(f => ({...f, endereco: e.target.value}))} placeholder="Rua, Av..." /></div>
            <div><label style={styles.label}>Número</label><input style={styles.input} value={form.numero} onChange={e => setForm(f => ({...f, numero: e.target.value}))} placeholder="123" /></div>
            <div><label style={styles.label}>Complemento</label><input style={styles.input} value={form.complemento} onChange={e => setForm(f => ({...f, complemento: e.target.value}))} placeholder="Apto, Sala..." /></div>
            <div><label style={styles.label}>Bairro</label><input style={styles.input} value={form.bairro} onChange={e => setForm(f => ({...f, bairro: e.target.value}))} placeholder="Bairro" /></div>
            <div><label style={styles.label}>Cidade</label><input style={styles.input} value={form.cidade} onChange={e => setForm(f => ({...f, cidade: e.target.value}))} placeholder="Cidade" /></div>
            <div><label style={styles.label}>Estado</label><input style={styles.input} value={form.estado} onChange={e => setForm(f => ({...f, estado: e.target.value}))} placeholder="AM" maxLength={2} /></div>
            <div style={{ gridColumn: "1 / -1" }}><label style={styles.label}>Observações</label><input style={styles.input} value={form.observacoes} onChange={e => setForm(f => ({...f, observacoes: e.target.value}))} placeholder="Observações sobre o cliente..." /></div>
          </div>
          <div style={{ marginTop: 14, display: "flex", gap: 8 }}>
            <button onClick={salvar} disabled={salvando} style={{ ...styles.btn("primary"), fontSize: 12 }}>{salvando ? "Salvando..." : editandoId ? "💾 Atualizar" : "💾 Salvar"}</button>
            <button onClick={() => { setShowForm(false); setForm(emptyForm); setEditandoId(null); }} style={{ ...styles.btn("secondary"), fontSize: 12 }}>Cancelar</button>
          </div>
        </div>
      )}

      {/* Painel de disparo */}
      {showDisparo && (
        <div style={{ 
          ...styles.card,
          marginBottom: 24,
          background: C.bgElevated,
          border: "1px solid #e5e7eb"
        }}>
          <div style={{ 
            fontSize: 14,
            fontWeight: 700,
            color: "#111827",
            marginBottom: 20,
            paddingBottom: 12,
            borderBottom: `2px solid ${C.accent}30`
          }}>📣 Disparar Mensagem</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
            <div>
              <label style={styles.label}>Canal</label>
              <select style={styles.input} value={disparoForm.canal} onChange={e => setDisparoForm(f => ({...f, canal: e.target.value}))}>
                <option value="email">📧 Email</option>
                <option value="sms">📱 SMS</option>
              </select>
            </div>
            {disparoForm.canal === "email" && (
              <div><label style={styles.label}>Assunto</label><input style={styles.input} value={disparoForm.assunto} onChange={e => setDisparoForm(f => ({...f, assunto: e.target.value}))} placeholder="Ex: Promoção especial para você!" /></div>
            )}
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={styles.label}>Mensagem</label>
            <textarea style={{ ...styles.input, height: 100, resize: "vertical" }} value={disparoForm.mensagem} onChange={e => setDisparoForm(f => ({...f, mensagem: e.target.value}))} placeholder="Use {nome} para personalizar. Ex: Olá {nome}, temos uma oferta especial para você!" />
            <div style={{ fontSize: 10, color: "#374151", marginTop: 3 }}>Use &#123;nome&#125; para inserir o nome do cliente automaticamente</div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
            <div>
              <label style={styles.label}>Link do botão (opcional)</label>
              <input style={styles.input} value={disparoForm.link} onChange={e => setDisparoForm(f => ({...f, link: e.target.value}))} placeholder="https://wa.me/5592..." />
            </div>
            <div>
              <label style={styles.label}>Texto do botão</label>
              <input style={styles.input} value={disparoForm.link_texto} onChange={e => setDisparoForm(f => ({...f, link_texto: e.target.value}))} placeholder="👉 Aproveitar no WhatsApp" />
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
            <div>
              <label style={styles.label}>Link do botão (opcional)</label>
              <input style={styles.input} value={disparoForm.link} onChange={e => setDisparoForm(f => ({...f, link: e.target.value}))} placeholder="https://wa.me/5592..." />
            </div>
            <div>
              <label style={styles.label}>Texto do botão</label>
              <input style={styles.input} value={disparoForm.link_texto} onChange={e => setDisparoForm(f => ({...f, link_texto: e.target.value}))} placeholder="👉 Aproveitar no WhatsApp" />
            </div>
          </div>
          <div style={{ marginBottom: 12 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <label style={styles.label}>Destinatários ({disparoForm.selecionados.length} selecionados)</label>
              <button onClick={selecionarTodos} style={{ ...styles.btn("secondary"), fontSize: 10, padding: "3px 8px" }}>
                {disparoForm.selecionados.length === clientes.length ? "Desmarcar todos" : "Selecionar todos"}
              </button>
            </div>
            <div style={{ maxHeight: 200, overflowY: "auto", border: "1px solid #e5e7eb", borderRadius: 8, padding: 8 }}>
              {clientes.map(c => (
                <div key={c.id} onClick={() => toggleSelecionado(c.id)} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 8px", cursor: "pointer", borderRadius: 6, background: disparoForm.selecionados.includes(c.id) ? C.accent + "15" : "transparent" }}>
                  <input type="checkbox" checked={disparoForm.selecionados.includes(c.id)} onChange={() => {}} />
                  <span style={{ fontSize: 12, color: "#111827" }}>{c.nome}</span>
                  <span style={{ fontSize: 11, color: "#374151" }}>{disparoForm.canal === "email" ? c.email : c.telefone}</span>
                </div>
              ))}
            </div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={dispararMensagem} disabled={disparando} style={{ ...styles.btn("primary"), fontSize: 12 }}>{disparando ? "Enviando..." : `📣 Disparar para ${disparoForm.selecionados.length} cliente(s)`}</button>
            <button onClick={() => setShowDisparo(false)} style={{ ...styles.btn("secondary"), fontSize: 12 }}>Cancelar</button>
          </div>
        </div>
      )}

      {/* Busca */}
      <input style={{ ...styles.input, marginBottom: 16, fontSize: 12 }} placeholder="🔍 Buscar por nome, email, telefone ou CPF/CNPJ..." value={busca} onChange={e => setBusca(e.target.value)} />

      {/* Tabela */}
      <div style={{ 
        ...styles.card,
        background: C.bgElevated,
        border: "1px solid #e5e7eb",
        padding: 0,
        overflow: "hidden"
      }}>
        <table style={{ ...styles.table, width: "100%", borderCollapse: "separate", borderSpacing: 0 }}>
          <thead>
            <tr style={{ 
              background: `linear-gradient(90deg, ${C.surface}, ${C.bgElevated})`,
              borderBottom: `2px solid ${C.accent}40`
            }}>{["Nome", "Telefone", "CEP", "Endereço", "Cidade/UF", "Compras", "Total", ""].map(h => <th key={h} style={{ ...styles.th, padding: "12px 16px", fontSize: 9, letterSpacing: 1.5, borderBottom: `2px solid ${C.accent}30` }}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {clientes.map(c => (
              <tr key={c.id} style={{ background: C.surface, cursor: "pointer", transition: "background 0.15s" }}
                onMouseEnter={e => e.currentTarget.style.background = "#fafafa"}
                onMouseLeave={e => e.currentTarget.style.background = C.surface}
                onClick={() => editar(c)}>
                <td style={{ ...styles.td, padding: "12px 16px", borderBottom: "1px solid #e5e7eb", fontWeight: 600, color: "#111827" }}>{c.nome}</td>
                <td style={{ ...styles.td, padding: "12px 16px", borderBottom: "1px solid #e5e7eb" }}>{c.telefone || "—"}</td>
                <td style={{ ...styles.td, padding: "12px 16px", borderBottom: "1px solid #e5e7eb" }}>{c.cep || "—"}</td>
                <td style={{ ...styles.td, padding: "12px 16px", borderBottom: "1px solid #e5e7eb" }}>{c.endereco ? `${c.endereco}, ${c.numero || 'S/N'}` : "—"}</td>
                <td style={{ ...styles.td, padding: "12px 16px", borderBottom: "1px solid #e5e7eb" }}>{c.cidade ? `${c.cidade}/${c.estado}` : "—"}</td>
                <td style={{ ...styles.td, padding: "12px 16px", borderBottom: "1px solid #e5e7eb" }}>{Number(c.num_pedidos || 0) || "—"}</td>
                <td style={{ ...styles.td, padding: "12px 16px", borderBottom: "1px solid #e5e7eb" }}>{Number(c.total_compras || 0) ? fmt(Number(c.total_compras)) : "—"}</td>
                <td style={styles.td}><button onClick={e => { e.stopPropagation(); handleDelete(c.id); }} style={{ background: "none", border: "none", color: C.warn, cursor: "pointer", fontSize: 13 }}>✕</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {clientes.length === 0 && <div style={{ ...styles.card, background: C.bgElevated, border: "1px solid #e5e7eb", textAlign: "center", color: "#374151", padding: 40 }}><div style={{ fontSize: 32, marginBottom: 10 }}>👤</div><div style={{ fontSize: 14, fontWeight: 600, color: "#111827", marginBottom: 4 }}>Nenhum cliente cadastrado</div><div style={{ fontSize: 12 }}>Adicione seu primeiro cliente para começar.</div></div>}
      </>)}
    </div>
  );
}


// ── CONFIGURAÇÕES PAGE ────────────────────────────────────────
function Configuracoes({ showToast }) {
  const [config, setConfig] = useState({
    nome_loja: '',
    logo_url: '',
    cnpj: '',
    endereco: '',
    telefone: '',
    email: ''
  });
  const [loading, setLoading] = useState(false);
  const [loadingUpload, setLoadingUpload] = useState(false);

  useEffect(() => {
    loadConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadConfig = async () => {
    try {
      const res = await fetch(`${API_BASE}?table=configuracoes_loja`, {
        headers: { Authorization: "Bearer " + getJWT() }
      });
      const configs = await res.json();
      if (configs && configs.length > 0) setConfig(configs[0]);
    } catch (err) {
      console.error('Erro ao carregar:', err);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      showToast('Imagem muito grande! Máximo 2MB', 'error');
      return;
    }
    setLoadingUpload(true);
    const reader = new FileReader();
    reader.onload = (ev) => {
      setConfig({ ...config, logo_url: ev.target.result });
      setLoadingUpload(false);
    };
    reader.readAsDataURL(file);
  };

  const salvar = async () => {
    setLoading(true);
    try {
      const tenant = getTenant();
      const checkRes = await fetch(`${API_BASE}?table=configuracoes_loja`, {
        headers: { Authorization: "Bearer " + getJWT() }
      });
      const existing = await checkRes.json();
      
      const payload = {
        tenant_id: tenant,
        nome_loja: config.nome_loja,
        logo_url: config.logo_url,
        cnpj: config.cnpj,
        endereco: config.endereco,
        telefone: config.telefone,
        email: config.email,
        updated_at: new Date().toISOString()
      };
      
      const method = existing && existing.length > 0 ? 'PATCH' : 'POST';
      const url = method === 'PATCH' 
        ? `${API_BASE}?table=configuracoes_loja`
        : `${API_BASE}?table=configuracoes_loja`;
      
      await fetch(url, {
        method,
        headers: {
          Authorization: "Bearer " + getJWT(),
          'Content-Type': 'application/json',
          Prefer: 'return=minimal'
        },
        body: JSON.stringify(payload)
      });
      
      showToast('✅ Configurações salvas com sucesso!', 'success');
    } catch (err) {
      console.error('Erro:', err);
      showToast('❌ Erro ao salvar configurações', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ padding: 30, maxWidth: 800, margin: '0 auto' }}>
      <h2 style={{ fontSize: 28, fontWeight: 700, color: C.navy, marginBottom: 8 }}>Configurações da Loja</h2>
      <p style={{ color: C.textGray, marginBottom: 30, fontSize: 14 }}>Personalize a identidade da sua loja</p>
      
      {/* IDENTIDADE VISUAL */}
      <div style={{ background: C.cardBg, border: '1px solid #e5e7eb', borderRadius: 12, padding: 24, marginBottom: 20 }}>
        <h3 style={{ fontSize: 14, fontWeight: 600, color: C.navy, marginBottom: 16, textTransform: 'uppercase', letterSpacing: 0.5 }}>Identidade Visual</h3>
        
        <div style={{ marginBottom: 20 }}>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: C.textDark, marginBottom: 8 }}>Nome da Loja</label>
          <input
            type="text"
            placeholder="Ex: KTSMARTS"
            value={config.nome_loja}
            onChange={(e) => setConfig({ ...config, nome_loja: e.target.value })}
            style={{ width: '100%', padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14 }}
          />
          <p style={{ fontSize: 12, color: C.textGray, marginTop: 6 }}>Aparece nos recibos e comunicações</p>
        </div>
        
        <div>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: C.textDark, marginBottom: 8 }}>Logo</label>
          {config.logo_url && (
            <div style={{ marginBottom: 12 }}>
              <img src={config.logo_url} alt="Logo" style={{ maxWidth: 200, maxHeight: 100, objectFit: 'contain', border: '1px solid #e5e7eb', borderRadius: 8, padding: 8 }} />
            </div>
          )}
          <input
            type="file"
            accept="image/png,image/jpeg,image/webp"
            onChange={handleLogoUpload}
            disabled={loadingUpload}
            style={{ fontSize: 13 }}
          />
          <p style={{ fontSize: 12, color: C.textGray, marginTop: 6 }}>PNG, JPG ou WEBP • Máximo 2MB</p>
        </div>
      </div>
      
      {/* DADOS DA EMPRESA */}
      <div style={{ background: C.cardBg, border: '1px solid #e5e7eb', borderRadius: 12, padding: 24, marginBottom: 20 }}>
        <h3 style={{ fontSize: 14, fontWeight: 600, color: C.navy, marginBottom: 16, textTransform: 'uppercase', letterSpacing: 0.5 }}>Dados da Empresa</h3>
        
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: C.textDark, marginBottom: 8 }}>CNPJ</label>
          <input
            type="text"
            placeholder="00.000.000/0000-00"
            value={config.cnpj}
            onChange={(e) => setConfig({ ...config, cnpj: e.target.value })}
            style={{ width: '100%', padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14 }}
          />
        </div>
        
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: C.textDark, marginBottom: 8 }}>Endereço Completo</label>
          <input
            type="text"
            placeholder="Rua, Número, Bairro, Cidade - UF"
            value={config.endereco}
            onChange={(e) => setConfig({ ...config, endereco: e.target.value })}
            style={{ width: '100%', padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14 }}
          />
        </div>
        
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: C.textDark, marginBottom: 8 }}>Telefone/WhatsApp</label>
          <input
            type="text"
            placeholder="(00) 0 0000-0000"
            value={config.telefone}
            onChange={(e) => setConfig({ ...config, telefone: e.target.value })}
            style={{ width: '100%', padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14 }}
          />
        </div>
        
        <div>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: C.textDark, marginBottom: 8 }}>E-mail</label>
          <input
            type="email"
            placeholder="contato@loja.com"
            value={config.email}
            onChange={(e) => setConfig({ ...config, email: e.target.value })}
            style={{ width: '100%', padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14 }}
          />
        </div>
      </div>
      
      <button
        onClick={salvar}
        disabled={loading}
        style={{
          ...styles.btn('primary'),
          width: '100%',
          padding: '14px 24px',
          fontSize: 15,
          fontWeight: 600
        }}
      >
        {loading ? '💾 Salvando...' : '💾 Salvar Configurações'}
      </button>
    </div>
  );
}

function Descontos() { // eslint-disable-line no-unused-vars

  return (
    <div style={{ padding: "0 0 40px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 28 }}>
        <div>
          <div style={styles.pageTitle}>Descontos & Cupons</div>
          <div style={styles.pageSub}>Gerencie cupons promocionais (em breve)</div>
        </div>
        <button disabled style={{ ...styles.btn("secondary"), opacity: 1, cursor: "not-allowed" }}>+ Novo Cupom</button>
      </div>

      {/* KPIs */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 20, marginBottom: 28 }}>
        {[
          { label: "Cupons Ativos", value: "0", sub: "Disponíveis para uso", color: C.accent, icon: "🏷️" },
          { label: "Total Descontos", value: "R$ 0,00", sub: "Valor concedido", color: C.gold, icon: "💰" },
          { label: "Cupons Usados", value: "0", sub: "Este mês", color: C.info, icon: "✓" },
          { label: "Taxa Conversão", value: "0%", sub: "Com cupom ativo", color: C.warn, icon: "📊" },
        ].map((k, idx) => (
          <div key={k.label} className={`tfx-fade-in-delay-${idx}`} style={{ 
            background: C.bgElevated,
            border: `1px solid rgba(0,0,0,0.08)`,
            borderRadius: 20,
            padding: 24,
            position: "relative",
            overflow: "hidden",
            boxShadow: `0 1px 3px rgba(0,0,0,0.08), 0 0 0 1px rgba(0,0,0,0.06)`,
            transition: "all 0.3s"
          }}>
            <div style={{ position: "absolute", top: -30, right: -30, width: 120, height: 120, background: `radial-gradient(circle, ${k.color}15, transparent)`, borderRadius: "50%" }} />
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
              <div style={{ width: 48, height: 48, borderRadius: 12, background: `${k.color}20`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}>{k.icon}</div>
              <div style={{ fontSize: 9, color: "#374151", letterSpacing: 2, textTransform: "uppercase", fontWeight: 600 }}>{k.label}</div>
            </div>
            <div style={{ fontFamily: "'Inter', sans-serif", fontWeight: 800, fontSize: 28, color: k.color, letterSpacing: -1.2 }}>{k.value}</div>
            <div style={{ fontSize: 10, color: "#374151", marginTop: 8 }}>{k.sub}</div>
          </div>
        ))}
      </div>

      {/* Empty state */}
      <div style={{ 
        ...styles.card,
        background: C.bgElevated,
        border: "1px solid #e5e7eb",
        textAlign: "center",
        padding: 60
      }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>🏷️</div>
        <div style={{ fontSize: 16, fontWeight: 700, color: "#111827", marginBottom: 8 }}>Funcionalidade em Desenvolvimento</div>
        <div style={{ fontSize: 13, color: "#374151", marginBottom: 24 }}>Em breve você poderá criar cupons de desconto, definir regras de uso e acompanhar a performance das promoções.</div>
        <div style={{ fontSize: 11, color: "#374151" }}>Previsão: próxima atualização</div>
      </div>
    </div>
  );
}


// ── CUPONS PAGE ───────────────────────────────────────────────
function CuponsPage({ data, setData, showToast }) {
  const emptyForm = { codigo: "", tipo: "percentual", valor: "", descricao: "", limite_usos: "", validade: "" };
  const [form, setForm] = useState(emptyForm);
  const [editandoId, setEditandoId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [salvando, setSalvando] = useState(false);

  const cupons = data.cupons || [];

  const salvar = async () => {
    if (!form.codigo || !form.valor) return alert("Código e valor são obrigatórios!");
    setSalvando(true);
    try {
      const row = { ...form, codigo: form.codigo.toUpperCase(), valor: parseFloat(form.valor), limite_usos: form.limite_usos ? parseInt(form.limite_usos) : null, validade: form.validade || null, tenant_id: getTenant() };
      if (editandoId) {
        await sb.update("cupons", editandoId, row);
        setData(d => ({ ...d, cupons: d.cupons.map(c => c.id === editandoId ? { ...c, ...row } : c) }));
      } else {
        await sb.insert("cupons", row);
        const atualizados = await sb.get("cupons");
        setData(d => ({ ...d, cupons: atualizados || [] }));
      }
      if (showToast) showToast(editandoId ? "Cupom atualizado!" : "Cupom criado!");
      setForm(emptyForm);
      setEditandoId(null);
      setShowForm(false);
    } catch(e) { alert("Erro: " + e.message); }
    setSalvando(false);
  };

  const toggleAtivo = async (cupom) => {
    await sb.update("cupons", cupom.id, { ativo: !cupom.ativo });
    setData(d => ({ ...d, cupons: d.cupons.map(c => c.id === cupom.id ? { ...c, ativo: !c.ativo } : c) }));
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Remover este cupom?")) return;
    await sb.remove("cupons", id);
    setData(d => ({ ...d, cupons: d.cupons.filter(c => c.id !== id) }));
  };

  const editar = (c) => {
    setForm({ codigo: c.codigo, tipo: c.tipo, valor: c.valor, descricao: c.descricao || "", limite_usos: c.limite_usos || "", validade: c.validade || "" });
    setEditandoId(c.id);
    setShowForm(true);
  };

  return (
    <div style={{ padding: "0 0 40px" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 28 }}>
        <h2 style={{ margin: 0, color: "#111827", fontSize: 18 }}>🏷️ Cupons de Desconto</h2>
        <button onClick={() => { setForm(emptyForm); setEditandoId(null); setShowForm(!showForm); }} style={{ ...styles.btn("primary"), fontSize: 11 }}>+ Novo Cupom</button>
      </div>

      {showForm && (
        <div style={{ ...styles.card, marginBottom: 20, borderColor: C.accent + "40" }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: "#111827", marginBottom: 14 }}>{editandoId ? "✏️ Editar Cupom" : "➕ Novo Cupom"}</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 16 }}>
            <div><label style={styles.label}>Código *</label><input style={{ ...styles.input, textTransform: "uppercase" }} value={form.codigo} onChange={e => setForm(f => ({...f, codigo: e.target.value.toUpperCase()}))} placeholder="EX: PROMO10" /></div>
            <div>
              <label style={styles.label}>Tipo *</label>
              <select style={styles.input} value={form.tipo} onChange={e => setForm(f => ({...f, tipo: e.target.value}))}>
                <option value="percentual">% Percentual</option>
                <option value="fixo">R$ Valor fixo</option>
              </select>
            </div>
            <div><label style={styles.label}>Valor * {form.tipo === "percentual" ? "(%)" : "(R$)"}</label><input type="number" style={styles.input} value={form.valor} onChange={e => setForm(f => ({...f, valor: e.target.value}))} placeholder={form.tipo === "percentual" ? "Ex: 10" : "Ex: 50"} /></div>
            <div><label style={styles.label}>Descrição</label><input style={styles.input} value={form.descricao} onChange={e => setForm(f => ({...f, descricao: e.target.value}))} placeholder="Ex: Desconto de boas-vindas" /></div>
            <div><label style={styles.label}>Limite de usos</label><input type="number" style={styles.input} value={form.limite_usos} onChange={e => setForm(f => ({...f, limite_usos: e.target.value}))} placeholder="Ilimitado" /></div>
            <div><label style={styles.label}>Validade</label><input type="date" style={styles.input} value={form.validade} onChange={e => setForm(f => ({...f, validade: e.target.value}))} /></div>
          </div>
          <div style={{ marginTop: 14, display: "flex", gap: 8 }}>
            <button onClick={salvar} disabled={salvando} style={{ ...styles.btn("primary"), fontSize: 12 }}>{salvando ? "Salvando..." : editandoId ? "💾 Atualizar" : "💾 Criar Cupom"}</button>
            <button onClick={() => { setShowForm(false); setForm(emptyForm); setEditandoId(null); }} style={{ ...styles.btn("secondary"), fontSize: 12 }}>Cancelar</button>
          </div>
        </div>
      )}

      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
          <thead>
            <tr>{["Código", "Tipo", "Valor", "Descrição", "Usos", "Validade", "Status", ""].map(h => <th key={h} style={styles.th}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {cupons.map(c => (
              <tr key={c.id} style={{ background: "transparent", opacity: c.ativo ? 1 : 0.5 }} onClick={() => editar(c)}>
                <td style={{ ...styles.td, fontWeight: 700, color: C.accent, letterSpacing: 1 }}>{c.codigo}</td>
                <td style={styles.td}>{c.tipo === "percentual" ? "%" : "R$"}</td>
                <td style={{ ...styles.td, color: C.gold }}>{c.tipo === "percentual" ? c.valor + "%" : fmt(c.valor)}</td>
                <td style={styles.td}>{c.descricao || "—"}</td>
                <td style={styles.td}>{c.usos || 0}{c.limite_usos ? `/${c.limite_usos}` : ""}</td>
                <td style={styles.td}>{c.validade ? fmtDataBR(c.validade) : "Sem validade"}</td>
                <td style={styles.td}>
                  <button onClick={e => { e.stopPropagation(); toggleAtivo(c); }} style={{ background: c.ativo ? C.accent + "20" : C.warn + "20", color: c.ativo ? C.accent : C.warn, border: "none", borderRadius: 6, padding: "3px 8px", fontSize: 10, cursor: "pointer" }}>
                    {c.ativo ? "✅ Ativo" : "⏸ Inativo"}
                  </button>
                </td>
                <td style={styles.td}><button onClick={e => { e.stopPropagation(); handleDelete(c.id); }} style={{ background: "none", border: "none", color: C.warn, cursor: "pointer", fontSize: 13 }}>✕</button></td>
              </tr>
            ))}
          </tbody>
        </table>
        {cupons.length === 0 && <div style={{ textAlign: "center", color: "#374151", padding: 30, fontSize: 13 }}>Nenhum cupom criado ainda.</div>}
      </div>
    </div>
  );
}


function Metas({ data, showToast }) {
  const fmt = (v) => "R$ " + Number(v || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 });
  const pct = (v, total) => total > 0 ? ((v / total) * 100).toFixed(1) + "%" : "0%";

  const hoje = hojeManaus();
  const mesAtual = hoje.slice(0, 7);
  const [mesSel, setMesSel] = useState(mesAtual);
  const [metas, setMetas] = useState(null);
  const [form, setForm] = useState({ meta_minima: "", meta_mensal: "", super_meta: "", meta_absurda: "" });
  const [salvando, setSalvando] = useState(false);
  const [editandoMeta, setEditandoMeta] = useState(false);

  // Busca metas do mês selecionado
  useEffect(() => {
    fetch(`${API_BASE}?table=metas_mensais&mes=eq.${mesSel}`, {
      headers: { Authorization: `Bearer ${getJWT()}` }
    }).then(r => r.json()).then(rows => {
      if (rows.length > 0) {
        setMetas(rows[0]);
        setForm({ meta_minima: rows[0].meta_minima || "", meta_mensal: rows[0].meta_mensal || "", super_meta: rows[0].super_meta || "", meta_absurda: rows[0].meta_absurda || "" });
      } else {
        setMetas(null);
        setForm({ meta_minima: "", meta_mensal: "", super_meta: "", meta_absurda: "" });
      }
    }).catch(() => {});
  }, [mesSel]);

  const salvarMetas = async () => {
    if (!form.meta_minima || !form.meta_mensal || !form.super_meta || !form.meta_absurda) return alert("Preencha todas as metas!");
    setSalvando(true);
    const row = { tenant_id: getTenant(), mes: mesSel, meta_minima: parseFloat(form.meta_minima), meta_mensal: parseFloat(form.meta_mensal), super_meta: parseFloat(form.super_meta), meta_absurda: parseFloat(form.meta_absurda) };
    if (metas) {
      await fetch(`${API_BASE}?table=metas_mensais&id=eq.${metas.id}`, {
        method: "PATCH",
        headers: { Authorization: `Bearer ${getJWT()}`, "Content-Type": "application/json" },
        body: JSON.stringify(row)
      });
      setMetas({ ...metas, ...row });
    } else {
      const r = await fetch(`${API_BASE}?table=metas_mensais`, {
        method: "POST",
        headers: { Authorization: `Bearer ${getJWT()}`, "Content-Type": "application/json", Prefer: "return=representation" },
        body: JSON.stringify(row)
      });
      const d = await r.json();
      setMetas(Array.isArray(d) ? d[0] : d);
    }
    setEditandoMeta(false);
    setSalvando(false);
    if (showToast) showToast("Metas salvas!");
  };

  // Faturamento por dia do mês selecionado vindo de indicadores + vendas
  const indicadoresMes = (data.indicadores || []).filter(i => i.data && i.data.startsWith(mesSel));
  const vendasMes = (data.vendas || []).filter(v => v.data && v.data.startsWith(mesSel));

  // Agrupa faturamento por dia
  const faturamentoPorDia = {};
  // Primeiro usa indicadores (tem faturamento registrado)
  indicadoresMes.forEach(i => {
    if (i.faturamento > 0) faturamentoPorDia[i.data] = parseFloat(i.faturamento);
  });
  // Dia com venda no sistema: vale a soma das vendas (mesma regra da aba Indicadores; o valor da venda já é o líquido do desconto).
  // Dia sem venda: fica o valor gravado nos indicadores (histórico digitado à mão).
  const somaVendasDia = {};
  vendasMes.forEach(v => { somaVendasDia[v.data] = (somaVendasDia[v.data] || 0) + Number(v.valor || 0) * Number(v.qtd || 1); });
  Object.keys(somaVendasDia).forEach(dia => { faturamentoPorDia[dia] = somaVendasDia[dia]; });

  const totalFaturado = Object.values(faturamentoPorDia).reduce((s, v) => s + v, 0);

  // Semanas do mês
  const ano = parseInt(mesSel.slice(0, 4));
  const mes = parseInt(mesSel.slice(5, 7));
  const diasNoMes = new Date(ano, mes, 0).getDate();
  const semanas = [];
  let semAtual = [];
  for (let d = 1; d <= diasNoMes; d++) {
    const dataStr = `${mesSel}-${String(d).padStart(2, "0")}`;
    const diaSemana = new Date(ano, mes - 1, d).getDay();
    semAtual.push({ dia: d, dataStr, faturamento: faturamentoPorDia[dataStr] || 0 });
    if (diaSemana === 6 || d === diasNoMes) {
      semanas.push(semAtual);
      semAtual = [];
    }
  }

  const metaColors = {
    meta_minima: { label: "Meta Mínima", color: "#374151", bg: "#ebf8ff" },
    meta_mensal: { label: "Meta Mensal", color: "#15803d", bg: "#f0fff4" },
    super_meta: { label: "Super Meta", color: "#374151", bg: "#fffff0" },
    meta_absurda: { label: "Meta Absurda", color: "#e53e3e", bg: "#fff5f5" },
  };

  const mesesOpcoes = [];
  for (let i = -2; i <= 3; i++) {
    const d = new Date(hoje.slice(0, 4), parseInt(hoje.slice(5, 7)) - 1 + i, 1);
    const val = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    const label = d.toLocaleDateString("pt-BR", { month: "long", year: "numeric" });
    mesesOpcoes.push({ val, label });
  }

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
        <div>
          <div style={styles.pageTitle}>Metas</div>
          <div style={styles.pageSub}>Acompanhamento mensal de faturamento</div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <select style={{ ...styles.input, width: 180 }} value={mesSel} onChange={e => setMesSel(e.target.value)}>
            {mesesOpcoes.map(m => <option key={m.val} value={m.val}>{m.label}</option>)}
          </select>
          <button onClick={() => setEditandoMeta(true)} style={{ ...styles.btn("primary"), fontSize: 11 }}>⚙️ Definir metas</button>
        </div>
      </div>

      {/* Modal definir metas */}
      {editandoMeta && (
        <div style={{ position: "fixed", inset: 0, background: "#00000080", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ background: "#f0f2f5", borderRadius: 16, padding: 28, width: 420, boxShadow: "0 20px 60px #0008" }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#111827", marginBottom: 28 }}>⚙️ Definir Metas — {mesesOpcoes.find(m => m.val === mesSel)?.label}</div>
            {Object.entries(metaColors).map(([key, mc]) => (
              <div key={key} style={{ marginBottom: 14 }}>
                <label style={{ ...styles.label, color: mc.color }}>{mc.label}</label>
                <input type="number" style={styles.input} value={form[key]} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))} placeholder="R$ 0,00" />
              </div>
            ))}
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <button onClick={salvarMetas} disabled={salvando} style={{ ...styles.btn("primary"), flex: 1 }}>{salvando ? "Salvando..." : "Salvar"}</button>
              <button onClick={() => setEditandoMeta(false)} style={styles.btn("secondary")}>Cancelar</button>
            </div>
          </div>
        </div>
      )}

      {/* Cards de metas */}
      {metas ? (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 20, marginBottom: 28 }}>
            {Object.entries(metaColors).map(([key, mc], idx) => {
              const metaVal = parseFloat(metas[key] || 0);
              const progresso = metaVal > 0 ? Math.min((totalFaturado / metaVal) * 100, 100) : 0;
              const atingiu = totalFaturado >= metaVal;
              return (
                <div key={key} className={`tfx-fade-in-delay-${idx}`} style={{ 
                  background: C.bgElevated,
                  border: `2px solid ${atingiu ? mc.color : mc.color}25`,
                  borderRadius: 20,
                  padding: 24,
                  position: "relative",
                  overflow: "hidden",
                  boxShadow: `0 10px 40px ${mc.color}${atingiu ? '50' : '20'}`,
                  transition: "all 0.3s"
                }}>
                  <div style={{ position: "absolute", top: -30, right: -30, width: 120, height: 120, background: `radial-gradient(circle, ${mc.color}15, transparent)`, borderRadius: "50%" }} />
                  <div style={{ fontSize: 9, color: mc.color, fontWeight: 700, letterSpacing: 2, textTransform: "uppercase", marginBottom: 12 }}>{mc.label}</div>
                  <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 28, fontWeight: 800, color: mc.color, marginBottom: 8, letterSpacing: -1.2 }}>{privado ? "••••••" : fmt(metaVal)}</div>
                  <div style={{ fontSize: 11, color: atingiu ? mc.color : C.muted, fontWeight: 600, marginBottom: 12 }}>{atingiu ? "✅ Atingida!" : privado ? "••••" : `Falta ${fmt(metaVal - totalFaturado)}`}</div>
                  <div style={{ height: 6, background: C.border, borderRadius: 3, overflow: "hidden" }}>
                    <div style={{ height: "100%", width: progresso + "%", background: mc.color, borderRadius: 3, transition: "width 0.5s" }} />
                  </div>
                  <div style={{ fontSize: 10, color: "#374151", marginTop: 4, textAlign: "right" }}>{privado ? "••%" : progresso.toFixed(1) + "%"}</div>
                </div>
              );
            })}
          </div>

          {/* Total faturado */}
          <div style={{ 
            background: C.bgElevated,
            border: `2px solid ${C.accent}40`,
            borderRadius: 20,
            padding: 28,
            marginBottom: 28,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            boxShadow: `0 10px 40px ${C.accent}30`
          }}>
            <div>
              <div style={{ fontSize: 11, color: "#374151", letterSpacing: 1 }}>TOTAL FATURADO NO MÊS</div>
              <div style={{ fontSize: 28, fontWeight: 800, color: C.accent }}>{privado ? "••••••" : fmt(totalFaturado)}</div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: 11, color: "#374151" }}>Meta Mensal</div>
              <div style={{ fontSize: 18, fontWeight: 800, color: "#111827", letterSpacing: -0.5 }}>{privado ? "••%" : pct(totalFaturado, metas.meta_mensal)} atingido</div>
            </div>
          </div>

          {/* Tabela semanal */}
          <div style={{ 
            background: C.bgElevated,
            border: "1px solid #e5e7eb",
            borderRadius: 16,
            overflow: "hidden",
            marginBottom: 24
          }}>
            <div style={{ 
              padding: "18px 24px",
              borderBottom: `2px solid ${C.accent}30`,
              fontSize: 14,
              fontWeight: 700,
              color: "#111827"
            }}>📅 Faturamento Semanal</div>
            {semanas.map((sem, si) => {
              const totalSem = sem.reduce((s, d) => s + d.faturamento, 0);
              const diasSem = sem.length;
              const metaSemMinima = metas.meta_minima / diasNoMes * diasSem;
              const metaSemMensal = metas.meta_mensal / diasNoMes * diasSem;
              const metaSemSuper = metas.super_meta / diasNoMes * diasSem;
              const metaSemAbsurda = metas.meta_absurda / diasNoMes * diasSem;
              return (
                <div key={si} style={{ borderBottom: si < semanas.length - 1 ? "1px solid #e5e7eb" : "none" }}>
                  <div style={{ display: "grid", gridTemplateColumns: "120px 1fr 100px 100px 100px 100px", gap: 0, padding: "12px 24px", background: C.bgAlt || C.surface, alignItems: "center" }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "#374151" }}>Semana {si + 1}</div>
                    <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                      {sem.map(d => (
                        <div key={d.dia} style={{ textAlign: "center", minWidth: 36, padding: "4px 6px", borderRadius: 6, background: d.faturamento > 0 ? C.accentDim || "#FFB80020" : C.border + "30" }}>
                          <div style={{ fontSize: 9, color: "#374151" }}>{d.dia}</div>
                          <div style={{ fontSize: 9, color: d.faturamento > 0 ? C.accent : C.muted, fontWeight: d.faturamento > 0 ? 700 : 400 }}>{d.faturamento > 0 ? (privado ? "••••" : fmt(d.faturamento).replace("R$ ", "")) : "-"}</div>
                        </div>
                      ))}
                    </div>
                    <div style={{ textAlign: "center" }}>
                      <div style={{ fontSize: 9, color: "#374151" }}>Mín</div>
                      <div style={{ fontSize: 10, fontWeight: 700, color: totalSem >= metaSemMinima ? "#374151" : C.muted }}>{privado ? "••••" : fmt(metaSemMinima)}</div>
                    </div>
                    <div style={{ textAlign: "center" }}>
                      <div style={{ fontSize: 9, color: "#15803d" }}>Mensal</div>
                      <div style={{ fontSize: 10, fontWeight: 700, color: totalSem >= metaSemMensal ? "#15803d" : C.muted }}>{privado ? "••••" : fmt(metaSemMensal)}</div>
                    </div>
                    <div style={{ textAlign: "center" }}>
                      <div style={{ fontSize: 9, color: "#374151" }}>Super</div>
                      <div style={{ fontSize: 10, fontWeight: 700, color: totalSem >= metaSemSuper ? "#374151" : C.muted }}>{privado ? "••••" : fmt(metaSemSuper)}</div>
                    </div>
                    <div style={{ textAlign: "center" }}>
                      <div style={{ fontSize: 9, color: "#e53e3e" }}>Absurda</div>
                      <div style={{ fontSize: 10, fontWeight: 700, color: totalSem >= metaSemAbsurda ? "#e53e3e" : C.muted }}>{privado ? "••••" : fmt(metaSemAbsurda)}</div>
                    </div>
                  </div>
                  <div style={{ padding: "6px 20px 10px", display: "flex", gap: 16, alignItems: "center" }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: "#111827" }}>Total: {privado ? "••••••" : fmt(totalSem)}</div>
                    <div style={{ flex: 1, height: 4, background: C.border, borderRadius: 2, overflow: "hidden" }}>
                      <div style={{ height: "100%", width: Math.min((totalSem / metaSemMensal) * 100, 100) + "%", background: totalSem >= metaSemAbsurda ? "#e53e3e" : totalSem >= metaSemSuper ? "#374151" : totalSem >= metaSemMensal ? "#15803d" : totalSem >= metaSemMinima ? "#374151" : C.muted, borderRadius: 2, transition: "width 0.5s" }} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Gráfico de evolução acumulada */}
          <div style={{ 
            background: C.bgElevated,
            border: "1px solid #e5e7eb",
            borderRadius: 16,
            padding: 24,
            marginBottom: 24
          }}>
            <div style={{ 
              fontSize: 14,
              fontWeight: 700,
              color: "#111827",
              marginBottom: 20,
              paddingBottom: 12,
              borderBottom: `2px solid ${C.accent}30`
            }}>📈 Evolução Acumulada do Mês</div>
            {(() => {
              const ano = parseInt(mesSel.slice(0, 4));
              const mes = parseInt(mesSel.slice(5, 7));
              const diasNoMes2 = new Date(ano, mes, 0).getDate();
              // Acumula faturamento dia a dia
              let acum = 0;
              const pontos = [];
              for (let d = 1; d <= diasNoMes2; d++) {
                const dataStr = `${mesSel}-${String(d).padStart(2, "0")}`;
                const fat = faturamentoPorDia[dataStr] || 0;
                acum += fat;
                pontos.push({ dia: d, acum, fat, dataStr });
              }
              const maxVal = Math.max(parseFloat(metas.meta_absurda || 0), acum) * 1.05;
              const w = 100;
              const h = 160;
              const xStep = w / (diasNoMes2 - 1);
              const toY = (v) => h - (v / maxVal) * h;
              const hoje2 = hojeManaus();
              const diaHoje2 = mesSel === mesAtual ? parseInt(hoje2.slice(8, 10)) : diasNoMes2;
              // Linha do faturamento acumulado
              const pontosReais = pontos.filter(p => p.dia <= diaHoje2);
              const pathFat = pontosReais.map((p, i) => `${i === 0 ? "M" : "L"}${((p.dia - 1) * xStep).toFixed(1)},${toY(p.acum).toFixed(1)}`).join(" ");
              // Linhas de meta
              const metaLines = [
                { key: "meta_minima", color: "#374151", label: "Mín" },
                { key: "meta_mensal", color: "#15803d", label: "Mensal" },
                { key: "meta_absurda", color: "#e53e3e", label: "Absurda" },
              ];
              return (
                <div style={{ position: "relative" }}>
                  <svg viewBox={`0 0 ${w} ${h}`} style={{ width: "100%", height: 160, overflow: "visible" }} preserveAspectRatio="none">
                    {/* Linhas de meta horizontais */}
                    {metaLines.map(ml => {
                      const metaV = parseFloat(metas[ml.key] || 0);
                      if (!metaV) return null;
                      const y = toY(metaV);
                      return (
                        <g key={ml.key}>
                          <line x1="0" y1={y} x2={w} y2={y} stroke={ml.color} strokeWidth="0.4" strokeDasharray="2,2" />
                          <text x={w + 1} y={y + 1} fill={ml.color} fontSize="3" dominantBaseline="middle">{ml.label}</text>
                        </g>
                      );
                    })}
                    {/* Área preenchida */}
                    {pontosReais.length > 1 && (
                      <path d={`${pathFat} L${((pontosReais[pontosReais.length-1].dia - 1) * xStep).toFixed(1)},${h} L0,${h} Z`} fill="#FFB80015" />
                    )}
                    {/* Linha do faturamento */}
                    {pontosReais.length > 1 && (
                      <path d={pathFat} fill="none" stroke="#111827" strokeWidth="0.8" strokeLinecap="round" strokeLinejoin="round" />
                    )}
                    {/* Ponto atual */}
                    {pontosReais.length > 0 && (
                      <circle cx={((pontosReais[pontosReais.length-1].dia - 1) * xStep).toFixed(1)} cy={toY(pontosReais[pontosReais.length-1].acum).toFixed(1)} r="1.5" fill="#111827" />
                    )}
                  </svg>
                  {/* Labels dos dias */}
                  <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4 }}>
                    {[1, Math.floor(diasNoMes2/4), Math.floor(diasNoMes2/2), Math.floor(diasNoMes2*3/4), diasNoMes2].map(d => (
                      <div key={d} style={{ fontSize: 9, color: "#374151" }}>{d}</div>
                    ))}
                  </div>
                  {/* Legenda */}
                  <div style={{ display: "flex", gap: 16, marginTop: 8, flexWrap: "wrap" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 4 }}><div style={{ width: 12, height: 2, background: "#111827", borderRadius: 1 }} /><span style={{ fontSize: 10, color: "#374151" }}>Faturamento acumulado</span></div>
                    {metaLines.map(ml => <div key={ml.key} style={{ display: "flex", alignItems: "center", gap: 4 }}><div style={{ width: 12, height: 1, background: ml.color, borderRadius: 1, borderTop: `1px dashed ${ml.color}` }} /><span style={{ fontSize: 10, color: "#374151" }}>{ml.label}</span></div>)}
                  </div>
                </div>
              );
            })()}
          </div>
          {/* LUCRO LIQUIDO PREVISTO v4 - gastos 100% historico, receita hibrida */}
          {mesSel === mesAtual && (() => {
            const diasNoMes = new Date(ano, mes, 0).getDate();
            const diaHoje = parseInt(hoje.slice(8, 10));
            const diasPassados = Math.max(1, diaHoje);
            const diasRestantes = Math.max(0, diasNoMes - diaHoje);

            const vendasMesAtual = (data.vendas || []).filter(v => v.data && v.data.startsWith(mesSel));
            const gastosMesAtual = (data.gastos || []).filter(g => g.data && g.data.startsWith(mesSel));

            const receitaRealizada = vendasMesAtual.reduce((s, v) => s + (parseFloat(v.valor) || 0) * (parseInt(v.qtd) || 1), 0);
            const cmvRealizado = vendasMesAtual.reduce((s, v) => s + (parseFloat(v.custo) || 0) * (parseInt(v.qtd) || 1), 0);
            const gastosLojaRealizado = gastosMesAtual.filter(g => g.tipo === "loja").reduce((s, g) => s + (parseFloat(g.valor) || 0), 0);
            const gastosPessoalRealizado = gastosMesAtual.filter(g => g.tipo === "pessoal").reduce((s, g) => s + (parseFloat(g.valor) || 0), 0);
            const lucroRealizado = receitaRealizada - cmvRealizado - gastosLojaRealizado - gastosPessoalRealizado;

            const mesesHist = [];
            for (let i = 1; i <= 3; i++) {
              const d = new Date(ano, mes - 1 - i, 1);
              mesesHist.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
            }

            const diaDoMes = (dataStr) => parseInt(dataStr.slice(8, 10));

            const historico = mesesHist.map(m => {
              const anoH = parseInt(m.slice(0, 4));
              const mesH = parseInt(m.slice(5, 7));
              const diasH = new Date(anoH, mesH, 0).getDate();
              const vH = (data.vendas || []).filter(v => v.data && v.data.startsWith(m));
              const gH = (data.gastos || []).filter(g => g.data && g.data.startsWith(m));

              const diasComDados = new Set();
              vH.forEach(v => { if (v.data) diasComDados.add(diaDoMes(v.data)); });
              gH.forEach(g => { if (g.data) diasComDados.add(diaDoMes(g.data)); });

              const periodoEquiv_receita = vH.filter(v => diaDoMes(v.data) <= diaHoje).reduce((s, v) => s + (parseFloat(v.valor) || 0) * (parseInt(v.qtd) || 1), 0);
              const periodoEquiv_cmv = vH.filter(v => diaDoMes(v.data) <= diaHoje).reduce((s, v) => s + (parseFloat(v.custo) || 0) * (parseInt(v.qtd) || 1), 0);
              const periodoEquiv_gLoja = gH.filter(g => diaDoMes(g.data) <= diaHoje && g.tipo === "loja").reduce((s, g) => s + (parseFloat(g.valor) || 0), 0);
              const periodoEquiv_gPess = gH.filter(g => diaDoMes(g.data) <= diaHoje && g.tipo === "pessoal").reduce((s, g) => s + (parseFloat(g.valor) || 0), 0);

              const periodoRest_receita = vH.filter(v => diaDoMes(v.data) > diaHoje).reduce((s, v) => s + (parseFloat(v.valor) || 0) * (parseInt(v.qtd) || 1), 0);
              const periodoRest_cmv = vH.filter(v => diaDoMes(v.data) > diaHoje).reduce((s, v) => s + (parseFloat(v.custo) || 0) * (parseInt(v.qtd) || 1), 0);
              const periodoRest_gLoja = gH.filter(g => diaDoMes(g.data) > diaHoje && g.tipo === "loja").reduce((s, g) => s + (parseFloat(g.valor) || 0), 0);
              const periodoRest_gPess = gH.filter(g => diaDoMes(g.data) > diaHoje && g.tipo === "pessoal").reduce((s, g) => s + (parseFloat(g.valor) || 0), 0);

              const receitaTotal = periodoEquiv_receita + periodoRest_receita;
              const cmvTotal = periodoEquiv_cmv + periodoRest_cmv;
              const cmvPct = receitaTotal > 0 ? cmvTotal / receitaTotal : 0;

              return {
                mes: m,
                diasH,
                qtdDiasComDados: diasComDados.size,
                valido: diasComDados.size >= 15,
                periodoEquiv_receita,
                periodoEquiv_gLoja,
                periodoEquiv_gPess,
                periodoRest_receita,
                periodoRest_gLoja,
                periodoRest_gPess,
                lucroTotal: receitaTotal - cmvTotal - periodoEquiv_gLoja - periodoRest_gLoja - periodoEquiv_gPess - periodoRest_gPess,
                cmvPct
              };
            }).filter(h => h.valido);

            const mediaCmvPct = historico.length > 0
              ? historico.reduce((s, h) => s + h.cmvPct, 0) / historico.length
              : (receitaRealizada > 0 ? cmvRealizado / receitaRealizada : 0);

            const mediaReceitaRestHist = historico.length > 0
              ? historico.reduce((s, h) => s + h.periodoRest_receita, 0) / historico.length
              : 0;
            const mediaLojaRestHist = historico.length > 0
              ? historico.reduce((s, h) => s + h.periodoRest_gLoja, 0) / historico.length
              : 0;
            const mediaPessRestHist = historico.length > 0
              ? historico.reduce((s, h) => s + h.periodoRest_gPess, 0) / historico.length
              : 0;

            const mediaLucroMensal = historico.length > 0
              ? historico.reduce((s, h) => s + h.lucroTotal, 0) / historico.length
              : 0;

            const receitaRitmoAtual = (receitaRealizada / diasPassados) * diasRestantes;
            const pesoAtual = historico.length > 0 ? 0.6 : 1.0;
            const pesoHist = historico.length > 0 ? 0.4 : 0.0;

            const receitaRestPrev = receitaRitmoAtual * pesoAtual + mediaReceitaRestHist * pesoHist;
            const lojaRestPrev = historico.length > 0 ? mediaLojaRestHist : (gastosLojaRealizado / diasPassados) * diasRestantes;
            const pessRestPrev = historico.length > 0 ? mediaPessRestHist : (gastosPessoalRealizado / diasPassados) * diasRestantes;

            const receitaPrevistaFinal = receitaRealizada + receitaRestPrev;
            const cmvPrevistoFinal = receitaPrevistaFinal * mediaCmvPct;
            const lojaPrevistaFinal = gastosLojaRealizado + lojaRestPrev;
            const pessPrevistaFinal = gastosPessoalRealizado + pessRestPrev;

            const lucroPrevisto = receitaPrevistaFinal - cmvPrevistoFinal - lojaPrevistaFinal - pessPrevistaFinal;
            const projetadoRestante = lucroPrevisto - lucroRealizado;

            let badgeCor, badgeTexto, badgeBg;
            if (historico.length === 0) {
              badgeCor = C.muted; badgeBg = C.muted + "20"; badgeTexto = "Sem histórico suficiente";
            } else {
              const diff = mediaLucroMensal !== 0 ? (lucroPrevisto - mediaLucroMensal) / Math.abs(mediaLucroMensal) : 0;
              if (diff > 0.05) { badgeCor = "#15803d"; badgeBg = "#22c55e20"; badgeTexto = `↑ ${(diff * 100).toFixed(0)}% acima da média`; }
              else if (diff < -0.05) { badgeCor = "#ef4444"; badgeBg = "#ef444420"; badgeTexto = `↓ ${Math.abs(diff * 100).toFixed(0)}% abaixo da média`; }
              else { badgeCor = "#374151"; badgeBg = "#d69e2e20"; badgeTexto = "Dentro da média"; }
            }

            const corLucro = lucroPrevisto >= 0 ? "#15803d" : "#ef4444";

            return (
              <div style={{
                background: C.bgElevated,
                border: `2px solid ${corLucro}40`,
                borderRadius: 20,
                padding: 28,
                marginBottom: 24,
                position: "relative",
                overflow: "hidden",
                boxShadow: `0 10px 40px ${corLucro}25`
              }}>
                <div style={{ position: "absolute", top: -40, right: -40, width: 160, height: 160, background: `radial-gradient(circle, ${corLucro}15, transparent)`, borderRadius: "50%" }} />

                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20, position: "relative" }}>
                  <div>
                    <div style={{ fontSize: 10, color: "#374151", letterSpacing: 2, textTransform: "uppercase", fontWeight: 700, marginBottom: 6 }}>💰 Lucro Líquido Previsto</div>
                    <div style={{ fontSize: 11, color: "#374151" }}>Projeção inteligente • receita híbrida, gastos 100% histórico</div>
                  </div>
                  <div style={{ background: badgeBg, color: badgeCor, padding: "6px 12px", borderRadius: 20, fontSize: 10, fontWeight: 700, border: `1px solid ${badgeCor}40` }}>{badgeTexto}</div>
                </div>

                <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 38, fontWeight: 800, color: corLucro, letterSpacing: -1.5, marginBottom: 4, position: "relative" }}>
                  {privado ? "••••••" : fmt(lucroPrevisto)}
                </div>
                <div style={{ fontSize: 11, color: "#374151", marginBottom: 20, position: "relative" }}>
                  Previsto para {mesesOpcoes.find(m => m.val === mesSel)?.label}
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginBottom: 16, position: "relative" }}>
                  <div style={{ background: C.bg + "60", borderRadius: 12, padding: 14, border: "1px solid #e5e7eb" }}>
                    <div style={{ fontSize: 9, color: "#374151", fontWeight: 700, letterSpacing: 1, marginBottom: 4 }}>REALIZADO ATÉ HOJE</div>
                    <div style={{ fontSize: 16, fontWeight: 800, color: lucroRealizado >= 0 ? C.accent : C.warn, letterSpacing: -0.5 }}>{privado ? "••••" : fmt(lucroRealizado)}</div>
                    <div style={{ fontSize: 9, color: "#374151", marginTop: 2 }}>dias 1–{diaHoje} de {diasNoMes}</div>
                  </div>
                  <div style={{ background: C.bg + "60", borderRadius: 12, padding: 14, border: "1px solid #e5e7eb" }}>
                    <div style={{ fontSize: 9, color: "#374151", fontWeight: 700, letterSpacing: 1, marginBottom: 4 }}>PROJEÇÃO DIAS {diaHoje + 1}–{diasNoMes}</div>
                    <div style={{ fontSize: 16, fontWeight: 800, color: projetadoRestante >= 0 ? C.accent : C.warn, letterSpacing: -0.5 }}>{privado ? "••••" : fmt(projetadoRestante)}</div>
                    <div style={{ fontSize: 9, color: "#374151", marginTop: 2 }}>{diasRestantes} {diasRestantes === 1 ? "dia restante" : "dias restantes"}</div>
                  </div>
                  <div style={{ background: C.bg + "60", borderRadius: 12, padding: 14, border: "1px solid #e5e7eb" }}>
                    <div style={{ fontSize: 9, color: "#374151", fontWeight: 700, letterSpacing: 1, marginBottom: 4 }}>MÉDIA {historico.length || 0} {historico.length === 1 ? "MÊS" : "MESES"}</div>
                    <div style={{ fontSize: 16, fontWeight: 800, color: mediaLucroMensal >= 0 ? C.accent : C.warn, letterSpacing: -0.5 }}>{privado ? "••••" : historico.length > 0 ? fmt(mediaLucroMensal) : "—"}</div>
                    <div style={{ fontSize: 9, color: "#374151", marginTop: 2 }}>{historico.length > 0 ? "lucro líquido médio" : "dados insuficientes"}</div>
                  </div>
                </div>

                <div style={{ fontSize: 10, color: "#374151", lineHeight: 1.6, padding: 12, background: C.bg + "40", borderRadius: 8, border: "1px solid #e5e7eb", position: "relative" }}>
                  <strong style={{ color: "#111827" }}>Como é calculado:</strong> receita usa modelo híbrido (60% ritmo atual + 40% histórico dos dias {diaHoje + 1}–fim do mês). CMV é calculado como {(mediaCmvPct * 100).toFixed(1)}% da receita prevista (proporcional às vendas). Gastos da loja e pessoais usam 100% o que historicamente acontece nesses dias (porque você tende a concentrar lançamentos no começo do mês). Baseado em {historico.length || 0} {historico.length === 1 ? "mês" : "meses"} com 15+ dias de dados.
                </div>
              </div>
            );
          })()}

        </>
      ) : (
        <div style={{ textAlign: "center", padding: 60, color: "#374151" }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>🎯</div>
          <div style={{ fontSize: 14, marginBottom: 16 }}>Nenhuma meta definida para este mês</div>
          <button onClick={() => setEditandoMeta(true)} style={styles.btn("primary")}>Definir Metas</button>
        </div>
      )}
    </div>
  );
}




  const salvarFabGasto = async () => {
    if (!fabForm.valor || !fabForm.categoria) return;
    setFabSalvando(true);
    try {
      await sb.insert("gastos", { categoria: fabForm.categoria, descricao: fabForm.descricao, valor: parseFloat(fabForm.valor), data: fabForm.data_gasto, tipo: "loja", recorrente: false });
      showToast("Gasto lançado!", true);
      setFabForm({ categoria: "Operacional", descricao: "", valor: "", data_gasto: hojeManaus() });
      setShowFabGasto(false);
    } catch(e) { showToast("Erro ao salvar", false); }
    setFabSalvando(false);
  };

  const pageMap = { dashboard: Dashboard, dre: DRE, saude: SaudeFinanceira, vendas: Vendas, gastos: Gastos, estoque: Estoque, compras: Compras, trocas: TrocasDevolucoes, funcionarios: Funcionarios, relatorio: RelatorioVendas, backup: Backup, indicadores: Indicadores, clientes: ClientesLoja, configuracoes: Configuracoes, cupons: CuponsPage, metas: Metas, caixa: CaixaDividas };
  const PageComp = pageMap[page];

  // tfx-metas-roas: metas e resumo da lateral consideram so o mes atual
  const _mesLat = hojeManaus().slice(0, 7);
  const _vendasMes = data.vendas.filter(v => String(v.data || "").startsWith(_mesLat));
  const _gastosMes = data.gastos.filter(g => String(g.data || "").startsWith(_mesLat));
  const faturamento = _vendasMes.reduce((s, v) => s + v.valor * v.qtd, 0);
  const gastoTotal = _gastosMes.reduce((s, g) => s + g.valor, 0);
  const lucroLiq = faturamento - _vendasMes.reduce((s,v)=>s+(v.custo||0)*v.qtd,0) - _gastosMes.filter(g=>g.tipo==="loja").reduce((s,g)=>s+g.valor,0);

  const metaFat = Number(metas.faturamento) || 0;
  const metaLucro = Number(metas.lucro) || 0;

  if (loading) return (
    <div style={{ background: "#f0f2f5", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 0 }}>
      <style>{`
        @keyframes tfxPulse { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:0.6;transform:scale(0.97)} }
        @keyframes tfxDot { 0%,80%,100%{opacity:0;transform:translateY(0)} 40%{opacity:1;transform:translateY(-6px)} }
      `}</style>
      {/* Logo animado */}
      <div style={{ animation: "tfxPulse 2s ease-in-out infinite", marginBottom: 32, textAlign: "center" }}>
        <div style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 72, height: 72, borderRadius: 20, background: corAccent || C.accent, marginBottom: 20, boxShadow: `0 0 40px ${corAccent || C.accent}40` }}>
          <span style={{ fontFamily: "Inter, sans-serif", fontWeight: 900, fontSize: 28, color: C.bg }}>T</span>
        </div>
        <div style={{ fontFamily: "Inter, sans-serif", fontWeight: 900, fontSize: 28, color: "#111827", letterSpacing: -1 }}>
          {nomeApp || "GESTÃO TFX"}
        </div>
      </div>
      {/* Dots loader */}
      <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
        {[0, 1, 2].map(i => (
          <div key={i} style={{ width: 6, height: 6, borderRadius: "50%", background: corAccent || C.accent, animation: `tfxDot 1.2s ease-in-out ${i * 0.2}s infinite` }} />
        ))}
      </div>
      <div style={{ fontSize: 10, color: "#374151", letterSpacing: 2, marginTop: 16, textTransform: "uppercase" }}>Conectando...</div>
    </div>
  );

  const NAV_FULL = [
    { id: "dashboard", label: "Dashboard", icon: "⬡" },
    { id: "dre", label: "DRE", icon: "◈" },
    { id: "saude", label: "Saúde Financeira", icon: "◎" },
    { id: "vendas", label: "Vendas", icon: "↗" },
    { id: "gastos", label: "Gastos", icon: "↘", badge: () => { const hoje = new Date(); const diaHoje = hoje.getDate(); return (window._data?.gastos || []).filter(g => g.recorrente && g.recorrente_ativo !== false && !g.pago && g.dia_vencimento && diaHoje >= g.dia_vencimento).length; } },
    { id: "estoque", label: "Estoque", icon: "▦" },
    { id: "compras", label: "Compras", icon: "⊕" },
    { id: "trocas", label: "Trocas & Devoluções", icon: "↺" },
    { id: "funcionarios", label: "Funcionários", icon: "👥" },
    { id: "relatorio", label: "Por Produto", icon: "📊" },
    { id: "backup", label: "Backup", icon: "💾" },
    { id: "indicadores", label: "Indicadores", icon: "📈" },
    { id: "clientes", label: "Clientes", icon: "👤" },
    { id: "caixa", label: "Caixa & Dívidas", icon: "💳" },
    { id: "configuracoes", label: "Configurações", icon: "⚙️" },
    { id: "cupons", label: "Descontos", icon: "🏷️" },
    { id: "metas", label: "Metas", icon: "🎯" },
  ];
  const isAdmin = logado?.perfil === "admin";

  return (
    <div style={styles.app}>
      <div className={"tfx-overlay" + (sidebarOpen ? " open" : "")} onClick={() => setSidebarOpen(false)} />

      {/* FAB - Lançamento Rápido de Gasto */}
      {logado && (
        <>
          <button onClick={() => setShowFabGasto(true)} style={{ position: "fixed", bottom: 28, right: 24, zIndex: 3000, background: "#e2e8f0", color: "#111827", border: "1px solid #FFB80030", borderRadius: "50%", width: 52, height: 52, fontSize: 22, fontWeight: 800, cursor: "pointer", boxShadow: "0 4px 16px rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center" }}>↘</button>
          {showFabGasto && (
            <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.5)", zIndex: 9998, display: "flex", alignItems: "flex-end", justifyContent: "center" }} onClick={() => setShowFabGasto(false)}>
              <div style={{ background: "#ffffff", border: "1px solid #1e1e28", borderRadius: "20px 20px 0 0", padding: "24px 24px 36px", width: "100%", maxWidth: 480 }} onClick={e => e.stopPropagation()}>
                <div style={{ width: 40, height: 4, background: "#e2e8f0", borderRadius: 2, margin: "0 auto 20px" }} />
                <div style={{ fontSize: 15, fontWeight: 800, color: "#f0f0f2", marginBottom: 20 }}>⚡ Lançamento Rápido</div>
                <div style={{ display: "grid", gap: 12 }}>
                  <div>
                    <div style={{ fontSize: 10, color: "#374151", fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, marginBottom: 6 }}>Categoria</div>
                    <select value={fabForm.categoria} onChange={e => setFabForm(f => ({...f, categoria: e.target.value}))} style={{ ...styles.input, width: "100%" }}>
                      <option>Operacional</option>
                      <option>Tráfego Pago</option>
                      <option>Imposto</option>
                      <option>Pessoal</option>
                      <option>Entrega / Frete</option>
                      <option>Uber Loja</option>
                      <option>ICMS</option>
                    </select>
                  </div>
                  <div>
                    <div style={{ fontSize: 10, color: "#374151", fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, marginBottom: 6 }}>Descrição (opcional)</div>
                    <input style={{ ...styles.input, width: "100%" }} value={fabForm.descricao} onChange={e => setFabForm(f => ({...f, descricao: e.target.value}))} placeholder="Ex: Motoboy, Braspress..." />
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                    <div>
                      <div style={{ fontSize: 10, color: "#374151", fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, marginBottom: 6 }}>Valor (R$)</div>
                      <input style={{ ...styles.input, width: "100%" }} type="number" value={fabForm.valor} onChange={e => setFabForm(f => ({...f, valor: e.target.value}))} placeholder="0,00" autoFocus />
                    </div>
                    <div>
                      <div style={{ fontSize: 10, color: "#374151", fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, marginBottom: 6 }}>Data</div>
                      <input style={{ ...styles.input, width: "100%" }} type="date" value={fabForm.data_gasto} onChange={e => setFabForm(f => ({...f, data_gasto: e.target.value}))} />
                    </div>
                  </div>
                  <button onClick={salvarFabGasto} disabled={fabSalvando || !fabForm.valor} style={{ ...styles.btn("primary"), width: "100%", padding: "14px", fontSize: 14, fontWeight: 800, marginTop: 4, opacity: (!fabForm.valor || fabSalvando) ? 0.5 : 1 }}>
                    {fabSalvando ? "Salvando..." : "Salvar Gasto"}
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
      <div className="tfx-topbar" style={{ position: "fixed", top: 0, left: 0, right: 0, height: 52, background: C.bgElevated, borderBottom: "1px solid #e5e7eb", zIndex: 800, alignItems: "center", padding: "0 16px", gap: 16 }}>
        <button onClick={() => setSidebarOpen(s => !s)} style={{ background: "none", border: "none", color: "#111827", cursor: "pointer", fontSize: 22, lineHeight: 1, padding: 4 }}>☰</button>
        <div style={{ fontWeight: 800, fontSize: 16, color: corAccent }}>{nomeApp}</div>
      </div>
      {/* SIDEBAR */}
      <aside className={"tfx-sidebar" + (sidebarOpen ? " open" : "")} style={{ ...styles.sidebar, overflowY: "auto" }}>
        <button onClick={() => setDarkMode(d => !d)} style={{ display: "flex", alignItems: "center", gap: 8, width: "calc(100% - 32px)", margin: "12px 16px 0", padding: "10px 14px", border: "1px solid #e5e7eb", borderRadius: 10, background: darkMode ? "#111827" : "#f9fafb", color: darkMode ? "#fff" : "#374151", cursor: "pointer", fontSize: 13, fontWeight: 600 }}>
          <span style={{ fontSize: 18 }}>{darkMode ? "☀️" : "🌙"}</span>
          <span>{darkMode ? "Modo Claro" : "Modo Escuro"}</span>
        </button>
        <div style={styles.logo}>
          {editandoNome ? (
            <div>
              <input value={nomeTemp} onChange={e => setNomeTemp(e.target.value)}
                style={{ ...styles.input, fontSize: 14, fontWeight: 800, marginBottom: 6, padding: "4px 8px" }}
                onKeyDown={e => e.key === "Enter" && salvarPersonalizacao()} autoFocus />
              <div style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 6 }}>
                <span style={{ fontSize: 11, color: "#374151" }}>Cor:</span>
                <input type="color" value={corAccent} onChange={e => setCorAccent(e.target.value)}
                  style={{ width: 32, height: 24, border: "none", background: "none", cursor: "pointer", borderRadius: 4 }} />
              </div>
              <div style={{ display: "flex", gap: 6 }}>
                <button onClick={salvarPersonalizacao} style={{ ...styles.btn("primary"), fontSize: 10, padding: "4px 10px" }}>Salvar</button>
                <button onClick={() => setEditandoNome(false)} style={{ ...styles.btn("secondary"), fontSize: 10, padding: "4px 10px" }}>Cancelar</button>
              </div>
            </div>
          ) : (
            <div>
              {/* App branding */}
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                <div style={{ width: 40, height: 40, borderRadius: 16, background: corAccent, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 900, fontSize: 22, color: "#f0f2f5", boxShadow: `0 6px 16px ${corAccent}50` }}>
                  {nomeApp.charAt(0)}
                </div>
                <div style={{ fontSize: 16, fontWeight: 800, letterSpacing: -0.5, color: "#111827", flex: 1 }}>{nomeApp}</div>
                <button onClick={() => { setNomeTemp(nomeApp); setEditandoNome(true); }}
                  style={{ background: "none", border: "none", color: "#374151", cursor: "pointer", fontSize: 12, lineHeight: 1, padding: 2 }} title="Personalizar">✏️</button>
              </div>
              {/* Avatar + info do usuário */}
              <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", background: "#ffffff05", border: "1px solid #f3f4f6", borderRadius: 10 }}>
                <div style={{ width: 30, height: 30, borderRadius: "50%", background: `linear-gradient(135deg, ${corAccent}40, ${corAccent}20)`, border: `1px solid ${corAccent}40`, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "Inter, sans-serif", fontWeight: 700, fontSize: 12, color: corAccent }}>
                  {(logado?.nome || "U").charAt(0).toUpperCase()}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: "#111827", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{logado?.nome || "Usuário"}</div>
                  <div style={{ display: "flex", alignItems: "center", gap: 4, marginTop: 1 }}>
                    <div style={{ width: 5, height: 5, borderRadius: "50%", background: "#15803d" }} />
                    <span style={{ fontSize: 9, color: "#374151", letterSpacing: 1, textTransform: "uppercase" }}>{logado?.perfil || "user"}</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <nav style={{ flex: 1, paddingTop: 4 }}>
          {(() => {
            const navFiltered = NAV_FULL.filter(n => isAdmin || ["vendas","estoque"].includes(n.id));
            const sections = [
              { label: "Principal", ids: ["dashboard","dre","saude","vendas","gastos","estoque","compras","trocas","funcionarios"] },
              { label: "Relatórios", ids: ["relatorio","indicadores","metas","stories"] },
              { label: "Gestão", ids: ["clientes","backup","bling","cupons","caixa","configuracoes"] },
            ];
            return sections.map(sec => {
              const items = navFiltered.filter(n => sec.ids.includes(n.id));
              if (items.length === 0) return null;
              return (
                <div key={sec.label}>
                  {isAdmin && <div style={{ fontSize: 9, color: "#64748b60", letterSpacing: 2, textTransform: "uppercase", padding: "10px 18px 4px" }}>{sec.label}</div>}
                  {items.map(n => (
                    <div key={n.id} style={styles.navItem(page === n.id)} onClick={() => { setPage(n.id); setSidebarOpen(false); }}>
                      <span style={{ fontSize: 14, width: 16, textAlign: "center" }}>{n.icon}</span>
                      <span>{n.label}</span>
                    </div>
                  ))}
                </div>
              );
            });
          })()}
        </nav>
        {logado && logado.status === "trial" && (
          <div style={{ padding: "8px 20px 4px" }}>
            <button onClick={async () => {
              try {
                const r = await fetch("https://gestao-tfx-saas.vercel.app/api/criar-assinatura", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ nome: logado.nome, email: logado.user }) });
                const d = await r.json();
                if (d.init_point) window.location.href = d.init_point;
              } catch(e) { alert("Erro ao gerar link."); }
            }} style={{ display: "block", width: "100%", background: "linear-gradient(135deg, #FFB800, #FFD700)", color: "#080808", fontFamily: "Inter, sans-serif", fontWeight: 700, fontSize: 11, padding: "10px 12px", borderRadius: 10, border: "none", cursor: "pointer", letterSpacing: 1, textAlign: "center" }}>
              ⚡ ASSINAR R$79/MÊS
            </button>
          </div>
        )}
        <div style={{ padding: "8px 20px" }}>
          <button onClick={() => setPrivado(p => !p)} style={{ display: "flex", alignItems: "center", gap: 8, background: privado ? C.accent + "20" : "none", border: `1px solid ${privado ? C.accent : C.border}`, borderRadius: 8, color: privado ? C.accent : C.muted, cursor: "pointer", fontSize: 11, padding: "7px 12px", width: "100%", letterSpacing: 1 }}>
            <span style={{ fontSize: 14 }}>{privado ? "🙈" : "👁️"}</span>
            {privado ? "VALORES OCULTOS" : "OCULTAR VALORES"}
          </button>
        </div>

        {/* Metas + Resumo */}
        {isAdmin && <div style={{ padding: "16px 20px", borderTop: "1px solid #e5e7eb" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
            <div style={{ fontSize: 9, color: "#374151", letterSpacing: 2, textTransform: "uppercase" }}>Metas do Mês</div>
            <button onClick={() => { setMetasForm({ faturamento: metas.faturamento || "", lucro: metas.lucro || "" }); setEditMetas(true); }} style={{ background: "none", border: "none", color: "#374151", cursor: "pointer", fontSize: 11 }}>✏️</button>
          </div>

          {metaFat > 0 && (
            <div style={{ marginBottom: 10 }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                <span style={{ fontSize: 9, color: "#374151" }}>FATURAMENTO</span>
                <span style={{ fontSize: 9, color: faturamento >= metaFat ? C.accent : C.muted }}>{Math.round(faturamento/metaFat*100)}%</span>
              </div>
              <div style={{ background: C.border, borderRadius: 3, height: 5, overflow: "hidden" }}>
                <div style={{ width: `${Math.min(faturamento/metaFat*100, 100)}%`, height: "100%", background: faturamento >= metaFat ? C.accent : C.info, borderRadius: 3, transition: "width 0.6s" }} />
              </div>
              <div style={{ fontSize: 9, color: "#374151", marginTop: 3 }}>{privado ? "•••••• / ••••••" : `${fmt(faturamento)} / ${fmt(metaFat)}`}</div>
            </div>
          )}

          {metaLucro > 0 && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                <span style={{ fontSize: 9, color: "#374151" }}>LUCRO LÍQUIDO</span>
                <span style={{ fontSize: 9, color: lucroLiq >= metaLucro ? C.gold : C.muted }}>{Math.round(lucroLiq/metaLucro*100)}%</span>
              </div>
              <div style={{ background: C.border, borderRadius: 3, height: 5, overflow: "hidden" }}>
                <div style={{ width: `${Math.min(lucroLiq/metaLucro*100, 100)}%`, height: "100%", background: lucroLiq >= metaLucro ? C.gold : C.info, borderRadius: 3, transition: "width 0.6s" }} />
              </div>
              <div style={{ fontSize: 9, color: "#374151", marginTop: 3 }}>{privado ? "•••••• / ••••••" : `${fmt(lucroLiq)} / ${fmt(metaLucro)}`}</div>
            </div>
          )}

          {metaFat === 0 && metaLucro === 0 && (
            <div style={{ fontSize: 10, color: "#374151", marginBottom: 12 }}>Clique em ✏️ para definir metas</div>
          )}

          <div style={{ marginBottom: 8 }}>
            <div style={{ fontSize: 9, color: "#374151", marginBottom: 3 }}>FATURAMENTO</div>
            <div style={{ fontWeight: 700, fontSize: 14, color: C.accent }}>{privado ? "••••••" : fmt(faturamento)}</div>
          </div>
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 9, color: "#374151", marginBottom: 3 }}>GASTOS</div>
            <div style={{ fontWeight: 700, fontSize: 14, color: C.warn }}>{privado ? "••••••" : fmt(gastoTotal)}</div>
          </div>

          {logado && logado.status === "trial" && logado.trial_expira && (() => {
            const dias = Math.ceil((new Date(logado.trial_expira) - new Date()) / (1000 * 60 * 60 * 24));
            if (dias < 0 || dias > 7) return null;
            const cor = dias <= 2 ? "#C0392B" : "#111827";
            return <div style={{ background: cor + "20", border: "1px solid " + cor + "50", borderRadius: 8, padding: "8px 10px", marginBottom: 8, textAlign: "center" }}>
              <div style={{ fontSize: 9, color: cor, letterSpacing: 1, marginBottom: 2 }}>⏳ TRIAL</div>
              <div style={{ fontSize: 20, fontWeight: 800, color: cor }}>{dias}d</div>
              <div style={{ fontSize: 9, color: "#6B6040" }}>{dias === 1 ? "restante" : "restantes"}</div>
            </div>;
          })()}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <div style={{ width: 6, height: 6, borderRadius: "50%", background: C.accent }} />
              <span style={{ fontSize: 9, color: C.accent, letterSpacing: 1 }}>CONECTADO</span>
            </div>
            <button onClick={handleLogout} style={{ background: "none", border: "none", color: "#374151", cursor: "pointer", fontSize: 10 }} title="Sair">Sair →</button>
          </div>
        </div>}
        {!isAdmin && <div style={{ padding: "16px 20px", borderTop: "1px solid #e5e7eb" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <div style={{ width: 6, height: 6, borderRadius: "50%", background: C.accent }} />
              <span style={{ fontSize: 9, color: C.accent, letterSpacing: 1 }}>CONECTADO</span>
            </div>
            <button onClick={handleLogout} style={{ background: "none", border: "none", color: "#374151", cursor: "pointer", fontSize: 10 }} title="Sair">Sair →</button>
          </div>
        </div>}
      </aside>

      {/* Toast de backup diário */}
      {/* Modal onboarding */}
      {showOnboarding && logado && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.6)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div style={{ background: "#ffffff", border: "1px solid #e5e7eb", borderRadius: 24, maxWidth: 520, width: "100%", position: "relative", overflow: "hidden" }}>
            {/* Top gradient */}
            <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 2, background: `linear-gradient(90deg, transparent, ${C.accent}, transparent)` }} />
            {/* Orb decorativo */}
            <div style={{ position: "absolute", top: -60, right: -60, width: 200, height: 200, borderRadius: "50%", background: `radial-gradient(circle, ${C.accent}10 0%, transparent 70%)`, pointerEvents: "none" }} />

            {/* Header */}
            <div style={{ padding: "36px 36px 24px", textAlign: "center", position: "relative" }}>
              <div style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 64, height: 64, borderRadius: 18, background: C.accentDim, border: `1px solid ${C.accent}40`, marginBottom: 16, fontSize: 28 }}>👋</div>
              <div style={{ fontFamily: "Inter, sans-serif", fontWeight: 900, fontSize: 24, color: "#111827", marginBottom: 8, letterSpacing: -0.5 }}>
                Bem-vindo, <span style={{ color: C.accent }}>{logado.nome?.split(" ")[0]}!</span>
              </div>
              <div style={{ fontSize: 14, color: "#374151", lineHeight: 1.7 }}>
                Seu sistema está pronto. Siga os 4 passos abaixo<br/>para extrair o máximo do Gestão TFX.
              </div>
            </div>

            {/* Trial badge */}
            {logado.status === "trial" && logado.trial_expira && (() => {
              const dias = Math.ceil((new Date(logado.trial_expira) - new Date()) / (1000 * 60 * 60 * 24));
              return dias > 0 ? (
                <div style={{ margin: "0 36px 20px", background: C.accentDim, border: `1px solid ${C.accent}40`, borderRadius: 10, padding: "10px 14px", fontSize: 12, color: C.accent, textAlign: "center", fontWeight: 600 }}>
                  ⏳ Trial ativo: <strong>{dias} dias</strong> restantes · Aproveite ao máximo!
                </div>
              ) : null;
            })()}

            {/* Passos */}
            <div style={{ padding: "0 36px 28px", display: "flex", flexDirection: "column", gap: 10 }}>
              {[
                { num: "1", icon: "📦", title: "Cadastre seus produtos", desc: "Adicione ao estoque para registrar vendas com custo e margem.", color: C.accent, page: "estoque" },
                { num: "2", icon: "💰", title: "Registre suas vendas", desc: "Lance canal, produto, valor e custo. O dashboard atualiza na hora.", color: C.gold, page: "vendas" },
                { num: "3", icon: "↘", title: "Lance seus gastos", desc: "Categorize por Imposto, Tráfego, Operacional e Pessoal.", color: C.warn, page: "gastos" },
                { num: "4", icon: "🎯", title: "Defina suas metas", desc: "Configure mínima, mensal, super meta e meta absurda.", color: "#15803d", page: "metas" },
              ].map(s => (
                <div key={s.num} style={{ display: "flex", gap: 14, alignItems: "center", background: C.bgElevated, border: "1px solid #e5e7eb", borderRadius: 16, padding: "12px 16px" }}>
                  <div style={{ background: s.color, color: C.bg, fontFamily: "Inter, sans-serif", fontWeight: 900, fontSize: 12, width: 28, height: 28, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center" }}>{s.num}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontFamily: "Inter, sans-serif", fontWeight: 700, fontSize: 12, color: s.color, marginBottom: 2 }}>{s.icon} {s.title}</div>
                    <div style={{ fontSize: 11, color: "#374151", lineHeight: 1.5 }}>{s.desc}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* Botão */}
            <div style={{ padding: "0 36px 36px" }}>
              <button onClick={fecharOnboarding} style={{ background: C.accent, color: C.bg, fontFamily: "Inter, sans-serif", fontWeight: 700, fontSize: 14, padding: "14px", borderRadius: 16, border: "none", cursor: "pointer", width: "100%", letterSpacing: 1, boxShadow: `0 4px 20px ${C.accent}30` }}>
                ENTENDIDO, VAMOS COMEÇAR →
              </button>
              <div style={{ textAlign: "center", fontSize: 11, color: "#374151", marginTop: 12 }}>
                O checklist de configuração aparecerá no dashboard
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal metas */}
      {editMetas && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.5)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", top: 0, left: 0, right: 0, bottom: 0 }}>
          <div style={{ background: C.card, border: `1px solid ${C.accent}40`, borderRadius: 16, padding: 28, width: 380 }}>
            <div style={{ fontWeight: 700, fontSize: 16, color: C.accent, marginBottom: 28 }}>🎯 Definir Metas do Mês</div>
            <div style={{ marginBottom: 14 }}>
              <label style={styles.label}>Meta de Faturamento (R$)</label>
              <input style={styles.input} type="number" value={metasForm.faturamento} onChange={e => setMetasForm({ ...metasForm, faturamento: e.target.value })} placeholder="Ex: 10000" />
            </div>
            <div style={{ marginBottom: 28 }}>
              <label style={styles.label}>Meta de Lucro Líquido (R$)</label>
              <input style={styles.input} type="number" value={metasForm.lucro} onChange={e => setMetasForm({ ...metasForm, lucro: e.target.value })} placeholder="Ex: 4000" />
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button style={styles.btn("primary")} onClick={() => { const m = { faturamento: metasForm.faturamento, lucro: metasForm.lucro }; setMetas(m); saveMetas(m); setEditMetas(false); }}>Salvar Metas</button>
              <button style={styles.btn("secondary")} onClick={() => setEditMetas(false)}>Cancelar</button>
            </div>
          </div>
        </div>
      )}

      <Toast msg={toast.msg} tipo={toast.tipo} />
      {/* MAIN */}
      <main className="tfx-main" style={{ ...styles.main, padding: "0 32px 32px" }}>
        <div style={{ padding: "14px 0 20px", borderBottom: "1px solid #f3f4f6", marginBottom: 28, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ fontSize: 10, color: "#374151", letterSpacing: 1 }}>
            {new Date().toLocaleDateString("pt-BR", { weekday: "long", day: "2-digit", month: "long", year: "numeric" })}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ fontSize: 10, color: "#374151" }}>{logado?.nome}</div>
            <div style={{ width: 28, height: 28, borderRadius: "50%", background: C.accentDim, border: `1px solid ${C.accent}40`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, color: C.accent }}>
              {logado?.nome?.charAt(0) || "U"}
            </div>
          </div>
        </div>
        <PageComp data={data} setData={setData} setPage={setPage} privado={privado} showToast={showToast} logado={logado} />
      </main>
    </div>

  );
}

export default App;
